import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import DOMPurify from 'dompurify';
import { onAuthStateChanged, signInWithEmailAndPassword, entrarConPase, signOut, auth, solicitarReset, reenviarVerificacion, marcarRol, perfilActual, refrescarPerfil } from '../../utils/auth';
import * as FS from '../../utils/datos';
import { audienciaVigente, estadoAudiencia, audienciaReagendada, nuevoIdAudiencia, ESTADOS_AUDIENCIA, TIPOS_CONTINUACION } from '../../utils/audiencias';
import Foro from './Foro';
import * as PER from '../../utils/periodos';
import Contabilidad from './Contabilidad';
import * as PLZ from '../../utils/plazos';
import type { SeleccionPeriodo } from '../../utils/periodos';
import { CHATBULARIS, PLANES_PANEL } from '../../utils/tarifa';
import RichText from '../RichText/RichText';
import HojasEditor from '../RichText/HojasEditor';
import ModoEscritura from '../RichText/ModoEscritura';
import { textoHojas } from '../RichText/hojas';
import { LogoLockup } from '../Icons';
import ClaveInput from '../ClaveInput';
import { api } from '../../utils/api';
import Soporte from './Soporte';
import LimiteError from './LimiteError';
import { useInstalarApp, AvisoInstalarApp, BotonInstalarApp } from './InstalarApp';
import Bloqueo, { DatosPago, MediosDePago } from '../Bloqueo';
import { usePagoVuelta, ResumenPago, HistorialPagos, pagarSuscripcion } from '../PagosEnLinea';
import jsPDF from 'jspdf';
import ImportarDocumentos from './ImportarDocumentos';
import { driveDisponible, elegirDeDrive } from '../../utils/drive';
import { Aprende, BotonComoSeUsa } from '../Capsulas';
import './PanelApp.css';

/** Minúsculas y sin tildes, para buscar: "José" y "jose" son la misma persona. */
const sinTildes = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/* El mismo botón en la ficha nueva, en el detalle del caso y a página completa.
   `compacto`: solo el ancho del texto, para el pie de la página completa. */
const BotonTransformarIA=({onClick,cargando,vacio,compacto}:{onClick:()=>void;cargando:boolean;vacio:boolean;compacto?:boolean})=>(
  <button type="button" onClick={onClick} disabled={cargando||vacio} style={{marginTop:compacto?0:8,padding:compacto?'8px 18px':'8px 0',width:compacto?'auto':'100%',background:cargando?'rgba(255,255,255,0.04)':'rgba(212,175,55,0.12)',border:'1px solid rgba(212,175,55,0.4)',color:'#fff',borderRadius:6,cursor:cargando?'wait':'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:6,opacity:cargando||vacio?0.5:1,transition:'all 0.2s'}}>
    {cargando?'⏳ Procesando con IA…':'✨ Transformar con IA → Evaluación para cliente'}
  </button>
);

/* ══════════════════════════════════════════════════════════════════════════
   HELPERS & CONSTANTS
   ══════════════════════════════════════════════════════════════════════════ */
const FAMILIA_MATERIAS = ['Alimento','Cuidado personal','Cumplimiento','RdR','Proteccional','VIF','Salida País','Otro'];
const LABORAL_MOTIVOS = [
  'Me despidieron injustificadamente',
  'Me deben cotizaciones o sueldos',
  'Quiero autodespedirme',
  'Sufrí acoso laboral o vulneración de derechos',
  'Sufrí accidente laboral o enfermedad profesional',
  'Tengo problemas con finiquito',
];
const LABORAL_FLUJO = [
  'Admisión','Revisión de documentos','Comparendo','Redacción','Revisión',
  'Ingreso OJV','Preparación minuta','Audiencia Preparatoria',
  'Preparación Audiencia de Juicio','Audiencia de juicio','Pago',
];
const FAMILIA_FLUJO = [
  'Admisión','Revisión de documentos','Comparendo','Redacción','Revisión',
  'Ingreso OJV','Preparación minuta','Audiencia Preparatoria',
  'Preparación Audiencia de Juicio','Audiencia de juicio','Pago',
];
// El cliente que llegó solo por la página. Se anota como captador —no es una
// persona, es el origen— y así se cuenta cuántos entran por la web. El id va en
// minúscula, que es como lo guarda el servidor; el nombre visible es "WEB".
/** El equipo que trabaja el caso por dentro: además de los abogados, el
 *  procurador, que es quien hace la tramitación, y el captador general, que
 *  coordina el estudio entero y abre casos como él. Ven y escriben lo
 *  interno del caso —la evaluación interna, quién lo captó al crearlo— igual
 *  que los abogados; lo que no ven es el dinero (ver vePagos y el filtro del
 *  servidor en lib/acceso.ts). Fuera quedan el captador común, que solo
 *  trabaja los casos que captó, y el abogado externo, que colabora desde
 *  afuera del estudio. */
const ROLES_EQUIPO_CASO = ['admin','abogada','abogado_asociado','procuradora','captadora_general'];
const esEquipoCaso = (rol:any):boolean => ROLES_EQUIPO_CASO.includes(rol);
/** Los dos captadores. El común ve y edita solo los casos que captó y no
 *  abre casos; el general trabaja todos los del estudio como un procurador.
 *  Ninguno ve dinero, usa Chatbularis ni cambia el captador de un caso
 *  existente (ver cambiaCaptador y lib/acceso.ts en el servidor). */
const ROLES_CAPTADOR = ['captadora','captadora_general'];
const esCaptador = (rol:any):boolean => ROLES_CAPTADOR.includes(rol);
/** Quién cambia el captador de un caso ya creado. Decide a quién se paga el
 *  bono, así que el captador general no lo toca aunque edite el resto: es la
 *  misma regla que puedeCambiarCaptador en el servidor. Al CREAR un caso sí
 *  lo elige, como todo el equipo (ver NuevoCaso). */
const cambiaCaptador = (rol:any):boolean => esEquipoCaso(rol) && !esCaptador(rol);

const CAPTADOR_WEB_ID = 'web';
const CAPTADOR_WEB_NOMBRE = 'WEB';
const esCaptadorWeb = (id:any):boolean => typeof id==='string' && id.trim().toLowerCase()===CAPTADOR_WEB_ID;
const SIN_ASIGNAR = '__sin__';
/** "Jueves, 1 de octubre de 2026": mayúscula solo al comienzo. Con el estilo
 *  de mayúscula por palabra salía "1 De Octubre De 2026". */
const fechaLarga = (d:Date):string => { const t=d.toLocaleDateString('es-CL',{weekday:'long',day:'numeric',month:'long',year:'numeric'}); return t.charAt(0).toUpperCase()+t.slice(1); };
/** A quién se le anota un caso en el filtro de captador: los de la página web
 *  (marcados como cliente web o con captador WEB) van todos juntos. */
const claveCaptador = (c:any):string =>
  (c.clienteWeb || esCaptadorWeb(c.captadoraId) || c.captadora === CAPTADOR_WEB_NOMBRE) ? CAPTADOR_WEB_ID : (c.captadoraId || SIN_ASIGNAR);
const MESES_NOMBRES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const TIPOS_AUDIENCIA = ['Audiencia','Comparendo','A.P.','C.A.P','A.J.','C.A.J.','Otro'];
/* Quién es nuestro cliente en el juicio. El plazo para contestar solo corre
   cuando defendemos al demandado, así que sin este dato el aviso no sale. */
const PARTES_REPRESENTADAS = [
  {valor:'demandante', texto:'Demandante'},
  {valor:'demandado',  texto:'Demandado'},
];

/** Un gasto del estudio, con la fecha real en que se hizo. */
// reparto: sueldo o comisión pagada de lo que les toca a abogados,
// administración o captadores. Sale de la cuenta, pero no es gasto de la
// oficina: descontarlo de su parte lo contaría dos veces.
type Gasto = {nombre:string;monto:number;fecha:string;reparto?:boolean};

/* ── Período contable del estudio ────────────────────────────────────────────
   El día en que el estudio cierra el mes. Lo leen reportes, flujo de caja y
   bonos de captadores para que las tres pantallas cuenten lo mismo. */
const useDiaCorte = () => {
  const [diaCorte,setDiaCorte]=useState<number>(PER.DIA_CORTE_DEFAULT);
  const [cargado,setCargado]=useState(false);
  useEffect(()=>{
    let vivo=true;
    FS.loadAjustes()
      .then((a:any)=>{ if(vivo) setDiaCorte(PER.normalizarDiaCorte(a?.periodo?.diaCorte)); })
      .catch(()=>{})
      .finally(()=>{ if(vivo) setCargado(true); });
    return ()=>{vivo=false;};
  },[]);
  return {diaCorte,cargado};
};

/* Selector de período compartido por Reportes y Flujo de Caja.
   Dos formas de elegir, y nada más: el MES CONTABLE del estudio (que con corte
   al 20 va del 21 al 20, no del 1 al 31) o un rango de fechas exactas. Los
   rangos rápidos de "últimos N meses" se sacaron: mezclaban meses calendario
   con meses contables y nadie cierra la caja por trimestres corridos. */
const SelectorPeriodo:React.FC<{
  sel:SeleccionPeriodo; onChange:(s:SeleccionPeriodo)=>void; diaCorte:number;
}>=({sel,onChange,diaCorte})=>{
  const hoy=new Date();
  const actual=PER.periodoDeFecha(hoy,diaCorte)!;
  const selMes=sel.tipo==='periodo'?sel.mes:actual.mes;
  const selAnio=sel.tipo==='periodo'?sel.anio:actual.anio;
  const anios:number[]=[]; for(let y=hoy.getFullYear()+1;y>=hoy.getFullYear()-4;y--) anios.push(y);
  const rango=PER.rangoDeSeleccion(sel,diaCorte,hoy);
  const selStyle:React.CSSProperties={padding:'7px 12px',fontSize:13,background:'#1a2540',border:'1px solid rgba(255,255,255,0.15)',borderRadius:8,color:'#fff',cursor:'pointer',outline:'none',minWidth:120};
  const esMes=sel.tipo==='periodo';
  const borde=esMes?'rgba(100,181,246,0.5)':'rgba(255,255,255,0.15)';
  const hoyISO=PER.aISO(hoy);
  return(
    <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'14px 16px',marginBottom:20}}>
      <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:10}}>Período</div>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <select value={selMes} onChange={e=>onChange({tipo:'periodo',mes:Number(e.target.value),anio:selAnio})}
          style={{...selStyle,borderColor:borde}}>
          {MESES_NOMBRES.map((m,i)=><option key={i} value={i}>{m}</option>)}
        </select>
        <select value={selAnio} onChange={e=>onChange({tipo:'periodo',mes:selMes,anio:Number(e.target.value)})}
          style={{...selStyle,minWidth:90,borderColor:borde}}>
          {anios.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <span style={{color:'var(--text-muted)',fontSize:12,margin:'0 2px'}}>o</span>
        <button className={sel.tipo==='rango'?'p-btn-primary':'p-btn-secondary'}
          onClick={()=>onChange({tipo:'rango',desde:PER.aISO(PER.rangoPeriodo(actual.anio,actual.mes,diaCorte).desde),hasta:hoyISO})}
          style={{padding:'6px 14px',fontSize:12}}>Fechas exactas</button>
      </div>
      {sel.tipo==='rango'&&(
        <div style={{display:'flex',gap:10,alignItems:'center',marginTop:12,flexWrap:'wrap'}}>
          <span style={{fontSize:12,color:'var(--text-muted)'}}>Desde</span>
          <input className="p-input" type="date" value={sel.desde} max={sel.hasta}
            onChange={e=>onChange({...sel,desde:e.target.value})} style={{fontSize:13,padding:'5px 8px',width:150}}/>
          <span style={{fontSize:12,color:'var(--text-muted)'}}>hasta</span>
          <input className="p-input" type="date" value={sel.hasta} min={sel.desde}
            onChange={e=>onChange({...sel,hasta:e.target.value})} style={{fontSize:13,padding:'5px 8px',width:150}}/>
          <span style={{fontSize:11,color:'var(--text-muted)'}}>Ambos días entran completos.</span>
        </div>
      )}
      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:10}}>
        Mostrando: <b style={{color:'var(--white)'}}>{PER.etiquetaSeleccion(sel)}</b>
        <span style={{margin:'0 6px'}}>·</span>
        <b style={{color:'var(--white)'}}>{PER.etiquetaRango(rango)}</b>
        {diaCorte>1&&sel.tipo!=='rango'&&<span> · el estudio cierra el mes el día {diaCorte}</span>}
      </div>
    </div>
  );
};

const formatNotifTime = (ts: any): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} día${days > 1 ? 's' : ''}`;
};

const renderMenciones = (text: string) => {
  const parts = text.split(/(@\S+)/g);
  return parts.map((part, i) =>
    part.startsWith('@')
      ? <span key={i} className="mention-highlight">{part}</span>
      : <Fragment key={i}>{part}</Fragment>
  );
};

const extractPlazo = (txt: string): string | null => {
  const m = txt.match(/@plazo\s+([\d\-/.]+(?:\s+\d{1,2}:\d{2})?)/i);
  if (!m) return null;
  const raw = m[1].trim();
  const nums = raw.replace(/[^\d]/g, '');
  if (nums.length < 6) return null;
  const dd = nums.slice(0, 2).padStart(2, '0');
  const mm = nums.slice(2, 4).padStart(2, '0');
  const yyyy = nums.length >= 8 ? nums.slice(4, 8) : `20${nums.slice(4, 6)}`;
  const d = parseInt(dd), mo = parseInt(mm);
  if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
  const timeM = raw.match(/(\d{1,2}):(\d{2})\s*$/);
  if (timeM) return `${dd}-${mm}-${yyyy} ${timeM[1].padStart(2,'0')}:${timeM[2]}`;
  return `${dd}-${mm}-${yyyy}`;
};

const parsePlazoDate = (plazo: string): Date | null => {
  if (!plazo) return null;
  const m = plazo.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (!m) return null;
  return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]), parseInt(m[4] || '0'), parseInt(m[5] || '0'));
};

const formatRut = (raw: string): string => {
  let clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 2) return clean;
  const dv = clean.slice(-1);
  let body = clean.slice(0, -1);
  let formatted = '';
  while (body.length > 3) {
    formatted = '.' + body.slice(-3) + formatted;
    body = body.slice(0, -3);
  }
  formatted = body + formatted;
  return formatted + '-' + dv;
};

const validatePhone = (digits: string): boolean => {
  const clean = digits.replace(/\D/g, '');
  return clean.length === 8;
};

/* ══════════════════════════════════════════════════════════════════════════
   NOTAS INTERNAS (reusable)
   ══════════════════════════════════════════════════════════════════════════ */
const NotasInternas: React.FC<{casoId:string;field:string;value:string;edita:boolean;onRefresh:()=>void}> = ({casoId,field,value,edita,onRefresh}) => {
  const [val,setVal]=useState(value||'');
  useEffect(()=>setVal(value||''),[value]);
  const save=async()=>{await FS.updateCampo(casoId,field,val);onRefresh();};
  return(
    <div className="p-section" style={{marginTop:20,borderTop:'1px solid rgba(255,255,255,0.06)',paddingTop:16}}>
      <div className="p-section-title">📝 Notas internas</div>
      <textarea className="p-input" rows={3} value={val} onChange={e=>setVal(e.target.value)} onBlur={save} readOnly={!edita} placeholder="Notas internas del equipo para esta sección..." style={{resize:'vertical',fontSize:13}}/>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CASO ROW
   ══════════════════════════════════════════════════════════════════════════ */
const CasoRow = React.memo(({ caso: c, rol, onOpen, onDelete }: {
  caso: any;
  rol: string;
  onOpen: (id: string) => void;
  onDelete: (data: { id: string; nombre: string }) => void;
}) => (
  <div className="caso-row" onClick={() => onOpen(c.id)}>
    <div className="caso-row-left">
      <div className="caso-nombre">{c.nombreCliente || '—'}</div>
      {/* Se unen solo los datos que existen: sin RUT, antes la línea partía
          con un "·" suelto. */}
      <div className="caso-sub">{[c.rut, c.tema || '—', c.abogada || 'Sin abogada asignada', c.rolCaso].filter(Boolean).join(' · ')}</div>
    </div>
    <div className="caso-row-right">
      <span className="caso-badge" style={{ background: (FS.statusColors[c.estado] || '#555') + '22', color: FS.statusColors[c.estado] || '#888', border: `1px solid ${FS.statusColors[c.estado] || '#555'}44` }}>{c.estado || '—'}</span>
      <span className="caso-fecha">{FS.fmtDate(c.createdAt)}</span>
      {rol === 'admin' && <button className="p-btn-danger-sm" onClick={e => { e.stopPropagation(); onDelete({ id: c.id, nombre: c.nombreCliente || 'este caso' }); }} title="Eliminar caso">🗑</button>}
    </div>
  </div>
));

/* ══════════════════════════════════════════════════════════════════════════
   MAIN PANEL APP
   ══════════════════════════════════════════════════════════════════════════ */
/** Abre Chatbularis en otra pestaña.
 *
 *  No es una vista del panel —es una aplicación aparte, con su propia pantalla
 *  completa— y por eso no está en navItems: aquello son vistas, esto es una
 *  salida. Se ve distinto, en dorado y entre separadores, para que la
 *  diferencia se note ANTES de apretarlo: quien espera cambiar de sección y
 *  termina en otra pestaña siente que se equivocó.
 *
 *  Va a nivel de módulo y no dentro del render: definido adentro, React lo
 *  trataría como un componente nuevo en cada pintado y lo desmontaría entero
 *  cada vez. */
const BotonChatbularis: React.FC = () => (
  <button
    className="pnav-btn pnav-btn--chatbularis"
    onClick={() => window.open(window.location.origin + '/asistente', '_blank')}
    title="Redacta escritos con IA. Se abre en otra pestaña."
  >
    <span style={{ fontSize: 14 }}>✍️</span>Chatbularis IA
  </button>
);

const iniciales = (nombre: string) =>
  nombre.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

/** Quién tiene la sesión abierta, arriba a la derecha. Igual que en
 *  Chatbularis: avatar y nombre; al tocarlo, correo, rol y estudio, y las
 *  salidas (Chatbularis, cerrar sesión). */
const MenuUsuario: React.FC<{ nombre: string; email: string; rol: string; estudio: string; verChatbularis: boolean; onSalir: () => void }> = ({ nombre, email, rol, estudio, verChatbularis, onSalir }) => {
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('click', cerrar);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', cerrar); document.removeEventListener('keydown', esc); };
  }, [abierto]);
  const nombreRol = ROLES_USUARIO.find(r => r.value === rol)?.label ?? rol;

  return (
    <div className="panel-usuario" onClick={e => e.stopPropagation()}>
      <button className="panel-usuario-boton" onClick={() => setAbierto(a => !a)} aria-expanded={abierto} title={email}>
        <span className="panel-usuario-avatar" aria-hidden>{iniciales(nombre)}</span>
        <span className="panel-usuario-nombre">{nombre}</span>
        <span className="panel-usuario-flecha" aria-hidden>▾</span>
      </button>
      {abierto && (
        <div className="panel-usuario-menu" role="menu">
          <div className="panel-usuario-quien">
            <strong>{nombre}</strong>
            {email && email !== nombre && <span>{email}</span>}
            <span>{[nombreRol, estudio].filter(Boolean).join(' · ')}</span>
          </div>
          {verChatbularis && <button role="menuitem" onClick={() => { setAbierto(false); window.open(window.location.origin + '/asistente', '_blank'); }}>Abrir Chatbularis IA</button>}
          <button role="menuitem" onClick={() => { setAbierto(false); onSalir(); }}>Cerrar sesión</button>
        </div>
      )}
    </div>
  );
};

const PanelApp: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [view, setView] = useState('casos');
  const [casos, setCasos] = useState<any[]>([]);
  const [currentCaso, setCurrentCaso] = useState<any>(null);
  const [tab, setTab] = useState('datos');
  const [loading, setLoading] = useState(true);
  // ?email= viene del registro recién terminado (el correo ya puesto), y
  // ?ir=asistente dice que después de entrar hay que pasar a Chatbularis:
  // el asistente no tiene ingreso propio, se entra por acá. ?pase= viene de
  // la portada, donde la persona ya escribió correo y contraseña sin decir
  // el estudio: se canjea por la sesión sin volver a pedirle nada.
  const [entradaInicial] = useState(() => {
    try { const p = new URLSearchParams(window.location.search); return { email: p.get('email') || '', ir: p.get('ir') || '', pase: p.get('pase') || '' }; }
    catch { return { email: '', ir: '', pase: '' }; }
  });
  const [entrandoConPase, setEntrandoConPase] = useState(!!entradaInicial.pase);
  const [loginEmail, setLoginEmail] = useState(entradaInicial.email);
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  // Filtros por quién trajo el caso y quién lo lleva. Reemplazan la antigua
  // sección "Ingreso Web": los casos de la página son los del captador WEB.
  const [filtroCaptador, setFiltroCaptador] = useState('');
  const [filtroAbogado, setFiltroAbogado] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{id:string;nombre:string}|null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notifs, setNotifs] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [verifMsg, setVerifMsg] = useState('');

  const handleForgot = async () => {
    setForgotMsg('');
    try { await solicitarReset(loginEmail); } catch {}
    setForgotMsg('Si el correo existe, te enviamos un enlace para restablecer la contraseña.');
  };
  const handleReenviarVerif = async () => {
    setVerifMsg('');
    try { await reenviarVerificacion(); setVerifMsg('Te reenviamos el correo de verificación.'); }
    catch { setVerifMsg('No se pudo reenviar. Intenta más tarde.'); }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // El estudio que solo contrató Chatbularis entra directo al redactor:
        // el panel de causas no es suyo, y cargarlo para que cada petición
        // rebote es mostrarle errores que no puede arreglar.
        if (perfilActual()?.conPanel === false) { window.location.replace('/asistente'); return; }
        // Venía a Chatbularis y ya entró: para allá.
        if (new URLSearchParams(window.location.search).get('ir') === 'asistente') { window.location.replace('/asistente'); return; }
        if (new URLSearchParams(window.location.search).has('email')) window.history.replaceState({}, '', window.location.pathname);
        setUser(u); const ud = await FS.getUserData(u.uid); setUserData(ud);
      }
      else { setUser(null); setUserData(null); }
      setLoading(false);
    });
    // Si el rol cambió desde que entró, el aviso de onAuthStateChanged vuelve
    // a cargar userData con el rol nuevo.
    refrescarPerfil();
    return () => unsub();
  }, []);

  const refreshCasos = useCallback(async () => {
    if (!user || !userData) return;
    setCasos(await FS.loadCasos(userData.rol, user.uid));
  }, [user, userData]);

  // Con la sesión vencida el servidor responde "No autenticado" y el panel
  // vuelve al ingreso; el rechazo no tiene que quedar suelto.
  useEffect(() => { if (user && userData) refreshCasos().catch(() => {}); }, [user, userData, refreshCasos]);

  // Bloqueo por vigencia comercial. El cliente API emite este evento cuando la
  // respuesta trae un motivo (vencido, suspendido, cancelado) y
  // acá se cambia la pantalla completa. "solo_lectura" no bloquea: solo avisa,
  // porque el estudio sí puede seguir consultando y descargando lo suyo.
  const [bloqueo, setBloqueo] = useState<null | { motivo: string; error: string; pago?: DatosPago; lecturaHasta?: string }>(null);
  // Vuelta de Banchile después de pagar la mensualidad (?pago=<id>): el
  // resumen del pago. Si quedó aprobado, el estudio ya está renovado y se
  // levanta el bloqueo, si lo había.
  const [pagoVuelta, cerrarPagoVuelta] = usePagoVuelta(!!user, () => setBloqueo(null));
  const [avisoLectura, setAvisoLectura] = useState('');
  // Los medios de pago vienen en la misma respuesta del 402; se guardan para
  // que el aviso lleve directo a pagar y no a escribir un correo.
  const [pagoLectura, setPagoLectura] = useState<DatosPago>(null);
  useEffect(() => {
    const onBloqueo = (ev: Event) => {
      const d = (ev as CustomEvent).detail || {};
      // Estudio que solo tiene Chatbularis: el panel no existe para él. No es
      // un bloqueo comercial sino la puerta equivocada, así que se lo lleva a
      // la correcta en vez de mostrarle la pantalla de pago.
      if (d.motivo === 'sin_panel') { window.location.replace('/asistente'); return; }
      if (d.motivo === 'solo_lectura') {
        setAvisoLectura(d.error || 'Tu período terminó: puedes consultar y descargar, pero no registrar cambios.');
        setPagoLectura(d.pago ?? null);
      }
      else setBloqueo(d);
    };
    window.addEventListener('tabularis:bloqueo', onBloqueo);
    return () => window.removeEventListener('tabularis:bloqueo', onBloqueo);
  }, []);

  // Sesión terminada por el servidor. El cliente API ya borró el token y el
  // perfil, así que la pantalla de acceso aparece sola; acá solo se explica
  // por qué, que es lo único que faltaba para que la persona sepa qué hacer.
  useEffect(() => {
    const onSesion = (ev: Event) => {
      const d = (ev as CustomEvent).detail || {};
      setLoginError(d.motivo === 'cuenta_inactiva'
        ? (d.error || 'Tu cuenta ya no está activa en este estudio. Habla con el administrador.')
        : 'Tu sesión expiró por seguridad. Vuelve a entrar para seguir trabajando.');
    };
    window.addEventListener('tabularis:sesion', onSesion);
    return () => window.removeEventListener('tabularis:sesion', onSesion);
  }, []);

  // Alguien leyendo el foro público quiso responder: la portada lo mandó acá
  // con ?foro=<hilo>. Se abre esa pantalla en ese hilo, en vez de dejarlo en
  // Casos buscándolo otra vez. El parámetro se lee una sola vez y se limpia de
  // la dirección, para que recargar no vuelva a saltar al foro.
  //
  // ESTOS DOS HOOKS VAN ACÁ Y NO MÁS ABAJO. Más abajo hay varios `return`
  // condicionales —el bloqueo por vigencia y la pantalla de acceso— y un hook
  // después de un return solo se ejecuta a veces: al iniciar sesión React ve
  // más hooks que en el render anterior y rompe el panel con una pantalla en
  // blanco. Ya pasó una vez, en producción.
  const [foroInicial, setForoInicial] = useState<string | undefined>(() => {
    try { return new URLSearchParams(window.location.search).get('foro') || undefined; } catch { return undefined; }
  });
  useEffect(() => {
    if (!foroInicial || !user) return;
    setView('foro');
    window.history.replaceState({}, '', window.location.pathname);
  }, [foroInicial, user]);
  // ?ir=importar viene del correo de bienvenida: el enlace lleva directo a
  // Importar documentos, que es lo primero que conviene hacer en la prueba.
  useEffect(() => {
    if (entradaInicial.ir !== 'importar' || !user) return;
    setView('importar');
    window.history.replaceState({}, '', window.location.pathname);
  }, [entradaInicial.ir, user]);

  useEffect(() => {
    if (!user) return;
    const unsub = FS.subscribeNotificaciones(user.uid, setNotifs);
    try {
      const CLEAN_KEY = 'lastNotifClean_' + user.uid;
      const last = Number(localStorage.getItem(CLEAN_KEY) || 0);
      if (Date.now() - last > 3_600_000) {
        FS.limpiarNotificacionesViejas(user.uid).catch(() => {});
        localStorage.setItem(CLEAN_KEY, String(Date.now()));
      }
    } catch {}
    return () => unsub();
  }, [user]);

  // Resetea toda la navegación cuando cambia el usuario que está autenticado
  useEffect(() => {
    setView('casos');
    setCurrentCaso(null);
    setSearch('');
    setFiltroEstado('');
    setNotifOpen(false);
  }, [user?.uid]);

  const handleLogin = async () => {
    setLoginError('');
    try { await signInWithEmailAndPassword(auth, loginEmail, loginPass); }
    catch (e:any) {
      // El servidor distingue entre clave equivocada, estudio bloqueado,
      // período vencido y demasiados intentos. Antes todo eso se mostraba como
      // "credenciales incorrectas", así que la persona cambiaba la contraseña
      // una y otra vez sin que ese fuera el problema.
      if (e?.status===400) setLoginError('Revisa que el correo esté completo y bien escrito, y que la contraseña no esté vacía.');
      else if (e?.status===401) setLoginError('Correo o contraseña incorrectos. Si nunca definiste una contraseña, usa "¿Olvidaste tu contraseña?".');
      else if (e?.status===429) setLoginError('Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.');
      else if (e?.message) setLoginError(e.message);
      else setLoginError('No pudimos iniciar sesión. Revisa tu conexión e inténtalo de nuevo.');
    }
  };

  // El pase sale de la dirección antes de canjearlo: es de un solo uso y no
  // tiene por qué quedar en el historial. Si falla (venció, ya se usó, falta
  // verificar el correo), la persona queda en el ingreso con el motivo
  // escrito; un bloqueo por vigencia llega por su evento, igual que siempre.
  useEffect(() => {
    if (!entradaInicial.pase) return;
    const q = new URLSearchParams(window.location.search);
    q.delete('pase');
    window.history.replaceState({}, '', window.location.pathname + (q.toString() ? `?${q}` : ''));
    entrarConPase(entradaInicial.pase)
      .catch((e: any) => setLoginError(e?.message || 'No pudimos iniciar sesión. Escribe tu correo y contraseña.'))
      .finally(() => setEntrandoConPase(false));
  }, [entradaInicial.pase]);
  const handleLogout = async () => { await signOut(auth); };

  const openCaso = useCallback(async (id: string) => { const c = await FS.getCaso(id); setCurrentCaso(c); setTab('datos'); setView('detalle'); }, []);

  /** Cada vista parte arriba. El scroll vive en .panel-content, no en la
   *  ventana, y es uno solo para todas las vistas: sin esto, quien bajó en
   *  Configuración y apretaba "Nuevo caso" (o abría un caso desde la lista)
   *  llegaba ya desplazado. La excepción es la lista de casos: al volver se
   *  repone donde estaba, para seguir buscando desde el mismo punto. */
  const contenidoRef = useRef<HTMLDivElement>(null);
  const scrollListaRef = useRef(0);
  const vistaRef = useRef(view);
  vistaRef.current = view;
  const onScrollContenido = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (vistaRef.current === 'casos') scrollListaRef.current = e.currentTarget.scrollTop;
  }, []);
  useLayoutEffect(() => {
    contenidoRef.current?.scrollTo({ top: view === 'casos' ? scrollListaRef.current : 0 });
  }, [view, currentCaso?.id]);
  const refreshCaso = async () => { if (!currentCaso) return; setCurrentCaso(await FS.getCaso(currentCaso.id)); };

  // Busca por nombre, RUT, código interno del caso ("2026-0007", o solo
  // "0007") y código público del cliente ("SVA-Q5VM7A"). Los códigos se
  // comparan sin distinguir mayúsculas: nadie los escribe igual dos veces. Los
  // nombres, además, sin tildes: "jose" encuentra a "José" y "pena" a "Peña".
  const filtered = useMemo(() => {
    const q = sinTildes(search.trim());
    return casos.filter(c =>
      (!q
        || sinTildes(c.nombreCliente||'').includes(q)
        || (c.rut||'').includes(search.trim())
        || (c.codigoCaso||'').toLowerCase().includes(q)
        || (c.codigoPublico||'').toLowerCase().includes(q)) &&
      (!filtroEstado || c.estado === filtroEstado) &&
      (!filtroCaptador || claveCaptador(c) === filtroCaptador) &&
      (!filtroAbogado || (c.abogadaId || SIN_ASIGNAR) === filtroAbogado)
    );
  }, [casos, search, filtroEstado, filtroCaptador, filtroAbogado]);

  // Las opciones salen de los casos que esta persona ve: no ofrece a nadie
  // que no aparezca en su lista. Ordenadas por nombre; los "sin" al final.
  const opcionesCaptador = useMemo(() => {
    const m = new Map<string, string>();
    casos.forEach(c => { const k = claveCaptador(c); if (!m.has(k)) m.set(k, k === CAPTADOR_WEB_ID ? '🌐 Página web' : k === SIN_ASIGNAR ? 'Sin captador' : (c.captadora || 'Sin nombre')); });
    return [...m.entries()].sort((a, b) => (a[0] === SIN_ASIGNAR ? 1 : b[0] === SIN_ASIGNAR ? -1 : a[1].localeCompare(b[1], 'es')));
  }, [casos]);
  const opcionesAbogado = useMemo(() => {
    const m = new Map<string, string>();
    casos.forEach(c => { const k = c.abogadaId || SIN_ASIGNAR; if (!m.has(k)) m.set(k, k === SIN_ASIGNAR ? 'Sin abogado asignado' : (c.abogada || 'Sin nombre')); });
    return [...m.entries()].sort((a, b) => (a[0] === SIN_ASIGNAR ? 1 : b[0] === SIN_ASIGNAR ? -1 : a[1].localeCompare(b[1], 'es')));
  }, [casos]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const instalarApp = useInstalarApp();

  /** Quién edita cualquier caso que ve. El captador común no está: edita
   *  solo los que captó, y eso se decide caso a caso en CasoDetalle. */
  const edita = userData && ['admin','abogada','abogado_asociado','procuradora','captadora_general','abogado_externo'].includes(userData.rol);
  const rol = userData?.rol;

  if (loading || entrandoConPase) return <div className="panel-loading">Cargando...</div>;

  if (user && userData && userData.activo === false) return (
    <div className="panel-overlay" style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,textAlign:'center',padding:32}}>
      <div style={{fontSize:'2.5rem'}}>🔒</div>
      <div style={{fontFamily:'Cinzel,serif',fontSize:'1.1rem',color:'var(--gold)',letterSpacing:'0.05em'}}>Cuenta desvinculada</div>
      <div style={{color:'var(--text-muted)',fontSize:'0.88rem',maxWidth:340}}>Tu cuenta ha sido desvinculada de {userData?.estudio || 'tu estudio'}. Contacta a la administración si crees que esto es un error.</div>
      <button className="p-btn-secondary" onClick={()=>signOut(auth)} style={{marginTop:8}}>Cerrar sesión</button>
    </div>
  );

  if (bloqueo) return (
    <Bloqueo
      motivo={bloqueo.motivo}
      mensaje={bloqueo.error}
      pago={bloqueo.pago ?? null}
      lecturaHasta={bloqueo.lecturaHasta}
      onSalir={() => { signOut(auth); setBloqueo(null); }}
      arriba={pagoVuelta && <ResumenPago v={pagoVuelta} onCerrar={cerrarPagoVuelta} />}
    />
  );

  if (!user) return (
    <div className="panel-overlay">
      <div className="login-box">
        <LogoLockup height={34} />
        <h2>Acceso interno</h2>
        <p>{entradaInicial.ir === 'asistente' ? 'Chatbularis IA' : 'Panel de gestión'}</p>
        <div style={{width:'100%'}}>
          <div className="p-field"><div className="p-field-label">Correo electrónico</div>
            <input className="p-input" type="email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} placeholder="tu@correo.cl" onKeyPress={e=>e.key==='Enter'&&handleLogin()} /></div>
          <div className="p-field"><div className="p-field-label">Contraseña</div>
            <ClaveInput className="p-input" value={loginPass} onChange={e=>setLoginPass(e.target.value)} placeholder="••••••••" onKeyPress={e=>e.key==='Enter'&&handleLogin()} colorOjo="#8FB0E6" /></div>
          <button className="p-btn-primary" onClick={handleLogin} style={{width:'100%',padding:'0.85rem'}}>Ingresar →</button>
          {loginError && <div className="login-error">{loginError}</div>}
          {!forgotOpen && (
            <button type="button" onClick={()=>{setForgotOpen(true);setForgotMsg('');}} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:12,marginTop:12,cursor:'pointer',textDecoration:'underline'}}>¿Olvidaste tu contraseña?</button>
          )}
          {forgotOpen && (
            <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid rgba(255,255,255,0.08)'}}>
              <div className="p-field-label" style={{marginBottom:6}}>Recuperar contraseña</div>
              <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:8}}>Escribe tu correo arriba y te enviamos un enlace.</div>
              <button type="button" className="p-btn-secondary" onClick={handleForgot} style={{fontSize:13}}>Enviar enlace de recuperación</button>
              {forgotMsg && <div style={{fontSize:12,color:'var(--gold)',marginTop:8}}>{forgotMsg}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // El asociado ve el trabajo (casos, calendario, avisos) y nada del dinero:
  // ni el consolidado del estudio —flujo de caja, reportes, ranking de
  // captadores— ni el honorario del caso, que tampoco le llega del servidor.
  const navItems = [
    { id:'casos', icon:'⬛', label:'Casos', roles:['admin','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'] },
    // Solo quienes el servidor deja crear casos (ver casos.ts, POST /casos).
    // El procurador está entre ellos: tramita, así que crea y edita casos.
    // El captador general también: coordina el estudio entero. El común no.
    { id:'nuevo', icon:'＋', label:'Nuevo caso', roles:['admin','abogada','abogado_asociado','procuradora','captadora_general'] },
    { id:'notificaciones', icon:'🔔', label:'Notificaciones', roles:['admin','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'] },
    // El calendario lo ve todo el estudio: cada uno ve las audiencias y tareas
    // de los casos que el servidor le deja ver (la captadora común y el
    // externo, solo los suyos; la captadora general, todos). Va con lo del día
    // a día, arriba, y no entre las secciones de gestión.
    { id:'calendario',  icon:'🗓️', label:'Calendario',   roles:['admin','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'] },
    { id:'usuarios', icon:'👤', label:'Usuarios', roles:['admin','abogada'] },
    { id:'reportes', icon:'📊', label:'Reportes', roles:['admin','abogada'] },
    { id:'flujocaja', icon:'💵', label:'Flujo de Caja', roles:['admin','abogada'] },
    { id:'captadoras',  icon:'🏆', label:'Captadores',   roles:['admin','abogada'] },
    { id:'asistencia',  icon:'🕒', label:'Asistencia',   roles:['admin','abogada'] },
    // Liquidaciones, contratos y anexos de los empleados del estudio. Solo en
    // los estudios donde la consola prendió el módulo (ver contabilidad.ts), y
    // solo para administradores: 'admin' incluye al admin no abogado.
    ...(userData?.modulos?.includes('contabilidad')?[{ id:'contabilidad', icon:'🧾', label:'Contabilidad', roles:['admin'] }]:[]),
    // El foro lo lee todo el estudio: quién puede PUBLICAR lo decide el
    // servidor, no este menú. Por eso van todos los roles.
    { id:'foro', icon:'💬', label:'Foro', roles:['admin','admin_no_abogado','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'] },
    { id:'plantillas',  icon:'🧩', label:'Plantillas',    roles:['admin'] },
    // Traer la carpeta de clientes entera (del computador o de Google Drive)
    // a sus casos. Los mismos roles que crean casos: la importación puede
    // crearlos, y lo que no exista se rechazaría igual en el servidor. Va al
    // final, antes de Configuración: se usa al migrar, no todos los días. No
    // va DENTRO de Configuración porque esa la ve solo el admin.
    { id:'importar', icon:'📥', label:'Importar documentos', roles:['admin','abogada','abogado_asociado','procuradora','captadora_general'] },
    // Las cápsulas de autocapacitación: las ve todo el estudio; cada uno ve
    // primero las de su rol (ver components/Capsulas.tsx).
    { id:'aprende', icon:'🎓', label:'Aprende Tabularis', roles:['admin','admin_no_abogado','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'] },
    { id:'configuracion', icon:'⚙️', label:'Configuración', roles:['admin'] },
  ].filter(i => i.roles.includes(rol));

  const allowedNavIds = navItems.map(i => i.id);

  /** Quiénes ven Chatbularis. Son los mismos roles que exige el servidor; si
   *  acá se ampliara sin ampliarlo allá, el botón abriría una pestaña que
   *  responde 403. */
  const verChatbularis = rol==='admin'||rol==='abogada'||rol==='abogado_asociado'||rol==='procuradora';

  const renderContent = () => {
    if (view === 'detalle' && currentCaso) return <CasoDetalle caso={currentCaso} edita={edita} rol={rol} tab={tab} setTab={setTab} onBack={()=>{setView('casos');refreshCasos();}} onRefresh={refreshCaso} currentUserId={user?.uid||''} currentUserName={userData?.nombre||user?.email||''} estudio={userData?.estudio||''} />;
    if (view === 'nuevo'         && allowedNavIds.includes('nuevo'))         return <NuevoCaso rol={rol} user={user} userData={userData} onBack={()=>{setView('casos');refreshCasos();}} />;
    if (view === 'notificaciones'&& allowedNavIds.includes('notificaciones')) return <NotificacionesView notifs={notifs} uid={user.uid} rol={rol} nombre={userData?.nombre||user?.email||''} onAbrirCaso={async(casoId)=>{ const c=await FS.getCaso(casoId); setCurrentCaso(c); setTab('proceso'); setView('detalle'); }} />;
    if (view === 'importar'      && allowedNavIds.includes('importar'))      return <ImportarDocumentos />;
    if (view === 'usuarios'      && allowedNavIds.includes('usuarios'))      return <Usuarios rol={rol} uid={user?.uid||''} />;
    if (view === 'reportes'      && allowedNavIds.includes('reportes'))      return <Reportes />;
    if (view === 'flujocaja'     && allowedNavIds.includes('flujocaja'))     return <FlujoCaja soloLectura={rol!=='admin'} />;
    if (view === 'captadoras'    && allowedNavIds.includes('captadoras'))    return <Captadoras />;
    if (view === 'contabilidad'  && allowedNavIds.includes('contabilidad'))  return <Contabilidad />;
    if (view === 'asistencia'    && allowedNavIds.includes('asistencia'))    return <Asistencia rol={rol} userId={user?.uid||''} userNombre={userData?.nombre||user?.email||''} />;
    if (view === 'calendario'    && allowedNavIds.includes('calendario'))    return <CalendarioView rol={rol} nombre={userData?.nombre||user?.email||''} uid={user?.uid||''} onAbrirCaso={async(casoId)=>{ const c=await FS.getCaso(casoId); if(c){ setCurrentCaso(c); setTab('proceso'); setView('detalle'); } }} />;
    if (view === 'foro'          && allowedNavIds.includes('foro'))          return <Foro slugInicial={foroInicial} onSlugUsado={() => setForoInicial(undefined)} />;
    if (view === 'plantillas'    && allowedNavIds.includes('plantillas'))    return <PlantillasView />;
    if (view === 'configuracion' && allowedNavIds.includes('configuracion')) return <ConfiguracionView rol={rol} />;
    if (view === 'aprende'       && allowedNavIds.includes('aprende'))       return <Aprende uid={user?.uid||''} rol={rol||''} />;

    const hasDraft=(()=>{try{return !!localStorage.getItem('nuevoCaso_draft_'+user.uid);}catch{return false;}})();
    return (
      <div>
        <div className="casos-toolbar">
          <div className="p-buscador">
            <input
              className="p-input"
              placeholder="🔍  Buscar por nombre, RUT o código (2026-0007)..."
              value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Escape'&&search) { e.preventDefault(); setSearch(''); } }}
            />
            {/* Sólo aparece cuando hay algo que borrar: un botón que no hace nada
                es ruido, y aquí compite por el poco espacio de la barra. */}
            {search&&(
              <button type="button" className="p-buscador-limpiar" onClick={()=>setSearch('')} title="Limpiar búsqueda (Esc)" aria-label="Limpiar búsqueda">✕</button>
            )}
          </div>
          <select className="p-select casos-filtro" value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            {Object.keys(FS.statusColors).map(s=><option key={s}>{s}</option>)}
          </select>
          {opcionesCaptador.length>1&&(
            <select className="p-select casos-filtro" value={filtroCaptador} onChange={e=>setFiltroCaptador(e.target.value)} aria-label="Filtrar por captador">
              <option value="">Todos los captadores</option>
              {opcionesCaptador.map(([k,n])=><option key={k} value={k}>{n}</option>)}
            </select>
          )}
          {opcionesAbogado.length>1&&(
            <select className="p-select casos-filtro" value={filtroAbogado} onChange={e=>setFiltroAbogado(e.target.value)} aria-label="Filtrar por abogado">
              <option value="">Todos los abogados</option>
              {opcionesAbogado.map(([k,n])=><option key={k} value={k}>{n}</option>)}
            </select>
          )}
          {allowedNavIds.includes('nuevo')&&hasDraft&&<button onClick={()=>setView('nuevo')} style={{padding:'8px 14px',background:'rgba(212,175,55,0.15)',border:'1px solid rgba(212,175,55,0.5)',color:'var(--gold)',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,whiteSpace:'nowrap'}}>📋 Recuperar borrador</button>}
          {allowedNavIds.includes('nuevo')&&<button className="p-btn-primary" onClick={()=>setView('nuevo')} style={{padding:'8px 14px'}}>＋ Nuevo caso</button>}
        </div>
        {filtered.length===0 ? <div className="p-empty">{casos.length===0?'No hay casos registrados aún.':'Ningún caso coincide con la búsqueda o los filtros.'}</div> :
          filtered.map(c=>(
            <CasoRow key={c.id} caso={c} rol={rol} onOpen={openCaso} onDelete={setConfirmDelete} />
          ))
        }
        {confirmDelete&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>!deleting&&setConfirmDelete(null)}>
            <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'28px 32px',maxWidth:420,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:32,marginBottom:10}}>🗑️</div>
              <div style={{fontSize:15,color:'var(--cream,#e8e0d0)',marginBottom:8,fontWeight:500}}>Eliminar caso</div>
              <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:20,lineHeight:1.6}}>¿Está seguro que desea eliminar el caso de <strong style={{color:'var(--cream,#e8e0d0)'}}>{confirmDelete.nombre}</strong>? Esta acción no se puede deshacer.</div>
              <div style={{display:'flex',gap:10,justifyContent:'center'}}>
                <button className="p-btn-primary" autoFocus onClick={()=>setConfirmDelete(null)} disabled={deleting}>Cancelar</button>
                <button className="p-btn-danger-sm" style={{padding:'8px 18px',fontSize:13,color:'#C0392B',opacity:deleting?0.6:1,cursor:deleting?'not-allowed':'pointer'}} disabled={deleting} onClick={async()=>{
                  setDeleting(true);
                  try{
                    await FS.deleteCaso(confirmDelete.id);
                    setConfirmDelete(null);
                    refreshCasos();
                  }catch(err:any){
                    alert('No se pudo eliminar el caso: '+(err?.message||'error desconocido'));
                  }finally{
                    setDeleting(false);
                  }
                }}>{deleting?'Eliminando…':'Eliminar'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="panel-fullscreen">
      {userData && userData.emailVerificado === false && (
        <div style={{background:'rgba(212,175,55,0.12)',borderBottom:'1px solid rgba(212,175,55,0.35)',padding:'8px 16px',display:'flex',alignItems:'center',justifyContent:'center',gap:12,flexWrap:'wrap',fontSize:13}}>
          <span style={{color:'var(--gold,#d4af37)'}}>✉️ Verifica tu correo para asegurar el acceso a tu cuenta.</span>
          <button type="button" className="p-btn-secondary" onClick={handleReenviarVerif} style={{fontSize:12,padding:'4px 10px'}}>Reenviar correo</button>
          {verifMsg && <span style={{color:'var(--text-muted)'}}>{verifMsg}</span>}
        </div>
      )}
      {avisoLectura && (
        <div style={{ background: '#FFF7E6', borderBottom: '1px solid #F0DCA8', color: '#6B4E00', padding: '9px 16px', fontSize: 13.5, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span>⚠️ {avisoLectura}</span>
          {pagoLectura?.link
            ? <a href={pagoLectura.link} target="_blank" rel="noopener noreferrer" style={{ color: '#6B4E00', fontWeight: 600 }}>Pagar y renovar</a>
            : <a href="mailto:contacto@tabularis.cl" style={{ color: '#6B4E00', fontWeight: 600 }}>Renovar</a>}
        </div>
      )}
      <AvisoInstalarApp app={instalarApp} />
      <Soporte pantalla={view} />
      <div className="panel-topbar">
        <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
          <button className="panel-hamburger" onClick={()=>setSidebarOpen(o=>!o)} aria-label="Menú">☰</button>
          <LogoLockup height={28} />
          <div className="panel-topbar-title">{userData?.estudio || 'Panel de Gestión'}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
          {view!=='aprende'&&<BotonComoSeUsa key={currentCaso?'caso':view} vista={currentCaso&&view==='casos'?'caso':view} uid={user?.uid||''} />}
          <button className="notif-bell-btn" onClick={()=>setNotifOpen(o=>!o)} title="Notificaciones">
            🔔
            {notifs.filter(n=>!n.leida).length > 0 && (
              <span className="notif-badge">{notifs.filter(n=>!n.leida).length > 99 ? '99+' : notifs.filter(n=>!n.leida).length}</span>
            )}
          </button>
          <MenuUsuario
            nombre={userData?.nombre || user.email || ''}
            email={user.email || ''}
            rol={userData?.rol || ''}
            estudio={userData?.estudio || ''}
            verChatbularis={verChatbularis}
            onSalir={handleLogout}
          />
          <button className="panel-close-x" onClick={()=>window.close()} title="Cerrar (Esc)">✕</button>
        </div>
      </div>
      {notifOpen && (
        <>
          <div className="notif-overlay" onClick={()=>setNotifOpen(false)} />
          <div className="notif-dropdown">
            <div className="notif-dropdown-header">
              <span>Notificaciones</span>
              {notifs.some(n=>!n.leida) && (
                <button className="notif-mark-all" onClick={async()=>{ await FS.marcarTodasLeidas(user.uid); }}>Marcar todas como leídas</button>
              )}
            </div>
            <div className="notif-list">
              {notifs.length === 0 ? (
                <div className="notif-empty">Sin notificaciones nuevas</div>
              ) : notifs.map(n => (
                <div key={n.id} className={`notif-item${n.leida?'':' unread'}${n.realizado?' notif-item-done':''}`} onClick={async()=>{
                  await FS.marcarNotificacionLeida(n.id);
                  setNotifOpen(false);
                  const caso=await FS.getCaso(n.casoId); setCurrentCaso(caso); setTab('proceso'); setView('detalle');
                }}>
                  <div className="notif-item-from">@ {n.deNombre}{n.realizado?' ✓':''}</div>
                  <div className="notif-item-msg">"{n.mensaje}"</div>
                  <div className="notif-item-caso">{n.casoNombre} · {n.codigoCaso}</div>
                  <div className="notif-item-time">{formatNotifTime(n.createdAt)}</div>
                </div>
              ))}
            </div>
            <button
              onClick={()=>{setNotifOpen(false);setView('notificaciones');setSidebarOpen(false);}}
              style={{width:'100%',padding:'10px',background:'rgba(100,181,246,0.06)',border:'none',borderTop:'1px solid rgba(255,255,255,0.07)',color:'#64B5F6',fontSize:'0.75rem',cursor:'pointer',letterSpacing:'0.05em'}}
            >Ver todas las notificaciones →</button>
          </div>
        </>
      )}
      <div className="panel-body">
        {sidebarOpen && <div className="panel-nav-overlay" onClick={()=>setSidebarOpen(false)} />}
        <div className={`panel-nav${sidebarOpen?' open':''}`}>
          {/* En el celular el menú ocupa toda la altura y la barra superior
              queda reducida al logo y la campana: el nombre, el rol y "Cerrar
              sesión" viven acá. En escritorio esta cabecera y el pie no se
              muestran (PanelApp.css). */}
          <div className="panel-nav-cabecera">
            <div className="panel-nav-usuario">
              <strong>{userData?.nombre || user.email}</strong>
              <span>{userData?.rol || ''}{userData?.estudio ? ` · ${userData.estudio}` : ''}</span>
            </div>
            <button className="panel-nav-cerrar" onClick={()=>setSidebarOpen(false)} aria-label="Cerrar menú">✕</button>
          </div>
          {navItems.map(i=>(
            <React.Fragment key={i.id}>
              {/* Chatbularis va ARRIBA del foro y no al final del menú.
                  Antes iba anclado al fondo con marginTop:auto, que en una
                  pantalla baja —o con el zoom subido, que es lo mismo— lo
                  dejaba bajo la línea de flotación: había que desplazar el
                  menú para encontrar la herramienta que más se usa.
                  Se ancla a 'foro' y no a una posición fija porque el menú
                  cambia con el rol; si por lo que sea el foro no estuviera,
                  el respaldo de más abajo lo pone igual. */}
              {i.id==='foro' && verChatbularis && <BotonChatbularis />}
              <button className={`pnav-btn ${view===i.id||(view==='detalle'&&i.id==='casos')?'active':''}`} onClick={()=>{setView(i.id);setCurrentCaso(null);if(i.id==='casos')refreshCasos();setSidebarOpen(false);}}>
                <span style={{fontSize:14}}>{i.icon}</span>{i.label}
              </button>
            </React.Fragment>
          ))}
          {/* Respaldo: si este rol no tiene foro en su menú, el botón igual
              tiene que existir. Un enlace que aparece o no según qué otras
              secciones tengas es un enlace que un día desaparece sin motivo. */}
          {verChatbularis && !allowedNavIds.includes('foro') && <BotonChatbularis />}
          <BotonInstalarApp app={instalarApp} onUsar={()=>setSidebarOpen(false)} />
          <div className="panel-nav-pie">
            <button className="pnav-btn" onClick={handleLogout}><span style={{fontSize:14}}>⏻</span>Cerrar sesión</button>
          </div>
        </div>
        <div className="panel-content" ref={contenidoRef} onScroll={onScrollContenido}>
          {pagoVuelta && <div style={{ marginBottom: 16 }}><ResumenPago v={pagoVuelta} onCerrar={cerrarPagoVuelta} /></div>}
          {/* La clave por vista fuerza a reiniciar el límite al cambiar de
              pantalla: si no, una vista que falló dejaría el error pegado
              aunque el usuario se vaya a otra parte. */}
          <LimiteError key={view} onVolver={()=>{setView('casos');refreshCasos();}}>
            {renderContent()}
          </LimiteError>
        </div>
      </div>
      {/* Barra inferior del celular: lo que se abre a cada rato queda a un
          toque, sin pasar por el menú. Se limita a lo que el rol puede ver;
          "Más" abre el menú completo. En escritorio no se muestra. */}
      <nav className="panel-tabbar" aria-label="Secciones principales">
        {TABBAR_IDS.filter(id=>allowedNavIds.includes(id)).map(id=>{
          const item=navItems.find(i=>i.id===id)!;
          const activo=!sidebarOpen&&(view===id||(view==='detalle'&&id==='casos'));
          const noLeidas=id==='notificaciones'?notifs.filter(n=>!n.leida).length:0;
          return (
            <button key={id} className={`tabbar-btn${activo?' active':''}`} onClick={()=>{setView(id);setCurrentCaso(null);if(id==='casos')refreshCasos();setSidebarOpen(false);}}>
              <span className="tabbar-icono">
                {TABBAR_ICONOS[id]||item.icon}
                {noLeidas>0&&<span className="notif-badge">{noLeidas>99?'99+':noLeidas}</span>}
              </span>
              <span className="tabbar-label">{TABBAR_LABELS[id]||item.label}</span>
            </button>
          );
        })}
        <button className={`tabbar-btn${sidebarOpen?' active':''}`} onClick={()=>setSidebarOpen(o=>!o)}>
          <span className="tabbar-icono">☰</span>
          <span className="tabbar-label">Más</span>
        </button>
      </nav>
    </div>
  );
};

/** Secciones de la barra inferior del celular, en orden. Cuatro como máximo
 *  más "Más": con cinco botones de texto en 390 px ya no cabe nada legible. */
const TABBAR_IDS = ['casos','notificaciones','calendario','nuevo'];
/* El ⬛ de Casos funciona como "lo principal" en el menú claro, pero sobre la
   barra oscura desaparece. Etiquetas cortas por el mismo motivo de espacio. */
const TABBAR_ICONOS: Record<string,string> = { casos:'📁' };
const TABBAR_LABELS: Record<string,string> = { notificaciones:'Avisos', nuevo:'Nuevo' };

/* ══════════════════════════════════════════════════════════════════════════
   CASO DETALLE
   ══════════════════════════════════════════════════════════════════════════ */
/* Campo editable de la ficha del caso.
   Vive FUERA del componente del caso a propósito. Definido dentro, React lo
   trataba como un componente DISTINTO en cada render: el sondeo de
   notificaciones (cada 30 s) desmontaba el input y se perdía lo que se
   estuviera escribiendo, sin que llegara a dispararse el guardado. Era el
   motivo de que cambiar el nombre de un cliente no quedara guardado. */
const PField:React.FC<{label:string;val:string;field?:string;edita?:boolean;onGuardar?:(field:string,valor:string)=>void|Promise<void>}>=({label,val,field,edita,onGuardar})=>{
  const [v,setV]=useState(val||'');
  const [guardado,setGuardado]=useState(false);
  // Se sincroniza solo cuando el valor de origen cambia de verdad, así un
  // refresco de fondo no pisa lo que la persona está escribiendo.
  useEffect(()=>{setV(val||'');},[val]);
  if(!edita||!field||!onGuardar) return (
    <div className="p-field"><div className="p-field-label">{label}</div><div className="p-field-val">{val||'—'}</div></div>
  );
  const guardar=async()=>{
    if((v||'')===(val||''))return;
    await onGuardar(field,v);
    setGuardado(true);setTimeout(()=>setGuardado(false),1800);
  };
  return (
    <div className="p-field">
      <div className="p-field-label">
        {label}
        {guardado&&<span style={{color:'#6aad6a',marginLeft:8,fontSize:11}}>guardado ✓</span>}
      </div>
      <input className="p-input" value={v}
        onChange={e=>setV(e.target.value)}
        onBlur={guardar}
        onKeyDown={e=>{if(e.key==='Enter')(e.target as HTMLInputElement).blur();}}/>
    </div>
  );
};

/* Valor de una cuota, editable en la propia tabla.
   Vive FUERA del componente del caso por lo mismo que PField: definido dentro,
   el sondeo de notificaciones desmonta el input a mitad de escritura.

   Acepta las dos formas de escribir plata —100000 y 100.000 son la misma
   cifra— y separa los miles a medida que se teclea, así la magnitud queda a la
   vista antes de soltar el campo. Un <input type="number"> no sirve acá: ahí
   el navegador lee el punto como coma decimal, y "250.000" se guardaba como
   250. Un caso quedó con las cuatro cuotas divididas por mil.

   Se guarda al salir del campo o con Enter, nunca en cada tecla: escribiendo
   250000 el campo pasaría por 2, 25, 250… y cada uno de ésos es un monto
   válido que quedaría guardado. Escape descarta y deja el valor que estaba.

   Con `enUf` el monto son UF, que sí llevan decimales ("3,5"). */
const ufEnCampo=(n:any)=>Number(n)>0?Number(n).toLocaleString('es-CL',{maximumFractionDigits:4}):'';
const soloUf=(t:string)=>t.replace(/[^0-9.,]/g,'');
const MontoCuota:React.FC<{monto:any;onGuardar:(valor:number)=>Promise<void>;enUf?:boolean}>=({monto,onGuardar,enUf})=>{
  const mostrar=(n:any)=>enUf?ufEnCampo(n):FS.fmtMiles(n);
  const [v,setV]=useState(()=>mostrar(monto));
  const [guardando,setGuardando]=useState(false);
  const [guardado,setGuardado]=useState(false);
  const descartar=useRef(false);
  useEffect(()=>{setV(mostrar(monto));},[monto]); // eslint-disable-line react-hooks/exhaustive-deps
  const guardar=async()=>{
    if(descartar.current){descartar.current=false;setV(mostrar(monto));return;}
    const valor=enUf?FS.parseUf(v):FS.parseMoney(v);
    if(enUf&&!valor){setV(mostrar(monto));return;}
    if(valor===(Number(monto)||0)){setV(mostrar(monto));return;}
    setGuardando(true);
    try{
      await onGuardar(valor);
      setGuardado(true);setTimeout(()=>setGuardado(false),1800);
    }catch{
      setV(mostrar(monto));
      alert('No se pudo guardar el valor de la cuota. Revisa tu conexión e inténtalo de nuevo.');
    }finally{setGuardando(false);}
  };
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
      {!enUf&&<span style={{color:'var(--text-muted)',fontSize:12}}>$</span>}
      <input className="p-input" type="text" inputMode={enUf?'decimal':'numeric'} value={v} disabled={guardando}
        title={enUf?'Cantidad de UF; se puede escribir 3,5 o 3.5':'Se puede escribir 100000 o 100.000'}
        onChange={e=>setV(enUf?soloUf(e.target.value):FS.fmtMiles(e.target.value))}
        onBlur={guardar}
        onKeyDown={e=>{
          if(e.key==='Enter')(e.target as HTMLInputElement).blur();
          if(e.key==='Escape'){descartar.current=true;(e.target as HTMLInputElement).blur();}
        }}
        style={{fontSize:12,padding:'3px 6px',width:enUf?70:100,textAlign:'right'}}/>
      {enUf&&<span style={{color:'var(--text-muted)',fontSize:12}}>UF</span>}
      {guardado&&<span style={{color:'#6aad6a',fontSize:11}}>✓</span>}
    </span>
  );
};

/* Selector de hora en 24 horas, siempre.
   Antes era <input type="time">, que se ve como AM/PM o como 24 horas según el
   idioma del navegador de cada persona: el mismo caso mostraba "1:30 PM" en un
   equipo y "13:30" en otro. En horarios de audiencia esa ambigüedad se paga
   cara, así que se elige de dos listas y no queda a criterio del sistema.
   El valor guardado sigue siendo "HH:MM", igual que antes. */
const HORAS = Array.from({length:24},(_,i)=>String(i).padStart(2,'0'));
const MINUTOS = Array.from({length:12},(_,i)=>String(i*5).padStart(2,'0'));
const SelectorHora:React.FC<{valor:string;onChange:(v:string)=>void}>=({valor,onChange})=>{
  const [h,m]=(valor||'').split(':');
  const sel:React.CSSProperties={flex:1,minWidth:0};
  const cambiar=(hh:string,mm:string)=>onChange(hh?`${hh}:${mm||'00'}`:'');
  return (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <select className="p-select" style={sel} value={h||''} onChange={e=>cambiar(e.target.value,m)} title="Hora">
        <option value="">Hora</option>
        {HORAS.map(x=><option key={x} value={x}>{x}</option>)}
      </select>
      <span style={{color:'var(--text-muted)',fontWeight:600}}>:</span>
      <select className="p-select" style={sel} value={m||''} onChange={e=>cambiar(h,e.target.value)} title="Minutos" disabled={!h}>
        <option value="">min</option>
        {MINUTOS.map(x=><option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
};

/** Qué se puede redactar con Chatbularis desde un caso, según la materia.
 *  Las demandas típicas primero —es lo que más se pide—, y después lo que
 *  sirve en cualquier causa. "Otro" deja escribirlo. */
const QUE_REDACTAR = (tema: string): string[] => {
  const t = (tema || '').toLowerCase();
  const demandas = t.includes('laboral')
    ? ['Demanda por despido injustificado', 'Demanda de tutela laboral', 'Demanda de nulidad del despido', 'Demanda de cobro de prestaciones', 'Demanda por autodespido']
    : t.includes('familia')
      ? ['Demanda de alimentos', 'Demanda de cuidado personal', 'Demanda de relación directa y regular', 'Demanda de divorcio', 'Demanda de compensación económica']
      : ['Demanda'];
  return [...demandas, 'Contestación de demanda', 'Recurso de apelación', 'Recurso de nulidad', 'Recurso de protección', 'Escrito de tramitación', 'Carta o informe al cliente'];
};

/** "Redactar con Chatbularis", en la cabecera del caso.
 *
 *  Pregunta QUÉ se va a redactar antes de abrir el asistente: "redactar" a
 *  secas obliga a explicárselo después en el chat, y saberlo de antemano deja
 *  la primera consulta escrita. El asistente se abre en otra pestaña con el
 *  caso y la tarea, y pide confirmar el caso antes de nada. */
const BotonRedactar: React.FC<{caso:any}> = ({caso}) => {
  const [abierto,setAbierto]=useState(false);
  const [otro,setOtro]=useState('');
  const ir=(tarea:string)=>{
    const t=tarea.trim();
    if(!t)return;
    setAbierto(false); setOtro('');
    window.open(window.location.origin+'/asistente?caso='+encodeURIComponent(caso.id)+'&redactar='+encodeURIComponent(t),'_blank');
  };
  return (
    <div className="caso-redactar">
      <button className="caso-redactar-btn" onClick={()=>setAbierto(a=>!a)} aria-haspopup="menu" aria-expanded={abierto}
        title="Redacta con Chatbularis IA sobre este caso: el asistente recibe la ficha (partes, RUT, hechos, rol, etapa) y puede adjuntar los documentos del caso.">
        <span className="caso-redactar-icono">✍️</span>
        <span>Redactar con Chatbularis</span>
        <span className="caso-redactar-flecha">{abierto?'▴':'▾'}</span>
      </button>
      {abierto&&(
        <>
          <div className="caso-redactar-fondo" onClick={()=>setAbierto(false)}/>
          <div className="caso-redactar-menu" role="menu">
            <div className="caso-redactar-titulo">¿Qué quieres redactar?</div>
            {QUE_REDACTAR(caso.tema).map(op=>(
              <button key={op} role="menuitem" className="caso-redactar-op" onClick={()=>ir(op)}>{op}</button>
            ))}
            <form className="caso-redactar-otro" onSubmit={e=>{e.preventDefault();ir(otro);}}>
              <input className="p-input" placeholder="Otro: escribe qué…" value={otro} onChange={e=>setOtro(e.target.value)} autoFocus/>
              <button type="submit" className="caso-redactar-ir" disabled={!otro.trim()}>Ir</button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

/* Estado de las audiencias: colores de la marca y de la etiqueta. */
const COLOR_ESTADO_AUD:Record<string,string>={
  'Agendada':'#8FB4E8','Realizada':'#6aad6a','Con continuación':'#E0A64A','Suspendida':'#e57373','Reprogramada':'#9BB0CC',
};
const cuandoAud=(a:any)=>`${FS.fmtDate(a?.fecha)||'sin fecha'}${a?.hora?' · '+a.hora:''}`;

/* Suspender, reprogramar o marcar con continuación una audiencia, y agendar
   la nueva fecha. "Reagendar" es agendar después la de una que ya quedó
   suspendida (o la continuación que quedó sin fecha). La nueva audiencia
   copia las características de la original; la original queda como registro
   y, si se suspendió o reprogramó, sale del calendario. */
type ModoAud='Suspendida'|'Reprogramada'|'Con continuación'|'Reagendar';
const CambioAudiencia:React.FC<{aud:any;modo:ModoAud;onCancelar:()=>void;onConfirmar:(cambios:Record<string,any>,nueva?:Record<string,any>)=>Promise<void>}>=({aud,modo,onCancelar,onConfirmar})=>{
  const esContinuacion=modo==='Con continuación'||(modo==='Reagendar'&&estadoAudiencia(aud)==='Con continuación');
  const fechaObligatoria=modo==='Reprogramada'||modo==='Reagendar';
  const [agendar,setAgendar]=useState(true);
  const [motivo,setMotivo]=useState('');
  const [tipoCont,setTipoCont]=useState('Audiencia');const [tipoOtro,setTipoOtro]=useState('');
  const [fecha,setFecha]=useState('');const [hora,setHora]=useState(aud?.hora||'');const [lugar,setLugar]=useState(aud?.lugar||'');
  const [guardando,setGuardando]=useState(false);
  const conFecha=fechaObligatoria||agendar;
  const tipoNuevo=esContinuacion?(tipoCont==='Otro'?tipoOtro.trim():tipoCont):(aud?.tipo||'Audiencia');
  const listo=!guardando&&(!conFecha||(!!fecha&&!!tipoNuevo));
  const titulo={'Suspendida':'Suspender audiencia','Reprogramada':'Reprogramar audiencia','Con continuación':'Audiencia con continuación','Reagendar':esContinuacion?'Agendar la continuación':'Reagendar audiencia'}[modo];
  const boton=modo==='Suspendida'?(agendar?'Suspender y reagendar':'Suspender')
    :modo==='Reprogramada'?'Reprogramar'
    :modo==='Con continuación'?(agendar?'Agendar continuación':'Guardar')
    :'Agendar';
  const confirmar=async()=>{
    if(!listo)return;
    setGuardando(true);
    try{
      const cambios=modo==='Reagendar'?{}:{estado:modo,...(motivo.trim()?{motivo:motivo.trim()}:{})};
      const nueva=conFecha?audienciaReagendada(aud,{tipo:tipoNuevo,fecha,hora,lugar},modo==='Reagendar'?estadoAudiencia(aud):modo):undefined;
      await onConfirmar(cambios,nueva);
    }catch(e:any){alert(e?.message||'No se pudo guardar. Inténtalo de nuevo.');setGuardando(false);}
  };
  const etiqueta:React.CSSProperties={fontSize:11,color:'var(--text-muted)',marginBottom:3};
  return (
    <div className="panel-modal-fondo" onClick={()=>!guardando&&onCancelar()}>
      <div className="panel-modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
        <div className="panel-modal-titulo">{titulo}</div>
        <div style={{fontSize:13,color:'#9BB0CC',marginBottom:14}}>{aud?.tipo||'Audiencia'} del {cuandoAud(aud)}{aud?.lugar?' · '+aud.lugar:''}</div>

        {modo!=='Reagendar'&&(
          <div style={{marginBottom:12}}>
            <div style={etiqueta}>{modo==='Con continuación'?'Nota (opcional)':'Motivo (opcional)'}</div>
            <input className="p-input" value={motivo} onChange={e=>setMotivo(e.target.value)} placeholder={modo==='Suspendida'?'Ej: licencia médica del juez':modo==='Reprogramada'?'Ej: solicitud de la contraparte':'Ej: quedó pendiente la prueba testimonial'}/>
          </div>
        )}

        {(modo==='Suspendida'||modo==='Con continuación')&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:13,color:'#FFFFFF',marginBottom:6}}>{modo==='Suspendida'?'¿Quieres reagendarla con una nueva fecha?':'¿Ya tienes la fecha de la continuación?'}</div>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#C8D8EC',marginBottom:4,cursor:'pointer'}}>
              <input type="radio" checked={agendar} onChange={()=>setAgendar(true)}/> Sí, agendar la nueva fecha
            </label>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#C8D8EC',cursor:'pointer'}}>
              <input type="radio" checked={!agendar} onChange={()=>setAgendar(false)}/> Todavía no hay fecha
            </label>
          </div>
        )}

        {conFecha&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            {esContinuacion&&(
              <div style={{gridColumn:'1 / -1',display:'flex',gap:8}}>
                <div style={{flex:1}}>
                  <div style={etiqueta}>Tipo de continuación</div>
                  <select className="p-select" value={tipoCont} onChange={e=>setTipoCont(e.target.value)}>
                    {TIPOS_CONTINUACION.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {tipoCont==='Otro'&&<div style={{flex:1}}><div style={etiqueta}>¿Cuál?</div><input className="p-input" value={tipoOtro} onChange={e=>setTipoOtro(e.target.value)} placeholder="Especifica"/></div>}
              </div>
            )}
            <div><div style={etiqueta}>Nueva fecha</div><input className="p-input" type="date" value={fecha} onChange={e=>setFecha(e.target.value)}/></div>
            <div><div style={etiqueta}>Hora</div><SelectorHora valor={hora} onChange={setHora}/></div>
            <div style={{gridColumn:'1 / -1'}}><div style={etiqueta}>Lugar</div><input className="p-input" value={lugar} onChange={e=>setLugar(e.target.value)} placeholder="Lugar"/></div>
          </div>
        )}

        <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.5,marginBottom:16}}>
          {modo==='Suspendida'||modo==='Reprogramada'
            ?(conFecha?`La del ${cuandoAud(aud)} se quita del calendario y la nueva fecha se agenda con las mismas características.`:'Se quita del calendario y de los recordatorios al cliente. Podrás reagendarla después desde la lista.')
            :modo==='Con continuación'
              ?(conFecha?'Esta audiencia queda en el calendario como realizada con continuación, y la continuación se agrega al calendario.':'Podrás agendar la continuación después desde la lista.')
              :'La nueva fecha se agrega al calendario con las mismas características.'}
          {' '}Todo queda en el historial de audiencias del caso.
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="p-btn-secondary" onClick={onCancelar} disabled={guardando}>Cancelar</button>
          <button className="p-btn-primary" onClick={confirmar} disabled={!listo} style={{opacity:listo?1:0.5}}>{guardando?'Guardando…':boton}</button>
        </div>
      </div>
    </div>
  );
};

/* Todo lo que pasó con las audiencias del caso, del más reciente al más
   antiguo. Lo escribe el servidor en cada guardado (backend/src/lib/audiencias.ts). */
const HistorialAudiencias:React.FC<{entradas:any[]}>=({entradas})=>{
  const lista=[...entradas].reverse();
  const cuando=(iso:string)=>{const d=new Date(iso);return isNaN(d.getTime())?'':d.toLocaleString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false});};
  const color=(accion:string)=>accion==='eliminada'||accion==='suspendida'?'#e57373':accion==='realizada'?'#6aad6a':accion==='con continuación'||accion==='reagendada'?'#E0A64A':accion==='reprogramada'?'#9BB0CC':'#8FB4E8';
  return (
    <details style={{marginTop:12,background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:8,padding:'8px 12px'}}>
      <summary style={{cursor:'pointer',fontSize:13,color:'#C8D8EC'}}>🕘 Historial de audiencias ({entradas.length})</summary>
      <div style={{marginTop:8}}>
        {lista.map((e:any,i:number)=>(
          <div key={i} style={{padding:'7px 0',borderTop:i?'1px solid rgba(255,255,255,0.05)':'none',fontSize:12.5,lineHeight:1.5}}>
            <div style={{color:'var(--text-muted)',fontSize:11}}>{cuando(e.cuando)}{e.quien?` · ${e.quien}`:''}</div>
            <div style={{color:'#E6EDF7'}}>
              {e.tipo||'Audiencia'} del {cuandoAud(e)}: <strong style={{color:color(e.accion)}}>{e.accion}</strong>
            </div>
            {e.detalle&&<div style={{color:'#9BB0CC'}}>{e.detalle}</div>}
          </div>
        ))}
      </div>
    </details>
  );
};

/** Hoy en la hora del computador, "AAAA-MM-DD". No toISOString(): esa va en
 *  UTC y después de las 21:00 en Chile ya es mañana, así que un pago marcado
 *  la noche del 31 caía en el mes siguiente. */
const hoyLocal=()=>PER.aISO(new Date());
const CasoDetalle: React.FC<{caso:any;edita:boolean;rol:string;tab:string;setTab:(t:string)=>void;onBack:()=>void;onRefresh:()=>void;currentUserId:string;currentUserName:string;estudio:string}> = ({caso:c,edita:editaPorRol,rol,tab,setTab,onBack,onRefresh,currentUserId,currentUserName,estudio}) => {
  /** Quién edita ESTE caso. Lo que decide el rol viene de arriba; el captador
   *  común se suma solo en los casos que captó. Es la misma regla que aplica
   *  el servidor (puedeEditarCaso en lib/acceso.ts); si no coincidieran, el
   *  panel mostraría campos que el servidor rechaza. */
  const edita = editaPorRol || (rol==='captadora' && !!currentUserId && c.captadoraId===currentUserId);
  const [abogadas,setAbogadas]=useState<any[]>([]);
  const [captadoras,setCaptadoras]=useState<any[]>([]);
  const [todosUsuarios,setTodosUsuarios]=useState<any[]>([]);
  /** Si el estudio exige captador, el selector no ofrece quitarlo (el
   *  servidor tampoco lo deja). */
  const [captadorObligatorio,setCaptadorObligatorio]=useState(false);
  useEffect(()=>{
    FS.loadAbogadas().then(setAbogadas).catch(()=>{});
    FS.loadCaptadoras().then(setCaptadoras).catch(()=>{});
    FS.loadAjustes().then((a:any)=>setCaptadorObligatorio(!!a?.casos?.captadorObligatorio)).catch(()=>{});
    FS.loadUsuarios().then(setTodosUsuarios).catch(err=>console.error('[loadUsuarios]',err));
  },[]);


  const [textModal,setTextModal]=useState<{open:boolean;field:string;label:string;value:string}>({open:false,field:'',label:'',value:''});
  const openTextModal=(field:string,label:string,value:string)=>setTextModal({open:true,field,label,value});
  const saveTextModal=async()=>{await handleCampo(textModal.field,textModal.value);setTextModal(m=>({...m,open:false}));onRefresh();};

  const handleCampo=async(field:string,value:any)=>{
    await FS.updateCampo(c.id,field,value);
    onRefresh(); // sin esto el título del caso seguía mostrando el nombre viejo
  };

  // Un caso sin disciplina —los que crea Importar documentos al migrar un
  // estudio— la elige acá. Con ella se cargan las etapas de su plantilla, como
  // al ingresar un caso nuevo, y aparecen los campos y plazos de su materia.
  // Los casos que ya tienen disciplina no la cambian desde la ficha.
  const [plantillasDisc,setPlantillasDisc]=useState<any[]>([]);
  useEffect(()=>{ if(!c.tema) FS.loadPlantillas().then(setPlantillasDisc).catch(()=>{}); },[c.tema]);
  const elegirDisciplina=async(nombre:string)=>{
    if(!nombre) return;
    const pl=plantillasDisc.find((p:any)=>p.nombre===nombre);
    const clave=nombre.includes('Familia')?'familiaFlowSteps':'laborFlowSteps';
    await FS.updateCamposData(c.id,{[clave]:pl?.pasos||[]});
    await FS.updateCampo(c.id,'tema',nombre);
    onRefresh();
  };

  const [evalVal,setEvalVal]=useState(c.evaluacion||c.resumen||'');
  const [notaVal,setNotaVal]=useState(c.notaInterna||'');
  const [evalInternaVal,setEvalInternaVal]=useState(c.evaluacionInterna||'');
  // La hoja abierta se guarda acá y no dentro del editor: así el recuadro chico
  // y la página completa muestran siempre la misma.
  const [hojaEvalInterna,setHojaEvalInterna]=useState(0);
  const [paginaEvalInterna,setPaginaEvalInterna]=useState(false);
  // Al cerrar con Esc el editor desaparece sin pasar por onBlur, así que lo
  // escrito se guarda acá si difiere de lo que ya tiene el caso.
  /** Guarda la evaluación interna una sola vez por cambio. Lo llaman el
   *  blur del editor y el cierre de la página completa, y los dos pueden
   *  dispararse por el mismo clic en "Listo" (el clic quita el foco y
   *  después cierra): se lleva lo último guardado en una referencia, y el
   *  segundo ve que ya está y no repite el PATCH. `c.evaluacionInterna` no
   *  sirve de comparación porque el refresco del caso es asíncrono. */
  const evalInternaGuardada=useRef(c.evaluacionInterna||'');
  useEffect(()=>{evalInternaGuardada.current=c.evaluacionInterna||'';},[c.evaluacionInterna]);
  const guardarEvalInterna=useCallback((v:string)=>{
    if(!edita||v===evalInternaGuardada.current)return;
    evalInternaGuardada.current=v;
    handleCampo('evaluacionInterna',v);
  },[edita]); // eslint-disable-line
  const cerrarPaginaEvalInterna=useCallback(()=>{
    setPaginaEvalInterna(false);
    // Esc cierra sin que el editor pierda el foco (no hay blur): se guarda
    // acá. Con "Listo", el blur ya guardó y esto no repite.
    guardarEvalInterna(evalInternaVal);
  },[evalInternaVal,guardarEvalInterna]);
  const [transformandoDetalle,setTransformandoDetalle]=useState(false);
  // Avisos al cliente. Los manda el servidor: acá solo se dispara y se muestra
  // si salió o no. Antes esto llamaba a una Cloud Function que ya no existe,
  // así que el botón decía "Enviando…" y el cliente no recibía nada.
  const [errCorreoCliente,setErrCorreoCliente]=useState('');
  const [enviandoBienvenida,setEnviandoBienvenida]=useState(false);
  const enviarCorreoBienvenida=async()=>{
    if(!c.correo||enviandoBienvenida) return;
    setEnviandoBienvenida(true);setErrCorreoCliente('');
    try{
      await FS.avisarCliente(c.id,'bienvenida');
      await FS.updateCampo(c.id,'bienvenidaEnviada',new Date().toISOString().slice(0,10));
      onRefresh();
    }catch(e:any){
      console.error('Error enviando correo:',e);
      setErrCorreoCliente(e?.message||'No se pudo enviar el correo de bienvenida.');
    }
    finally{setEnviandoBienvenida(false);}
  };
  const [enviandoNotifEstado,setEnviandoNotifEstado]=useState(false);
  const [notifEstadoEnviada,setNotifEstadoEnviada]=useState(false);
  const notificarEstado=async()=>{
    if(!c.correo||enviandoNotifEstado) return;
    setEnviandoNotifEstado(true);setErrCorreoCliente('');
    try{
      await FS.avisarCliente(c.id,'estado');
      setNotifEstadoEnviada(true);
      setTimeout(()=>setNotifEstadoEnviada(false),4000);
    }catch(e:any){
      console.error('Error notificando estado:',e);
      setErrCorreoCliente(e?.message||'No se pudo enviar el aviso al cliente.');
    }
    finally{setEnviandoNotifEstado(false);}
  };
  useEffect(()=>{setEvalVal(c.evaluacion||c.resumen||'');},[c.evaluacion,c.resumen]);
  useEffect(()=>{setNotaVal(c.notaInterna||'');},[c.notaInterna]);
  useEffect(()=>{setEvalInternaVal(c.evaluacionInterna||'');},[c.evaluacionInterna]);

  const transformarConIADetalle=async()=>{
    const raw=textoHojas(evalInternaVal);
    if(!raw) return;
    setTransformandoDetalle(true);
    try{
      const texto=await FS.redactarEvaluacion(raw,c.tema);
      const resultado=DOMPurify.sanitize('<p>'+texto.replace(/\n\n+/g,'</p><p>').replace(/\n/g,'<br>')+'</p>');
      setEvalVal(resultado);
      await handleCampo('evaluacion',resultado);
    }catch(e:any){console.error('Error IA:',e);alert(e?.status===503?'El redactor con IA aún no está configurado.':(e?.message||'La IA no pudo responder. Intenta de nuevo.'));}
    finally{setTransformandoDetalle(false);}
  };
  const handleEstado=async(estado:string)=>{
    await FS.updateEstado(c.id, estado, c.estado, !!c.fechaAceptado);
    onRefresh();
  };

  const [audF,setAudF]=useState('');const[audH,setAudH]=useState('');const[audL,setAudL]=useState('');const[audT,setAudT]=useState('');
  const [audTOtro,setAudTOtro]=useState('');
  // Cuándo se pidió el comparendo. Es lo que abre la suspensión del plazo del
  // art. 168; la cierra la fecha del comparendo mismo.
  const [audSol,setAudSol]=useState('');
  const [savingAud,setSavingAud]=useState(false);
  // La audiencia se guarda en Tabularis; la sincronización con Google Calendar la
  // resuelve el feed .ics del estudio (sección Calendario), sin evento por evento.
  const addAud=async()=>{
    if(savingAud)return;
    setSavingAud(true);
    const tipoFinal=audT==='Otro'?audTOtro.trim():audT;
    const nuevaAud:any={id:nuevoIdAudiencia(),tipo:tipoFinal,fecha:audF,hora:audH,lugar:audL};
    if(tipoFinal==='Comparendo'&&audSol) nuevaAud.fechaSolicitud=audSol;
    try{ await FS.addAudiencia(c.id,nuevaAud); }
    finally{
      setAudF('');setAudH('');setAudL('');setAudT('');setAudTOtro('');setAudSol('');
      setSavingAud(false); onRefresh();
    }
  };
  /** Corrige la fecha de solicitud de un comparendo ya guardado. Sin esto,
   *  arreglarla obligaba a borrar el comparendo y volver a crearlo. */
  const setSolicitudAud=async(i:number,valor:string)=>{
    await FS.updateAudiencia(c.id,i,{fechaSolicitud:valor});
    onRefresh();
  };
  const delAud=async(i:number)=>{ await FS.removeAudiencia(c.id,i);onRefresh(); };
  // Estado de la audiencia. Agendada y Realizada se guardan de una; las demás
  // abren la ventana que pregunta por la nueva fecha (ver CambioAudiencia).
  const [cambioAud,setCambioAud]=useState<{aud:any;modo:ModoAud}|null>(null);
  const elegirEstadoAud=async(a:any,estado:string)=>{
    if(estado===estadoAudiencia(a))return;
    if(estado==='Agendada'||estado==='Realizada'){
      try{ await FS.cambiarAudiencia(c.id,a.id,{estado}); }
      catch(e:any){ alert(e?.message||'No se pudo guardar el estado.'); }
      onRefresh(); return;
    }
    setCambioAud({aud:a,modo:estado as ModoAud});
  };
  const confirmarCambioAud=async(cambios:Record<string,any>,nueva?:Record<string,any>)=>{
    if(!cambioAud)return;
    await FS.cambiarAudiencia(c.id,cambioAud.aud.id,cambios,nueva);
    setCambioAud(null); onRefresh();
  };

  const [confirmDel,setConfirmDel]=useState<{msg:string;onConfirm:()=>void}|null>(null);

  const [checkTxt,setCheckTxt]=useState('');
  const [savingCheck,setSavingCheck]=useState(false);
  const checkInputRef=useRef<HTMLInputElement>(null);


  const notificarTarea=async(paraId:string,plazo:string|undefined,texto:string,tareaId?:string)=>{
    const notifData=(uid:string)=>({
      para:uid,de:currentUserId,deNombre:currentUserName,
      casoId:c.id,casoNombre:c.nombreCliente||'—',
      codigoCaso:c.codigoCaso||c.id.slice(0,8).toUpperCase(),
      mensaje:texto,plazo,tareaId,
    });
    if(paraId==='__todos__'){
      // "Todos" es literal y transversal: TODO el estudio, sin filtrar por rol,
      // y también quien lo escribió. Que al emisor le llegue no es un descuido:
      // es lo que hace que quede constancia de que el aviso salió, y que él lo
      // vea igual que el resto en su bandeja.
      const vistos=new Set<string>();
      for(const u of todosUsuarios){
        if(!u.id||u.activo===false||vistos.has(u.id))continue;
        vistos.add(u.id);
        await FS.crearNotificacion(notifData(u.id));
      }
      if(!vistos.has(currentUserId)) await FS.crearNotificacion(notifData(currentUserId));
      return;
    }
    if(paraId) await FS.crearNotificacion(notifData(paraId));
  };

  // Destinatario y plazo se eligen, no se escriben. El texto guardado conserva
  // el formato de siempre ("@Nombre @plazo dd-mm-aaaa comentario") para que el
  // calendario, las tareas y las acciones ya creadas sigan funcionando igual.
  const [checkPara,setCheckPara]=useState('');
  const [checkPlazo,setCheckPlazo]=useState('');

  const addCheck=async()=>{
    const comentario=checkTxt.trim();
    if(!comentario||savingCheck)return;
    const persona=checkPara==='__todos__'
      ? 'todos'
      : (todosUsuarios.find(u=>u.id===checkPara)?.nombre||'').split(/\s+/)[0];
    const plazoTxt=checkPlazo?checkPlazo.split('-').reverse().join('-'):''; // aaaa-mm-dd → dd-mm-aaaa
    const txt=[persona?`@${persona}`:'',plazoTxt?`@plazo ${plazoTxt}`:'',comentario].filter(Boolean).join(' ');

    setSavingCheck(true);
    setCheckTxt('');setCheckPara('');setCheckPlazo('');
    try{
      // El mismo identificador va al checklist y a los avisos: es lo que
      // mantiene tachada la acción en el caso, el calendario y las
      // notificaciones al marcarla en cualquiera de los tres.
      const tareaId=await FS.addCheckItem(c.id,txt);
      await notificarTarea(checkPara,plazoTxt||undefined,txt,tareaId);
    }catch(e:any){
      console.error('[addCheck] Error:', e?.code, e?.message);
    }finally{
      setSavingCheck(false);
      onRefresh();
    }
  };
  const toggleCheck=async(i:number,done:boolean)=>{
    await FS.toggleCheckItem(c.id,i,done);
    const item=c.checklist?.[i];
    if(item?.texto) await FS.actualizarRealizadaPorTexto(c.id,item.texto,done,item.id);
    onRefresh();
  };
  const delCheck=async(i:number)=>{
    await FS.removeCheckItem(c.id,i);onRefresh();
  };
  const [editCheckIdx,setEditCheckIdx]=useState<number|null>(null);
  const [editCheckTxt,setEditCheckTxt]=useState('');
  const [editCheckOriginal,setEditCheckOriginal]=useState('');
  const startEditCheck=(i:number,texto:string)=>{setEditCheckIdx(i);setEditCheckTxt(texto);setEditCheckOriginal(texto);};
  const saveEditCheck=async()=>{
    if(editCheckIdx===null)return;
    const txt=editCheckTxt.trim();
    if(txt&&txt!==editCheckOriginal){
      await FS.updateCheckItem(c.id,editCheckIdx,txt);
      await FS.actualizarNotificacionesMensaje(c.id,editCheckOriginal,txt,extractPlazo(txt)||undefined);
    }
    setEditCheckIdx(null);setEditCheckTxt('');setEditCheckOriginal('');onRefresh();
  };

  const [subCheckInputs,setSubCheckInputs]=useState<{[k:string]:string}>({});
  const updateSubChecklist=async(step:string,flowType:string,sub:any[])=>{
    const isFamily=flowType==='familia';
    const flowField=isFamily?'familiaFlow':'laborFlow';
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const updated={...currentFlow,[step]:{...(currentFlow[step]||{}),subChecklist:sub}};
    await FS.updateCampo(c.id,flowField,updated);onRefresh();
  };
  const addSubCheck=async(step:string,flowType:string,txt:string)=>{
    const isFamily=flowType==='familia';
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const sub=[...((currentFlow[step]||{}).subChecklist||[]),{texto:txt,done:false}];
    await updateSubChecklist(step,flowType,sub);
  };
  const toggleSubCheck=async(step:string,flowType:string,i:number,done:boolean)=>{
    const isFamily=flowType==='familia';
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const sub=[...((currentFlow[step]||{}).subChecklist||[])];
    sub[i]={...sub[i],done};
    await updateSubChecklist(step,flowType,sub);
  };
  const delSubCheck=async(step:string,flowType:string,i:number)=>{
    const isFamily=flowType==='familia';
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const sub=((currentFlow[step]||{}).subChecklist||[]).filter((_:any,idx:number)=>idx!==i);
    await updateSubChecklist(step,flowType,sub);
  };

  const laborFlow = c.laborFlow || {};
  const familiaFlow = c.familiaFlow || {};
  const laborSteps: string[] = c.laborFlowSteps || LABORAL_FLUJO;
  const familiaSteps: string[] = c.familiaFlowSteps || FAMILIA_FLUJO;

  const toggleFlowStep=async(step:string,done:boolean,flowType:string='labor')=>{
    const flowField = flowType === 'familia' ? 'familiaFlow' : 'laborFlow';
    const currentFlow = flowType === 'familia' ? familiaFlow : laborFlow;
    const updated={...currentFlow,[step]:{done,instrucciones:currentFlow[step]?.instrucciones||''}};
    await FS.updateCampo(c.id,flowField,updated);
    onRefresh();
  };

  const [editStepIdx,setEditStepIdx]=useState<{flowType:string;idx:number}|null>(null);
  const [editStepTxt,setEditStepTxt]=useState('');
  const startEditStep=(flowType:string,idx:number,name:string)=>{setEditStepIdx({flowType,idx});setEditStepTxt(name);};
  const saveEditStep=async()=>{
    if(!editStepIdx)return;
    const{flowType,idx}=editStepIdx;
    const newName=editStepTxt.trim();
    if(!newName){setEditStepIdx(null);return;}
    const isFamily=flowType==='familia';
    const stepsField=isFamily?'familiaFlowSteps':'laborFlowSteps';
    const flowField=isFamily?'familiaFlow':'laborFlow';
    const currentSteps=isFamily?familiaSteps:laborSteps;
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const oldName=currentSteps[idx];
    if(oldName===newName){setEditStepIdx(null);return;}
    const newSteps=currentSteps.map((s,i)=>i===idx?newName:s);
    const newFlow:{[k:string]:any}={};
    Object.entries(currentFlow).forEach(([k,v])=>newFlow[k===oldName?newName:k]=v);
    await FS.updateCamposData(c.id,{[stepsField]:newSteps,[flowField]:newFlow});
    setEditStepIdx(null);onRefresh();
  };
  const deleteFlowStep=async(flowType:string,idx:number)=>{
    const isFamily=flowType==='familia';
    const stepsField=isFamily?'familiaFlowSteps':'laborFlowSteps';
    const flowField=isFamily?'familiaFlow':'laborFlow';
    const currentSteps=isFamily?familiaSteps:laborSteps;
    const currentFlow=isFamily?familiaFlow:laborFlow;
    const stepName=currentSteps[idx];
    const newSteps=currentSteps.filter((_,i)=>i!==idx);
    const newFlow={...currentFlow};
    delete newFlow[stepName];
    await FS.updateCamposData(c.id,{[stepsField]:newSteps,[flowField]:newFlow});
    onRefresh();
  };

  const [newStepInputs,setNewStepInputs]=useState<{[k:string]:string}>({});
  const addFlowStep=async(flowType:string)=>{
    const key=`newStep_${flowType}`;
    const name=(newStepInputs[key]||'').trim();
    if(!name)return;
    const isFamily=flowType==='familia';
    const stepsField=isFamily?'familiaFlowSteps':'laborFlowSteps';
    const currentSteps=isFamily?familiaSteps:laborSteps;
    const newSteps=[...currentSteps,name];
    await FS.updateCampo(c.id,stepsField,newSteps);
    setNewStepInputs(p=>({...p,[key]:''}));
    onRefresh();
  };

  const [confirmDocDel,setConfirmDocDel]=useState<{id:string;nombre:string}|null>(null);
  const delDoc=async(id:string)=>{await FS.eliminarDocumento(id);setConfirmDocDel(null);onRefresh();};
  // Renombrar un documento: el nombre con que se subió el archivo casi nunca
  // dice de qué se trata ("IMG_4821.jpg", "escaneo (3).pdf").
  const [editDoc,setEditDoc]=useState<{id:string;nombre:string}|null>(null);
  const [guardandoDoc,setGuardandoDoc]=useState(false);
  const guardarNombreDoc=async()=>{
    if(!editDoc)return;
    const nombre=editDoc.nombre.trim();
    // Sin cambios o vacío: se cierra sin llamar al servidor.
    const actual=(c.documentos||[]).find((d:any)=>d.id===editDoc.id);
    if(!nombre||nombre===(actual?.nombre||'')){setEditDoc(null);return;}
    setGuardandoDoc(true);
    try{ await FS.renombrarDocumento(editDoc.id,nombre); setEditDoc(null); onRefresh(); }
    catch(e:any){ alert(e?.message||'No se pudo cambiar el nombre.'); }
    finally{ setGuardandoDoc(false); }
  };
  // Baja el documento con un enlace temporal firmado (directo desde S3). Los
  // que el navegador sabe mostrar se abren en otra pestaña; el resto (Word,
  // Excel) llegan como descarga, y para eso se pincha un <a> en vez de
  // window.open, que el bloqueador de ventanas emergentes suele cortar.
  // "Descargar": baja el archivo directo, sin abrirlo en otra pestaña. El
  // enlace ya viene marcado como descarga, así que basta pincharlo.
  const bajarDoc=async(id:string)=>{
    try{
      const url=await FS.documentoUrl(id,true);
      const a=document.createElement('a');a.href=url;a.rel='noopener';a.style.display='none';
      document.body.appendChild(a);a.click();a.remove();
    }catch(e:any){alert(e?.message||'No se pudo descargar el documento.');}
  };
  const [bajandoTodo,setBajandoTodo]=useState(false);
  // Sin carpeta: todo el caso (con sus carpetas adentro del .zip).
  const bajarTodo=async(carpetaId?:string)=>{
    if(bajandoTodo)return;
    setBajandoTodo(true);
    try{ await FS.descargarDocumentosCaso(c.id,carpetaId); }
    catch(e:any){ alert(e?.message||'No se pudieron descargar los documentos.'); }
    finally{ setBajandoTodo(false); }
  };

  // Carpetas de documentos: opcionales y de un solo nivel. Mientras el caso no
  // tenga ninguna, la lista se ve igual que siempre y solo aparece "+ Carpeta".
  // La pestaña elegida es 'todos', 'sin' (sin carpeta) o el id de una carpeta.
  const carpetas:{id:string;nombre:string}[]=c.carpetas||[];
  const [carpetaSelRaw,setCarpetaSel]=useState<string>('todos');
  // Si la carpeta abierta se borró (aquí o en otra pestaña), se vuelve a Todos.
  const carpetaSel=carpetaSelRaw==='todos'||carpetaSelRaw==='sin'||carpetas.some(k=>k.id===carpetaSelRaw)?carpetaSelRaw:'todos';
  // Lo que se sube con una carpeta abierta queda en esa carpeta.
  const carpetaDestino=carpetaSel!=='todos'&&carpetaSel!=='sin'?carpetaSel:null;
  const docsVisibles=(c.documentos||[]).filter((d:any)=>carpetaSel==='todos'?true:carpetaSel==='sin'?!d.carpetaId:d.carpetaId===carpetaSel);
  const nombreCarpetaDe=(id?:string|null)=>carpetas.find(k=>k.id===id)?.nombre;
  const [nuevaCarpeta,setNuevaCarpeta]=useState<string|null>(null);
  const [editCarpeta,setEditCarpeta]=useState<{id:string;nombre:string}|null>(null);
  const [confirmCarpetaDel,setConfirmCarpetaDel]=useState<{id:string;nombre:string;n:number}|null>(null);
  const [guardandoCarpeta,setGuardandoCarpeta]=useState(false);
  const crearCarpeta=async()=>{
    const nombre=(nuevaCarpeta||'').trim();
    if(!nombre){setNuevaCarpeta(null);return;}
    setGuardandoCarpeta(true);
    try{ const k=await FS.crearCarpeta(c.id,nombre); setNuevaCarpeta(null); setCarpetaSel(k.id); onRefresh(); }
    catch(e:any){ alert(e?.message||'No se pudo crear la carpeta.'); }
    finally{ setGuardandoCarpeta(false); }
  };
  const guardarNombreCarpeta=async()=>{
    if(!editCarpeta)return;
    const nombre=editCarpeta.nombre.trim();
    if(!nombre||nombre===nombreCarpetaDe(editCarpeta.id)){setEditCarpeta(null);return;}
    setGuardandoCarpeta(true);
    try{ await FS.renombrarCarpeta(editCarpeta.id,nombre); setEditCarpeta(null); onRefresh(); }
    catch(e:any){ alert(e?.message||'No se pudo cambiar el nombre.'); }
    finally{ setGuardandoCarpeta(false); }
  };
  const borrarCarpeta=async(id:string)=>{
    setGuardandoCarpeta(true);
    try{ await FS.eliminarCarpeta(id); setConfirmCarpetaDel(null); onRefresh(); }
    catch(e:any){ alert(e?.message||'No se pudo eliminar la carpeta.'); }
    finally{ setGuardandoCarpeta(false); }
  };
  // Selección para mover varios de una vez: así se ordena un caso que ya
  // trae decenas de documentos sin ir uno por uno.
  const [docsMarcados,setDocsMarcados]=useState<string[]>([]);
  // Al cambiar de caso no se arrastran pestaña ni selección del anterior.
  useEffect(()=>{setCarpetaSel('todos');setDocsMarcados([]);setNuevaCarpeta(null);setEditCarpeta(null);},[c.id]);
  const marcarDoc=(id:string)=>setDocsMarcados(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const [moviendo,setMoviendo]=useState(false);
  const moverDocs=async(ids:string[],destino:string)=>{
    if(!ids.length||moviendo)return;
    setMoviendo(true);
    try{ await FS.moverDocumentos(c.id,ids,destino==='sin'?null:destino); setDocsMarcados(p=>p.filter(x=>!ids.includes(x))); onRefresh(); }
    catch(e:any){ alert(e?.message||'No se pudieron mover los documentos.'); }
    finally{ setMoviendo(false); }
  };
  // Arrastrar una fila hasta una pestaña (en el computador). Si la fila
  // arrastrada está marcada, se mueven todas las marcadas.
  const [soltarEn,setSoltarEn]=useState<string|null>(null);
  const propsSoltar=(destino:string)=>edita?{
    onDragOver:(e:React.DragEvent)=>{if(e.dataTransfer.types.includes('text/x-tabularis-doc')){e.preventDefault();setSoltarEn(destino);}},
    onDragLeave:()=>setSoltarEn(v=>v===destino?null:v),
    onDrop:(e:React.DragEvent)=>{
      e.preventDefault();setSoltarEn(null);
      const id=e.dataTransfer.getData('text/x-tabularis-doc');
      if(id) moverDocs(docsMarcados.includes(id)?docsMarcados:[id],destino);
    },
  }:{};
  const descargarDoc=async(id:string,nombre?:string)=>{
    try{
      const url=await FS.documentoUrl(id);
      const ext=(nombre||'').toLowerCase().split('.').pop()||'';
      if(['pdf','png','jpg','jpeg','gif','webp','svg','txt','html','htm'].includes(ext)){
        window.open(url,'_blank','noopener');
      }else{
        const a=document.createElement('a');a.href=url;a.rel='noopener';a.style.display='none';
        document.body.appendChild(a);a.click();a.remove();
      }
    }
    catch(e:any){alert(e?.message||'No se pudo abrir el documento.');}
  };
  // Vista previa de un Word dentro del panel: el servidor lo convierte a HTML
  // y se lee sin salir del caso. No se manda a un visor de Google ni de
  // Microsoft a propósito: son documentos de clientes.
  const [docVista,setDocVista]=useState<{id:string;nombre:string;html:string|null;error:string|null}|null>(null);
  const verDoc=async(id:string,nombre?:string)=>{
    if((nombre||'').toLowerCase().endsWith('.docx')){
      setDocVista({id,nombre:nombre||'Documento',html:null,error:null});
      try{
        const r=await FS.documentoVista(id);
        setDocVista(v=>v&&v.id===id?{...v,html:DOMPurify.sanitize(r.html)}:v);
      }catch(e:any){
        setDocVista(v=>v&&v.id===id?{...v,error:e?.message||'No se pudo abrir el documento.'}:v);
      }
      return;
    }
    await descargarDoc(id,nombre);
  };

  const fileInputRef=useRef<HTMLInputElement>(null);
  const [uploadingDrive,setUploadingDrive]=useState(false);
  const handleFileUpload=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=e.target.files;if(!files||files.length===0)return;
    setUploadingDrive(true);
    try{
      for(const file of Array.from(files)){
        try{ await FS.subirDocumento(c.id,file,file.name,'Archivo adjunto',carpetaDestino); }
        catch(err:any){
          console.error('Error subiendo documento:',err);
          // Se muestra el motivo real: "no se pudo subir" a secas no deja
          // saber si fue la conexión, el tamaño o un rechazo del almacenamiento.
          alert(`No se pudo subir "${file.name}".\n\n${err?.message||'Inténtalo de nuevo.'}`);
        }
      }
      onRefresh();
    }finally{
      setUploadingDrive(false);if(fileInputRef.current)fileInputRef.current.value='';
    }
  };

  // Desde Google Drive: la ventana de tabularis.cl/drive trae los archivos
  // elegidos (ver utils/drive.ts) y se suben igual que un adjunto.
  const desdeDrive=async()=>{
    if(uploadingDrive)return;
    let elegidos:{archivo:File;ruta:string}[]=[];
    try{ elegidos=await elegirDeDrive(); }
    catch(e:any){ alert(e?.message||'No se pudo traer desde Google Drive.'); return; }
    if(!elegidos.length)return;
    setUploadingDrive(true);
    const fallidos:string[]=[];
    try{
      for(const {archivo,ruta} of elegidos){
        const nombre=ruta.slice(-255);
        try{ await FS.subirDocumento(c.id,archivo,nombre,'Desde Google Drive',carpetaDestino); }
        catch(err:any){ fallidos.push(`${nombre}: ${err?.message||'no se pudo subir'}`); }
      }
    }finally{
      setUploadingDrive(false);
      onRefresh();
    }
    if(fallidos.length) alert(`No se pudieron subir ${fallidos.length} de ${elegidos.length}:\n\n${fallidos.slice(0,10).join('\n')}`);
  };

  const cameraInputRef=useRef<HTMLInputElement>(null);
  /** Las fotos que ya están en el teléfono o el PC. Sin `capture`, el celular
   *  abre la galería y deja marcar varias; con `capture` (el de arriba) va
   *  directo a la cámara y no hay forma de llegar a la galería. */
  const galeriaInputRef=useRef<HTMLInputElement>(null);
  const [capturedPhotos,setCapturedPhotos]=useState<{name:string;dataUrl:string;w:number;h:number}[]>([]);
  const [pdfNombre,setPdfNombre]=useState('');
  const handleCameraCapture=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const input=e.target;
    const files=input.files?Array.from(input.files):[];if(files.length===0)return;
    const MAX_PX=1600;
    const compressPhoto=(file:File):Promise<{name:string;dataUrl:string;w:number;h:number}>=>(
      new Promise((resolve,reject)=>{
        const objUrl=URL.createObjectURL(file);
        const img=new Image();
        img.onerror=()=>{URL.revokeObjectURL(objUrl);reject(new Error('No se pudo leer la imagen'));};
        img.onload=()=>{
          URL.revokeObjectURL(objUrl);
          try{
            let w=img.naturalWidth,h=img.naturalHeight;
            if(Math.max(w,h)>MAX_PX){
              if(w>=h){h=Math.round(h*MAX_PX/w);w=MAX_PX;}
              else{w=Math.round(w*MAX_PX/h);h=MAX_PX;}
            }
            const canvas=document.createElement('canvas');
            canvas.width=w;canvas.height=h;
            const ctx=canvas.getContext('2d');
            if(!ctx){reject(new Error('Canvas no disponible'));return;}
            ctx.drawImage(img,0,0,w,h);
            resolve({name:file.name,dataUrl:canvas.toDataURL('image/jpeg',0.72),w,h});
          }catch(err){reject(err);}
        };
        img.src=objUrl;
      })
    );
    // De a una y no todas a la vez: desde la galería llegan veinte o treinta
    // fotos de 12 MP, y decodificarlas juntas agota la memoria del iPhone.
    // Una que no se pueda leer (un HEIC en Chrome del PC, por ejemplo) no
    // bota las demás: se agregan las que sí y se avisa cuáles no.
    const fallidas:string[]=[];
    for(const f of files){
      try{
        const foto=await compressPhoto(f);
        setCapturedPhotos(prev=>[...prev,foto]);
      }catch(err){
        console.error('Error procesando foto:',f.name,err);
        fallidas.push(f.name);
      }
    }
    if(fallidas.length) alert(fallidas.length===files.length
      ?'No se pudo leer la foto. Si es HEIC, conviértela a JPG o súbela desde el celular.'
      :`No se pudieron leer ${fallidas.length} de ${files.length} fotos: ${fallidas.join(', ')}. Si son HEIC, conviértelas a JPG o súbelas desde el celular.`);
    input.value='';
  };
  const removePhoto=(idx:number)=>setCapturedPhotos(prev=>prev.filter((_,i)=>i!==idx));
  /** Cada foto es una página, en el orden de las miniaturas. */
  const moverFoto=(idx:number,paso:-1|1)=>setCapturedPhotos(prev=>{
    const j=idx+paso; if(j<0||j>=prev.length) return prev;
    const n=[...prev]; [n[idx],n[j]]=[n[j],n[idx]]; return n;
  });
  const compileToPdf=async()=>{
    if(capturedPhotos.length===0)return;
    setUploadingDrive(true);
    try{
      const TARGET=5*1024*1024; // 5 MB — límite típico de las plataformas de gobierno
      // Re-codifica una imagen (dataUrl) a una calidad JPEG y escala dadas.
      const reencode=(dataUrl:string,quality:number,scale:number):Promise<{url:string;w:number;h:number}>=>new Promise((res,rej)=>{
        const img=new Image();
        img.onerror=()=>rej(new Error('img'));
        img.onload=()=>{
          const w=Math.max(1,Math.round(img.naturalWidth*scale)),h=Math.max(1,Math.round(img.naturalHeight*scale));
          const cv=document.createElement('canvas');cv.width=w;cv.height=h;
          const cx=cv.getContext('2d'); if(!cx){rej(new Error('canvas'));return;}
          cx.drawImage(img,0,0,w,h);
          res({url:cv.toDataURL('image/jpeg',quality),w,h});
        };
        img.src=dataUrl;
      });
      const buildPdf=async(quality:number,scale:number):Promise<Blob>=>{
        const enc=await Promise.all(capturedPhotos.map(p=>reencode(p.dataUrl,quality,scale)));
        const pdf=new jsPDF({orientation:enc[0].h>=enc[0].w?'portrait':'landscape',unit:'mm',format:'a4'});
        for(let i=0;i<enc.length;i++){
          if(i>0) pdf.addPage('a4',enc[i].h>=enc[i].w?'portrait':'landscape');
          const pageW=pdf.internal.pageSize.getWidth(),pageH=pdf.internal.pageSize.getHeight();
          const{url,w,h}=enc[i]; const ratio=Math.min((pageW-10)/w,(pageH-10)/h);
          pdf.addImage(url,'JPEG',(pageW-w*ratio)/2,(pageH-h*ratio)/2,w*ratio,h*ratio);
        }
        return pdf.output('blob');
      };
      // Baja calidad/escala progresivamente hasta que el PDF quede bajo 5 MB.
      let blob:Blob|null=null;
      for(const [q,s] of [[0.72,1],[0.6,1],[0.5,0.85],[0.42,0.75],[0.35,0.65]] as [number,number][]){
        blob=await buildPdf(q,s);
        if(blob.size<=TARGET) break;
      }
      if(!blob) throw new Error('No se pudo generar el PDF');
      const base=pdfNombre.trim()||`Fotos_${c.codigoCaso||c.id.slice(0,8)}_${new Date().toISOString().slice(0,10)}`;
      const fileName=base.toLowerCase().endsWith('.pdf')?base:`${base}.pdf`;
      const pdfFile=new File([blob],fileName,{type:'application/pdf'});
      await FS.subirDocumento(c.id,pdfFile,fileName,'Fotos compiladas (PDF)',carpetaDestino);
      if(blob.size>TARGET) alert(`El PDF quedó en ${(blob.size/1048576).toFixed(1)} MB (sobre 5 MB). Se subió igual; usá menos fotos para bajarlo.`);
      setCapturedPhotos([]);setPdfNombre('');onRefresh();
    }catch(err:any){
      console.error('Error compilando fotos a PDF:',err);
      alert(`Error al compilar el PDF: ${err?.message||'Intenta de nuevo.'}`);
    }finally{
      setUploadingDrive(false);
    }
  };

  const [pConc,setPConc]=useState('');const[pMon,setPMon]=useState('');const[pFec,setPFec]=useState('');const[pEst,setPEst]=useState('Pendiente');const[pFecPago,setPFecPago]=useState('');const[pNot,setPNot]=useState('');
  const [pForma,setPForma]=useState('Transferencia');const[pVenc,setPVenc]=useState('');
  const [autoCuotas,setAutoCuotas]=useState('');const[autoMonto,setAutoMonto]=useState('');const[autoVencInicio,setAutoVencInicio]=useState('');
  const [confirmGenerar,setConfirmGenerar]=useState(false);
  // Cuotas en pesos o en UF. Las de UF guardan `montoUf` y el servidor les pone
  // el monto en pesos: con la UF de hoy mientras están pendientes y con la del
  // día del pago cuando se pagan (ver backend/src/lib/uf.ts). El monto que se
  // calcula acá es solo un anticipo hasta que el servidor responde.
  const [pMoneda,setPMoneda]=useState<'CLP'|'UF'>('CLP');const[autoMoneda,setAutoMoneda]=useState<'CLP'|'UF'>('CLP');
  const montoDeCuota=(texto:string,moneda:'CLP'|'UF'):{monto:number;montoUf?:number}=>{
    if(moneda==='CLP') return {monto:FS.parseMoney(texto)};
    const montoUf=FS.parseUf(texto);
    return {montoUf,monto:Math.round(montoUf*(ufHoy?.valor||0))};
  };
  const addPago=async()=>{
    const m=montoDeCuota(pMon,pMoneda);
    if(pMoneda==='UF'&&!m.montoUf){alert('Escribe la cantidad de UF de la cuota, por ejemplo 3,5.');return;}
    // Una cuota que se registra ya pagada necesita su fecha de pago: sin ella
    // no entra en ningún período de Reportes ni de Flujo de caja.
    const fechaPago=pEst==='Pagado'?(pFecPago||hoyLocal()):'';
    await FS.addPago(c.id,{concepto:pConc,...m,fecha:pFec,estado:pEst,nota:pNot,formaPago:pForma,vencimiento:pVenc,...(fechaPago?{fechaPago}:{})});setPConc('');setPMon('');setPFec('');setPNot('');setPVenc('');setPFecPago('');onRefresh();
  };
  const [confirmPagoDel,setConfirmPagoDel]=useState<{idx:number;nombre:string}|null>(null);
  const delPago=async(i:number)=>{await FS.removePago(c.id,i);setConfirmPagoDel(null);onRefresh();};
  const ejecutarGenerarCuotas=async()=>{
    const n=parseInt(autoCuotas);const valor=montoDeCuota(autoMonto,autoMoneda);
    if(!n||n<1||!(valor.montoUf||valor.monto))return;
    const startDate=autoVencInicio?new Date(autoVencInicio+'T00:00:00'):new Date();
    const startDay=startDate.getDate();
    const startMonth=startDate.getMonth();
    const startYear=startDate.getFullYear();
    const nuevasCuotas=Array.from({length:n},(_,i)=>{
      const totalMonth=startMonth+i;
      const y=startYear+Math.floor(totalMonth/12);
      const m=totalMonth%12;
      const lastDayOfMonth=new Date(y,m+1,0).getDate();
      const d=Math.min(startDay,lastDayOfMonth);
      const vencStr=`${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      return {concepto:`Cuota ${i+1}`,...valor,fecha:'',estado:'Pendiente',nota:'',formaPago:'',vencimiento:vencStr};
    });
    setAutoCuotas('');setAutoMonto('');setAutoVencInicio('');setConfirmGenerar(false);
    FS.updateCampo(c.id,'pagos',[...(c.pagos||[]),...nuevasCuotas]).then(()=>onRefresh());
  };
  const generarCuotas=()=>{
    if((c.pagos||[]).length>0){setConfirmGenerar(true);}
    else{ejecutarGenerarCuotas();}
  };

  const [confirmBorrarCuotas,setConfirmBorrarCuotas]=useState(false);
  const borrarTodasCuotas=()=>{
    FS.updateCampo(c.id,'pagos',[]).then(()=>onRefresh());
    setConfirmBorrarCuotas(false);
  };

  // El check "visto en el banco" certifica un monto y una fecha: si cambia
  // alguno, o el estado, deja de valer y hay que volver a revisarlo.
  const sinCheckBanco=(p:any)=>{const {enBanco:_e,...resto}=p||{};return resto;};
  const changePagoEstado=async(idx:number,nuevoEstado:string)=>{
    const updated=[...(c.pagos||[])];
    updated[idx]={...sinCheckBanco(updated[idx]),estado:nuevoEstado};
    if(nuevoEstado==='Pagado' && !updated[idx].fechaPago) updated[idx].fechaPago=hoyLocal();
    if(nuevoEstado!=='Pagado') updated[idx].fechaPago='';
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };
  const changePagoMonto=async(idx:number,monto:number)=>{
    const updated=[...(c.pagos||[])];updated[idx]={...sinCheckBanco(updated[idx]),monto};
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };
  const changePagoMontoUf=async(idx:number,montoUf:number)=>{
    const updated=[...(c.pagos||[])];updated[idx]={...sinCheckBanco(updated[idx]),montoUf};
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };
  const changePagoFechaPago=async(idx:number,fecha:string)=>{
    const updated=[...(c.pagos||[])];updated[idx]={...sinCheckBanco(updated[idx]),fechaPago:fecha};
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };
  const changePagoFormaPago=async(idx:number,forma:string)=>{
    const updated=[...(c.pagos||[])];updated[idx]={...updated[idx],formaPago:forma};
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };

  const pagos=c.pagos||[];
  const totalP=pagos.reduce((s:number,p:any)=>s+(Number(p.monto)||0),0);
  const pagado=pagos.filter((p:any)=>p.estado==='Pagado').reduce((s:number,p:any)=>s+(Number(p.monto)||0),0);
  const saldo=totalP-pagado;
  const totalUf=pagos.reduce((s:number,p:any)=>s+(Number(p.montoUf)>0?Number(p.montoUf):0),0);

  // Recordatorio de cuotas por WhatsApp. Abre WhatsApp (la app de empresa en
  // el teléfono, o WhatsApp Web/Escritorio en el computador) con el mensaje
  // ya escrito: la abogada lo revisa y lo manda desde SU número, que es el
  // que el cliente conoce. Existe porque la mayoría de los casos no tiene el
  // correo del cliente cargado, y sin correo el recordatorio automático no
  // tiene a quién escribirle; por eso el mensaje además pide el correo.
  const whatsappCuotas=useMemo(()=>{
    const digitos=String(c.telefono||'').replace(/\D/g,'');
    // "+56 9 1234 5678" → 56912345678; "912345678" → 56912345678.
    const numero=digitos.length===9&&digitos.startsWith('9')?'56'+digitos:digitos;
    if(numero.length<11) return null;
    const hoy=new Date();const hoyStr=`${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
    const venc=(p:any)=>String(p.vencimiento||p.fecha||'').slice(0,10);
    const impagas=pagos.map((p:any,i:number)=>({...p,concepto:/^\d+$/.test(String(p.concepto||'').trim())?`Cuota ${String(p.concepto).trim()}`:(p.concepto||`Cuota ${i+1}`)})).filter((p:any)=>p.estado!=='Pagado'&&Number(p.monto)>0);
    const vencidas=impagas.filter((p:any)=>/^\d{4}-\d{2}-\d{2}$/.test(venc(p))&&venc(p)<=hoyStr).sort((a:any,b:any)=>venc(a).localeCompare(venc(b)));
    const proxima=impagas.filter((p:any)=>/^\d{4}-\d{2}-\d{2}$/.test(venc(p))&&venc(p)>hoyStr).sort((a:any,b:any)=>venc(a).localeCompare(venc(b)))[0];
    if(!vencidas.length&&!proxima) return null;
    const nombre=String(c.nombreCliente||c.cliente||'').trim().split(/\s+/)[0]||'';
    const deuda=vencidas.reduce((s:number,p:any)=>s+Number(p.monto||0),0);
    const lineas=[
      `Hola${nombre?' '+nombre:''}, te escribimos de ${estudio||'tu estudio de abogados'}.`,
      '',
      ...(vencidas.length?[
        vencidas.length===1?'Nos aparece pendiente esta cuota de los honorarios de tu caso:':'Nos aparecen pendientes estas cuotas de los honorarios de tu caso:',
        ...vencidas.map((p:any)=>`• ${p.concepto} · ${FS.fmtMoney(p.monto)} · venció el ${FS.fmtDate(venc(p))}`),
        ...(vencidas.length>1?[`Total pendiente: ${FS.fmtMoney(deuda)}`]:[]),
        '',
        vencidas.length===1?'Si ya la pagaste, cuéntanos y la dejamos al día. Si se te complicó, sin problema: respóndenos y buscamos una fecha que te acomode.':'Si ya pagaste alguna, cuéntanos y la dejamos al día. Si se te complicó, sin problema: respóndenos y buscamos una fecha que te acomode.',
      ]:[
        `Te recordamos que la próxima cuota de los honorarios de tu caso, ${proxima.concepto} (${FS.fmtMoney(proxima.monto)}), vence el ${FS.fmtDate(venc(proxima))}.`,
        '',
        'Si ya la pagaste, no hagas caso a este mensaje.',
      ]),
      ...(vencidas.length&&proxima?[`La próxima cuota (${proxima.concepto}, ${FS.fmtMoney(proxima.monto)}) vence el ${FS.fmtDate(venc(proxima))}.`]:[]),
      '',
      ...(c.correo?[]:['¿Nos compartes tu correo electrónico? Así te recordamos cada cuota el mismo día en que vence y te llegan las novedades de tu caso.','']),
      ...(c.codigoPublico?[`Puedes ver el detalle en https://tabularis.cl/estado con tu código ${c.codigoPublico}.`]:[]),
    ];
    return {url:`https://wa.me/${numero}?text=${encodeURIComponent(lineas.join('\n').replace(/\n{3,}/g,'\n\n').trim())}`,vencidas:vencidas.length,sinCorreo:!c.correo};
  },[c.telefono,c.nombreCliente,c.cliente,c.correo,c.codigoPublico,pagos,estudio]);

  const handleAbogada=async(e:React.ChangeEvent<HTMLSelectElement>)=>{
    const uid=e.target.value;const nombre=e.target.options[e.target.selectedIndex]?.text||'';
    await FS.cambiarAbogada(c.id,uid,nombre);onRefresh();
  };

  const handleCaptadora=async(e:React.ChangeEvent<HTMLSelectElement>)=>{
    const uid=e.target.value;const nombre=e.target.options[e.target.selectedIndex]?.text||'';
    await FS.cambiarCaptadora(c.id,uid,nombre);onRefresh();
  };


  const isLaboral = (c.tema||'').toLowerCase().includes('laboral');

  // Honorarios y cuotas: solo el estudio y sus abogados. Captadores,
  // procuradores y abogados externos trabajan el caso sin ver lo que se cobró
  // ni lo que se debe. El servidor además NO les manda los pagos, así que esto
  // es para que la pantalla no muestre una pestaña vacía, no la barrera real.
  const vePagos = rol==='admin'||rol==='abogada';
  // Si venía parado en Pagos y su rol dejó de verlos, se lo devuelve a Datos:
  // sin esto quedaría mirando una pantalla en blanco sin pestaña que pulsar.
  useEffect(()=>{ if(!vePagos&&tab==='pagos') setTab('datos'); },[vePagos,tab,setTab]);
  // UF del día (SII), para la pestaña Pagos. Se pide al entrar a la pestaña.
  const [ufHoy,setUfHoy]=useState<{fecha:string;valor:number}|null>(null);
  useEffect(()=>{ if(tab!=='pagos'||!vePagos||ufHoy) return; FS.getUf().then(setUfHoy).catch(()=>{}); },[tab,vePagos,ufHoy]);

  // Próximas audiencias/comparendos (de hoy en adelante), para mostrarlas en la
  // cabecera del caso además del calendario.
  const hoy0=new Date();hoy0.setHours(0,0,0,0);
  const proximasAud=((c.audiencias||[]) as any[])
    .filter(audienciaVigente)
    .map(a=>({...a,_d:parseFechaLocal(a?.fecha)}))
    .filter(a=>a._d&&a._d>=hoy0)
    .sort((a,b)=>a._d-b._d)
    .slice(0,3);

  // Plazos legales del caso que todavía importan, para la cabecera: los que
  // corren y los vencidos sin marcar (hasta 30 días después, el mismo corte
  // que Notificaciones), del más urgente al más holgado. Los cumplidos no van
  // acá: siguen en Notificaciones, tachados. Lo pidió svabogadas el
  // 22-sep-2026: el plazo tiene que saltar a la vista al abrir el caso.
  const plazosCabecera=PLZ.plazosDeCasos([c]).filter(p=>!p.realizado);

  return (<>
    <button className="p-btn-back" onClick={onBack}>← Volver a casos</button>
    <div className="caso-header-bar">
      <div>
        <div className="caso-titulo">{c.nombreCliente||'Cliente'}</div>
        <div style={{display:'flex',alignItems:'center',gap:10,marginTop:6,flexWrap:'wrap'}}>
          {(c.codigoCaso||c.id)&&<span title="Código interno del caso (año-correlativo)" style={{fontSize:'0.78rem',fontWeight:700,letterSpacing:'0.12em',padding:'4px 12px',borderRadius:5,background:'rgba(255,255,255,0.05)',color:'#EDF1F7',border:'1px solid rgba(255,255,255,0.18)',fontFamily:'monospace'}}>{c.codigoCaso||c.id.slice(0,8).toUpperCase()}</span>}
          {c.codigoPublico&&<span onClick={()=>{navigator.clipboard?.writeText(c.codigoPublico).catch(()=>{});}} title="Código para que tu cliente consulte el estado de su caso en tabularis.cl/estado (clic para copiar)" style={{fontSize:'0.72rem',fontWeight:600,padding:'4px 10px',borderRadius:5,background:'rgba(255,255,255,0.08)',color:'#F5F8FC',border:'1px solid rgba(255,255,255,0.28)',fontFamily:'monospace',cursor:'pointer'}}>🔗 Cliente: {c.codigoPublico} 📋</span>}
          {c.rolCaso&&<span title="Rol de la causa" style={{fontSize:'0.72rem',fontWeight:600,padding:'4px 10px',borderRadius:5,background:'rgba(255,255,255,0.05)',color:'#E8EDF5',border:'1px solid rgba(255,255,255,0.18)',fontFamily:'monospace'}}>⚖️ Rol: {c.rolCaso}</span>}
          {c.rut&&<span className="caso-sub" style={{display:'inline-flex',alignItems:'center',gap:5}} title="RUT del cliente"><span style={{opacity:0.65}}>RUT:</span><strong style={{fontWeight:600}}>{c.rut}</strong></span>}
          <span className="caso-sub" style={{display:'inline-flex',alignItems:'center',gap:5}} title="Fecha en que se atendió al cliente por primera vez">
            <span style={{opacity:0.65}}>🗓️ Atendido:</span>
            <strong style={{fontWeight:600}}>{FS.fmtDate(c.createdAt)}</strong>
          </span>
        </div>
        {plazosCabecera.length>0&&(
          <div style={{display:'flex',alignItems:'flex-start',gap:8,marginTop:10,flexWrap:'wrap'}}>
            {/* El rótulo ocupa la línea entera: con dos o tres tarjetas, la
                que no cabe se baja y quedaría debajo del rótulo, descuadrada. */}
            <span style={{flexBasis:'100%',fontSize:'0.68rem',color:'var(--text-muted)',letterSpacing:'0.04em'}}>⚖️ PLAZOS LEGALES</span>
            {plazosCabecera.map(p=>{
              const color=COLOR_PLAZO_CABECERA[PLZ.urgencia(p)];
              return (
                <span key={p.id} title={p.computo} style={{display:'inline-flex',flexDirection:'column',gap:2,padding:'6px 12px',borderRadius:6,background:color+'26',border:`1px solid ${color}99`,maxWidth:380}}>
                  {/* Primero lo que urge: los días y la fecha, en color. El
                      título va debajo, en chico y neutro, porque es largo. */}
                  <span style={{fontSize:'0.82rem',fontWeight:700,color,fontVariantNumeric:'tabular-nums'}}>
                    {textoPlazoCabecera(p.dias)}<span style={{fontWeight:500,opacity:0.85}}> · vence {PLZ.fmtDia(p.vence)}</span>
                  </span>
                  <span style={{fontSize:'0.7rem',fontWeight:500,color:'#DCE3EE',opacity:0.9}}>{p.titulo} · {p.norma}</span>
                </span>
              );
            })}
          </div>
        )}
        {proximasAud.length>0&&(
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:8,flexWrap:'wrap'}}>
            <span style={{fontSize:'0.68rem',color:'var(--text-muted)',letterSpacing:'0.04em'}}>PRÓXIMAS FECHAS:</span>
            {proximasAud.map((a:any,i:number)=>(
              <span key={i} title={a.lugar||''} style={{fontSize:'0.72rem',fontWeight:600,padding:'4px 10px',borderRadius:5,background:'rgba(255,255,255,0.05)',color:'#DCE3EE',border:'1px solid rgba(255,255,255,0.16)'}}>
                📅 {a.tipo||'Audiencia'} · {FS.fmtDate(a.fecha)}{a.hora?' · '+a.hora:''}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="caso-acciones" style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8}}>
        {edita?<select className="p-select estado-select" value={c.estado||''} onChange={e=>handleEstado(e.target.value)} style={{borderColor:FS.statusColors[c.estado]||'#888'}}>
          {Object.keys(FS.statusColors).map(s=><option key={s}>{s}</option>)}
        </select>:<span className="caso-badge" style={{background:(FS.statusColors[c.estado]||'#555')+'22',color:FS.statusColors[c.estado]||'#888'}}>{c.estado||'—'}</span>}
        {/* Redactar con Chatbularis SOBRE este caso. Pregunta primero QUÉ se
            va a redactar: el asistente se abre con el caso propuesto y con la
            tarea, pide confirmar el caso, y desde ahí recibe la ficha (partes,
            RUT, hechos, rol, etapa) en cada consulta. Solo para quien usa el
            asistente: los captadores no. */}
        {['admin','abogada','abogado_asociado','procuradora'].includes(rol)&&<BotonRedactar caso={c}/>}
        {/* Los correos al cliente. Se muestran SIEMPRE a quien puede mandarlos,
            con o sin correo del cliente: sin correo van apagados y el título
            dice qué falta. Antes, sin correo no se mostraban, y con correo se
            pintaban con var(--gold) —que hoy es azul marino sobre fondo azul
            marino—, así que en los dos casos parecían no existir (23-sep-2026). */}
        {(edita||esEquipoCaso(rol))&&(
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
            <button className="caso-correo-btn caso-correo-btn--estado" onClick={notificarEstado} disabled={enviandoNotifEstado||!c.estado||!c.correo}
              title={!c.correo?'Agrega el correo del cliente en Contacto para poder avisarle.':'Le avisa el estado actual, las audiencias y comparendos agendados, los documentos que le faltan por entregar y el avance del procedimiento.'}>
              {enviandoNotifEstado?'Enviando…':'📨 Notificar estado al cliente'}
            </button>
            {notifEstadoEnviada&&<span style={{fontSize:'0.65rem',color:'#6aad6a'}}>✓ Notificación enviada</span>}
            {/* Un correo que no sale tiene que decirlo: antes fallaba en silencio. */}
            {errCorreoCliente&&<span style={{fontSize:'0.65rem',color:'#e57373',maxWidth:220,textAlign:'right',lineHeight:1.4}}>{errCorreoCliente}</span>}
            <button className="caso-correo-btn" onClick={enviarCorreoBienvenida} disabled={enviandoBienvenida||!c.correo}
              title={!c.correo?'Agrega el correo del cliente en Contacto para poder escribirle.':'Le manda el enlace y el código para consultar su caso, las etapas del procedimiento, la tabla de cuotas, las audiencias agendadas y los documentos que le faltan.'}>
              {enviandoBienvenida?'Enviando…':'📧 Enviar correo de bienvenida'}
            </button>
            {!c.correo&&<span style={{fontSize:'0.65rem',color:'var(--text-muted)'}}>Sin correo del cliente: agrégalo en Contacto.</span>}
            {c.bienvenidaEnviada&&(
              <span style={{fontSize:'0.65rem',color:'#6aad6a',letterSpacing:'0.05em'}}>
                {/* String() antes de partir: el campo guarda una fecha "YYYY-MM-DD",
                    pero si por lo que sea trae otra cosa (un booleano, un número),
                    llamar .split sobre eso lanza y deja el caso COMPLETO en blanco.
                    Un dato raro debe verse raro, nunca tumbar la pantalla. */}
                ✓ Bienvenida enviada{/^\d{4}-\d{2}-\d{2}/.test(String(c.bienvenidaEnviada))
                  ? ' el '+String(c.bienvenidaEnviada).slice(0,10).split('-').reverse().join('-')
                  : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
    <div className="caso-tabs">
      {(vePagos?['datos','proceso','documentos','pagos']:['datos','proceso','documentos']).map(t=>(
        <button key={t} className={`ctab ${tab===t?'active':''}`} onClick={()=>setTab(t)}>
          {t==='datos'?'📋 Datos':t==='proceso'?'⚖️ Proceso':t==='documentos'?'📁 Documentos':'💰 Pagos'}
        </button>
      ))}
    </div>

    {tab==='datos'&&(
      <div className="p-grid3-datos">
        <div className="p-section">
          <div className="p-section-title">Datos personales</div>
          <PField label="Nombre completo" val={c.nombreCliente} field="nombreCliente" edita={edita} onGuardar={handleCampo}/>
          {edita?<div className="p-field"><div className="p-field-label">RUT</div><input className="p-input" defaultValue={c.rut||''} onChange={e=>{const cl=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);e.target.value=cl.length>=2?formatRut(cl):cl;}} onBlur={e=>{const v=formatRut(e.target.value);e.target.value=v;handleCampo('rut',v);}}/></div>:<div className="p-field"><div className="p-field-label">RUT</div><div className="p-field-val">{c.rut||'—'}</div></div>}
          <PField label="Estado civil" val={c.estadoCivil} field="estadoCivil" edita={edita} onGuardar={handleCampo}/>
          <PField label="Nacionalidad" val={c.nacionalidad} field="nacionalidad" edita={edita} onGuardar={handleCampo}/>
          <PField label="Ocupación" val={c.ocupacion} field="ocupacion" edita={edita} onGuardar={handleCampo}/>
          {isLaboral && <>
            <div className="p-sub-title" style={{marginTop:14}}>Datos laborales</div>
            <PField label="Empleador" val={c.empleador} field="empleador" edita={edita} onGuardar={handleCampo}/>
            {edita?<div className="p-field"><div className="p-field-label">RUT Empleador</div><input className="p-input" defaultValue={c.rutEmpleador||''} onChange={e=>{const cl=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);e.target.value=cl.length>=2?formatRut(cl):cl;}} onBlur={e=>{const v=formatRut(e.target.value);e.target.value=v;handleCampo('rutEmpleador',v);}}/></div>:<div className="p-field"><div className="p-field-label">RUT Empleador</div><div className="p-field-val">{c.rutEmpleador||'—'}</div></div>}
            <PField label="Representante legal" val={c.representanteLegal} field="representanteLegal" edita={edita} onGuardar={handleCampo}/>
            <PField label="Domicilio empleador" val={c.domicilioEmpleador} field="domicilioEmpleador" edita={edita} onGuardar={handleCampo}/>
            {edita?(
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><input className="p-input" type="date" defaultValue={c.fechaDespido||''} onBlur={e=>handleCampo('fechaDespido',e.target.value)}/></div>
            ):(
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><div className="p-field-val">{c.fechaDespido?c.fechaDespido.split('-').reverse().join('-'):'—'}</div></div>
            )}
            {(c.laboralMotivos||[]).length>0 && (
              <div className="p-field">
                <div className="p-field-label">Motivo principal consulta</div>
                <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:4}}>
                  {(c.laboralMotivos||[]).map((m:string)=>(
                    <div key={m} style={{fontSize:13,color:'var(--white)',padding:'4px 10px',background:'rgba(255,255,255,0.04)',borderRadius:5,borderLeft:'3px solid var(--gold)'}}>
                      {m}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>}
        </div>
        <div className="p-section">
          <div className="p-section-title">Contacto</div>
          <PField label="Teléfono / WhatsApp" val={c.telefono} field="telefono" edita={edita} onGuardar={handleCampo}/>
          <PField label="Correo electrónico" val={c.correo} field="correo" edita={edita} onGuardar={handleCampo}/>
          {/* Un correo que rebota se traga todos los avisos en silencio: los
              recordatorios de audiencia salen igual y nadie se entera de que
              el cliente nunca los recibió. */}
          {c.correoRebotado&&(
            <div style={{gridColumn:'1 / -1',background:'rgba(229,115,115,0.10)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:8,padding:'10px 12px',fontSize:12.5,lineHeight:1.55,color:'#e57373',marginTop:-4}}>
              <strong>⚠️ A este correo no le llegan los avisos.</strong>{' '}
              {c.correoRebotado.tipo==='queja'
                ? 'El destinatario marcó nuestros correos como spam, así que dejamos de escribirle.'
                : 'La dirección rebotó como inexistente.'}
              {' '}Corrígela acá y volveremos a intentarlo; mientras tanto, el cliente no recibe los recordatorios de audiencia.
            </div>
          )}
          <PField label="Dirección" val={c.direccion} field="direccion" edita={edita} onGuardar={handleCampo}/>
          <div className="p-section" style={{marginTop:'1.2rem'}}>
            <div className="p-section-title">Caso</div>
            {!c.tema&&edita?(
              <div className="p-field">
                <div className="p-field-label">Disciplina</div>
                <select className="p-select" value="" onChange={e=>elegirDisciplina(e.target.value)}>
                  <option value="">Elegir disciplina…</option>
                  {plantillasDisc.map((p:any)=><option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                </select>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Este caso llegó sin disciplina. Al elegirla se cargan sus etapas.</div>
              </div>
            ):<PField label="Tema" val={c.tema}/>}
            {/* La lista es todo el equipo del estudio, sin mirar el rol: quien
                trajo al cliente puede ser cualquiera. Solo se oculta mientras
                el equipo no haya cargado y el caso no traiga captador. */}
            {(captadoras.length>0||c.captadora)&&(cambiaCaptador(rol)?(
              <div className="p-field">
                <div className="p-field-label">Captador</div>
                {/* El valor va en minúscula porque así lo normaliza el servidor;
                    el texto sigue siendo WEB, que es lo que se guarda como
                    nombre y con lo que cuadran las métricas de Ingreso Web. */}
                <select className="p-select" value={CAPTADOR_WEB_ID===(c.captadoraId||'').toLowerCase()?CAPTADOR_WEB_ID:(c.captadoraId||'')} onChange={handleCaptadora}>
                  {/* Con captador obligatorio, "Sin captador" solo aparece en un
                      caso viejo que no lo tiene, para mostrar cómo está. */}
                  {(!captadorObligatorio||!c.captadoraId)&&<option value="">Sin captador</option>}
                  <option value={CAPTADOR_WEB_ID}>WEB</option>
                  {captadoras.map((a:any)=><option key={a.id} value={a.id}>{a.nombre||a.email}</option>)}
                </select>
              </div>
            ):(
              <PField label="Captador" val={c.captadora}/>
            ))}
            <div className="p-field">
              <div className="p-field-label">Abogado asignado</div>
              {edita?<select className="p-select" value={c.abogadaId||''} onChange={handleAbogada}>
                <option value="">Sin asignar</option>
                {abogadas.map((a:any)=><option key={a.id} value={a.id}>{(a as any).nombre||(a as any).email}</option>)}
              </select>:<div className="p-field-val">{c.abogada||'—'}</div>}
            </div>
            <PField label="Procedimiento" val={c.procedimiento} field="procedimiento" edita={edita} onGuardar={handleCampo}/>
            {/* De qué lado estamos. Sin esto no se puede saber si al estudio le
                corre el plazo para contestar (arts. 452 CT y 58 Ley 19.968). */}
            <div className="p-field">
              <div className="p-field-label">Parte que representamos</div>
              {edita?(
                <select className="p-select" value={c.parteRepresentada||''} onChange={e=>handleCampo('parteRepresentada',e.target.value)}>
                  <option value="">Sin indicar</option>
                  {PARTES_REPRESENTADAS.map(o=><option key={o.valor} value={o.valor}>{o.texto}</option>)}
                </select>
              ):(
                <div className="p-field-val">{PARTES_REPRESENTADAS.find(o=>o.valor===c.parteRepresentada)?.texto||'—'}</div>
              )}
            </div>
            <PField label="Rol" val={c.rolCaso} field="rolCaso" edita={edita} onGuardar={handleCampo}/>
            <div className="p-field">
              <div className="p-field-label">Contraparte</div>
              {edita ? (
                <textarea className="p-input" rows={4} defaultValue={c.contraparte||''} onBlur={e=>handleCampo('contraparte',e.target.value)} style={{resize:'vertical',whiteSpace:'pre-wrap'}}/>
              ) : (
                <div className="p-field-val" style={{whiteSpace:'pre-wrap'}}>{c.contraparte||'—'}</div>
              )}
            </div>
            {(c.tema||'').includes('Familia') && (c.familiaMaterias||[]).length>0 && (
              <div className="p-field">
                <div className="p-field-label">Materia</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:4}}>
                  {(c.familiaMaterias||[]).map((m:string)=>(
                    <span key={m} style={{fontSize:12,color:'var(--white)',padding:'3px 10px',background:'rgba(255,255,255,0.06)',borderRadius:12,border:'1px solid rgba(255,255,255,0.1)'}}>
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-section">
          <div className="p-section-title">Evaluación</div>

          {esEquipoCaso(rol)&&(
            <div className="p-field">
              <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>Evaluación interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Borrador rápido · Solo equipo</span></span>
                <button type="button" onClick={()=>setPaginaEvalInterna(true)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}}>⛶ Página completa</button>
              </div>
              <HojasEditor papel value={evalInternaVal} onChange={setEvalInternaVal} onBlur={guardarEvalInterna} readOnly={!edita} placeholder="Borrador rápido de evaluación — Solo visible para el equipo" minHeight={120} activa={hojaEvalInterna} onActiva={setHojaEvalInterna}/>
              {edita&&<BotonTransformarIA onClick={transformarConIADetalle} cargando={transformandoDetalle} vacio={!textoHojas(evalInternaVal)}/>}
              {paginaEvalInterna&&(
                <ModoEscritura titulo="Evaluación interna" subtitulo={`Borrador rápido · Solo equipo · ${c.nombreCliente||'Cliente'}`} nota={edita?'Se guarda solo al salir de la hoja':undefined} onCerrar={cerrarPaginaEvalInterna}
                  pie={edita?<BotonTransformarIA compacto onClick={transformarConIADetalle} cargando={transformandoDetalle} vacio={!textoHojas(evalInternaVal)}/>:undefined}>
                  <HojasEditor papel value={evalInternaVal} onChange={setEvalInternaVal} onBlur={guardarEvalInterna} readOnly={!edita} placeholder={edita?'Anote aquí de forma libre y rápida los antecedentes del caso.':'(sin contenido)'} minHeight="calc(100vh - 240px)" activa={hojaEvalInterna} onActiva={setHojaEvalInterna}/>
                </ModoEscritura>
              )}
            </div>
          )}

          <div className="p-field">
            <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>Evaluación (visible para cliente)</span>
              {edita&&<button onClick={()=>openTextModal('evaluacion','Evaluación (visible para cliente)',evalVal)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12}}>⛶ Expandir</button>}
            </div>
            <RichText value={evalVal} onChange={setEvalVal} onBlur={v=>edita&&handleCampo('evaluacion',v)} readOnly={!edita} placeholder="Evaluación profesional del caso (visible para el cliente)" minHeight={120}/>
          </div>

          <div className="p-field">
            <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>Nota interna (solo equipo)</span>
              {edita&&<button onClick={()=>openTextModal('notaInterna','Nota interna (solo equipo)',notaVal)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12}}>⛶ Expandir</button>}
            </div>
            <RichText value={notaVal} onChange={setNotaVal} onBlur={v=>edita&&handleCampo('notaInterna',v)} readOnly={!edita} placeholder="Notas internas del equipo" minHeight={100}/>
          </div>

          <div className="p-field"><div className="p-field-label">Recordatorio para cliente</div>
            <textarea className="p-input" rows={3} defaultValue={c.recordatorio||''} onBlur={e=>edita&&handleCampo('recordatorio',e.target.value)} readOnly={!edita} style={{resize:'vertical'}}/>
            {/* El cliente LEE esto: va en los correos y en la consulta pública.
                Decirlo acá evita que alguien lo use de bloc de notas interno. */}
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Lo lee el cliente: se incluye en los correos que le envíes y en su consulta de estado.</div></div>
        </div>

        {textModal.open&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setTextModal(m=>({...m,open:false}))}}>
            <div style={{background:'#1a2540',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,width:'100%',maxWidth:760,maxHeight:'calc(100vh - 40px)',display:'flex',flexDirection:'column',gap:0,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                <span style={{fontWeight:600,color:'var(--white)',fontSize:15}}>{textModal.label}</span>
                <button onClick={()=>setTextModal(m=>({...m,open:false}))} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
              </div>
              <div style={{padding:'8px 20px 0',flex:1,overflowY:'auto'}}>
                <RichText value={textModal.value} onChange={v=>setTextModal(m=>({...m,value:v}))} readOnly={!edita} placeholder={edita?'Escriba aquí…':'(sin contenido)'} minHeight={350}/>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10,padding:'12px 20px',borderTop:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                <button onClick={()=>setTextModal(m=>({...m,open:false}))} className="p-btn-secondary">Cancelar</button>
                {edita&&<button onClick={saveTextModal} className="p-btn-primary">Guardar</button>}
              </div>
            </div>
          </div>
        )}
        <div style={{gridColumn:'1/-1'}}><NotasInternas casoId={c.id} field="notasInternasDatos" value={c.notasInternasDatos} edita={edita} onRefresh={onRefresh}/></div>
      </div>
    )}

    {tab==='proceso'&&(<>
      {!(c.tema||'').includes('Familia') && (
        <div className="p-section">
          <div className="p-section-title">⚙️ Procedimiento</div>
          {(()=>{
            const done=laborSteps.filter(s=>(laborFlow[s]||{}).done).length;
            const total=laborSteps.length;
            const next=laborSteps.find(s=>!(laborFlow[s]||{}).done);
            const pct=total>0?Math.round(done/total*100):0;
            return(
              <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>{done===total&&total>0?'Procedimiento completado':`Etapa ${done+1} de ${total}`}</span>
                  <span style={{fontSize:12,color:done===total&&total>0?'#4CAF50':'var(--text-muted)'}}>{done===total&&total>0?'✅ Completado':`Faltan ${total-done} etapas`}</span>
                </div>
                <div style={{background:'rgba(255,255,255,0.06)',borderRadius:3,height:5,marginBottom:next?8:0,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:3,transition:'width 0.4s'}}/>
                </div>
                {next&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Próxima etapa: <span style={{color:'var(--gold)',fontWeight:500}}>{next}</span></div>}
              </div>
            );
          })()}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {laborSteps.map((step,i)=>{
              const sd=laborFlow[step]||{done:false,instrucciones:''};
              const isNext=!sd.done&&laborSteps.slice(0,i).every(s=>(laborFlow[s]||{}).done);
              const isEditing=editStepIdx?.flowType==='labor'&&editStepIdx?.idx===i;
              return(
                <div key={i} style={{background:isNext?'rgba(184,150,90,0.05)':'rgba(255,255,255,0.02)',borderRadius:8,padding:'10px 14px',borderLeft:sd.done?'3px solid var(--gold)':isNext?'3px solid rgba(184,150,90,0.5)':'3px solid rgba(255,255,255,0.08)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                      <input type="checkbox" checked={sd.done} onChange={e=>edita&&toggleFlowStep(step,e.target.checked,'labor')} disabled={!edita} style={{width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,minWidth:22,flexShrink:0}}>{i+1}.</span>
                      {isEditing?(
                        <input autoFocus className="p-input" value={editStepTxt} onChange={e=>setEditStepTxt(e.target.value)} onBlur={saveEditStep} onKeyDown={e=>{if(e.key==='Enter')saveEditStep();if(e.key==='Escape')setEditStepIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
                      ):(
                        <span style={{textDecoration:sd.done?'line-through':'none',color:sd.done?'var(--text-muted)':isNext?'var(--gold)':'var(--white)',fontWeight:isNext?600:500,fontSize:14}}>{step}</span>
                      )}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      {sd.done&&!isEditing&&<span className="pago-badge ok" style={{fontSize:10}}>✓</span>}
                      {!sd.done&&isNext&&!isEditing&&<span style={{fontSize:10,color:'var(--gold)',border:'1px solid rgba(184,150,90,0.4)',padding:'2px 6px',borderRadius:3}}>Actual</span>}
                      {edita&&!isEditing&&<button onClick={()=>startEditStep('labor',i,step)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Renombrar etapa">✏️</button>}
                      {edita&&!isEditing&&<button onClick={()=>setConfirmDel({msg:`la etapa "${step}"`,onConfirm:()=>deleteFlowStep('labor',i)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.6)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Eliminar etapa">✕</button>}
                    </div>
                  </div>
                  {!isEditing&&(
                    <div style={{marginTop:8,marginLeft:36}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,marginBottom:5,letterSpacing:'0.05em',textTransform:'uppercase'}}>Notas internas</div>
                      {edita&&<div style={{display:'flex',gap:6,marginBottom:6}}>
                        <input className="p-input" placeholder="Agregar nota..." value={subCheckInputs[`labor_${i}`]||''} onChange={e=>setSubCheckInputs(p=>({...p,[`labor_${i}`]:e.target.value}))} onKeyDown={e=>{const t=(subCheckInputs[`labor_${i}`]||'').trim();if(e.key==='Enter'&&t){addSubCheck(step,'labor',t);setSubCheckInputs(p=>({...p,[`labor_${i}`]:''}));}}} style={{flex:1,fontSize:12,padding:'3px 8px'}}/>
                        <button className="p-btn-primary" onClick={()=>{const t=(subCheckInputs[`labor_${i}`]||'').trim();if(t){addSubCheck(step,'labor',t);setSubCheckInputs(p=>({...p,[`labor_${i}`]:''}));}}} style={{fontSize:12,padding:'3px 10px'}}>＋</button>
                      </div>}
                      {(sd.subChecklist||[]).length===0&&<div style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic',opacity:0.6}}>Sin notas.</div>}
                      {(sd.subChecklist||[]).map((sc:any,si:number)=>(
                        <div key={si} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                          <input type="checkbox" checked={sc.done} onChange={e=>toggleSubCheck(step,'labor',si,e.target.checked)} style={{width:13,height:13,flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12,textDecoration:sc.done?'line-through':'none',color:sc.done?'var(--text-muted)':'var(--white)',opacity:0.85}}>{sc.texto}</span>
                          {edita&&<button onClick={()=>setConfirmDel({msg:`la nota "${sc.texto}"`,onConfirm:()=>delSubCheck(step,'labor',si)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.5)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {edita&&<div style={{display:'flex',gap:8,marginTop:10}}>
            <input className="p-input" placeholder="Nueva etapa..." value={newStepInputs['newStep_labor']||''} onChange={e=>setNewStepInputs(p=>({...p,newStep_labor:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addFlowStep('labor')} style={{flex:1,fontSize:13}}/>
            <button className="p-btn-primary" onClick={()=>addFlowStep('labor')} style={{whiteSpace:'nowrap'}}>＋ Agregar etapa</button>
          </div>}
        </div>
      )}

      {(c.tema||'').includes('Familia') && (
        <div className="p-section">
          <div className="p-section-title">⚙️ Procedimiento</div>
          {(()=>{
            const done=familiaSteps.filter(s=>(familiaFlow[s]||{}).done).length;
            const total=familiaSteps.length;
            const next=familiaSteps.find(s=>!(familiaFlow[s]||{}).done);
            const pct=total>0?Math.round(done/total*100):0;
            return(
              <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>{done===total&&total>0?'Procedimiento completado':`Etapa ${done+1} de ${total}`}</span>
                  <span style={{fontSize:12,color:done===total&&total>0?'#4CAF50':'var(--text-muted)'}}>{done===total&&total>0?'✅ Completado':`Faltan ${total-done} etapas`}</span>
                </div>
                <div style={{background:'rgba(255,255,255,0.06)',borderRadius:3,height:5,marginBottom:next?8:0,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:3,transition:'width 0.4s'}}/>
                </div>
                {next&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Próxima etapa: <span style={{color:'var(--gold)',fontWeight:500}}>{next}</span></div>}
              </div>
            );
          })()}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {familiaSteps.map((step,i)=>{
              const sd=familiaFlow[step]||{done:false,instrucciones:''};
              const isNext=!sd.done&&familiaSteps.slice(0,i).every(s=>(familiaFlow[s]||{}).done);
              const isEditing=editStepIdx?.flowType==='familia'&&editStepIdx?.idx===i;
              return(
                <div key={i} style={{background:isNext?'rgba(184,150,90,0.05)':'rgba(255,255,255,0.02)',borderRadius:8,padding:'10px 14px',borderLeft:sd.done?'3px solid var(--gold)':isNext?'3px solid rgba(184,150,90,0.5)':'3px solid rgba(255,255,255,0.08)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                      <input type="checkbox" checked={sd.done} onChange={e=>edita&&toggleFlowStep(step,e.target.checked,'familia')} disabled={!edita} style={{width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,minWidth:22,flexShrink:0}}>{i+1}.</span>
                      {isEditing?(
                        <input autoFocus className="p-input" value={editStepTxt} onChange={e=>setEditStepTxt(e.target.value)} onBlur={saveEditStep} onKeyDown={e=>{if(e.key==='Enter')saveEditStep();if(e.key==='Escape')setEditStepIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
                      ):(
                        <span style={{textDecoration:sd.done?'line-through':'none',color:sd.done?'var(--text-muted)':isNext?'var(--gold)':'var(--white)',fontWeight:isNext?600:500,fontSize:14}}>{step}</span>
                      )}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      {sd.done&&!isEditing&&<span className="pago-badge ok" style={{fontSize:10}}>✓</span>}
                      {!sd.done&&isNext&&!isEditing&&<span style={{fontSize:10,color:'var(--gold)',border:'1px solid rgba(184,150,90,0.4)',padding:'2px 6px',borderRadius:3}}>Actual</span>}
                      {edita&&!isEditing&&<button onClick={()=>startEditStep('familia',i,step)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Renombrar etapa">✏️</button>}
                      {edita&&!isEditing&&<button onClick={()=>setConfirmDel({msg:`la etapa "${step}"`,onConfirm:()=>deleteFlowStep('familia',i)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.6)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Eliminar etapa">✕</button>}
                    </div>
                  </div>
                  {!isEditing&&(
                    <div style={{marginTop:8,marginLeft:36}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,marginBottom:5,letterSpacing:'0.05em',textTransform:'uppercase'}}>Notas internas</div>
                      {edita&&<div style={{display:'flex',gap:6,marginBottom:6}}>
                        <input className="p-input" placeholder="Agregar nota..." value={subCheckInputs[`familia_${i}`]||''} onChange={e=>setSubCheckInputs(p=>({...p,[`familia_${i}`]:e.target.value}))} onKeyDown={e=>{const t=(subCheckInputs[`familia_${i}`]||'').trim();if(e.key==='Enter'&&t){addSubCheck(step,'familia',t);setSubCheckInputs(p=>({...p,[`familia_${i}`]:''}));}}} style={{flex:1,fontSize:12,padding:'3px 8px'}}/>
                        <button className="p-btn-primary" onClick={()=>{const t=(subCheckInputs[`familia_${i}`]||'').trim();if(t){addSubCheck(step,'familia',t);setSubCheckInputs(p=>({...p,[`familia_${i}`]:''}));}}} style={{fontSize:12,padding:'3px 10px'}}>＋</button>
                      </div>}
                      {(sd.subChecklist||[]).length===0&&<div style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic',opacity:0.6}}>Sin notas.</div>}
                      {(sd.subChecklist||[]).map((sc:any,si:number)=>(
                        <div key={si} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                          <input type="checkbox" checked={sc.done} onChange={e=>toggleSubCheck(step,'familia',si,e.target.checked)} style={{width:13,height:13,flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12,textDecoration:sc.done?'line-through':'none',color:sc.done?'var(--text-muted)':'var(--white)',opacity:0.85}}>{sc.texto}</span>
                          {edita&&<button onClick={()=>setConfirmDel({msg:`la nota "${sc.texto}"`,onConfirm:()=>delSubCheck(step,'familia',si)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.5)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {edita&&<div style={{display:'flex',gap:8,marginTop:10}}>
            <input className="p-input" placeholder="Nueva etapa..." value={newStepInputs['newStep_familia']||''} onChange={e=>setNewStepInputs(p=>({...p,newStep_familia:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addFlowStep('familia')} style={{flex:1,fontSize:13}}/>
            <button className="p-btn-primary" onClick={()=>addFlowStep('familia')} style={{whiteSpace:'nowrap'}}>＋ Agregar etapa</button>
          </div>}
        </div>
      )}

      <div className="p-section" style={{marginTop:isLaboral?20:0}}>
        <div className="p-section-title">Audiencias y fechas clave</div>
        {edita&&<div className="p-grid3" style={{marginBottom:12}}>
          <input className="p-input" type="date" value={audF} onChange={e=>setAudF(e.target.value)}/>
          <SelectorHora valor={audH} onChange={setAudH}/>
          <input className="p-input" placeholder="Lugar" value={audL} onChange={e=>setAudL(e.target.value)}/>
          <select className="p-select" value={audT} onChange={e=>{setAudT(e.target.value);if(e.target.value!=='Otro')setAudTOtro('');}} style={{gridColumn:audT==='Otro'?'1/2':'1/3'}}>
            <option value="">Selecciona tipo...</option>
            {TIPOS_AUDIENCIA.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {audT==='Otro'&&<input className="p-input" placeholder="Especifica el tipo" value={audTOtro} onChange={e=>setAudTOtro(e.target.value)} style={{gridColumn:'2/3'}}/>}
          {audT==='Comparendo'&&(
            <div style={{gridColumn:'1 / -1',display:'flex',flexWrap:'wrap',alignItems:'center',gap:10,background:'rgba(184,150,90,0.08)',border:'1px solid rgba(184,150,90,0.28)',borderRadius:6,padding:'8px 12px'}}>
              <span style={{fontSize:12,color:'#E5D5AE',whiteSpace:'nowrap'}}>Fecha en que se solicitó el comparendo</span>
              <input className="p-input" type="date" value={audSol} onChange={e=>setAudSol(e.target.value)} style={{width:150,fontSize:13}}/>
              <span style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.4}}>
                Con ella el plazo del art. 168 deja de correr hasta el día del comparendo. Si se omite, el plazo se muestra sin suspender.
              </span>
            </div>
          )}
          <button className="p-btn-primary" onClick={addAud} disabled={savingAud} style={{cursor:savingAud?'not-allowed':'pointer',opacity:savingAud?0.6:1}}>{savingAud?'Agregando…':'＋ Agregar'}</button>
        </div>}
        {(c.audiencias||[]).length?(c.audiencias||[]).map((a:any,i:number)=>{
          const estado=estadoAudiencia(a);
          const fuera=!audienciaVigente(a);
          const lista=(c.audiencias||[]) as any[];
          const nueva=a.reagendadaId?lista.find(x=>x?.id===a.reagendadaId):null;
          const origen=a.origenId?lista.find(x=>x?.id===a.origenId):null;
          return(
          <div className="aud-row" key={a.id||i} style={fuera?{opacity:0.72}:undefined}><div className="aud-dot" style={{background:COLOR_ESTADO_AUD[estado]}}/><div className="aud-body">
          <div className="aud-tipo" style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            {a.tipo||'Audiencia'}
            {estado!=='Agendada'&&<span style={{fontSize:10.5,padding:'1px 8px',borderRadius:999,border:`1px solid ${COLOR_ESTADO_AUD[estado]}`,color:COLOR_ESTADO_AUD[estado],letterSpacing:'0.04em'}}>{estado}</span>}
          </div>
          <div className="aud-info" style={fuera?{textDecoration:'line-through'}:undefined}>{FS.fmtDate(a.fecha)||''} {a.hora?'· '+a.hora:''} {a.lugar?'· '+a.lugar:''}</div>
          {a.motivo&&<div style={{fontSize:11.5,color:'var(--text-muted)',marginTop:2}}>{estado==='Con continuación'?'Nota':'Motivo'}: {a.motivo}</div>}
          {nueva&&<div style={{fontSize:11.5,color:'#E0A64A',marginTop:2}}>→ {estado==='Con continuación'?'Continúa':'Nueva fecha'}: {nueva.tipo&&nueva.tipo!==a.tipo?nueva.tipo+' · ':''}{cuandoAud(nueva)}</div>}
          {origen&&<div style={{fontSize:11.5,color:'var(--text-muted)',marginTop:2}}>{a.origen==='Con continuación'?'Continuación':`Nueva fecha de la ${String(a.origen||'audiencia').toLowerCase()}`} del {cuandoAud(origen)}</div>}
          {edita&&a.id&&(
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6,flexWrap:'wrap'}}>
              <select className="p-select" value={estado} onChange={e=>elegirEstadoAud(a,e.target.value)} aria-label="Estado de la audiencia"
                style={{fontSize:12,padding:'3px 6px',width:'auto',minWidth:140,borderColor:COLOR_ESTADO_AUD[estado]}}>
                {ESTADOS_AUDIENCIA.map(x=><option key={x} value={x}>{x}</option>)}
              </select>
              {(fuera||estado==='Con continuación')&&!nueva&&(
                <button className="p-btn-secondary" onClick={()=>setCambioAud({aud:a,modo:'Reagendar'})} style={{fontSize:12,padding:'4px 12px'}}>
                  {estado==='Con continuación'?'Agendar continuación':'Reagendar'}
                </button>
              )}
            </div>
          )}
          {a.tipo==='Comparendo'&&(edita?(
            <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:8,marginTop:6}}>
              <span style={{fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap'}}>Solicitado el</span>
              <input className="p-input" type="date" defaultValue={a.fechaSolicitud||''} onBlur={e=>setSolicitudAud(i,e.target.value)} style={{width:145,fontSize:12,padding:'2px 6px'}}/>
              {!a.fechaSolicitud&&<span style={{fontSize:11,color:'#E0A64A'}}>Sin esta fecha el plazo del art. 168 no se suspende.</span>}
            </div>
          ):(
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
              {a.fechaSolicitud?`Solicitado el ${FS.fmtDate(a.fechaSolicitud)}`:'Sin fecha de solicitud: el plazo del art. 168 no se suspende.'}
            </div>
          ))}
          </div>
          {edita&&<button className="p-btn-danger-sm" onClick={()=>setConfirmDel({msg:`la audiencia "${a.tipo||'Audiencia'}" (${FS.fmtDate(a.fecha)||''})`,onConfirm:()=>delAud(i)})}>✕</button>}</div>
          );}):<div className="p-empty">No hay audiencias registradas.</div>}
        {(c.historialAudiencias||[]).length>0&&<HistorialAudiencias entradas={c.historialAudiencias}/>}
        {cambioAud&&<CambioAudiencia aud={cambioAud.aud} modo={cambioAud.modo} onCancelar={()=>setCambioAud(null)} onConfirmar={confirmarCambioAud}/>}
      </div>
      <div className="p-section" style={{marginTop:20}}>
        <div className="p-section-title">Checklist de acciones pendientes</div>
        {edita&&(
          /* Antes esto era un solo campo donde había que escribir "@Nombre" y
             "@plazo 28-05-2026" de memoria. Ahora la persona se elige de una
             lista y la fecha se toma de un calendario: lo único que se escribe
             es el comentario. */
          <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
            <select className="p-select" value={checkPara} onChange={e=>setCheckPara(e.target.value)}
              title="A quién avisar" style={{minWidth:170,fontSize:13}}>
              <option value="">Sin avisar a nadie</option>
              <option value="__todos__">📢 Todo el equipo</option>
              {todosUsuarios.filter(u=>u.activo!==false).map(u=>(
                <option key={u.id} value={u.id}>{u.nombre||u.email}</option>
              ))}
            </select>
            <input className="p-input" type="date" value={checkPlazo} onChange={e=>setCheckPlazo(e.target.value)}
              title="Fecha límite" style={{width:150,fontSize:13}}/>
            <input
              ref={checkInputRef}
              className="p-input"
              placeholder="¿Qué hay que hacer?"
              value={checkTxt}
              onChange={e=>setCheckTxt(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter') addCheck(); }}
              style={{flex:1,minWidth:200}}
            />
            <button className="p-btn-primary" onClick={()=>addCheck()} disabled={savingCheck||!checkTxt.trim()}>{savingCheck?'…':'＋'}</button>
            {checkPara&&!checkPlazo&&(
              <div style={{width:'100%',fontSize:11.5,color:'var(--text-muted)',marginTop:-2}}>
                Sin fecha límite igual se avisa, pero la acción no aparece en el calendario.
              </div>
            )}
          </div>
        )}
        {(c.checklist||[]).length?(c.checklist||[]).map((ch:any,i:number)=>(
          <div className="check-row" key={i}>
            <input type="checkbox" checked={ch.done} onChange={e=>edita&&toggleCheck(i,e.target.checked)} disabled={!edita}/>
            {edita&&editCheckIdx===i?(
              <input autoFocus className="p-input" value={editCheckTxt} onChange={e=>setEditCheckTxt(e.target.value)} onBlur={saveEditCheck} onKeyDown={e=>{if(e.key==='Enter')saveEditCheck();if(e.key==='Escape')setEditCheckIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
            ):(
              <span style={{flex:1,textDecoration:ch.done?'line-through':'none',color:ch.done?'var(--text-muted)':'var(--white)'}}>{renderMenciones(ch.texto)}</span>
            )}
            {edita&&editCheckIdx!==i&&<button onClick={()=>startEditCheck(i,ch.texto)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Editar">✏️</button>}
            {edita&&editCheckIdx!==i&&<button className="p-btn-danger-sm" onClick={()=>setConfirmDel({msg:`"${ch.texto}"`,onConfirm:()=>delCheck(i)})}>✕</button>}
          </div>
        )):<div className="p-empty">Sin acciones pendientes.</div>}
      </div>
      <NotasInternas casoId={c.id} field="notasInternasProceso" value={c.notasInternasProceso} edita={edita} onRefresh={onRefresh}/>
    </>)}

    {tab==='documentos'&&(
      <div className="p-section">
        <div className="p-section-title">Documentos del caso</div>
        <div className="p-info-box">💡 Adjunte archivos o tome fotos con su celular.</div>

        {/* Lo que falta por entregar, escrito para el cliente. Va en los correos
            que se le envían y en su consulta de estado, así que se avisa en
            pantalla: acá no se anotan pendientes internos del equipo. */}
        <div className="p-field" style={{marginBottom:18}}>
          <div className="p-field-label">📋 Recordatorio de documentos faltantes</div>
          <textarea className="p-input" rows={3} defaultValue={c.recordatorioDocumentos||''}
            onBlur={e=>edita&&handleCampo('recordatorioDocumentos',e.target.value)} readOnly={!edita}
            placeholder={edita?'Ej.: Cédula de identidad por ambos lados\nEj.: Últimas 3 liquidaciones de sueldo':undefined}
            style={{resize:'vertical'}}/>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
            Lo lee el cliente: se incluye en los correos que le envíes y en su consulta de estado. Uno por línea.
            {' '}Si está vacío (lo gris es solo un ejemplo), el cliente no ve nada.
          </div>
        </div>
        {edita&&<>
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <button className="p-btn-secondary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&fileInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>{uploadingDrive?'⏳ Subiendo…':'📎 Adjuntar archivos'}</button>
            <input ref={fileInputRef} type="file" multiple accept="*/*" onChange={handleFileUpload} style={{display:'none'}}/>
            <button className="p-btn-secondary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&cameraInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>📷 Tomar fotos</button>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handleCameraCapture} style={{display:'none'}}/>
            <button className="p-btn-secondary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&galeriaInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>🖼️ Elegir de tus fotos</button>
            <input ref={galeriaInputRef} type="file" accept="image/*" multiple onChange={handleCameraCapture} style={{display:'none'}}/>
            {driveDisponible()&&<button className="p-btn-secondary" disabled={uploadingDrive} onClick={desdeDrive} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>🟢 Desde Google Drive</button>}
            {capturedPhotos.length>0 && <button className="p-btn-primary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&compileToPdf()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>{uploadingDrive?'⏳ Subiendo…':`📄 Compilar ${capturedPhotos.length} foto${capturedPhotos.length>1?'s':''} a PDF`}</button>}
          </div>
          {capturedPhotos.length>0 && (
            <div style={{marginBottom:16}}>
              <div className="p-field-label" style={{marginBottom:6}}>Nombre del PDF</div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
                <input
                  className="p-input"
                  style={{flex:1,fontSize:13}}
                  value={pdfNombre}
                  onChange={e=>setPdfNombre(e.target.value)}
                  placeholder={`Fotos_${c.codigoCaso||c.id.slice(0,8)}_${new Date().toISOString().slice(0,10)}`}
                  maxLength={100}
                />
                <span style={{color:'var(--text-muted)',fontSize:12,whiteSpace:'nowrap'}}>.pdf</span>
              </div>
              <div className="p-field-label" style={{marginBottom:8}}>Fotos para el PDF ({capturedPhotos.length}), en este orden</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {capturedPhotos.map((p,i)=>(
                  <div key={i} style={{position:'relative',width:80,height:80,borderRadius:6,overflow:'hidden',border:'1px solid rgba(255,255,255,0.1)'}}>
                    <img src={p.dataUrl} alt={p.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    <button onClick={()=>removePhoto(i)} style={{position:'absolute',top:2,right:2,background:'rgba(0,0,0,0.7)',color:'#fff',border:'none',borderRadius:'50%',width:18,height:18,cursor:'pointer',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                    <span style={{position:'absolute',top:2,left:2,background:'rgba(0,0,0,0.7)',color:'#fff',borderRadius:9,minWidth:18,height:18,padding:'0 5px',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center'}}>{i+1}</span>
                    {capturedPhotos.length>1&&(
                      <div style={{position:'absolute',bottom:2,left:2,right:2,display:'flex',justifyContent:'space-between'}}>
                        {[[-1,'◀','Mover antes'],[1,'▶','Mover después']].map(([paso,flecha,titulo])=>(
                          <button key={paso as number} onClick={()=>moverFoto(i,paso as -1|1)} disabled={(paso===-1&&i===0)||(paso===1&&i===capturedPhotos.length-1)}
                            title={titulo as string} aria-label={titulo as string}
                            style={{background:'rgba(0,0,0,0.7)',color:'#fff',border:'none',borderRadius:4,width:22,height:20,cursor:'pointer',fontSize:10,visibility:((paso===-1&&i===0)||(paso===1&&i===capturedPhotos.length-1))?'hidden':'visible'}}>{flecha}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>}
        {(carpetas.length>0||nuevaCarpeta!==null)&&(
          <div className="carpetas-tabs">
            {[{id:'todos',nombre:'Todos',n:(c.documentos||[]).length},{id:'sin',nombre:'Sin carpeta',n:(c.documentos||[]).filter((d:any)=>!d.carpetaId).length},
              ...carpetas.map(k=>({id:k.id,nombre:k.nombre,n:(c.documentos||[]).filter((d:any)=>d.carpetaId===k.id).length}))].map(k=>(
              edita&&editCarpeta&&editCarpeta.id===k.id?(
                <span key={k.id} style={{display:'inline-flex',gap:4,alignItems:'center'}}>
                  <input className="p-input" value={editCarpeta.nombre} disabled={guardandoCarpeta} autoFocus maxLength={80}
                    onChange={e=>setEditCarpeta({id:k.id,nombre:e.target.value})}
                    onKeyDown={e=>{if(e.key==='Enter')guardarNombreCarpeta();if(e.key==='Escape')setEditCarpeta(null);}}
                    style={{fontSize:12,width:160,padding:'4px 8px'}}/>
                  <button className="p-btn-secondary" disabled={guardandoCarpeta} style={{padding:'2px 8px',fontSize:11}} onClick={guardarNombreCarpeta}>✓</button>
                  <button className="p-btn-danger-sm" disabled={guardandoCarpeta} style={{fontSize:11}} onClick={()=>setEditCarpeta(null)}>✕</button>
                </span>
              ):(
                <span key={k.id} role="button" tabIndex={0}
                  className={`carpeta-tab ${carpetaSel===k.id?'active':''} ${soltarEn===k.id?'soltar':''}`}
                  onClick={()=>setCarpetaSel(k.id)} onKeyDown={e=>{if(e.key==='Enter')setCarpetaSel(k.id);}}
                  {...(k.id!=='todos'?propsSoltar(k.id):{})}>
                  {k.id==='todos'?'':k.id==='sin'?'':'📁 '}{k.nombre} <span className="cuenta">({k.n})</span>
                  {edita&&carpetaSel===k.id&&k.id!=='todos'&&k.id!=='sin'&&<>
                    <button className="carpeta-accion" title="Cambiar el nombre de la carpeta" aria-label="Cambiar el nombre de la carpeta"
                      onClick={e=>{e.stopPropagation();setEditCarpeta({id:k.id,nombre:k.nombre});}}>✏️</button>
                    <button className="carpeta-accion" title="Eliminar la carpeta (los documentos no se borran)" aria-label="Eliminar la carpeta"
                      onClick={e=>{e.stopPropagation();setConfirmCarpetaDel({id:k.id,nombre:k.nombre,n:k.n});}}>✕</button>
                  </>}
                </span>
              )
            ))}
            {edita&&(nuevaCarpeta!==null?(
              <span style={{display:'inline-flex',gap:4,alignItems:'center'}}>
                <input className="p-input" value={nuevaCarpeta} disabled={guardandoCarpeta} autoFocus maxLength={80} placeholder="Nombre de la carpeta"
                  onChange={e=>setNuevaCarpeta(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')crearCarpeta();if(e.key==='Escape')setNuevaCarpeta(null);}}
                  style={{fontSize:12,width:180,padding:'4px 8px'}}/>
                <button className="p-btn-secondary" disabled={guardandoCarpeta} style={{padding:'2px 8px',fontSize:11}} onClick={crearCarpeta}>✓</button>
                <button className="p-btn-danger-sm" disabled={guardandoCarpeta} style={{fontSize:11}} onClick={()=>setNuevaCarpeta(null)}>✕</button>
              </span>
            ):(
              <button className="carpeta-tab" title="Crear una carpeta" onClick={()=>setNuevaCarpeta('')}>+ Carpeta</button>
            ))}
          </div>
        )}
        {(edita&&carpetas.length===0&&nuevaCarpeta===null)||(c.documentos||[]).length>1||(carpetaDestino&&docsVisibles.length>0)?(
          <div className="carpetas-barra">
            <div>
              {edita&&carpetas.length===0&&nuevaCarpeta===null&&(
                <button className="p-btn-secondary" style={{fontSize:12,padding:'4px 12px'}} onClick={()=>setNuevaCarpeta('')}
                  title="Ordena los documentos del caso en carpetas. Es opcional.">+ Carpeta</button>
              )}
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {carpetaDestino&&docsVisibles.length>0&&(
                <button className="p-btn-secondary" disabled={bajandoTodo} onClick={()=>bajarTodo(carpetaDestino)}
                  style={{fontSize:12,padding:'4px 12px',opacity:bajandoTodo?0.6:1,cursor:bajandoTodo?'wait':'pointer'}}>
                  {bajandoTodo?'⏳ Preparando el .zip…':`⬇ Descargar carpeta (${docsVisibles.length})`}
                </button>
              )}
              {(c.documentos||[]).length>1&&(
                <button className="p-btn-secondary" disabled={bajandoTodo} onClick={()=>bajarTodo()}
                  title={carpetas.length?'Un .zip con todo el caso, cada documento dentro de su carpeta':undefined}
                  style={{fontSize:12,padding:'4px 12px',opacity:bajandoTodo?0.6:1,cursor:bajandoTodo?'wait':'pointer'}}>
                  {bajandoTodo?'⏳ Preparando el .zip…':`⬇ Descargar todo (${(c.documentos||[]).length})`}
                </button>
              )}
            </div>
          </div>
        ):null}
        {edita&&carpetas.length>0&&docsMarcados.length>0&&(
          <div className="carpetas-seleccion" style={{marginBottom:8}}>
            <span>{docsMarcados.length} seleccionado{docsMarcados.length>1?'s':''}</span>
            <select className="p-select" value="" disabled={moviendo} onChange={e=>{if(e.target.value)moverDocs(docsMarcados,e.target.value);}}
              style={{fontSize:12,padding:'3px 8px',width:'auto'}}>
              <option value="">{moviendo?'Moviendo…':'Mover a…'}</option>
              {carpetas.map(k=><option key={k.id} value={k.id}>📁 {k.nombre}</option>)}
              <option value="sin">Sin carpeta</option>
            </select>
            <button className="carpeta-accion" onClick={()=>setDocsMarcados([])}>Quitar selección</button>
          </div>
        )}
        {docsVisibles.length?<table className="p-table"><thead><tr>
          {edita&&carpetas.length>0&&<th style={{width:28}}>
            <input type="checkbox" aria-label="Marcar todos" checked={docsVisibles.every((d:any)=>docsMarcados.includes(d.id))}
              onChange={e=>{const ids=docsVisibles.map((d:any)=>d.id);setDocsMarcados(p=>e.target.checked?Array.from(new Set([...p,...ids])):p.filter(x=>!ids.includes(x)));}}/>
          </th>}
          <th>Archivo</th><th>Fecha</th><th>Tamaño</th><th></th>{edita&&<th></th>}</tr></thead><tbody>
          {docsVisibles.map((d:any)=>(
            <tr key={d.id}
              draggable={edita&&carpetas.length>0&&!(editDoc&&editDoc.id===d.id)}
              onDragStart={e=>{e.dataTransfer.setData('text/x-tabularis-doc',d.id);e.dataTransfer.effectAllowed='move';}}
              onDragEnd={()=>setSoltarEn(null)}>
              {edita&&carpetas.length>0&&<td><input type="checkbox" aria-label={`Marcar ${d.nombre||'documento'}`} checked={docsMarcados.includes(d.id)} onChange={()=>marcarDoc(d.id)}/></td>}
              <td>
                {edita&&editDoc&&editDoc.id===d.id?(
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <input className="p-input" value={editDoc.nombre} disabled={guardandoDoc} autoFocus
                      onChange={e=>setEditDoc({id:d.id,nombre:e.target.value})}
                      onKeyDown={e=>{if(e.key==='Enter')guardarNombreDoc();if(e.key==='Escape')setEditDoc(null);}}
                      style={{fontSize:12,flex:1,minWidth:140}} maxLength={255}/>
                    <button className="p-btn-secondary" disabled={guardandoDoc} style={{padding:'2px 8px',fontSize:11}} onClick={guardarNombreDoc}>✓</button>
                    <button className="p-btn-danger-sm" disabled={guardandoDoc} style={{fontSize:11}} onClick={()=>setEditDoc(null)}>✕</button>
                  </div>
                ):(
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span>{d.nombre||'—'}</span>
                    {d.tipo?<span style={{color:'var(--text-muted)',fontSize:11}}>· {d.tipo}</span>:null}
                    {carpetaSel==='todos'&&nombreCarpetaDe(d.carpetaId)?<span style={{color:'#CFC394',fontSize:11}}>· 📁 {nombreCarpetaDe(d.carpetaId)}</span>:null}
                    {edita&&<button onClick={()=>setEditDoc({id:d.id,nombre:d.nombre||''})} title="Cambiar el nombre" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:11,padding:'0 2px',lineHeight:1}}>✏️</button>}
                  </div>
                )}
              </td>
              <td>{FS.fmtDate(d.creado)}</td>
              <td>{d.size?((d.size/1048576).toFixed(d.size>=1048576?1:2)+' MB'):'—'}</td>
              <td style={{whiteSpace:'nowrap'}}>
                <button className="p-btn-secondary" style={{fontSize:12,padding:'2px 10px'}} onClick={()=>verDoc(d.id,d.nombre)}>{(d.nombre||'').toLowerCase().endsWith('.docx')?'Ver':'Ver ↗'}</button>
                <button className="p-btn-secondary" style={{fontSize:12,padding:'2px 10px',marginLeft:6}} onClick={()=>bajarDoc(d.id)} title="Descargar el archivo">⬇ Descargar</button>
                {edita&&carpetas.length>0&&(
                  <select className="p-select" value="" disabled={moviendo} title="Mover a otra carpeta" aria-label="Mover a otra carpeta"
                    onChange={e=>{if(e.target.value)moverDocs([d.id],e.target.value);}}
                    style={{fontSize:12,padding:'2px 6px',marginLeft:6,width:'auto',display:'inline-block'}}>
                    <option value="">Mover a…</option>
                    {carpetas.filter(k=>k.id!==d.carpetaId).map(k=><option key={k.id} value={k.id}>📁 {k.nombre}</option>)}
                    {d.carpetaId&&<option value="sin">Sin carpeta</option>}
                  </select>
                )}
              </td>
              {edita&&(
                <td><button className="p-btn-danger-sm" onClick={()=>setConfirmDocDel({id:d.id,nombre:d.nombre||'este documento'})}>✕</button></td>
              )}
            </tr>
          ))}
        </tbody></table>:<div className="p-empty">{(c.documentos||[]).length?(carpetaDestino?'Esta carpeta está vacía. Sube archivos con la carpeta abierta o mueve documentos desde "Todos".':'No hay documentos sin carpeta.'):'No hay documentos.'}</div>}
        <NotasInternas casoId={c.id} field="notasInternasDocumentos" value={c.notasInternasDocumentos} edita={edita} onRefresh={onRefresh}/>
      </div>
    )}

    {docVista&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setDocVista(null)}}>
        <div style={{background:'var(--dark-2,#F4F7FB)',border:'1px solid rgba(13,27,53,0.14)',borderRadius:12,width:'min(900px,96vw)',maxHeight:'92vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 24px 60px rgba(13,27,53,0.35)'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,padding:'14px 18px',borderBottom:'1px solid rgba(13,27,53,0.12)',background:'var(--white,#fff)'}}>
            <span style={{fontWeight:600,color:'var(--cream,#0D1B35)',fontSize:15,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{docVista.nombre}</span>
            <div style={{display:'flex',gap:10,alignItems:'center',flexShrink:0}}>
              <button onClick={()=>descargarDoc(docVista.id,docVista.nombre)} style={{background:'var(--gold,#1A3A6E)',border:'none',color:'#fff',borderRadius:6,padding:'7px 14px',fontSize:12.5,fontWeight:500,cursor:'pointer',whiteSpace:'nowrap'}}>⬇ Descargar para editar</button>
              <button onClick={()=>setDocVista(null)} title="Cerrar" style={{background:'none',border:'none',color:'var(--text-body,#3A4F6E)',fontSize:20,cursor:'pointer',lineHeight:1,padding:'0 2px'}}>✕</button>
            </div>
          </div>
          <div style={{flex:1,overflow:'auto',padding:20,background:'var(--dark-4,#DDE6F3)'}}>
            {docVista.error?(
              <div style={{color:'#8f2f2f',background:'#fff',border:'1px solid rgba(143,47,47,0.28)',borderRadius:8,padding:'18px 20px',fontSize:14,lineHeight:1.5}}>
                {docVista.error}
              </div>
            ):docVista.html===null?(
              <div style={{color:'var(--text-body,#3A4F6E)',textAlign:'center',padding:40,fontSize:14}}>Abriendo el documento…</div>
            ):(
              // Ojo: el HTML viene del servidor y ya pasó por DOMPurify arriba.
              <div className="doc-vista" style={{background:'#fff',color:'#1c1c1c',padding:'40px 48px',borderRadius:6,boxShadow:'0 2px 14px rgba(13,27,53,0.18)',fontFamily:'Georgia, serif',fontSize:15,lineHeight:1.65,maxWidth:820,margin:'0 auto',overflowWrap:'anywhere'}} dangerouslySetInnerHTML={{__html:docVista.html}}/>
            )}
          </div>
          <div style={{padding:'10px 18px',borderTop:'1px solid rgba(13,27,53,0.12)',background:'var(--white,#fff)',color:'var(--text-body,#3A4F6E)',fontSize:11.5}}>
            Vista de solo lectura: el formato puede verse distinto al de Word. Para editarlo, descárgalo, cámbialo y súbelo de nuevo.
          </div>
        </div>
      </div>
    )}

    {confirmCarpetaDel&&(
      <div className="panel-modal-fondo" onClick={()=>setConfirmCarpetaDel(null)}>
        <div className="panel-modal" style={{maxWidth:400,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:'1.6rem',marginBottom:10}}>📁</div>
          <div style={{fontSize:15,color:'#FFFFFF',marginBottom:8,fontWeight:500}}>Eliminar carpeta</div>
          <div style={{fontSize:13,color:'#9BB0CC',marginBottom:22,lineHeight:1.6}}>
            ¿Eliminar la carpeta <strong style={{color:'#FFFFFF'}}>{confirmCarpetaDel.nombre}</strong>?<br/>
            {confirmCarpetaDel.n?`Sus ${confirmCarpetaDel.n} documento${confirmCarpetaDel.n>1?'s':''} no se borran: quedan en "Sin carpeta".`:'Está vacía.'}
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmCarpetaDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'#C9D6E8',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
            <button disabled={guardandoCarpeta} onClick={()=>borrarCarpeta(confirmCarpetaDel.id)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar carpeta</button>
          </div>
        </div>
      </div>
    )}

    {confirmDocDel&&(
      <div className="panel-modal-fondo" onClick={()=>setConfirmDocDel(null)}>
        <div className="panel-modal" style={{maxWidth:380,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:'1.6rem',marginBottom:10}}>🗑️</div>
          <div style={{fontSize:15,color:'#FFFFFF',marginBottom:8,fontWeight:500}}>Eliminar documento</div>
          <div style={{fontSize:13,color:'#9BB0CC',marginBottom:22,lineHeight:1.6}}>¿Seguro que desea eliminar <strong style={{color:'#FFFFFF'}}>{confirmDocDel.nombre}</strong>?<br/>Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmDocDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'#C9D6E8',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
            <button onClick={()=>delDoc(confirmDocDel.id)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

    {confirmPagoDel&&(
      <div className="panel-modal-fondo" onClick={()=>setConfirmPagoDel(null)}>
        <div className="panel-modal" style={{maxWidth:380,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:'1.6rem',marginBottom:10}}>🗑️</div>
          <div style={{fontSize:15,color:'#FFFFFF',marginBottom:8,fontWeight:500}}>Eliminar cuota</div>
          <div style={{fontSize:13,color:'#9BB0CC',marginBottom:22,lineHeight:1.6}}>¿Seguro que desea eliminar <strong style={{color:'#FFFFFF'}}>{confirmPagoDel.nombre}</strong>?<br/>Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmPagoDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'#C9D6E8',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
            <button onClick={()=>delPago(confirmPagoDel.idx)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

    {tab==='pagos'&&vePagos&&(
      <div className="p-section">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap',marginBottom:12}}>
          <div className="p-section-title" style={{margin:0}}>Estado financiero del caso</div>
          {ufHoy&&(
            <div title="Valor oficial publicado por el Servicio de Impuestos Internos. Con él se calculan en pesos las cuotas pactadas en UF."
              style={{display:'inline-flex',alignItems:'baseline',gap:8,padding:'5px 12px',borderRadius:999,border:'1px solid rgba(184,150,90,0.35)',background:'rgba(184,150,90,0.08)',fontSize:12,whiteSpace:'nowrap'}}>
              <span style={{color:'var(--text-muted)'}}>UF hoy {FS.fmtDate(ufHoy.fecha)}</span>
              <strong style={{color:'#FFFFFF',fontSize:14}}>{FS.fmtValorUf(ufHoy.valor)}</strong>
              <span style={{color:'var(--text-muted)',fontSize:10.5}}>SII</span>
            </div>
          )}
        </div>
        <div className="p-grid3" style={{marginBottom:16}}>
          <div className="fin-card"><div className="fin-label">Total acordado</div><div className="fin-val">{FS.fmtMoney(totalP)}</div>
            {totalUf>0&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:6}}>Incluye cuotas por {FS.fmtUf(totalUf)}</div>}
          </div>
          <div className="fin-card ok"><div className="fin-label">Pagado</div><div className="fin-val">{FS.fmtMoney(pagado)}</div></div>
          <div className={`fin-card ${saldo>0?'warn':'ok'}`}><div className="fin-label">Saldo pendiente</div><div className="fin-val">{FS.fmtMoney(saldo)}</div></div>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:10,marginTop:20,marginBottom:10,flexWrap:'wrap'}}>
          <div className="p-section-title" style={{margin:0}}>Tabla de cuotas</div>
          {(rol==='admin'||rol==='abogada')&&pagos.length>0&&!confirmBorrarCuotas&&(
            <button className="p-btn-danger-sm" onClick={()=>setConfirmBorrarCuotas(true)} style={{fontSize:11,padding:'3px 10px'}}>Eliminar todas</button>
          )}
          {whatsappCuotas&&(rol==='admin'||rol==='abogada')&&(
            <a href={whatsappCuotas.url} target="_blank" rel="noopener noreferrer"
              title={whatsappCuotas.vencidas?'Abre WhatsApp con un mensaje listo con las cuotas pendientes, sus fechas y montos'+(whatsappCuotas.sinCorreo?', y le pide su correo':'')+'. Tú lo revisas y lo mandas desde tu número.':'Abre WhatsApp con un recordatorio de la próxima cuota, listo para revisar y mandar desde tu número.'}
              style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:6,background:'none',border:'1px solid rgba(37,211,102,0.45)',borderRadius:6,padding:'4px 12px',fontSize:'0.7rem',color:'#25D366',letterSpacing:'0.08em',whiteSpace:'nowrap',textDecoration:'none'}}>
              💬 {whatsappCuotas.vencidas?`Recordar ${whatsappCuotas.vencidas>1?'cuotas pendientes':'cuota pendiente'} por WhatsApp`:'Recordar próxima cuota por WhatsApp'}
            </a>
          )}
          {whatsappCuotas&&whatsappCuotas.sinCorreo&&(rol==='admin'||rol==='abogada')&&(
            <span style={{flexBasis:'100%',fontSize:11.5,color:'var(--text-muted)',lineHeight:1.5}}>
              Este cliente no tiene correo cargado, así que no recibe los recordatorios automáticos. El mensaje de WhatsApp se lo pide; cuando te lo dé, anótalo en Datos.
            </span>
          )}
        </div>
        {confirmBorrarCuotas&&(
          <div style={{marginBottom:12,padding:'12px 16px',background:'rgba(229,115,115,0.08)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:8,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#e57373',flex:1}}>⚠️ ¿Eliminar las {pagos.length} cuota{pagos.length!==1?'s':''} registrada{pagos.length!==1?'s':''}? Esta acción no se puede deshacer.</span>
            <button className="p-btn-secondary" onClick={()=>setConfirmBorrarCuotas(false)} style={{padding:'6px 14px',fontSize:12}}>Cancelar</button>
            <button className="p-btn-danger-sm" onClick={borrarTodasCuotas} style={{padding:'6px 14px',fontSize:12}}>Sí, eliminar todas</button>
          </div>
        )}
        {pagos.length?<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cuota</th><th>Vencimiento</th><th>Valor cuota</th><th>Estado</th><th>Fecha pago</th><th>Forma de pago</th>{edita&&<th></th>}</tr></thead><tbody>
          {pagos.map((p:any,i:number)=>{
            const isAtrasado = p.estado==='Pendiente' && p.vencimiento && (/^\d{4}-\d{2}-\d{2}$/.test(p.vencimiento)?new Date(p.vencimiento+'T00:00:00'):new Date(p.vencimiento)) < new Date();
            const displayEstado = isAtrasado ? 'Atrasado' : (p.estado||'Pendiente');
            return(
              <tr key={i}>
                <td>{p.concepto||`Cuota ${i+1}`}</td>
                <td>{FS.fmtDate(p.vencimiento||p.fecha)||'—'}</td>
                <td>{Number(p.montoUf)>0 ? (<>
                  {edita ? <MontoCuota enUf monto={p.montoUf} onGuardar={v=>changePagoMontoUf(i,v)}/> : FS.fmtUf(p.montoUf)}
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,whiteSpace:'nowrap'}}
                    title={p.ufValor?`Calculado con la UF ${p.estado==='Pagado'?'del día del pago':'de hoy'} (${FS.fmtDate(p.ufFecha)}): ${FS.fmtValorUf(p.ufValor)}`:undefined}>
                    = {FS.fmtMoney(p.monto)}{p.ufFecha?(p.estado==='Pagado'?` · UF del ${FS.fmtDate(p.ufFecha)}`:' · UF de hoy'):''}
                  </div>
                </>) : edita ? <MontoCuota monto={p.monto} onGuardar={v=>changePagoMonto(i,v)}/> : FS.fmtMoney(p.monto)}</td>
                <td>
                  {edita ? (
                    <select className="p-select" value={displayEstado} onChange={e=>changePagoEstado(i,e.target.value)} style={{fontSize:12,padding:'3px 6px',minWidth:110,background:displayEstado==='Pagado'?'rgba(76,175,80,0.1)':displayEstado==='Atrasado'?'rgba(255,152,0,0.1)':'transparent',borderColor:displayEstado==='Pagado'?'rgba(76,175,80,0.3)':displayEstado==='Atrasado'?'rgba(255,152,0,0.3)':'rgba(255,255,255,0.1)'}}>
                      <option>Pendiente</option><option>Pagado</option><option>Atrasado</option>
                    </select>
                  ) : (
                    <span className={`pago-badge ${displayEstado==='Pagado'?'ok':displayEstado==='Atrasado'?'warn':'neutral'}`}>{displayEstado}</span>
                  )}
                </td>
                <td>
                  {edita && (p.estado==='Pagado'||displayEstado==='Pagado') ? (
                    <input className="p-input" type="date" value={p.fechaPago||''} onChange={e=>changePagoFechaPago(i,e.target.value)} style={{fontSize:12,padding:'3px 6px',width:130}}/>
                  ) : (
                    <span style={{fontSize:12,color:'var(--text-muted)'}}>{FS.fmtDate(p.fechaPago)||'—'}</span>
                  )}
                  {p.enBanco&&<div style={{fontSize:11,color:'#6aad6a',marginTop:3,whiteSpace:'nowrap'}} title={`Revisado en Reportes${p.enBanco.por?` por ${p.enBanco.por}`:''}`}>✓ visto en el banco</div>}
                </td>
                <td>
                  {edita && (p.estado==='Pagado'||displayEstado==='Pagado') ? (
                    <select className="p-select" value={p.formaPago||''} onChange={e=>changePagoFormaPago(i,e.target.value)} style={{fontSize:12,padding:'3px 6px',minWidth:120}}>
                      <option value="">Seleccionar…</option>
                      <option>Transferencia</option>
                      <option>Efectivo</option>
                      <option>Tarjeta débito</option>
                      <option>Tarjeta crédito</option>
                      <option>Cheque</option>
                      <option>Webpay</option>
                      <option>Otro</option>
                    </select>
                  ) : (
                    <span style={{color:'var(--text-muted)',fontSize:12}}>{p.formaPago||'—'}</span>
                  )}
                </td>
                {edita&&<td><button className="p-btn-danger-sm" onClick={()=>setConfirmPagoDel({idx:i,nombre:p.concepto||`Cuota ${i+1}`})}>✕</button></td>}
              </tr>
            );
          })}
        </tbody></table></div>:<div className="p-empty">Sin pagos registrados.</div>}

        {edita&&<>
          {/* Auto-generate cuotas */}
          <div style={{marginTop:16,background:'rgba(255,255,255,0.03)',borderRadius:8,padding:16,border:'1px solid rgba(255,255,255,0.06)',marginBottom:16}}>
            <div className="p-section-title" style={{marginBottom:8}}>⚡ Generar cuotas automáticamente</div>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div className="p-field" style={{margin:0,flex:'0 0 120px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>N° de cuotas</div>
                <input className="p-input" type="number" min="1" max="60" value={autoCuotas} onChange={e=>setAutoCuotas(e.target.value)} placeholder="Ej: 6"/>
              </div>
              <div className="p-field" style={{margin:0,flex:'0 0 90px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Moneda</div>
                <select className="p-select" value={autoMoneda} onChange={e=>{setAutoMoneda(e.target.value as 'CLP'|'UF');setAutoMonto('');}}>
                  <option value="CLP">Pesos</option><option value="UF">UF</option>
                </select>
              </div>
              <div className="p-field" style={{margin:0,flex:'0 0 150px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Valor cada cuota ({autoMoneda==='UF'?'UF':'$'})</div>
                {autoMoneda==='UF'
                  ? <input className="p-input" type="text" value={autoMonto} onChange={e=>setAutoMonto(soloUf(e.target.value))} placeholder="Ej: 3,5" inputMode="decimal"/>
                  : <input className="p-input" type="text" value={autoMonto} onChange={e=>setAutoMonto(FS.fmtMiles(e.target.value))} placeholder="Ej: 200.000" inputMode="numeric"/>}
              </div>
              <div className="p-field" style={{margin:0,flex:'0 0 160px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Vencimiento 1ª cuota</div>
                <input className="p-input" type="date" value={autoVencInicio} onChange={e=>setAutoVencInicio(e.target.value)}/>
              </div>
              <button className="p-btn-primary" onClick={generarCuotas} disabled={!autoCuotas||!autoMonto} style={{padding:'8px 16px',height:38}}>
                Generar {autoCuotas?autoCuotas+' cuotas':''}
              </button>
            </div>
            {autoCuotas && autoMonto && autoMoneda==='CLP' && <div style={{fontSize:12,color:'var(--gold)',marginTop:8}}>Total: {FS.fmtMoney(parseInt(autoCuotas)*FS.parseMoney(autoMonto))} en {autoCuotas} cuotas de {FS.fmtMoney(FS.parseMoney(autoMonto))}</div>}
            {autoCuotas && FS.parseUf(autoMonto)>0 && autoMoneda==='UF' && <div style={{fontSize:12,color:'var(--gold)',marginTop:8}}>
              Total: {FS.fmtUf(parseInt(autoCuotas)*FS.parseUf(autoMonto))} en {autoCuotas} cuotas de {FS.fmtUf(FS.parseUf(autoMonto))}
              {ufHoy&&<span style={{color:'var(--text-muted)'}}> · hoy cada una son {FS.fmtMoney(Math.round(FS.parseUf(autoMonto)*ufHoy.valor))}; el monto en pesos se recalcula con la UF del día y queda fijo con la del día en que se paga</span>}
            </div>}
            {confirmGenerar&&(
              <div style={{marginTop:12,padding:'12px 16px',background:'rgba(229,115,115,0.08)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:8,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                <span style={{fontSize:13,color:'#e57373',flex:1}}>⚠️ Ya existen {(c.pagos||[]).length} cuota{(c.pagos||[]).length!==1?'s':''} registrada{(c.pagos||[]).length!==1?'s':''}. ¿Generar {autoCuotas} cuota{parseInt(autoCuotas)!==1?'s':''} adicional{parseInt(autoCuotas)!==1?'es':''}?</span>
                <button className="p-btn-secondary" onClick={()=>setConfirmGenerar(false)} style={{padding:'6px 14px',fontSize:12}}>Cancelar</button>
                <button className="p-btn-primary" onClick={ejecutarGenerarCuotas} style={{padding:'6px 14px',fontSize:12,background:'#e57373',borderColor:'#e57373'}}>Sí, generar igual</button>
              </div>
            )}
          </div>

          <div style={{marginTop:0}}>
          <div className="p-section-title" style={{marginBottom:8}}>Registrar cuota individual</div>
          <div className="p-grid3" style={{marginBottom:12}}>
            <input className="p-input" placeholder="Cuota (ej: Cuota 1)" value={pConc} onChange={e=>setPConc(e.target.value)}/>
            <div style={{display:'flex',gap:6}}>
              <select className="p-select" value={pMoneda} onChange={e=>{setPMoneda(e.target.value as 'CLP'|'UF');setPMon('');}} style={{flex:'0 0 84px'}} aria-label="Moneda de la cuota">
                <option value="CLP">Pesos</option><option value="UF">UF</option>
              </select>
              {pMoneda==='UF'
                ? <input className="p-input" type="text" inputMode="decimal" placeholder="Valor cuota (UF) — ej: 3,5" value={pMon} onChange={e=>setPMon(soloUf(e.target.value))} style={{flex:1,minWidth:0}}/>
                : <input className="p-input" type="text" inputMode="numeric" placeholder="Valor cuota ($) — ej: 250.000" value={pMon} onChange={e=>setPMon(FS.fmtMiles(e.target.value))} style={{flex:1,minWidth:0}}/>}
            </div>
            <div className="p-field" style={{margin:0}}><div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Vencimiento</div><input className="p-input" type="date" value={pVenc} onChange={e=>setPVenc(e.target.value)}/></div>
            <select className="p-select" value={pEst} onChange={e=>setPEst(e.target.value)}><option>Pendiente</option><option>Pagado</option><option>Atrasado</option></select>
            {pEst==='Pagado'&&<div className="p-field" style={{margin:0}}><div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Fecha de pago</div><input className="p-input" type="date" value={pFecPago||hoyLocal()} onChange={e=>setPFecPago(e.target.value)}/></div>}
            <select className="p-select" value={pForma} onChange={e=>setPForma(e.target.value)}><option>Transferencia</option><option>Efectivo</option><option>Tarjeta de crédito</option><option>Tarjeta de débito</option><option>Cheque</option><option>Otro</option></select>
            <input className="p-input" placeholder="Nota (opcional)" value={pNot} onChange={e=>setPNot(e.target.value)}/>
          </div>
          {pMoneda==='UF'&&FS.parseUf(pMon)>0&&ufHoy&&(
            <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:10}}>
              {FS.fmtUf(FS.parseUf(pMon))} son hoy <strong style={{color:'var(--gold)'}}>{FS.fmtMoney(Math.round(FS.parseUf(pMon)*ufHoy.valor))}</strong>. El monto en pesos se recalcula con la UF del día y, al marcarla pagada, queda fijo con la UF de la fecha de pago.
            </div>
          )}
          <button className="p-btn-primary" onClick={addPago}>＋ Registrar cuota</button>
        </div>
        </>}
        <NotasInternas casoId={c.id} field="notasInternasPagos" value={c.notasInternasPagos} edita={edita} onRefresh={onRefresh}/>
      </div>
    )}

    {confirmDel&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmDel(null)}>
        <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'24px 28px',maxWidth:380,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:28,marginBottom:8}}>🗑️</div>
          <div style={{fontSize:14,color:'var(--cream,#e8e0d0)',marginBottom:6,fontWeight:500}}>¿Eliminar elemento?</div>
          <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:20,lineHeight:1.5}}>Se eliminará {confirmDel.msg}. Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmDel(null)} style={{padding:'8px 20px',fontSize:13,fontWeight:600,color:'#fff',background:'#2563EB',border:'none',borderRadius:7,cursor:'pointer'}}>Cancelar</button>
            <button onClick={()=>{confirmDel.onConfirm();setConfirmDel(null);}} style={{padding:'8px 18px',fontSize:13,fontWeight:500,color:'#f28b8b',background:'none',border:'1px solid rgba(229,115,115,0.4)',borderRadius:7,cursor:'pointer'}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

  </>);
};

/* ══════════════════════════════════════════════════════════════════════════
   NUEVO CASO
   ══════════════════════════════════════════════════════════════════════════ */
const NuevoCaso: React.FC<{rol:string;user:any;userData:any;onBack:()=>void}> = ({rol,user,userData,onBack}) => {
  const DRAFT_KEY = 'nuevoCaso_draft_' + user.uid;
  const DEFAULT_FORM = {tema:'Derecho Laboral',abogadaId:'',abogadaNombre:'',clienteWeb:false,familiaMaterias:[],laboralMotivos:[],nacionalidad:'Chilena',telDigits:'',evaluacionInterna:'',parteRepresentada:''};
  const [f,setF]=useState<any>(()=>{try{const d=localStorage.getItem(DRAFT_KEY);return d?JSON.parse(d):DEFAULT_FORM;}catch{return DEFAULT_FORM;}});
  const [draftRestored,setDraftRestored]=useState(()=>{try{return !!localStorage.getItem(DRAFT_KEY);}catch{return false;}});
  const [abogadas,setAbogadas]=useState<any[]>([]);
  const [captadoras,setCaptadoras]=useState<any[]>([]);
  const [plantillas,setPlantillas]=useState<any[]>([]);
  /** Si el estudio exige captador (Configuración → Casos). El servidor lo
   *  valida igual; esto es para avisarlo en el formulario. */
  const [captadorObligatorio,setCaptadorObligatorio]=useState(false);
  const [msg,setMsg]=useState('');
  const [errors,setErrors]=useState<Record<string,string>>({});
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    FS.loadAbogadas().then(setAbogadas).catch(()=>{});
    FS.loadPlantillas().then(setPlantillas).catch(()=>{});
    if(esEquipoCaso(rol)) FS.loadCaptadoras().then(setCaptadoras).catch(()=>{});
    FS.loadAjustes().then((a:any)=>setCaptadorObligatorio(!!a?.casos?.captadorObligatorio)).catch(()=>{});
  },[]);

  useEffect(()=>{
    const timer=setTimeout(()=>{
      try{
        const strip=(s:string)=>(s||'').replace(/<[^>]*>/g,'').trim();
        const hasContent=!!(f.nombre||f.rut||f.correo||f.dir||f.contra||strip(f.evaluacion)||strip(f.nota)||f.procedimiento||f.rolCaso||f.empleador||strip(f.evaluacionInterna));
        if(hasContent) localStorage.setItem(DRAFT_KEY,JSON.stringify(f));
        else localStorage.removeItem(DRAFT_KEY);
      }catch{}
    },800);
    return ()=>clearTimeout(timer);
  },[f]);

  const [transformando,setTransformando]=useState(false);
  const [expandEvalInterna,setExpandEvalInterna]=useState(false);
  const [hojaEvalInterna,setHojaEvalInterna]=useState(0);
  const transformarConIA=async()=>{
    const raw=textoHojas(f.evaluacionInterna||'');
    if(!raw) return;
    setTransformando(true);setMsg('');
    try{
      const texto=await FS.redactarEvaluacion(raw,f.tema);
      const resultado=DOMPurify.sanitize('<p>'+texto.replace(/\n\n+/g,'</p><p>').replace(/\n/g,'<br>')+'</p>');
      setF((prev:any)=>({...prev,evaluacion:resultado}));
    }catch(e:any){setMsg(e?.status===503?'El redactor con IA aún no está configurado.':('Error IA: '+(e?.message||'Intenta nuevamente')));}
    finally{setTransformando(false);}
  };

  const toggleChecklistItem=(list:string,item:string)=>{
    const current=f[list]||[];
    const updated=current.includes(item)?current.filter((x:string)=>x!==item):[...current,item];
    setF({...f,[list]:updated});
  };

  const guardar=async()=>{
    if(saving) return;
    const errs:Record<string,string>={};
    if(!f.nombre) errs.nombre='Campo obligatorio';
    if(!f.rut) errs.rut='Campo obligatorio';
    if(!f.telDigits) errs.tel='Campo obligatorio';
    else if(!validatePhone(f.telDigits)) errs.tel='Debe ingresar exactamente 8 dígitos';
    // El captador es opcional salvo que el estudio lo exija: hay estudios que
    // no trabajan con captadores. Solo se valida cuando se puede elegir; si
    // no, se anota WEB o quien lo crea.
    if(captadorObligatorio&&esEquipoCaso(rol)&&!f.clienteWeb&&!f.captadoraSelId) errs.captadora='Elige quién captó el caso (o marca que llegó por la página)';
    if(Object.keys(errs).length){setErrors(errs);setMsg('');setTimeout(()=>document.querySelector<HTMLElement>('.input-error')?.scrollIntoView({behavior:'smooth',block:'center'}),50);return;}
    setErrors({});setMsg('Guardando...');setSaving(true);
    try{
      let captadora:string, captadoraId:string;
      if(f.clienteWeb){captadora=CAPTADOR_WEB_NOMBRE;captadoraId=CAPTADOR_WEB_ID;}
      else if(esEquipoCaso(rol)){
        // Si no se eligió a nadie, el caso queda sin captador. Antes caía al
        // else de abajo y anotaba al abogado que lo creó, lo que además le
        // habría dado comisión de captación sin haber captado.
        captadora=f.captadoraSelId?f.captadoraSelNombre:'';captadoraId=f.captadoraSelId||'';
      }
      else{captadora=userData?.nombre||user.email;captadoraId=user.uid;}
      // Precargar las etapas del proceso según la plantilla de la disciplina elegida.
      const plSel=plantillas.find(p=>p.nombre===f.tema);
      const flowKey=(f.tema||'').includes('Familia')?'familiaFlowSteps':'laborFlowSteps';
      const {codigo}=await FS.crearCaso({
        [flowKey]:plSel?.pasos||[],
        nombreCliente:f.nombre,rut:formatRut(f.rut),telefono:'+569'+f.telDigits.replace(/\D/g,''),correo:f.correo||'',direccion:f.dir||'',
        estadoCivil:f.estadoCivil||'',nacionalidad:f.nacionalidad||'Chilena',ocupacion:f.ocup||'',tema:f.tema,
        abogada:f.abogadaNombre,abogadaId:f.abogadaId,
        contraparte:f.contra||'',evaluacion:f.evaluacion||'',notaInterna:f.nota||'',evaluacionInterna:f.evaluacionInterna||'',
        procedimiento:f.procedimiento||'',rolCaso:f.rolCaso||'',
        captadora,captadoraId,clienteWeb:f.clienteWeb,
        familiaMaterias:f.tema==='Derecho de Familia'?f.familiaMaterias:[],
        laboralMotivos:f.tema==='Derecho Laboral'?f.laboralMotivos:[],
        empleador:f.tema==='Derecho Laboral'?(f.empleador||''):'',
        rutEmpleador:f.tema==='Derecho Laboral'?(f.rutEmpleador?formatRut(f.rutEmpleador):''):'',
        representanteLegal:f.tema==='Derecho Laboral'?(f.representanteLegal||''):'',
        domicilioEmpleador:f.tema==='Derecho Laboral'?(f.domicilioEmpleador||''):'',
        fechaDespido:f.tema==='Derecho Laboral'?(f.fechaDespido||''):'',
        parteRepresentada:f.parteRepresentada||'',
      });
      try{localStorage.removeItem(DRAFT_KEY);}catch{}
      setMsg('✓ Caso creado. Código: '+codigo);
      setTimeout(onBack,1800);
    }catch(e:any){setMsg('Error: '+e.message);setSaving(false);}
  };

  const phoneDigits = (f.telDigits||'').replace(/\D/g,'');
  const phoneLen = phoneDigits.length;
  // Quién puede ELEGIR captador (ya no quién está obligado a hacerlo). La
  // lista ofrece a todo el equipo, sin importar el rol de cada uno.
  const puedeElegirCaptador = esEquipoCaso(rol)&&!f.clienteWeb;

  return(
    <div className="p-section">
      <div className="p-section-title">Ingresar nuevo caso</div>
      {draftRestored&&(
        <div style={{background:'rgba(212,175,55,0.1)',border:'1px solid rgba(212,175,55,0.35)',borderRadius:8,padding:'10px 16px',marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,fontSize:13,color:'var(--white)'}}>
          <span>📋 Se recuperó un borrador no guardado. Revisa los datos antes de guardar.</span>
          <button style={{background:'transparent',border:'1px solid rgba(212,175,55,0.5)',color:'var(--gold)',borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>{setF(DEFAULT_FORM);setDraftRestored(false);try{localStorage.removeItem(DRAFT_KEY);}catch{}}}>Descartar borrador</button>
        </div>
      )}
      <div className="p-grid2">
        <div>
          <div className="p-sub-title" style={{marginBottom:10}}>Datos del cliente</div>

          <div className="p-field"><div className="p-field-label">Nombre completo *</div>
            <input className={`p-input ${errors.nombre?'input-error':''}`} value={f.nombre||''} onChange={e=>{if(errors.nombre)setErrors(prev=>({...prev,nombre:''}));setF({...f,nombre:e.target.value});}} placeholder="Nombre completo"/>
            {errors.nombre&&<div className="field-error">{errors.nombre}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">RUT * <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:400}}>Se formatea automáticamente</span></div>
            <input className={`p-input ${errors.rut?'input-error':''}`} value={f.rut||''} onChange={e=>{if(errors.rut)setErrors(prev=>({...prev,rut:''}));const c=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);setF({...f,rut:c.length>=2?formatRut(c):c});}} placeholder="Ej: 12.345.678-9"/>
            {errors.rut&&<div className="field-error">{errors.rut}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">Teléfono / WhatsApp *</div>
            <div style={{display:'flex',alignItems:'center',gap:0}}>
              <div style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRight:'none',borderRadius:'6px 0 0 6px',padding:'8px 10px',fontSize:14,color:'var(--white)',fontWeight:600,letterSpacing:0.5,whiteSpace:'nowrap'}}>+569</div>
              <input className={`p-input ${errors.tel?'input-error':''}`} style={{borderRadius:'0 6px 6px 0',flex:1}} value={f.telDigits||''} onChange={e=>{if(errors.tel)setErrors(prev=>({...prev,tel:''}));const v=e.target.value.replace(/\D/g,'').slice(0,8);setF({...f,telDigits:v});}} placeholder="8 dígitos" maxLength={8} inputMode="numeric"/>
            </div>
            {phoneLen>0 && phoneLen!==8 && <div className="field-error">{phoneLen<8?`Faltan ${8-phoneLen} dígitos`:'Máximo 8 dígitos'}</div>}
            {errors.tel&&phoneLen===0&&<div className="field-error">{errors.tel}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">Correo electrónico</div><input className="p-input" type="email" value={f.correo||''} onChange={e=>setF({...f,correo:e.target.value})}/></div>
          <div className="p-field"><div className="p-field-label">Dirección</div><input className="p-input" value={f.dir||''} onChange={e=>setF({...f,dir:e.target.value})}/></div>

          <div className="p-field"><div className="p-field-label">Estado civil</div>
            <select className="p-select" value={f.estadoCivil||''} onChange={e=>setF({...f,estadoCivil:e.target.value})}>
              <option value="">Seleccionar...</option><option>Soltero/a</option><option>Casado/a</option><option>Divorciado/a</option><option>Viudo/a</option><option>Conviviente civil</option><option>Separado/a</option>
            </select>
          </div>

          <div className="p-field"><div className="p-field-label">Nacionalidad</div>
            <select className="p-select" value={f.nacionalidad||'Chilena'} onChange={e=>setF({...f,nacionalidad:e.target.value})}>
              <option>Chilena</option><option>Argentina</option><option>Boliviana</option><option>Brasileña</option><option>Colombiana</option><option>Ecuatoriana</option><option>Haitiana</option><option>Peruana</option><option>Venezolana</option><option>Dominicana</option><option>Cubana</option><option>Mexicana</option><option>Española</option><option>Otra</option>
            </select>
            {f.nacionalidad==='Otra'&&<input className="p-input" style={{marginTop:6}} placeholder="Especificar nacionalidad" onChange={e=>setF({...f,nacionalidad:e.target.value})}/>}
          </div>

          <div className="p-field"><div className="p-field-label">Ocupación</div><input className="p-input" value={f.ocup||''} onChange={e=>setF({...f,ocup:e.target.value})}/></div>
        </div>

        <div>
          <div className="p-sub-title" style={{marginBottom:10}}>Datos del caso</div>

          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,marginBottom:14,border:'1px solid rgba(255,255,255,0.06)'}}>
            <input type="checkbox" checked={f.clienteWeb||false} onChange={e=>setF({...f,clienteWeb:e.target.checked})} id="clienteWeb" style={{width:18,height:18}}/>
            <label htmlFor="clienteWeb" style={{cursor:'pointer',fontSize:13,color:'var(--white)'}}>🌐 Cliente llegó por página web</label>
          </div>

          <div className="p-field"><div className="p-field-label">Disciplina del caso *</div>
            <select className="p-select" value={f.tema} onChange={e=>setF({...f,tema:e.target.value,familiaMaterias:[],laboralMotivos:[]})}>
              {plantillas.length
                ? plantillas.map(p=><option key={p.id} value={p.nombre}>{p.nombre}</option>)
                : <option>{f.tema}</option>}
              <option value="Otro">Otro</option>
            </select>
          </div>

          <div className="p-field"><div className="p-field-label">Parte que representamos</div>
            <select className="p-select" value={f.parteRepresentada||''} onChange={e=>setF({...f,parteRepresentada:e.target.value})}>
              <option value="">Sin indicar</option>
              {PARTES_REPRESENTADAS.map(o=><option key={o.valor} value={o.valor}>{o.texto}</option>)}
            </select>
          </div>

          {f.tema==='Derecho de Familia' && (
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.06)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Materia</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px'}}>
                {FAMILIA_MATERIAS.map(m=>(
                  <label key={m} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'5px 0'}}>
                    <input type="checkbox" checked={(f.familiaMaterias||[]).includes(m)} onChange={()=>toggleChecklistItem('familiaMaterias',m)} style={{width:16,height:16,flexShrink:0}}/>
                    <span style={{fontSize:13,color:'var(--white)'}}>{m}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {f.tema==='Derecho Laboral' && (
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.06)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Motivo principal consulta <span style={{fontWeight:400,fontSize:11,color:'var(--text-muted)'}}>(puede seleccionar varios)</span></div>
              <div style={{display:'flex',flexDirection:'column',gap:0}}>
                {LABORAL_MOTIVOS.map((m,i)=>(
                  <label key={m} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'8px 0',borderBottom:i<LABORAL_MOTIVOS.length-1?'1px solid rgba(255,255,255,0.04)':'none'}}>
                    <input type="checkbox" checked={(f.laboralMotivos||[]).includes(m)} onChange={()=>toggleChecklistItem('laboralMotivos',m)} style={{width:16,height:16,flexShrink:0}}/>
                    <span style={{fontSize:13,color:'var(--white)',lineHeight:1.4}}>{m}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {f.tema==='Derecho Laboral' && (
            <div style={{background:'rgba(255,255,255,0.02)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.05)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Datos laborales</div>
              <div className="p-field"><div className="p-field-label">Empleador</div><input className="p-input" value={f.empleador||''} onChange={e=>setF({...f,empleador:e.target.value})} placeholder="Nombre empresa o empleador"/></div>
              <div className="p-field"><div className="p-field-label">RUT Empleador</div><input className="p-input" value={f.rutEmpleador||''} onChange={e=>{const c=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);setF({...f,rutEmpleador:c.length>=2?formatRut(c):c});}} placeholder="Ej: 12.345.678-9"/></div>
              <div className="p-field"><div className="p-field-label">Representante legal</div><input className="p-input" value={f.representanteLegal||''} onChange={e=>setF({...f,representanteLegal:e.target.value})} placeholder="Nombre del representante legal"/></div>
              <div className="p-field"><div className="p-field-label">Domicilio empleador</div><input className="p-input" value={f.domicilioEmpleador||''} onChange={e=>setF({...f,domicilioEmpleador:e.target.value})} placeholder="Dirección de la empresa"/></div>
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><input className="p-input" type="date" value={f.fechaDespido||''} onChange={e=>setF({...f,fechaDespido:e.target.value})}/></div>
            </div>
          )}

          <div className="p-field"><div className="p-field-label">Abogado asignado</div><select className="p-select" value={f.abogadaId||''} onChange={e=>{const idx=e.target.selectedIndex;setF({...f,abogadaId:e.target.value,abogadaNombre:e.target.options[idx]?.text||''});}}><option value="">Sin asignar</option>{abogadas.map((a:any)=><option key={a.id} value={a.id}>{a.nombre||a.email}</option>)}</select></div>

          <div className="p-field"><div className="p-field-label">Captador{captadorObligatorio?' *':<> <span style={{color:'var(--text-muted)',fontWeight:400}}>(opcional)</span></>}</div>
            {puedeElegirCaptador?(
              <>
                <select className={`p-select ${errors.captadora?'input-error':''}`} value={f.captadoraSelId||''} onChange={e=>{if(errors.captadora)setErrors(prev=>({...prev,captadora:''}));const opt=e.target.options[e.target.selectedIndex];setF({...f,captadoraSelId:e.target.value,captadoraSelNombre:opt?.text||''});}}>
                  <option value="">— Seleccionar captador —</option>
                  {captadoras.map((c:any)=><option key={c.id} value={c.id}>{c.nombre||c.email}</option>)}
                </select>
                {errors.captadora&&<div className="field-error">{errors.captadora}</div>}
              </>
            ):(
              // Lo que se muestra tiene que coincidir con lo que se va a guardar:
              // WEB si vino del sitio, y si no, el propio nombre —a quien no
              // puede elegir captador se le anota como tal al guardar.
              <input className="p-input" readOnly style={{opacity:0.7}}
                value={f.clienteWeb?CAPTADOR_WEB_NOMBRE:(userData?.nombre||user?.email||'Sin captador')}/>
            )}
          </div>

          <div className="p-field"><div className="p-field-label">Procedimiento</div><input className="p-input" value={f.procedimiento||''} onChange={e=>setF({...f,procedimiento:e.target.value})} placeholder="Ej: Demanda, Mediación, etc."/></div>
          <div className="p-field"><div className="p-field-label">Rol</div><input className="p-input" value={f.rolCaso||''} onChange={e=>setF({...f,rolCaso:e.target.value})} placeholder="Ej: O-1234-2026 (rol o RIT de la causa)"/></div>

          <div className="p-field"><div className="p-field-label">Contraparte</div><textarea className="p-input" rows={3} value={f.contra||''} onChange={e=>setF({...f,contra:e.target.value})} style={{resize:'vertical'}} placeholder="Puede agregar varias líneas con Enter"/></div>
          {esEquipoCaso(rol)&&(
            <div className="p-field">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                <div className="p-field-label" style={{marginBottom:0}}>Evaluación interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Borrador rápido · Solo equipo</span></div>
                <button type="button" onClick={()=>setExpandEvalInterna(true)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}}>⛶ Página completa</button>
              </div>
              <HojasEditor papel value={f.evaluacionInterna||''} onChange={v=>setF((prev:any)=>({...prev,evaluacionInterna:v}))} placeholder="Anote aquí de forma libre y rápida los antecedentes del caso durante la consulta. Puede usar lenguaje informal, abreviaturas, etc." minHeight={130} activa={hojaEvalInterna} onActiva={setHojaEvalInterna}/>
              <BotonTransformarIA onClick={transformarConIA} cargando={transformando} vacio={!textoHojas(f.evaluacionInterna||'')}/>
            </div>
          )}
          {/* Página completa para escribir la evaluación interna. Comparte el
              estado del recuadro chico: no hay nada que guardar al cerrar. */}
          {expandEvalInterna&&(
            <ModoEscritura titulo="Evaluación interna" subtitulo={`Borrador rápido · Solo equipo${f.nombre?' · '+f.nombre:''}`} nota="Se guarda con el caso al pulsar Guardar en la ficha" onCerrar={()=>setExpandEvalInterna(false)}
              pie={<BotonTransformarIA compacto onClick={transformarConIA} cargando={transformando} vacio={!textoHojas(f.evaluacionInterna||'')}/>}>
              <HojasEditor papel value={f.evaluacionInterna||''} onChange={v=>setF((prev:any)=>({...prev,evaluacionInterna:v}))} placeholder="Anote aquí de forma libre y rápida los antecedentes del caso durante la consulta." minHeight="calc(100vh - 240px)" activa={hojaEvalInterna} onActiva={setHojaEvalInterna}/>
            </ModoEscritura>
          )}
          <div className="p-field">
            <div style={{border:'1px solid rgba(220,50,50,0.55)',borderRadius:10,padding:'12px 14px',background:'rgba(220,50,50,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                <span style={{fontSize:16,lineHeight:1}}>⚠️</span>
                <span style={{fontSize:12,color:'#ff8a80',lineHeight:1.4}}><b>Visible para el cliente</b> — Revisa antes de guardar.</span>
              </div>
              <div className="p-field-label" style={{marginBottom:6}}>Evaluación <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Visible para el cliente</span></div>
              <RichText value={f.evaluacion||''} onChange={v=>setF({...f,evaluacion:v})} placeholder="Evaluación profesional del caso (se mostrará al cliente)" minHeight={120}/>
            </div>
          </div>
          <div className="p-field">
            <div className="p-field-label">Nota interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>Solo equipo</span></div>
            <RichText value={f.nota||''} onChange={v=>setF({...f,nota:v})} placeholder="Notas internas del equipo" minHeight={80}/>
          </div>
        </div>
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:16,gap:10}}>
        <button className="p-btn-secondary" onClick={onBack}>Cancelar</button>
        <button className="p-btn-primary" onClick={guardar} disabled={saving} style={{padding:'10px 24px',opacity:saving?0.7:1,cursor:saving?'not-allowed':'pointer'}}>
          {saving?'Guardando…':'Guardar caso →'}
        </button>
      </div>
      {msg&&<div style={{marginTop:10,fontSize:13,color:'var(--gold)',textAlign:'right'}}>{msg}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   USUARIOS
   ══════════════════════════════════════════════════════════════════════════ */
// De menos a más acceso. Los dos admin van juntos al final y se distinguen por
// una sola cosa: si además litiga. El que no litiga queda fuera del selector de
// abogado del caso y, por lo tanto, del reparto de honorarios.
const ROLES_USUARIO = [
  {value:'captadora',        label:'Captador'},
  {value:'captadora_general',label:'Captador general'},
  {value:'procuradora',      label:'Procurador'},
  {value:'abogado_externo',  label:'Abogado Externo'},
  {value:'abogado_asociado', label:'Abogado Asociado'},
  {value:'abogada',          label:'Abogado'},
  {value:'admin',            label:'Admin / Abogado'},
  {value:'admin_no_abogado', label:'Admin / No abogado'},
];

// Qué se le advierte al elegir cada rol, arriba del botón de crear. El plan se
// cobra por abogado, así que conviene decirlo donde se elige y no cuando el
// servidor ya rechazó.
const NOTA_ROL: Record<string,string> = {
  admin: 'Manda en el estudio y puede llevar causas: entra al reparto de honorarios. Ocupa un cupo del plan.',
  admin_no_abogado: 'Ve y edita todo, incluido el dinero, pero no puede ser el abogado de un caso: no entra al reparto de honorarios. Ocupa un cupo del plan.',
  abogada: 'Ocupa un cupo de abogado del plan.',
  abogado_asociado: 'Trabaja todos los casos, sin ver honorarios, flujo de caja, reportes ni la administración del estudio. Entra al reparto de honorarios: lo que le toca se acuerda aparte. Ocupa un cupo de abogado del plan.',
  abogado_externo: 'Ocupa un cupo de abogado del plan.',
  procuradora: 'Incluido: dos procuradores por cada abogado del plan.',
  captadora: 'Ve y edita solo los casos que captó; no abre casos ni ve honorarios. Los captadores no ocupan cupo: puedes crear los que necesites.',
  captadora_general: 'Trabaja como un procurador: abre casos y ve y edita todos los del estudio. No ve honorarios, flujo de caja, reportes, Chatbularis ni configuración, y no cambia el captador de un caso ya creado. No ocupa cupo.',
};

const Usuarios:React.FC<{rol?:string;uid?:string}>=({rol,uid})=>{
  const [users,setUsers]=useState<any[]>([]);
  const [showForm,setShowForm]=useState(false);
  const [f,setF]=useState({nombre:'',email:'',password:'',rol:'captadora'});
  const [creating,setCreating]=useState(false);
  const [msg,setMsg]=useState('');
  const [err,setErr]=useState('');
  const [credModal,setCredModal]=useState<{uid:string,nombre:string,email:string}|null>(null);
  const [sendingReset,setSendingReset]=useState(false);
  const [resetMsg,setResetMsg]=useState('');
  const [resetErr,setResetErr]=useState('');

  const refresh=()=>FS.loadUsuarios().then(setUsers).catch(()=>{});
  useEffect(()=>{refresh();},[]);

  const [togglingUid,setTogglingUid]=useState<string|null>(null);
  const [rolUid,setRolUid]=useState<string|null>(null);
  const [rolErr,setRolErr]=useState<{uid:string;msg:string}|null>(null);
  // Por omisión se invita: así el admin nunca conoce la contraseña de su
  // colega, y no hay que mandarla por WhatsApp para que quede ahí guardada.
  const [porInvitacion,setPorInvitacion]=useState(true);
  const [enlaceInvit,setEnlaceInvit]=useState<{url:string;email:string;enviada:boolean}|null>(null);

  const handleCreate=async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!f.nombre.trim()||!f.email.trim()){setErr('Completa nombre y correo.');return;}
    if(!porInvitacion&&!f.password){setErr('Escribe una contraseña o elige invitar por correo.');return;}
    setCreating(true);setErr('');setMsg('');setEnlaceInvit(null);
    try{
      // Sin contraseña, el backend crea al usuario con una clave aleatoria que
      // nadie conoce y le manda un enlace para que defina la suya.
      const r:any=await FS.crearUsuario(porInvitacion?{...f,password:undefined}:f);
      if(porInvitacion&&r?.invitacion){
        setEnlaceInvit({url:r.invitacion.enlace,email:f.email,enviada:r.invitacion.enviada});
        setMsg(r.invitacion.enviada
          ? `Invitación enviada a ${f.email}. Tiene 24 horas para definir su contraseña.`
          : `Usuario creado, pero el correo no salió. Pásale tú el enlace de abajo.`);
      } else {
        setMsg(`Usuario "${f.nombre}" creado correctamente.`);
      }
      setShowForm(false);
      setF({nombre:'',email:'',password:'',rol:'captadora'});
      refresh();
    }catch(e:any){
      setErr(e?.message||'Error al crear usuario.');
    }finally{setCreating(false);}
  };

  const abrirCredencial=(uid:string,nombre:string,email:string)=>{
    setCredModal({uid,nombre,email});
    setResetMsg('');
    setResetErr('');
  };

  const handleEnviarRecuperacion=async()=>{
    if(!credModal)return;
    setSendingReset(true);setResetMsg('');setResetErr('');
    try{
      await FS.enviarRecuperacion(credModal.email);
      setResetMsg('Enlace de recuperación enviado al correo del usuario.');
    }catch(e:any){
      setResetErr(e?.message||'Error al enviar el enlace de recuperación.');
    }finally{setSendingReset(false);}
  };

  const handleDesvincular=async(id:string,nombre:string)=>{
    if(!window.confirm(`¿Desvincular a "${nombre}"? No podrá acceder al sistema hasta que sea reactivado.`))return;
    setTogglingUid(id);
    try{ await FS.desactivarUsuario(id); refresh(); }
    catch(e:any){ alert('Error: '+(e?.message||'No se pudo desvincular.')); }
    finally{ setTogglingUid(null); }
  };

  // Eliminar: primero se muestra lo que tiene la persona (ingresos, casos que
  // editó, asistencia, ficha de empleado...) y recién ahí se confirma. El
  // servidor no deja eliminar a alguien activo ni con casos a su nombre.
  const [eliminar,setEliminar]=useState<{id:string;nombre:string;r:FS.ResumenEliminar|null;error:string;ocupado:boolean}|null>(null);
  const handleEliminar=async(id:string,nombre:string)=>{
    setEliminar({id,nombre,r:null,error:'',ocupado:false});
    try{ const r=await FS.antesDeEliminarUsuario(id); setEliminar(x=>x&&x.id===id?{...x,r}:x); }
    catch(e:any){ setEliminar(x=>x&&x.id===id?{...x,error:e?.message||'No se pudo revisar al usuario.'}:x); }
  };
  const confirmarEliminar=async()=>{
    if(!eliminar)return;
    setEliminar({...eliminar,ocupado:true,error:''});
    try{ await FS.eliminarUsuario(eliminar.id); setEliminar(null); refresh(); }
    catch(e:any){ setEliminar(x=>x&&{...x,ocupado:false,error:e?.message||'No se pudo eliminar.'}); }
  };

  const handleReactivar=async(id:string)=>{
    setTogglingUid(id);
    try{ await FS.reactivarUsuario(id); refresh(); }
    catch(e:any){ alert('Error: '+(e?.message||'No se pudo reactivar.')); }
    finally{ setTogglingUid(null); }
  };

  // Cambiar el rol de alguien del equipo.
  //
  // Se confirma siempre, aunque sea un solo clic en un selector: cambiar un rol
  // mueve cupos del plan (y por lo tanto lo que se paga) y puede sacar a alguien
  // del reparto de honorarios. El servidor tiene la última palabra —no deja al
  // estudio sin admin, no pasa del cupo, y no deja casos con un abogado que ya
  // no puede serlo— así que su error se muestra tal cual, junto a la fila.
  const etiquetaRol=(v:string)=>ROLES_USUARIO.find(r=>r.value===v)?.label||v;

  const handleRol=async(u:any,nuevo:string)=>{
    if(!nuevo||nuevo===u.rol)return;
    const nombre=u.nombre||u.email;
    const propio=!!uid&&u.id===uid;
    const aviso=propio
      ? `Vas a cambiar TU rol de ${etiquetaRol(u.rol)} a ${etiquetaRol(nuevo)}.\n\n`+
        `Se aplica de inmediato: si el rol nuevo puede menos, perderás acceso a parte del panel.`
      : `¿Cambiar a ${nombre} de ${etiquetaRol(u.rol)} a ${etiquetaRol(nuevo)}?`;
    if(!window.confirm(aviso))return;
    setRolUid(u.id);setRolErr(null);setMsg('');
    try{
      await FS.actualizarUsuario(u.id,{rol:nuevo});
      setMsg(`${nombre} ahora es ${etiquetaRol(nuevo)}.`);
      // El propio rol también viaja en el perfil guardado del navegador, que no
      // se entera de este cambio: se corrige y se recarga, para no quedar viendo
      // un menú que el servidor ya no autoriza.
      if(propio){ marcarRol(nuevo); window.location.reload(); return; }
      refresh();
    }catch(e:any){
      setRolErr({uid:u.id,msg:e?.message||'No se pudo cambiar el rol.'});
    }finally{ setRolUid(null); }
  };

  const copiar=(texto:string)=>navigator.clipboard.writeText(texto).catch(()=>{});

  return(
    <div className="p-section">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <div className="p-section-title" style={{marginBottom:0}}>Equipo del estudio</div>
        {rol==='admin'&&(
          <button className="p-btn-primary" onClick={()=>{setShowForm(v=>!v);setMsg('');setErr('');}}>
            {showForm?'× Cancelar':'+ Nuevo usuario'}
          </button>
        )}
      </div>

      {msg&&<div style={{color:'#81C784',fontSize:13,marginBottom:12,padding:'8px 12px',background:'rgba(129,199,132,0.08)',borderRadius:6}}>✓ {msg}</div>}
      {enlaceInvit&&(
        // Se muestra siempre, no solo cuando el correo falla: sirve igual para
        // dictárselo por teléfono a quien no lo encuentra en su bandeja.
        <div style={{marginBottom:12,padding:'10px 12px',borderRadius:6,border:`1px solid ${enlaceInvit.enviada?'#DDE3E9':'#E8C98A'}`,background:enlaceInvit.enviada?'rgba(0,0,0,0.02)':'rgba(232,201,138,0.10)'}}>
          <div style={{fontSize:12,color:'#6B82A0',marginBottom:6}}>
            Enlace para {enlaceInvit.email}{enlaceInvit.enviada?' (también le llegó por correo)':' — el correo no salió, pásaselo tú'}
          </div>
          <input readOnly value={enlaceInvit.url} onFocus={e=>e.currentTarget.select()}
                 style={{width:'100%',fontSize:12,fontFamily:'monospace',padding:'7px 9px',border:'1px solid #DDE3E9',borderRadius:6}}/>
          <button type="button" onClick={()=>{navigator.clipboard?.writeText(enlaceInvit.url).then(()=>setMsg('Enlace copiado.'),()=>setMsg('No se pudo copiar: selecciona el enlace y cópialo a mano.'));}}
                  style={{marginTop:7,fontSize:12,padding:'5px 10px',borderRadius:6,border:'1px solid #DDE3E9',background:'#fff',cursor:'pointer'}}>
            Copiar enlace
          </button>
        </div>
      )}

      {showForm&&rol==='admin'&&(
        <form onSubmit={handleCreate} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.09)',borderRadius:8,padding:20,marginBottom:20}}>
          <div className="p-section-title" style={{marginBottom:14,fontSize:13}}>Crear nuevo usuario</div>
          <div style={{display:'flex',gap:18,flexWrap:'wrap',marginBottom:14,fontSize:13.5}}>
            <label style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer'}}>
              <input type="radio" checked={porInvitacion} onChange={()=>setPorInvitacion(true)}/>
              Enviarle una invitación por correo <span style={{color:'#1F7A52',fontWeight:600}}>(recomendado)</span>
            </label>
            <label style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer'}}>
              <input type="radio" checked={!porInvitacion} onChange={()=>setPorInvitacion(false)}/>
              Definir yo su contraseña
            </label>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="p-field">
              <div className="p-field-label">Nombre completo</div>
              <input className="p-input" value={f.nombre} onChange={e=>setF({...f,nombre:e.target.value})} placeholder="Ej: María González" required/>
            </div>
            <div className="p-field">
              <div className="p-field-label">Correo electrónico</div>
              <input className="p-input" type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} placeholder="correo@ejemplo.cl" required/>
            </div>
            <div className="p-field">
              <div className="p-field-label">Contraseña</div>
              {porInvitacion ? (
                <div style={{fontSize:13,color:'#6B82A0',padding:'9px 0',lineHeight:1.45}}>
                  La define esta persona desde el enlace que le llega por correo.
                </div>
              ) : (
                <ClaveInput className="p-input" value={f.password} onChange={e=>setF({...f,password:e.target.value})} placeholder="Mínimo 12 caracteres" minLength={12} colorOjo="#8FB0E6"/>
              )}
            </div>
            <div className="p-field">
              <div className="p-field-label">Rol</div>
              <select className="p-select" value={f.rol} onChange={e=>setF({...f,rol:e.target.value})}>
                {ROLES_USUARIO.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4,lineHeight:1.5}}>
                {NOTA_ROL[f.rol]||''}
              </div>
            </div>
          </div>
          {err&&<div style={{color:'#ef5350',fontSize:13,marginTop:10}}>{err}</div>}
          <div style={{marginTop:14}}>
            <button type="submit" className="p-btn-primary" disabled={creating} style={{opacity:creating?0.7:1,cursor:creating?'not-allowed':'pointer'}}>
              {creating?'Creando…':'Crear usuario →'}
            </button>
          </div>
        </form>
      )}

      <table className="p-table">
        <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Estado</th>{rol==='admin'&&<th>Acciones</th>}</tr></thead>
        <tbody>
          {users.map(u=>{
            const activo=u.activo!==false;
            return(
              <tr key={u.id} style={{opacity:activo?1:0.6}}>
                <td>{u.nombre||'—'}</td>
                <td style={{color:'var(--text-muted)'}}>{u.email||'—'}</td>
                <td>
                  {rol==='admin'?(
                    <>
                      <select className="p-select" value={u.rol||''} disabled={rolUid===u.id}
                              onChange={e=>handleRol(u,e.target.value)}
                              style={{fontSize:12,padding:'4px 8px',minWidth:150,opacity:rolUid===u.id?0.6:1}}>
                        {/* Si el rol guardado no está en la lista (uno viejo),
                            igual se muestra: si no, el selector mentiría
                            diciendo que esta persona es captador. */}
                        {!ROLES_USUARIO.some(r=>r.value===u.rol)&&<option value={u.rol}>{u.rol||'—'}</option>}
                        {ROLES_USUARIO.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      {NOTA_ROL[u.rol]&&(
                        <div style={{fontSize:10.5,color:'var(--text-muted)',marginTop:3,lineHeight:1.4,maxWidth:260}}>{NOTA_ROL[u.rol]}</div>
                      )}
                      {rolErr&&rolErr.uid===u.id&&(
                        <div style={{fontSize:11,color:'#ef5350',marginTop:4,lineHeight:1.4,maxWidth:260}}>{rolErr.msg}</div>
                      )}
                    </>
                  ):(
                    <span className="doc-tipo-badge">{etiquetaRol(u.rol)||'—'}</span>
                  )}
                </td>
                <td><span className={`pago-badge ${activo?'ok':'atrasado'}`}>{activo?'Activo':'Inactivo'}</span></td>
                {rol==='admin'&&(
                  <td style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    <button
                      onClick={()=>abrirCredencial(u.id,u.nombre||u.email,u.email||'')}
                      style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer'}}
                    >Ver acceso</button>
                    {activo?(
                      <button
                        onClick={()=>handleDesvincular(u.id,u.nombre||u.email)}
                        disabled={togglingUid===u.id}
                        style={{background:'rgba(239,83,80,0.12)',border:'1px solid rgba(239,83,80,0.3)',color:'#ef5350',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer',opacity:togglingUid===u.id?0.6:1}}
                      >{togglingUid===u.id?'…':'Desvincular'}</button>
                    ):(
                      <button
                        onClick={()=>handleReactivar(u.id)}
                        disabled={togglingUid===u.id}
                        style={{background:'rgba(129,199,132,0.12)',border:'1px solid rgba(129,199,132,0.3)',color:'#81C784',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer',opacity:togglingUid===u.id?0.6:1}}
                      >{togglingUid===u.id?'…':'Reactivar'}</button>
                    )}
                    {!activo&&u.id!==uid&&(
                      <button
                        onClick={()=>handleEliminar(u.id,u.nombre||u.email)}
                        disabled={togglingUid===u.id}
                        title="Muestra lo que tiene antes de eliminarlo."
                        style={{background:'transparent',border:'1px solid rgba(239,83,80,0.3)',color:'#ef5350',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer',opacity:togglingUid===u.id?0.6:1}}
                      >Eliminar</button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {eliminar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={()=>!eliminar.ocupado&&setEliminar(null)}>
          <div style={{background:'#1a2235',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,padding:24,width:460,maxWidth:'92vw',maxHeight:'88vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:600,fontSize:15,color:'#F0F4FF',marginBottom:4}}>Eliminar a {eliminar.nombre}</div>
            <div style={{fontSize:12.5,color:'var(--text-muted)',marginBottom:14}}>Antes de eliminar, esto es lo que tiene en el estudio:</div>
            {!eliminar.r&&!eliminar.error&&<div style={{fontSize:13,color:'var(--text-muted)'}}>Revisando…</div>}
            {eliminar.r&&(()=>{const r=eliminar.r;
              const fila=(t:string,v:React.ReactNode,alerta=false)=>(
                <div style={{display:'flex',justifyContent:'space-between',gap:12,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:13}}>
                  <span style={{color:'var(--text-muted)'}}>{t}</span><span style={{color:alerta?'#E0A64A':'#F0F4FF',textAlign:'right'}}>{v}</span>
                </div>);
              const lista=(cs:{codigo:string;cliente:string}[])=>cs.map(c=>`${c.codigo} ${c.cliente}`).join(', ');
              return(<>
                {fila('Ingresos al panel',r.ingresos?`${r.ingresos}${r.ultimaActividad?` · última actividad ${new Date(r.ultimaActividad).toLocaleString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}`:''}`:'Nunca entró')}
                {fila('Casos a su nombre',r.casosACargo.length?lista(r.casosACargo):'Ninguno',r.casosACargo.length>0)}
                {fila('Ediciones de casos',r.edicionesDeCasos?<>{r.edicionesDeCasos}<div style={{fontSize:11.5,color:'var(--text-muted)'}}>{lista(r.casosEditados)}</div></>:'Ninguna',r.edicionesDeCasos>0)}
                {fila('Asistencia',r.diasDeAsistencia?`${r.diasDeAsistencia} días marcados`:'Sin registros')}
                {fila('Avisos',r.avisos||'Ninguno')}
                {fila('Ficha de empleado',r.empleado?`${r.empleado} (se conserva con sus documentos)`:'No tiene',!!r.empleado)}
                {fila('Conversaciones de Chatbularis',r.conversacionesChatbularis||'Ninguna')}
                <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.55,margin:'12px 0'}}>
                  {r.puedeEliminar
                    ?'Al eliminarlo no podrá volver a entrar y deja de aparecer en el estudio. Lo que hizo se conserva; en los historiales figurará como usuario eliminado. No se puede deshacer.'
                    :<span style={{color:'#E0A64A'}}>{r.motivo}</span>}
                </div>
              </>);})()}
            {eliminar.error&&<div style={{fontSize:12.5,color:'#ef5350',marginBottom:10}}>{eliminar.error}</div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="p-btn-secondary" disabled={eliminar.ocupado} onClick={()=>setEliminar(null)}>Cancelar</button>
              <button disabled={!eliminar.r?.puedeEliminar||eliminar.ocupado} onClick={confirmarEliminar}
                style={{background:eliminar.r?.puedeEliminar?'#c0504d':'rgba(255,255,255,0.08)',border:'none',color:'#fff',borderRadius:6,padding:'8px 16px',fontSize:13,cursor:eliminar.r?.puedeEliminar?'pointer':'not-allowed'}}>
                {eliminar.ocupado?'Eliminando…':'Eliminar definitivamente'}</button>
            </div>
          </div>
        </div>
      )}

      {credModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={()=>setCredModal(null)}>
          <div style={{background:'#1a2235',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,padding:28,width:380,maxWidth:'90vw'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div style={{fontWeight:600,fontSize:14,color:'var(--text-primary)'}}>Credenciales de acceso</div>
              <button onClick={()=>setCredModal(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:16}}>{credModal.nombre}</div>

            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Usuario (correo)</div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  <span style={{fontSize:13,color:'var(--text-primary)',wordBreak:'break-all'}}>{credModal.email}</span>
                  <button onClick={()=>copiar(credModal.email)} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:5,padding:'3px 8px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>Copiar</button>
                </div>
              </div>

              <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:14,marginTop:2}}>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>
                  Contraseña
                </div>
                <button
                  onClick={handleEnviarRecuperacion}
                  disabled={sendingReset}
                  className="p-btn-primary"
                  style={{width:'100%',opacity:sendingReset?0.6:1,cursor:sendingReset?'not-allowed':'pointer',fontSize:13}}
                >
                  {sendingReset?'Enviando…':'Enviar enlace de recuperación por correo'}
                </button>
                {resetErr&&<div style={{color:'#ef5350',fontSize:12,marginTop:6}}>{resetErr}</div>}
                {resetMsg&&<div style={{color:'#81C784',fontSize:12,marginTop:6}}>✓ {resetMsg}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   INGRESO WEB
   ══════════════════════════════════════════════════════════════════════════ */

/* ── Plazos legales: helpers compartidos por Tasks y Notificaciones ────── */
const COLOR_URGENCIA: Record<string,string> = {
  realizado: 'var(--text-muted)', vencido: '#EF5350', urgente: '#EF5350', proximo: '#E0A64A', holgado: '#9BB0CC',
};

/** Colores de los plazos en la cabecera del caso. Los mismos umbrales que el
 *  semáforo de arriba (vencido o hasta 5 días hábiles en rojo, hasta 15 en
 *  naranjo), pero lo holgado en verde: en la cabecera el plazo tiene que
 *  llamar la atención, y "está bien" también es información (decisión de
 *  Jans, 22-sep-2026). Notificaciones y Tasks siguen con el gris azulado. */
const COLOR_PLAZO_CABECERA: Record<string,string> = {
  ...COLOR_URGENCIA, holgado: '#6ABF7B',
};

/** "Vence hoy" / "quedan 3 días hábiles" / "vencido hace 4 días hábiles". */
const textoPlazoCabecera = (dias:number):string => {
  if (dias === 0) return 'VENCE HOY';
  if (dias < 0) return `vencido hace ${-dias} ${-dias === 1 ? 'día hábil' : 'días hábiles'}`;
  return `${dias === 1 ? 'queda' : 'quedan'} ${dias} ${dias === 1 ? 'día hábil' : 'días hábiles'}`;
};

/** Qué se muestra en vez de los días cuando el plazo ya no corre. Si la
 *  causa tiene rol, se dice cuál: así se entiende por qué quedó tachado sin
 *  que nadie lo marcara. */
const textoCumplido = (p:PLZ.PlazoLegal):string => p.judicializado ? `Judicializada · rol ${p.judicializado}` : 'Cumplido';
const tituloJudicializado = (rol:string):string =>
  `La causa ya tiene rol (${rol}), así que la demanda se presentó y este plazo se da por cumplido. Para volver a verlo pendiente, borra el rol en el caso.`;

/** "Vence hoy" / "3 días hábiles" / "Vencido hace 4 días hábiles". */
const textoDias = (dias:number):string => {
  if (dias === 0) return 'Vence hoy';
  if (dias < 0) return `Vencido hace ${-dias} ${-dias === 1 ? 'día hábil' : 'días hábiles'}`;
  return `${dias} ${dias === 1 ? 'día hábil' : 'días hábiles'}`;
};

/** Marcar un plazo como cumplido, con respuesta inmediata en pantalla.
 *
 *  El check tarda lo que tarda el servidor, y en una tabla de plazos ese medio
 *  segundo sin reacción se lee como "no se guardó" y la gente vuelve a pulsar.
 *  Se pinta al instante y se revierte solo si la grabación falla. */
const useMarcarPlazo = (nombre:string, recargar:()=>void) => {
  const [local,setLocal]=useState<Record<string,boolean>>({});
  const marcar=async(p:PLZ.PlazoLegal,done:boolean)=>{
    setLocal(m=>({...m,[p.id]:done}));
    try{ await FS.marcarPlazoRealizado(p.casoId,p.clave,done,nombre); recargar(); }
    catch{ setLocal(m=>({...m,[p.id]:!done})); }
  };
  const hecho=(p:PLZ.PlazoLegal):boolean => p.id in local ? local[p.id] : !!p.realizado;
  return {marcar,hecho};
};

/* ══════════════════════════════════════════════════════════════════════════
   CALENDARIO
   ══════════════════════════════════════════════════════════════════════════ */
// Parsea "YYYY-MM-DD" o "DD-MM-YYYY" (con - / .) a Date local (medianoche).
const parseFechaLocal=(fecha:string):Date|null=>{
  const s=String(fecha||'').trim();
  let m=s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if(m)return new Date(+m[1],+m[2]-1,+m[3]);
  m=s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if(m)return new Date(+m[3],+m[2]-1,+m[1]);
  return null;
};
// `tipo` es la categoría interna del evento; `clase` es lo que el abogado
// escribió al agendarlo (Audiencia, Comparendo, Asesoría, A.J., ...) y es lo
// que el popup muestra como título.
type EvCal={fecha:Date;tipo:'audiencia'|'plazo';clase:string;titulo:string;hora?:string;lugar?:string;cliente:string;casoId:string};

type TareaCal={casoId:string;index:number;texto:string;done:boolean;plazo:Date|null;cliente:string;tareaId?:string};

const CalendarioView:React.FC<{rol:string;nombre:string;uid:string;onAbrirCaso?:(casoId:string)=>void}>=({rol,nombre,uid,onAbrirCaso})=>{
  const [casos,setCasos]=useState<any[]|null>(null);
  const [ref,setRef]=useState(()=>{const d=new Date();return {y:d.getFullYear(),m:d.getMonth()};});
  const [feed,setFeed]=useState<{url:string;https:string}|null>(null);
  const [copiado,setCopiado]=useState(false);
  const [ayuda,setAyuda]=useState(false);
  const [tab,setTab]=useState<'calendario'|'tasks'>('calendario');
  const [evento,setEvento]=useState<EvCal|null>(null); // audiencia seleccionada (popup)
  // Pulsar una tarea abre su detalle. Antes la marcaba como hecha al
  // instante: mirar qué era la completaba sin querer.
  const [tarea,setTarea]=useState<TareaCal|null>(null);

  const cargar=useCallback(()=>{ FS.loadCasos(rol,uid).then(setCasos).catch(()=>setCasos([])); },[rol,uid]);
  useEffect(()=>{ cargar(); FS.calendarioFeedUrl().then(setFeed).catch(()=>{}); },[cargar]);

  // Calendario = SOLO audiencias/comparendos (los plazos/acciones van en Tasks).
  const audiencias=useMemo<EvCal[]>(()=>{
    if(!casos)return[];
    const evs:EvCal[]=[];
    for(const c of casos){
      const cliente=c.nombreCliente||c.cliente||'—';
      for(const a of (c.audiencias||[])){
        if(!audienciaVigente(a))continue; // suspendida o reprogramada: vale su nueva fecha
        const f=parseFechaLocal(a?.fecha); if(!f)continue;
        const clase=(a?.tipo||'').trim()||'Audiencia';
        evs.push({fecha:f,tipo:'audiencia',clase,titulo:`${clase} · ${cliente}`,hora:a?.hora,lugar:a?.lugar,cliente,casoId:c.id});
      }
    }
    return evs;
  },[casos]);

  // Tasks = checklist de acciones pendientes de todos los casos.
  const tareas=useMemo<TareaCal[]>(()=>{
    if(!casos)return[];
    const ts:TareaCal[]=[];
    for(const c of casos){
      const cliente=c.nombreCliente||c.cliente||'—';
      (c.checklist||[]).forEach((item:any,index:number)=>{
        const plazoStr=extractPlazo(item?.texto||'');
        ts.push({casoId:c.id,index,texto:item?.texto||'',done:!!item?.done,plazo:plazoStr?parseFechaLocal(plazoStr):null,cliente,tareaId:item?.id});
      });
    }
    return ts;
  },[casos]);
  const conPlazo=useMemo(()=>tareas.filter(t=>t.plazo).sort((a,b)=>(a.plazo!.getTime())-(b.plazo!.getTime())),[tareas]);
  const sinPlazo=useMemo(()=>tareas.filter(t=>!t.plazo),[tareas]);

  // Los plazos legales no son acciones que alguien escribió: los calcula la
  // ley con los mismos casos. Van en Tasks porque es donde el estudio mira lo
  // que tiene que hacer, y encabezan la lista porque son los que no se pueden
  // recuperar si se pasan.
  const plazosLegales=useMemo(()=>casos?PLZ.plazosDeCasos(casos):[],[casos]);
  const {marcar:marcarPlazo,hecho:plazoHecho}=useMarcarPlazo(nombre,cargar);

  const toggleTarea=async(t:TareaCal)=>{
    await FS.toggleCheckItem(t.casoId,t.index,!t.done);
    if(t.texto) await FS.actualizarRealizadaPorTexto(t.casoId,t.texto,!t.done,t.tareaId);
    cargar();
  };

  const porDia=useMemo(()=>{
    const map:Record<string,EvCal[]>={};
    for(const e of audiencias){
      if(e.fecha.getFullYear()!==ref.y||e.fecha.getMonth()!==ref.m)continue;
      const k=String(e.fecha.getDate());
      (map[k]=map[k]||[]).push(e);
    }
    return map;
  },[audiencias,ref]);

  const primerDia=new Date(ref.y,ref.m,1).getDay();
  const offset=(primerDia+6)%7;
  const diasEnMes=new Date(ref.y,ref.m+1,0).getDate();
  const celdas:(number|null)[]=[...Array(offset).fill(null),...Array.from({length:diasEnMes},(_,i)=>i+1)];
  while(celdas.length%7!==0)celdas.push(null);
  const hoyN=new Date();
  const esHoy=(d:number)=>hoyN.getFullYear()===ref.y&&hoyN.getMonth()===ref.m&&hoyN.getDate()===d;
  const mover=(delta:number)=>setRef(r=>{const nm=r.m+delta;return {y:r.y+Math.floor(nm/12),m:((nm%12)+12)%12};});
  const copiar=()=>{if(!feed)return;navigator.clipboard.writeText(feed.https).then(()=>{setCopiado(true);setTimeout(()=>setCopiado(false),2000);}).catch(()=>{});};

  // Tareas con @plazo, ubicadas por día en el mes visible (para la grilla de Tasks).
  const tareaPorDia=useMemo(()=>{
    const map:Record<string,TareaCal[]>={};
    for(const t of tareas){
      if(!t.plazo||t.plazo.getFullYear()!==ref.y||t.plazo.getMonth()!==ref.m)continue;
      const k=String(t.plazo.getDate());
      (map[k]=map[k]||[]).push(t);
    }
    return map;
  },[tareas,ref]);
  // Los plazos legales del mes visible, para pintarlos en la grilla.
  const plazoPorDia=useMemo(()=>{
    const map:Record<string,PLZ.PlazoLegal[]>={};
    for(const p of plazosLegales){
      if(p.vence.getFullYear()!==ref.y||p.vence.getMonth()!==ref.m)continue;
      const k=String(p.vence.getDate());
      (map[k]=map[k]||[]).push(p);
    }
    return map;
  },[plazosLegales,ref]);

  // Texto de la tarea sin el token @plazo (se conserva @menciones como en svabogadas).
  const limpiarTarea=(txt:string)=>String(txt||'').replace(/@plazo\s+[\d\-/.]+/gi,'').replace(/\s+/g,' ').trim();

  const tabBtn=(id:'calendario'|'tasks',label:string)=>(
    <button onClick={()=>setTab(id)} style={{padding:'8px 16px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',border:'1px solid '+(tab===id?'rgba(100,181,246,0.5)':'rgba(255,255,255,0.14)'),background:tab===id?'rgba(100,181,246,0.14)':'transparent',color:tab===id?'#64B5F6':'var(--text-muted)'}}>{label}</button>
  );

  // Grilla mensual reutilizable: recibe cómo pintar el contenido de cada día.
  const renderGrid=(celda:(d:number)=>React.ReactNode)=>(
    <div style={{border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,overflow:'hidden'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(0,1fr))',background:'rgba(255,255,255,0.03)',fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em'}}>
        {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d=>(<div key={d} style={{padding:'8px 6px',textAlign:'center'}}>{d}</div>))}
      </div>
      {/* minmax(0,1fr) y no 1fr: "1fr" es minmax(auto,1fr), así que una tarea
          larga escrita en una sola línea ensancha SU columna y descuadra las
          siete. Con el mínimo en 0 los anchos quedan fijos y el texto se
          recorta con puntos suspensivos, que era la intención. */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(0,1fr))'}}>
        {celdas.map((d,i)=>(
          <div key={i} style={{height:118,minWidth:0,padding:6,display:'flex',flexDirection:'column',borderTop:'1px solid rgba(255,255,255,0.06)',borderLeft:i%7?'1px solid rgba(255,255,255,0.06)':'none',background:d&&esHoy(d)?'rgba(100,181,246,0.08)':'transparent'}}>
            {d&&(<><div style={{fontSize:11,fontWeight:esHoy(d)?700:400,color:esHoy(d)?'#64B5F6':'var(--text-muted)',marginBottom:4,flexShrink:0}}>{d}</div><div style={{flex:1,minWidth:0,minHeight:0,overflowY:'auto',overflowX:'hidden'}}>{celda(d)}</div></>)}
          </div>
        ))}
      </div>
    </div>
  );
  const mesNav=(
    <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',gap:8,marginBottom:10}}>
      <button className="p-btn-secondary" onClick={()=>mover(-1)}>‹</button>
      <span style={{minWidth:150,textAlign:'center',fontWeight:600}}>{MESES_NOMBRES[ref.m]} {ref.y}</span>
      <button className="p-btn-secondary" onClick={()=>mover(1)}>›</button>
    </div>
  );

  return (
    <div className="p-section">
      <div className="p-section-title" style={{margin:'0 0 12px'}}>🗓️ Calendario</div>

      {/* Pestañas: Calendario de audiencias / Tasks */}
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        {tabBtn('calendario','📅 Calendario de audiencias')}
        {tabBtn('tasks','✅ Tasks')}
      </div>

      {/* Vinculación con Google Calendar (suscripción .ics, simple y rápida) */}
      <div style={{padding:'12px 14px',border:'1px solid rgba(100,181,246,0.25)',borderRadius:10,background:'rgba(100,181,246,0.05)',marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <div style={{fontSize:13,color:'var(--text-muted)'}}>
            📆 <strong style={{color:'#fff'}}>Vincular con Google Calendar</strong> <span style={{color:'#cdd8ea'}}>— pega el enlace una sola vez y tus audiencias, comparendos y tareas con fecha límite aparecen solos en tu Google Calendar. No pide iniciar sesión ni permisos.</span>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="p-btn-secondary" onClick={copiar} disabled={!feed}>{copiado?'✓ Copiado':'Copiar enlace'}</button>
            <button className="p-btn-secondary" onClick={()=>setAyuda(a=>!a)}>¿Cómo?</button>
          </div>
        </div>
        {ayuda&&(
          <div style={{margin:'10px 0 0',fontSize:12.5,color:'var(--text-muted)',lineHeight:1.7}}>
            <div style={{color:'#cdd8ea',fontWeight:600,marginBottom:2}}>Para añadirlo</div>
            <ol style={{margin:0,paddingLeft:20}}>
              <li>Copia el enlace con el botón de arriba.</li>
              <li>En Google Calendar (computador): <strong>Otros calendarios</strong> → <strong>＋</strong> → <strong>Desde una URL</strong>.</li>
              <li>Pega el enlace y <strong>Añadir calendario</strong>. Listo, se sincroniza solo.</li>
              <li>Cada miembro del equipo hace lo mismo con este mismo enlace.</li>
            </ol>
            <div style={{color:'#cdd8ea',fontWeight:600,margin:'8px 0 2px'}}>Para ocultarlo o quitarlo</div>
            <ol style={{margin:0,paddingLeft:20}}>
              <li><strong>Ocultarlo un rato:</strong> en la lista de la izquierda, desmarca la casilla del calendario. Sigue actualizándose, solo deja de verse.</li>
              <li><strong>Quitarlo del todo:</strong> pasa el cursor sobre su nombre → <strong>⋮</strong> → <strong>Cancelar la suscripción</strong>.</li>
              <li>Quitarlo de tu Google <strong>no borra nada</strong> en Tabularis: las audiencias y las tareas siguen aquí. Puedes volver a añadirlo cuando quieras con el mismo enlace.</li>
            </ol>
            <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid rgba(255,255,255,0.08)',fontSize:12}}>
              ⏱️ Google decide cada cuánto vuelve a leer el enlace y suele tardar <strong>varias horas</strong> en reflejar un cambio. No es un fallo: es así para todos los calendarios por URL. Si acabas de agendar una audiencia y la necesitas ya, mírala en esta pantalla.
            </div>
          </div>
        )}
      </div>

      {mesNav}

      {tab==='calendario'?(
        <>
          {renderGrid(d=>(porDia[String(d)]||[]).map((e,j)=>(
            <div key={j} onClick={()=>setEvento(e)} title={`${e.hora?e.hora+' · ':''}${e.titulo} — ${e.cliente}`}
              style={{fontSize:10.5,lineHeight:1.25,marginBottom:3,padding:'2px 5px',borderRadius:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',background:'rgba(184,150,90,0.22)',color:'#E0C08A',cursor:'pointer'}}>
              {e.hora?e.hora+' ':''}{e.titulo}
            </div>
          )))}
          {casos&&audiencias.length===0&&<div style={{color:'var(--text-muted)',fontSize:13,padding:'14px 4px'}}>No hay audiencias registradas. Agrégalas dentro de cada caso, en Proceso → Audiencias y fechas clave.</div>}
        </>
      ):(
        <>
          {/* Grilla de tareas: cada acción con @plazo aparece como píldora azul en su día */}
          {/* La tarea primero y el cliente debajo en chico: al angostarse la
              columna, poner el nombre del cliente delante hacía que se comiera
              todo el ancho y no se viera qué había que hacer. */}
          {renderGrid(d=>(<>
            {/* Dorados y arriba: los plazos legales se distinguen a simple
                vista de las acciones que escribe el equipo, que van en azul. */}
            {(plazoPorDia[String(d)]||[]).map((p,j)=>{
              const done=plazoHecho(p);
              return (
                <div key={'p'+j} onClick={()=>onAbrirCaso&&onAbrirCaso(p.casoId)} title={`${p.cliente} — ${p.titulo} (${p.norma})`}
                  style={{fontSize:10.5,lineHeight:1.25,marginBottom:3,padding:'3px 6px',borderRadius:5,overflow:'hidden',cursor:'pointer',
                    background:done?'rgba(184,150,90,0.30)':'#8a6d2f',color:'#fff',textDecoration:done?'line-through':'none',opacity:done?0.65:1}}>
                  <div style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>⚖️ {p.titulo}</div>
                  <div style={{fontSize:9.5,opacity:0.8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.cliente}</div>
                </div>
              );
            })}
            {(tareaPorDia[String(d)]||[]).map((t,j)=>(
              <div key={'t'+j} onClick={()=>setTarea(t)} title={`${t.cliente} — ${limpiarTarea(t.texto)}`}
                style={{fontSize:10.5,lineHeight:1.25,marginBottom:3,padding:'3px 6px',borderRadius:5,overflow:'hidden',cursor:'pointer',
                  background:t.done?'rgba(59,111,212,0.4)':'#3b6fd4',color:'#fff',textDecoration:t.done?'line-through':'none',opacity:t.done?0.65:1}}>
                <div style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{limpiarTarea(t.texto)}</div>
                <div style={{fontSize:9.5,opacity:0.75,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.cliente}</div>
              </div>
            ))}
          </>))}

          {/* Plazos legales (calculados, marcables acá y en Notificaciones) */}
          {plazosLegales.length>0&&(<>
            <div className="p-section-title" style={{fontSize:12,marginTop:20,color:'#E5D5AE',letterSpacing:'0.06em'}}>⚖️ PLAZOS LEGALES</div>
            <div style={{marginTop:6}}>
              {plazosLegales.map(p=>{
                const done=plazoHecho(p);
                const color=COLOR_URGENCIA[done?'realizado':PLZ.urgencia(p)];
                return (
                  <label key={p.id} title={p.judicializado?tituloJudicializado(p.judicializado):undefined} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 4px',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:13.5,cursor:'pointer',opacity:done?0.5:1}}>
                    <input type="checkbox" checked={done} disabled={!!p.judicializado} onChange={e=>marcarPlazo(p,e.target.checked)} style={{cursor:p.judicializado?'not-allowed':'pointer'}}/>
                    <span style={{fontVariantNumeric:'tabular-nums',minWidth:88,fontSize:12.5,color}}>{PLZ.fmtDia(p.vence)}</span>
                    <span style={{flex:1,textDecoration:done?'line-through':'none',color:done?'var(--text-muted)':'inherit'}}>
                      <strong style={{fontWeight:600}}>{p.cliente}</strong> — {p.titulo}
                      <span style={{color:'var(--text-muted)',fontSize:12}}> · {p.norma}</span>
                    </span>
                    <span style={{fontSize:12,color,whiteSpace:'nowrap'}}>{done?textoCumplido(p):textoDias(p.dias)}</span>
                  </label>
                );
              })}
            </div>
          </>)}

          {/* Con fecha límite (lista marcable, además del calendario) */}
          <div className="p-section-title" style={{fontSize:12,marginTop:20,color:'var(--text-muted)',letterSpacing:'0.06em'}}>CON FECHA LÍMITE</div>
          {conPlazo.length===0?(
            <div style={{color:'var(--text-muted)',fontSize:13,padding:'8px 4px'}}>No hay tareas con fecha límite.</div>
          ):(
            <div style={{marginTop:6}}>
              {conPlazo.map((t,i)=>{
                const vencido=t.plazo!&&t.plazo!.getTime()<Date.now()-86400000&&!t.done;
                return (
                  <label key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 4px',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:13.5,cursor:'pointer'}}>
                    <input type="checkbox" checked={t.done} onChange={()=>toggleTarea(t)} style={{cursor:'pointer'}}/>
                    <span style={{fontVariantNumeric:'tabular-nums',minWidth:88,fontSize:12.5,color:t.done?'var(--text-muted)':vencido?'#EF5350':'#E8A0A0'}}>{FS.fmtDate(t.plazo)}</span>
                    <span style={{flex:1,textDecoration:t.done?'line-through':'none',color:t.done?'var(--text-muted)':'inherit'}}>
                      <strong style={{fontWeight:600}}>{t.cliente}</strong> — {renderMenciones(t.texto)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Sin fecha límite (checklist marcable) */}
          <div className="p-section-title" style={{fontSize:12,marginTop:20,color:'var(--text-muted)',letterSpacing:'0.06em'}}>SIN FECHA LÍMITE</div>
          {sinPlazo.length===0?(
            <div style={{color:'var(--text-muted)',fontSize:13,padding:'8px 4px'}}>No hay tareas sin fecha límite.</div>
          ):(
            <div style={{marginTop:6}}>
              {sinPlazo.map((t,i)=>(
                <label key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 4px',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:13.5,cursor:'pointer'}}>
                  <input type="checkbox" checked={t.done} onChange={()=>toggleTarea(t)} style={{cursor:'pointer'}}/>
                  <span style={{flex:1,textDecoration:t.done?'line-through':'none',color:t.done?'var(--text-muted)':'inherit'}}>
                    <strong style={{fontWeight:600}}>{t.cliente}</strong> — {renderMenciones(t.texto)}
                  </span>
                </label>
              ))}
            </div>
          )}
          {casos&&tareas.length===0&&plazosLegales.length===0&&<div style={{color:'var(--text-muted)',fontSize:13,padding:'8px 4px'}}>Sin acciones pendientes. Agrégalas dentro de cada caso, en Proceso → Checklist de acciones pendientes (usa @plazo para la fecha límite).</div>}
        </>
      )}

      {/* Popup del evento (como Google Calendar) */}
      {tarea&&(
        <div onClick={()=>setTarea(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:16}}>
          <div onClick={ev=>ev.stopPropagation()} style={{background:'#1a2540',border:'1px solid rgba(255,255,255,0.14)',borderRadius:14,width:'100%',maxWidth:420,boxShadow:'0 20px 60px rgba(0,0,0,0.5)',overflow:'hidden'}}>
            <div style={{height:6,background:tarea.done?'#3b6fd4':'#5B8DEF'}}/>
            <div style={{padding:'20px 22px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                <div style={{fontSize:17,fontWeight:700,color:'#fff',lineHeight:1.35}}>{limpiarTarea(tarea.texto)}</div>
                <button onClick={()=>setTarea(null)} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
              </div>
              <div style={{fontSize:14,color:'#cdd8ea',marginTop:4}}>{tarea.cliente}</div>
              <div style={{display:'grid',gap:12,marginTop:18}}>
                {tarea.plazo&&(
                  <div style={{display:'flex',alignItems:'center',gap:12,color:'#e6ecf5',fontSize:14}}>
                    <span style={{fontSize:16}}>⏰</span>
                    <span>
                      {fechaLarga(tarea.plazo)}
                      {!tarea.done&&tarea.plazo.getTime()<Date.now()-86400000&&<strong style={{color:'#e57373'}}> · vencida</strong>}
                    </span>
                  </div>
                )}
                <div style={{display:'flex',alignItems:'center',gap:12,color:'#e6ecf5',fontSize:14}}>
                  <span style={{fontSize:16}}>{tarea.done?'✅':'⬜'}</span>
                  <span>{tarea.done?'Realizada':'Pendiente'}</span>
                </div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20,flexWrap:'wrap'}}>
                <button className="p-btn-primary" style={{fontSize:13}}
                  onClick={async()=>{await toggleTarea(tarea);setTarea(null);}}>
                  {tarea.done?'Marcar como pendiente':'Marcar como realizada'}
                </button>
                {onAbrirCaso&&(
                  <button className="p-btn-secondary" style={{fontSize:13}}
                    onClick={()=>{const id=tarea.casoId;setTarea(null);onAbrirCaso(id);}}>
                    Abrir el caso →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {evento&&(
        <div onClick={()=>setEvento(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div onClick={ev=>ev.stopPropagation()} style={{background:'#1a2540',border:'1px solid rgba(255,255,255,0.14)',borderRadius:14,width:'100%',maxWidth:420,boxShadow:'0 18px 50px rgba(0,0,0,0.5)',overflow:'hidden'}}>
            <div style={{height:6,background:'#B8965A'}}/>
            <div style={{padding:'20px 22px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                <div style={{fontSize:19,fontWeight:700,color:'#fff'}}>{evento.clase}</div>
                <button onClick={()=>setEvento(null)} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
              </div>
              <div style={{fontSize:14,color:'#cdd8ea',marginTop:2}}>{evento.cliente}</div>
              <div style={{display:'grid',gap:12,marginTop:18}}>
                <div style={{display:'flex',alignItems:'center',gap:12,color:'#e6ecf5',fontSize:14}}>
                  <span style={{fontSize:16}}>📅</span>
                  <span>{fechaLarga(evento.fecha)}</span>
                </div>
                {evento.hora&&<div style={{display:'flex',alignItems:'center',gap:12,color:'#e6ecf5',fontSize:14}}><span style={{fontSize:16}}>🕘</span><span>{evento.hora} hrs</span></div>}
                {evento.lugar&&<div style={{display:'flex',alignItems:'flex-start',gap:12,color:'#e6ecf5',fontSize:14}}>
                  <span style={{fontSize:16}}>📍</span>
                  {/^https?:\/\//i.test(evento.lugar)
                    ? <a href={evento.lugar} target="_blank" rel="noreferrer" style={{color:'#64B5F6',wordBreak:'break-all'}}>{evento.lugar}</a>
                    : <span style={{wordBreak:'break-word'}}>{evento.lugar}</span>}
                </div>}
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:22}}>
                <button className="p-btn-secondary" onClick={()=>setEvento(null)}>Cerrar</button>
                {onAbrirCaso&&<button className="p-btn-primary" onClick={()=>{const id=evento.casoId;setEvento(null);onAbrirCaso(id);}}>Ver caso →</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
/* ══════════════════════════════════════════════════════════════════════════
   CONFIGURACIÓN (admin): respaldo exportable de todos los datos del estudio
   ══════════════════════════════════════════════════════════════════════════ */
/* Día en que ESTE estudio cierra el mes. Se guarda en los ajustes del estudio,
   así que cambiarlo acá no altera el cierre de ningún otro estudio. */
const CierreDeMes: React.FC<{esAdmin:boolean}> = ({esAdmin}) => {
  const [dia,setDia]=useState<number>(PER.DIA_CORTE_DEFAULT);
  const [cargando,setCargando]=useState(true);
  const [guardando,setGuardando]=useState(false);
  const [estado,setEstado]=useState<'idle'|'ok'|'error'>('idle');
  useEffect(()=>{
    FS.loadAjustes()
      .then((a:any)=>setDia(PER.normalizarDiaCorte(a?.periodo?.diaCorte)))
      .catch(()=>{})
      .finally(()=>setCargando(false));
  },[]);
  const guardar=async(d:number)=>{
    setDia(d); setGuardando(true); setEstado('idle');
    try{ await FS.guardarAjustes({periodo:{diaCorte:d}}); setEstado('ok'); }
    catch{ setEstado('error'); }
    finally{ setGuardando(false); }
  };
  const dias=Array.from({length:PER.DIA_CORTE_MAX},(_,i)=>i+1);
  return (
    <div className="cfg-card">
      <div className="cfg-card-title">📅 Cierre de mes</div>
      <p className="cfg-desc">
        Muchos estudios no cierran el mes el día 1. Si el suyo cierra <strong>el día 20</strong>, el mes de agosto va
        del 21 de julio al 20 de agosto, y ese es el corte que usan los reportes, el flujo de caja y los bonos
        de captadores para repartir lo cobrado y contar los gastos.
      </p>
      {cargando?<div style={{color:'var(--text-muted)',fontSize:13}}>Cargando…</div>:(
        <>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{fontSize:13}}>El mes cierra el día</span>
            <select className="p-select" value={dia} disabled={!esAdmin||guardando}
              onChange={e=>guardar(Number(e.target.value))}
              style={{fontSize:13,padding:'6px 10px',width:90}}>
              {dias.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            {guardando&&<span style={{fontSize:12,color:'var(--text-muted)'}}>Guardando…</span>}
            {!guardando&&estado==='ok'&&<span style={{fontSize:12,color:'#4CAF50'}}>Guardado ✓</span>}
            {!guardando&&estado==='error'&&<span style={{fontSize:12,color:'#e57373'}}>No se pudo guardar</span>}
          </div>
          <div style={{fontSize:12.5,color:'var(--text-muted)',marginTop:10,lineHeight:1.6}}>
            {PER.explicacionCorte(dia)}
          </div>
          {!esAdmin&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>Solo el administrador puede cambiarlo.</div>}
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>
            Los gastos se siguen anotando con su fecha real; lo que cambia es en qué período se suman.
          </div>
        </>
      )}
    </div>
  );
};

/* Reglas para guardar un caso. Por ahora una: exigir el captador, que decide
   a quién se le paga el bono de captación. */
const ReglasCasos: React.FC<{esAdmin:boolean}> = ({esAdmin}) => {
  const [obligatorio,setObligatorio]=useState(false);
  const [cargando,setCargando]=useState(true);
  const [guardando,setGuardando]=useState(false);
  const [estado,setEstado]=useState<'idle'|'ok'|'error'>('idle');
  useEffect(()=>{
    FS.loadAjustes()
      .then((a:any)=>setObligatorio(!!a?.casos?.captadorObligatorio))
      .catch(()=>{})
      .finally(()=>setCargando(false));
  },[]);
  const cambiar=async(v:boolean)=>{
    setObligatorio(v); setGuardando(true); setEstado('idle');
    try{ await FS.guardarAjustes({casos:{captadorObligatorio:v}}); setEstado('ok'); }
    catch{ setObligatorio(!v); setEstado('error'); }
    finally{ setGuardando(false); }
  };
  return (
    <div className="cfg-card">
      <div className="cfg-card-title">📁 Casos</div>
      <p className="cfg-desc">
        Con el captador obligatorio no se puede guardar un caso nuevo sin anotar quién lo captó (alguien del equipo, o WEB si llegó por la página),
        ni quitárselo a un caso que ya lo tiene. Los casos antiguos sin captador se siguen pudiendo editar.
      </p>
      {cargando?<div style={{color:'var(--text-muted)',fontSize:13}}>Cargando…</div>:(
        <label style={{display:'flex',alignItems:'center',gap:10,cursor:esAdmin?'pointer':'default',fontSize:13.5,marginTop:10}}>
          <input type="checkbox" checked={obligatorio} disabled={!esAdmin||guardando} onChange={e=>cambiar(e.target.checked)}/>
          <span>Captador obligatorio</span>
          {guardando&&<span style={{fontSize:12,color:'var(--text-muted)'}}>Guardando…</span>}
          {estado==='ok'&&!guardando&&<span style={{fontSize:12,color:'#4CAF50'}}>Guardado ✓</span>}
          {estado==='error'&&!guardando&&<span style={{fontSize:12,color:'#e57373'}}>No se pudo guardar</span>}
        </label>
      )}
    </div>
  );
};

/* Recordatorios automáticos al cliente: audiencias, cuotas y atrasos. Cada
   uno se puede apagar por separado: son correos que salen a nombre del
   estudio, así que la decisión es suya. */
type RecordatoriosAj = {activo:boolean;cuotas:boolean;atrasos:boolean};
const RecordatoriosCliente: React.FC<{esAdmin:boolean}> = ({esAdmin}) => {
  const [aj,setAj]=useState<RecordatoriosAj>({activo:true,cuotas:true,atrasos:false});
  const [cargando,setCargando]=useState(true);
  const [guardando,setGuardando]=useState<keyof RecordatoriosAj|null>(null);
  const [estado,setEstado]=useState<'idle'|'ok'|'error'>('idle');
  useEffect(()=>{
    FS.loadAjustes()
      .then((a:any)=>{ if(a?.recordatorios) setAj(prev=>({...prev,...a.recordatorios})); })
      .catch(()=>{})
      .finally(()=>setCargando(false));
  },[]);
  const cambiar=async(campo:keyof RecordatoriosAj,v:boolean)=>{
    setAj(prev=>({...prev,[campo]:v})); setGuardando(campo); setEstado('idle');
    try{ await FS.guardarAjustes({recordatorios:{[campo]:v}}); setEstado('ok'); }
    catch{ setAj(prev=>({...prev,[campo]:!v})); setEstado('error'); }
    finally{ setGuardando(null); }
  };
  const Interruptor=({campo}:{campo:keyof RecordatoriosAj})=>(
    <label style={{display:'flex',alignItems:'center',gap:10,cursor:esAdmin?'pointer':'default',fontSize:13.5}}>
      <input type="checkbox" checked={aj[campo]} disabled={!esAdmin||guardando!==null} onChange={e=>cambiar(campo,e.target.checked)}/>
      <span>{aj[campo]?'Activados':'Apagados'}</span>
      {guardando===campo&&<span style={{fontSize:12,color:'var(--text-muted)'}}>Guardando…</span>}
    </label>
  );
  const nota:React.CSSProperties={fontSize:12,color:'var(--text-muted)',marginTop:8,lineHeight:1.6};
  return (
    <div className="cfg-card">
      <div className="cfg-card-title">✉️ Recordatorios automáticos al cliente</div>
      <p className="cfg-desc">
        Salen a nombre del estudio y, si el cliente responde, la respuesta les llega a ustedes.
        Solo se le avisa a los casos que tengan el correo del cliente cargado, y nunca se manda el mismo aviso dos veces.
      </p>
      {cargando?<div style={{color:'var(--text-muted)',fontSize:13}}>Cargando…</div>:(
        <>
          <div style={{marginTop:14}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Audiencias y comparendos</div>
            <div style={{...nota,marginTop:0,marginBottom:8}}>
              Cada audiencia o comparendo que cargues en el caso se le recuerda al cliente
              <strong> 72 horas antes y 24 horas antes</strong>. Si cambias la fecha, se vuelve a avisar con la nueva.
            </div>
            <Interruptor campo="activo"/>
          </div>
          <div style={{marginTop:18}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Cuotas de honorarios</div>
            <div style={{...nota,marginTop:0,marginBottom:8}}>
              <strong>El mismo día en que vence</strong> cada cuota, un recordatorio amable: "hoy vence tu cuota; si ya la pagaste, ignora este correo".
              Si el cliente tiene cuotas anteriores sin pagar, el mismo correo se las menciona. No es una cobranza.
              Las causas de <strong>Derecho Laboral</strong> no reciben avisos de cuotas (ni del día ni de atraso): ahí normalmente paga el empleador y el estudio le transfiere al cliente.
            </div>
            <Interruptor campo="cuotas"/>
          </div>
          <div style={{marginTop:18}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Cuotas atrasadas</div>
            <div style={{...nota,marginTop:0,marginBottom:8}}>
              Cuando al cliente ya no le quedan cuotas por vencer y sigue debiendo, se le escribe <strong>a la semana y al mes</strong> de la última,
              invitándolo a conversar la fecha. Después de dos meses el sistema deja de insistir. Mientras tenga cuotas por venir no recibe estos avisos:
              lo pendiente se le recuerda en el correo del día de la siguiente cuota.
            </div>
            <Interruptor campo="atrasos"/>
            {!aj.atrasos&&<div style={nota}>Ojo: al activarlo se les escribe también a los clientes sin cuotas por vencer que deben alguna vencida hace menos de dos meses.</div>}
          </div>
          <div style={{marginTop:10,minHeight:18}}>
            {estado==='ok'&&guardando===null&&<span style={{fontSize:12,color:'#4CAF50'}}>Guardado ✓</span>}
            {estado==='error'&&guardando===null&&<span style={{fontSize:12,color:'#e57373'}}>No se pudo guardar</span>}
          </div>
          {!esAdmin&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Solo el administrador puede cambiarlos.</div>}
        </>
      )}
    </div>
  );
};

// Plan y cupos de abogado. El estudio se los cambia solo: subir rige al
// instante y bajar solo si no quedan más abogados activos que cupos. El nuevo
// precio corre desde la próxima renovación; el ciclo en curso no se prorratea.
// Es lo que se le promete en la landing ("sumas o quitas abogados cuando
// quieras"), y antes había que escribirnos para cualquiera de las dos cosas.
type PlanEstudio = {
  plan: string|null; autoservicio: boolean; cupos: number; abogadosActivos: number; maxCupos: number;
  ciclo: string; periodoFin: string|null; estado: string|null; precioMensualClp: number|null;
  cobro: { meses: number; totalClp: number; panelClp: number; mesesConOferta?: number }|null; precios: (number|null)[];
  /** Hasta cuándo tiene el precio de lanzamiento; null si no lo tiene o aún no paga. */
  lanzamientoHasta?: string|null;
  conPanel: boolean;
  /** El saldo prepago de Chatbularis del estudio. */
  saldoIa: { saldoClp: number; agotado: boolean; porAgotarse: boolean; sinTope: boolean }|null;
  /** Cómo pagar: link del Banco de Chile y datos de transferencia. */
  pago: DatosPago;
};
const Dato: React.FC<{etiqueta: string; valor: React.ReactNode; alerta?: boolean}> = ({etiqueta, valor, alerta}) => (
  <div className="cfg-dato">
    <div className="cfg-dato-etiqueta">{etiqueta}</div>
    <div className="cfg-dato-valor" style={alerta?{color:'#F5A3A3'}:undefined}>{valor}</div>
  </div>
);
const clpPlan = (n: number|null|undefined) => (typeof n === 'number' ? '$' + n.toLocaleString('es-CL') : '—');
const PlanYAbogados: React.FC = () => {
  const [plan,setPlan]=useState<PlanEstudio|null>(null);
  const [cupos,setCupos]=useState(1);
  const [guardando,setGuardando]=useState(false);
  const [msg,setMsg]=useState<{ok?:string;error?:string}>({});
  const cargar=useCallback(()=>{
    api<PlanEstudio>('/ajustes/plan').then(p=>{ setPlan(p); setCupos(p.cupos); }).catch(()=>setPlan(null));
  },[]);
  useEffect(()=>{ cargar(); },[cargar]);
  if(!plan) return null;
  const cambio = cupos !== plan.cupos;
  const guardar=async()=>{
    setMsg({}); setGuardando(true);
    try{
      const p = await api<PlanEstudio>('/ajustes/cupos',{method:'PATCH',body:{cupos}});
      setPlan(p); setCupos(p.cupos);
      setMsg({ok: cupos > plan.cupos
        ? `Listo: ahora tienes hasta ${p.cupos} abogados y ya puedes invitar. El nuevo precio se cobra desde la próxima renovación.`
        : `Listo: ahora tienes ${p.cupos===1?'1 abogado':`hasta ${p.cupos} abogados`}. El nuevo precio rige desde la próxima renovación.`});
    }catch(e:any){ setMsg({error: e?.message || 'No se pudo cambiar los cupos.'}); }
    finally{ setGuardando(false); }
  };
  const btn: React.CSSProperties = {width:36,height:34,borderRadius:8,border:'1px solid rgba(255,255,255,0.2)',background:'transparent',color:'#EDF1F7',fontSize:18,cursor:'pointer'};
  return (
    <div className="cfg-grid">
    <div className="cfg-card">
      <div className="cfg-card-title">👥 Plan y abogados</div>
      <p className="cfg-desc">
        Pagas según cuántos abogados son: cada cupo lo ocupa un socio, abogado asociado, externo o administrador. Los captadores no se cobran y los procuradores vienen incluidos, dos por abogado.
        {plan.periodoFin && <> Tu {plan.estado==='trial'?'prueba':'plan'} {plan.ciclo!=='mensual'&&plan.estado!=='trial'?`(${plan.ciclo}) `:''}vence el {new Date(plan.periodoFin).toLocaleDateString('es-CL')}.</>}
      </p>
      <div className="cfg-stats">
        <Dato etiqueta="Plan" valor={plan.plan||'—'}/>
        <Dato etiqueta="Cupos" valor={plan.cupos}/>
        <Dato etiqueta="Abogados activos" valor={plan.abogadosActivos} alerta={plan.abogadosActivos>plan.cupos}/>
        {plan.precioMensualClp!=null && <Dato etiqueta="Panel" valor={<>{clpPlan(plan.precioMensualClp)}<span className="cfg-dato-unidad"> /mes</span></>}/>}
      </div>
      {plan.autoservicio ? (
        <>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            {/* Los planes cerrados (nov-2026). El precio lo trae el servidor,
                que es el que cobra; el espejo solo pone los nombres. */}
            {PLANES_PANEL.map(pl=>(
              <button key={pl.abogados} onClick={()=>setCupos(pl.abogados)} disabled={guardando} style={{
                ...btn,width:'auto',height:'auto',padding:'8px 12px',fontSize:13,lineHeight:1.3,textAlign:'left',
                background:cupos===pl.abogados?'rgba(255,255,255,0.12)':'transparent',
                borderColor:cupos===pl.abogados?'#8FB0E6':'rgba(255,255,255,0.2)',fontWeight:cupos===pl.abogados?700:400,
              }}>
                {pl.etiqueta}
                <div style={{fontSize:12,color:'var(--text-muted)',fontWeight:400}}>{clpPlan(plan.precios[pl.abogados-1])} al mes · {pl.procuradores} procuradores</div>
              </button>
            ))}
            <button className="p-btn-primary" onClick={guardar} disabled={!cambio||guardando} style={{opacity:!cambio||guardando?0.5:1}}>
              {guardando?'Guardando…':cupos>plan.cupos?'Subir de plan':cupos<plan.cupos?'Bajar de plan':'Guardar'}
            </button>
          </div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:8,lineHeight:1.5}}>
            Subir rige al instante. Bajar solo si no tienes más abogados activos que los del plan. El nuevo precio se cobra desde la próxima renovación; lo que ya pagaste no cambia.
            {' '}{plan.cupos>plan.maxCupos
              ? <>Tu estudio tiene {plan.cupos} cupos, más que el plan mayor: para cambiarlos escríbenos.</>
              : <>¿Más de {plan.maxCupos} abogados? Escríbenos y armamos un plan para tu estudio.</>}
          </div>
        </>
      ) : (
        <div className="cfg-aviso">Tu plan lo administra Tabularis directamente. Para cambiar los cupos escríbenos a <a href="mailto:contacto@tabularis.cl" style={{color:'#8FB0E6'}}>contacto@tabularis.cl</a> o por WhatsApp al +56 9 6786 9656.</div>
      )}
      {msg.ok&&<div style={{background:'#EAF6EE',color:'#1B5E20',borderRadius:8,padding:'9px 12px',fontSize:13,marginTop:10}}>{msg.ok}</div>}
      {msg.error&&<div style={{background:'#FDEDED',color:'#8A2A2A',borderRadius:8,padding:'9px 12px',fontSize:13,marginTop:10}}>{msg.error}</div>}
    </div>
    <SaldoChatbularis plan={plan}/>
    <PagoYRenovacion plan={plan}/>
    </div>
  );
};

/* Pagar y renovar. Va en Configuración, junto al plan, porque es la misma
   pregunta: qué tengo, cuánto cuesta y cómo lo pago. Con tarjeta en el portal
   del banco se resuelve solo; con transferencia, el "Ya pagué" avisa al dueño
   para que lo registre el mismo día. El sistema no renueva solo con el aviso:
   el micrositio del banco no confirma nada de vuelta. */
const PagoYRenovacion: React.FC<{plan: PlanEstudio}> = ({plan}) => {
  const [monto,setMonto]=useState('');
  const [medio,setMedio]=useState<'banchile'|'transferencia'>(plan.pago?.enLinea ? 'transferencia' : 'banchile');
  const [ref,setRef]=useState('');
  const [enviando,setEnviando]=useState(false);
  const [listo,setListo]=useState(false);
  const [error,setError]=useState('');
  const [pagando,setPagando]=useState(false);
  const [errorPago,setErrorPago]=useState('');
  const c = plan.cobro;
  if (!c || c.totalClp <= 0 || !plan.pago) return null;
  // Pagar la renovación en línea (Banchile Pagos): el monto lo pone el
  // servidor desde el plan, y al volver el estudio ya está renovado.
  const pagarEnLinea=async()=>{
    setPagando(true); setErrorPago('');
    try{ window.location.href=(await pagarSuscripcion()).url; }
    catch(e:any){ setErrorPago(e?.message||'No pudimos abrir el pago. Inténtalo de nuevo.'); setPagando(false); }
  };
  const sugerido = c.totalClp;
  const montoClp = Math.round(Number((monto || String(sugerido)).replace(/[^\d]/g,''))||0);
  const vence = plan.periodoFin ? new Date(plan.periodoFin) : null;
  const vencida = !!vence && vence.getTime() < Date.now();
  const fmtDia = (d: Date) => d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', timeZone: 'America/Santiago' });
  const avisar=async()=>{
    if(montoClp<1000||enviando) return;
    setEnviando(true); setError('');
    try{ await api('/ajustes/pago/aviso',{method:'POST',body:{montoClp,medio,referencia:ref.trim()||undefined}}); setListo(true); }
    catch(e:any){ setError(e?.message||'No pudimos avisar. Escríbenos a contacto@tabularis.cl'); }
    finally{ setEnviando(false); }
  };
  const inputStyle: React.CSSProperties = {padding:'9px 11px',borderRadius:8,border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.05)',color:'#EDF1F7',fontSize:13.5,width:'100%',boxSizing:'border-box'};
  return (
    <div className="cfg-card cfg-ancho">
      <div className="cfg-card-title">💳 Pago y renovación</div>
      <p className="cfg-desc">
        {plan.estado==='trial'
          ? <>Cuando termine tu prueba{vence?` (${fmtDia(vence)})`:''}, tu plan cuesta <strong style={{color:'#EDF1F7'}}>{clpPlan(c.totalClp)}</strong> por {c.meses} {c.meses===1?'mes':'meses'}</>
          : vencida
            ? <>Tu período terminó{vence?` el ${fmtDia(vence)}`:''}. Para seguir, paga <strong style={{color:'#EDF1F7'}}>{clpPlan(c.totalClp)}</strong> por {c.meses} {c.meses===1?'mes':'meses'}</>
            : <>Tu próximo pago es de <strong style={{color:'#EDF1F7'}}>{clpPlan(c.totalClp)}</strong> por {c.meses} {c.meses===1?'mes':'meses'}{vence?`, al vencer el ${fmtDia(vence)}`:''}</>}
.
        {/* Precio de lanzamiento: el monto de arriba ya lo trae (lo calcula el
            servidor). Acá solo se dice que está, y hasta cuándo. */}
        {!!c.mesesConOferta && (
          <> <span style={{color:'#E3B54F',fontWeight:600}}>
            {plan.lanzamientoHasta
              ? `Incluye tu precio de lanzamiento, vigente hasta el ${fmtDia(new Date(plan.lanzamientoHasta))}.`
              : 'Incluye el precio de lanzamiento: lo mantienes 6 meses desde tu primer pago.'}
          </span></>
        )}
        {plan.pago.enLinea
          ? ' Pagando en línea queda renovado al instante; con transferencia, avísanos y lo activamos el mismo día.'
          : ' Con tarjeta en el portal del Banco de Chile queda listo al tiro; con transferencia, avísanos y lo activamos el mismo día.'}
      </p>
      {plan.pago.enLinea && (
        <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',margin:'4px 0 14px'}}>
          <button className="p-btn-primary" onClick={pagarEnLinea} disabled={pagando} style={{padding:'11px 18px'}}>
            {pagando ? 'Abriendo el pago…' : `Pagar ${clpPlan(c.totalClp)} en línea`}
          </button>
          <span style={{display:'inline-flex',alignItems:'center',gap:8,background:'#fff',borderRadius:8,padding:'4px 10px'}}>
            <img src="/logo-banchilepagos.png" alt="Banchile Pagos" style={{height:26}} />
            <span style={{fontSize:12,color:'#0D1B35'}}>Débito o crédito · CLP, IVA incluido</span>
          </span>
          {errorPago && <div style={{background:'#FDEDED',color:'#8A2A2A',borderRadius:8,padding:'9px 12px',fontSize:13,width:'100%'}}>{errorPago}</div>}
        </div>
      )}
      <div style={{background:'#fff',borderRadius:10,padding:'4px 14px 10px',color:'#0D1B35'}}>
        {/* Con el pago en línea activo, este recuadro queda solo para
            transferir: el link del banco duplicaría el botón de arriba. */}
        <MediosDePago
          pago={plan.pago.enLinea ? { ...plan.pago, link: null } : plan.pago}
          cierre={<>{plan.pago.enLinea ? 'Si prefieres transferir, ' : 'Después de pagar, '}avísanos acá abajo para registrarlo el mismo día.</>}
        />
      </div>
      {listo ? (
        <div style={{background:'#EAF6EE',color:'#1B5E20',borderRadius:8,padding:'9px 12px',fontSize:13,marginTop:10}}>Listo, avisamos. Revisamos el pago y renovamos el mismo día hábil; te llega un correo cuando esté registrado.</div>
      ) : (
        <div style={{marginTop:12}}>
          <div style={{fontSize:13.5,fontWeight:600,marginBottom:6}}>Ya pagué</div>
          <div className="cfg-ya-pague">
            <select value={medio} onChange={e=>setMedio(e.target.value as any)} style={inputStyle}>
              {!plan.pago.enLinea && <option value="banchile">Banco de Chile (en línea)</option>}
              <option value="transferencia">Transferencia</option>
            </select>
            <input inputMode="numeric" placeholder={`Monto: ${sugerido}`} value={monto} onChange={e=>setMonto(e.target.value)} style={inputStyle}/>
            <input placeholder="N° de operación (opcional)" value={ref} onChange={e=>setRef(e.target.value)} style={inputStyle}/>
            <button className="p-btn-primary" onClick={avisar} disabled={montoClp<1000||enviando} style={{whiteSpace:'nowrap'}}>{enviando?'Avisando…':'Avisar'}</button>
          </div>
          {error&&<div style={{background:'#FDEDED',color:'#8A2A2A',borderRadius:8,padding:'9px 12px',fontSize:13,marginTop:10}}>{error}</div>}
        </div>
      )}
      {/* Los pagos en línea del estudio (mensualidad y cargas de Chatbularis),
          del más reciente al más antiguo, con su autorización. */}
      {plan.pago.enLinea && <div style={{marginTop:14}}><HistorialPagos /></div>}
    </div>
  );
};

/* Chatbularis IA en Configuración: es prepago y va aparte de la suscripción.
   Acá se ve el saldo y se va a cargar; la carga y la cartola viven en el
   propio Chatbularis, que es donde se gasta. */
const SaldoChatbularis: React.FC<{plan: PlanEstudio}> = ({plan}) => {
  const c = plan.saldoIa;
  const estado = !c
    ? `Chatbularis parte con ${clpPlan(CHATBULARIS.bienvenidaClp)} de saldo de regalo la primera vez que lo abres.`
    : c.sinTope
      ? 'Tu estudio usa Chatbularis sin tope.'
      : c.agotado
        ? `Tu saldo de Chatbularis es ${clpPlan(c.saldoClp)} y no alcanza para una consulta nueva.`
        : `Tu saldo de Chatbularis es ${clpPlan(c.saldoClp)}.`;
  return (
    <div className="cfg-card">
      <div className="cfg-card-title">✍️ Chatbularis IA</div>
      <p className="cfg-desc">
        Redacta demandas y escritos completos con IA, apoyado en una base jurídica chilena. Es prepago: cargas saldo y cada consulta descuenta lo que cuesta, con IVA incluido. No hay mensualidad y lo cargado no vence.
      </p>
      <div className={`cfg-aviso${c?.agotado?' cfg-aviso-alerta':''}`}>{estado}</div>
      {!c?.sinTope && (
        <button className="p-btn-primary" style={{marginTop:10}} onClick={()=>window.open(`${window.location.origin}/asistente?cargar=1`,'_blank')}>
          Cargar saldo
        </button>
      )}
    </div>
  );
};

/* Qué versión está desplegada, del panel y del servidor, y desde cuándo.
   Responde "¿esto ya está en producción?" sin entrar al servidor: el panel
   trae su commit y su hora de build desde el despliegue (REACT_APP_*), y el
   servidor los cuenta por /health. */
const VersionDesplegada: React.FC = () => {
  const [srv,setSrv]=useState<{version:string;construido:string|null;arriba_desde:string}|null|undefined>(undefined);
  useEffect(()=>{ FS.versionServidor().then(setSrv).catch(()=>setSrv(null)); },[]);
  const cuando=(iso:string|null|undefined)=>{
    if(!iso) return '';
    const d=new Date(iso); if(isNaN(d.getTime())) return '';
    return d.toLocaleString('es-CL',{timeZone:'America/Santiago',day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false});
  };
  const panelVer=process.env.REACT_APP_VERSION||'dev';
  const panelFecha=cuando(process.env.REACT_APP_FECHA_BUILD);
  const fila:React.CSSProperties={display:'flex',gap:8,flexWrap:'wrap',fontSize:12.5,lineHeight:1.7};
  const eti:React.CSSProperties={color:'var(--text-muted)',minWidth:64};
  const val:React.CSSProperties={fontFamily:'monospace',color:'var(--white,#fff)'};
  return (
    <div className="cfg-card cfg-ancho cfg-card-sutil">
      <div className="cfg-card-title">🏷️ Versión</div>
      <div style={fila}><span style={eti}>Panel</span><span style={val}>{panelVer}</span>{panelFecha&&<span style={{color:'var(--text-muted)'}}>desplegado el {panelFecha}</span>}</div>
      <div style={fila}><span style={eti}>Servidor</span>
        {srv===undefined&&<span style={{color:'var(--text-muted)'}}>consultando…</span>}
        {srv===null&&<span style={{color:'#e57373'}}>no responde</span>}
        {srv&&<><span style={val}>{srv.version}</span><span style={{color:'var(--text-muted)'}}>desplegado el {cuando(srv.construido)||cuando(srv.arriba_desde)}{srv.construido&&<> · arriba desde el {cuando(srv.arriba_desde)}</>}</span></>}
      </div>
    </div>
  );
};

const ConfiguracionView: React.FC<{rol?:string}> = ({rol}) => {
  const [exportando,setExportando]=useState(false);
  const [error,setError]=useState('');
  const exportar=async()=>{
    setError('');setExportando(true);
    try{ await FS.exportarDatos(); }
    catch(e:any){ setError(e?.message||'No se pudo generar el respaldo.'); }
    finally{ setExportando(false); }
  };

  // Papelera
  const [papelera,setPapelera]=useState<any[]|null>(null);
  const [pErr,setPErr]=useState('');
  const [confirmar,setConfirmar]=useState<{id:string;cliente:string}|null>(null);
  const cargarPapelera=useCallback(()=>{ FS.loadPapelera().then(setPapelera).catch(()=>setPapelera([])); },[]);
  useEffect(()=>{ cargarPapelera(); },[cargarPapelera]);
  const restaurar=async(id:string)=>{ setPErr(''); try{ await FS.restaurarCaso(id); cargarPapelera(); }catch{ setPErr('No se pudo restaurar.'); } };
  const eliminarDef=async(id:string)=>{ setPErr(''); try{ await FS.eliminarCasoDefinitivo(id); setConfirmar(null); cargarPapelera(); }catch{ setPErr('No se pudo eliminar.'); } };

  return (
    <div className="p-section cfg">
      <div className="p-section-title">⚙️ Configuración</div>

      {rol==='admin' && <>
        <div className="cfg-grupo">Suscripción</div>
        <PlanYAbogados/>
      </>}

      <div className="cfg-grupo">Cómo trabaja el estudio</div>
      <div className="cfg-grid">
      <CierreDeMes esAdmin={rol==='admin'}/>
      <RecordatoriosCliente esAdmin={rol==='admin'}/>
      <ReglasCasos esAdmin={rol==='admin'}/>
      </div>

      <div className="cfg-grupo">Datos del estudio</div>
      <div className="cfg-grid">
      <div className="cfg-card">
        <div className="cfg-card-title">💾 Respaldo de datos</div>
        <p className="cfg-desc">
          Descarga un archivo <strong>.zip</strong> con un Excel (casos, usuarios y flujo de caja) y <strong>todos los documentos</strong> subidos, organizados por caso. Sirve para tener una copia de seguridad o para entregarle sus datos al estudio si decide irse.
        </p>
        <button className="p-btn-primary" onClick={exportar} disabled={exportando} style={{opacity:exportando?0.6:1,cursor:exportando?'wait':'pointer'}}>
          {exportando?'⏳ Generando respaldo…':'⬇️ Exportar todos mis datos (.zip)'}
        </button>
        {exportando&&<div style={{fontSize:12,color:'var(--text-muted)',marginTop:8}}>Puede tardar un momento si hay muchos documentos. No cierres esta pestaña.</div>}
        {error&&<div style={{background:'#FDEDED',color:'#8A2A2A',borderRadius:8,padding:'9px 12px',fontSize:13,marginTop:10}}>{error}</div>}
      </div>

      <div className="cfg-card">
        <div className="cfg-card-title">🗑️ Papelera</div>
        <p className="cfg-desc">
          Los casos que eliminas quedan aquí y se pueden <strong>restaurar</strong>. Nada se pierde salvo que los elimines definitivamente.
        </p>
        {pErr&&<div style={{background:'#FDEDED',color:'#8A2A2A',borderRadius:8,padding:'9px 12px',fontSize:13,marginBottom:10}}>{pErr}</div>}
        {papelera===null?(
          <div style={{color:'var(--text-muted)',fontSize:13}}>Cargando…</div>
        ):papelera.length===0?(
          <div style={{color:'var(--text-muted)',fontSize:13}}>La papelera está vacía.</div>
        ):(
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {papelera.map((c:any)=>(
              <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px solid rgba(255,255,255,0.08)',borderRadius:8,flexWrap:'wrap'}}>
                <span style={{fontFamily:'monospace',fontSize:12,color:'#EDF1F7',opacity:0.8}}>{c.codigoCaso||'—'}</span>
                <span style={{flex:1,fontSize:14}}>{c.nombreCliente||'Sin nombre'}{c.tema?<span style={{color:'var(--text-muted)',fontSize:12}}> · {c.tema}</span>:null}</span>
                <button className="p-btn-secondary" onClick={()=>restaurar(c.id)}>↩ Restaurar</button>
                <button className="p-btn-danger-sm" onClick={()=>setConfirmar({id:c.id,cliente:c.nombreCliente||'este caso'})}>Eliminar definitivo</button>
              </div>
            ))}
          </div>
        )}
      </div>

      </div>

      {confirmar&&(
        <div onClick={()=>setConfirmar(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'var(--navy,#0D1B35)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:12,padding:24,maxWidth:420,margin:20}}>
            <div style={{fontSize:16,fontWeight:600,marginBottom:8}}>¿Eliminar definitivamente?</div>
            <p style={{color:'var(--text-muted)',fontSize:13.5,lineHeight:1.6}}>Se borrará <strong>{confirmar.cliente}</strong> y todos sus documentos de forma permanente. Esta acción <strong>no se puede deshacer</strong>.</p>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:12}}>
              <button className="p-btn-secondary" onClick={()=>setConfirmar(null)}>Cancelar</button>
              <button className="p-btn-danger-sm" onClick={()=>eliminarDef(confirmar.id)}>Sí, eliminar definitivo</button>
            </div>
          </div>
        </div>
      )}

      <div className="cfg-grid"><VersionDesplegada/></div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   PLANTILLAS DE PROCESO (disciplinas personalizables por estudio)
   ══════════════════════════════════════════════════════════════════════════ */
/* Aviso previo a guardar una plantilla.
   Cambiar las etapas de una disciplina toca expedientes en curso: una etapa
   renombrada o eliminada pierde su marca de cumplida, y alguien tendrá que
   volver a marcarla. Esto lo dice ANTES de escribir, con los casos y las
   etapas concretas en pantalla, para que la decisión se tome con el dato
   a la vista y no con una advertencia genérica. */
const AvisoImpacto: React.FC<{
  disciplina: string;
  impacto: FS.ImpactoPlantilla;
  guardando: boolean;
  onCancelar: () => void;
  onConfirmar: (propagar: boolean) => void;
}> = ({ disciplina, impacto, guardando, onCancelar, onConfirmar }) => {
  const { casos, totalCasos, casosConPerdida, etapasPerdidasTotal } = impacto;
  const conPerdida = casos.filter(c => c.etapasPerdidas.length > 0);
  const sinPerdida = casos.filter(c => c.etapasPerdidas.length === 0);

  return (
    <div className="panel-modal-fondo" onClick={onCancelar}>
      <div className="panel-modal" style={{maxWidth:620}} onClick={e=>e.stopPropagation()}>
        <div className="panel-modal-titulo">Cambios en «{disciplina}»</div>

        {totalCasos === 0 ? (
          <p className="panel-modal-texto">
            Ningún caso usa esta disciplina todavía, así que el cambio no afecta a nadie.
            Se aplicará a los casos que crees de ahora en adelante.
          </p>
        ) : (
          <>
            <p className="panel-modal-texto">
              <strong style={{color:'#FFFFFF'}}>{totalCasos}</strong> {totalCasos===1?'caso usa':'casos usan'} esta
              disciplina. Si aplicás el cambio a los casos existentes,
              {etapasPerdidasTotal > 0
                ? <> <strong style={{color:'#e57373'}}>{casosConPerdida} {casosConPerdida===1?'perdería':'perderían'} {etapasPerdidasTotal} {etapasPerdidasTotal===1?'etapa ya marcada':'etapas ya marcadas'}</strong> y habrá que volver a marcarlas.</>
                : <> ninguno pierde avance: las etapas que tenían marcadas siguen existiendo.</>}
            </p>

            <div style={{maxHeight:260,overflowY:'auto',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8}}>
              {conPerdida.map(c=>(
                <div key={c.id} style={{padding:'9px 12px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{fontSize:13,color:'#FFFFFF'}}>
                    {c.codigo} — {c.cliente}
                  </div>
                  <div style={{fontSize:12,color:'#e57373',marginTop:3}}>
                    Pierde: {c.etapasPerdidas.join(' · ')}
                  </div>
                </div>
              ))}
              {sinPerdida.map(c=>(
                <div key={c.id} style={{padding:'9px 12px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{fontSize:13,color:'#9BB0CC'}}>
                    {c.codigo} — {c.cliente}
                    <span style={{fontSize:11,marginLeft:8}}>
                      {c.marcadasAntes>0 ? `conserva sus ${c.marcadasAntes} etapa${c.marcadasAntes===1?'':'s'} marcada${c.marcadasAntes===1?'':'s'}` : 'sin avance marcado'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="panel-modal-acciones">
          <button className="p-btn-secondary" onClick={onCancelar} disabled={guardando}>Cancelar</button>
          {totalCasos > 0 && (
            <button className="p-btn-secondary" onClick={()=>onConfirmar(false)} disabled={guardando}
              title="La plantilla queda actualizada para los casos nuevos; los actuales no se tocan">
              Solo casos nuevos
            </button>
          )}
          <button className="p-btn-primary" onClick={()=>onConfirmar(true)} disabled={guardando}>
            {guardando ? 'Aplicando…' : totalCasos > 0 ? `Aplicar a los ${totalCasos}` : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Disciplinas que trae Tabularis (backend/src/lib/plantillas.ts). Su nombre no
 *  se cambia: de él dependen los campos del caso y los plazos legales (un caso
 *  es laboral o de familia por el nombre de su disciplina). */
const NO_SE_ELIMINA_LABORAL = 'Derecho Laboral no se puede eliminar.\n\nTabularis la usa para calcular los plazos legales de los casos laborales (despido, nulidad del despido, tutela y contestación de la demanda). Sin ella, los casos nuevos no podrían registrarse como laborales y sus plazos dejarían de calcularse.\n\nSus etapas sí puedes ajustarlas cuando quieras.';
const DISCIPLINAS_BASE = ['Derecho Laboral','Derecho de Familia','Derecho Civil','Derecho Penal','Derecho Migratorio','Derecho Tributario','Derecho Administrativo','Derecho Comercial'];

const PlantillaCard: React.FC<{plantilla:any; onChange:()=>void; onDelete:()=>void}> = ({plantilla,onChange,onDelete}) => {
  const [nombre,setNombre]=useState(plantilla.nombre);
  const esBase=DISCIPLINAS_BASE.includes(plantilla.nombre);
  const [pasos,setPasos]=useState<string[]>(plantilla.pasos||[]);
  const [nuevoPaso,setNuevoPaso]=useState('');
  const [dirty,setDirty]=useState(false);
  const [guardando,setGuardando]=useState(false);
  const addPaso=()=>{const p=nuevoPaso.trim();if(!p)return;setPasos([...pasos,p]);setNuevoPaso('');setDirty(true);};
  const delPaso=(i:number)=>{setPasos(pasos.filter((_,x)=>x!==i));setDirty(true);};
  const cambiarPaso=(i:number,txt:string)=>{setPasos(pasos.map((p,x)=>x===i?txt:p));setDirty(true);};
  // Al reordenar, la etapa cambia de fila y es fácil perderle el rastro: se
  // la resalta un segundo en su nueva posición.
  const [movida,setMovida]=useState<number|null>(null);
  const movePaso=(i:number,dir:number)=>{
    const j=i+dir;
    if(j<0||j>=pasos.length)return;
    const arr=[...pasos];
    [arr[i],arr[j]]=[arr[j],arr[i]];
    setPasos(arr);
    setDirty(true);
    setMovida(j);
    setTimeout(()=>setMovida(m=>m===j?null:m),900);
  };
  // Guardar en dos tiempos: primero se pregunta al servidor a qué casos afecta
  // el cambio y qué avance se perdería, y eso se le muestra al usuario. Recién
  // si confirma se escribe. Cambiar etapas puede borrar trabajo ya registrado
  // en expedientes en curso; hacerlo sin avisar sería inaceptable.
  const [impacto,setImpacto]=useState<FS.ImpactoPlantilla|null>(null);
  const [consultando,setConsultando]=useState(false);

  const pedirConfirmacion=async()=>{
    const limpios=pasos.map(p=>p.trim()).filter(Boolean);
    if(!limpios.length){window.alert('La disciplina necesita al menos una etapa.');return;}
    setConsultando(true);
    try{
      setImpacto(await FS.impactoPlantilla(plantilla.id,limpios));
    }catch{
      window.alert('No se pudo revisar a qué casos afecta el cambio. Intenta de nuevo.');
    }finally{
      setConsultando(false);
    }
  };

  const confirmar=async(propagar:boolean)=>{
    setGuardando(true);
    try{
      const limpios=pasos.map(p=>p.trim()).filter(Boolean);
      await FS.editarPlantilla(plantilla.id,{nombre:nombre.trim()||plantilla.nombre,pasos:limpios,propagar});
      setPasos(limpios);
      setDirty(false);
      setImpacto(null);
      onChange();
    }finally{
      setGuardando(false);
    }
  };
  return (
    <div style={{border:'1px solid rgba(255,255,255,0.1)',borderRadius:10,padding:16,marginBottom:14,background:'rgba(255,255,255,0.02)'}}>
      {/* Las disciplinas que trae Tabularis no cambian de nombre (ver
          DISCIPLINAS_BASE); las que crea el estudio, sí. */}
      <div style={{marginBottom:12}}>
        <div className="p-field-label" style={{marginBottom:4}}>Nombre de la disciplina</div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input className="p-input" value={nombre} placeholder="Ej. Derecho de Familia" readOnly={esBase}
            title={esBase?'Disciplina de Tabularis: su nombre no se cambia. Sus etapas sí.':undefined}
            onChange={e=>{if(esBase)return;setNombre(e.target.value);setDirty(true);}}
            style={{flex:1,fontWeight:600,...(esBase?{opacity:0.75,cursor:'default'}:{})}}/>
          <button className="p-btn-danger-sm" onClick={onDelete} title={plantilla.nombre==='Derecho Laboral'?'Esta disciplina no se puede eliminar: de ella dependen los plazos laborales':'Eliminar disciplina'} style={plantilla.nombre==='Derecho Laboral'?{opacity:0.45}:undefined}>🗑</button>
        </div>
      </div>
      <div className="p-field-label" style={{marginBottom:6}}>Etapas del proceso ({pasos.length})</div>
      {pasos.map((p,i)=>(
        <div key={i} className={`p-etapa${movida===i?' movida':''}`}>
          <span className="p-etapa-num">{i+1}.</span>
          <input className="p-input p-etapa-txt" value={p} maxLength={120}
            onChange={e=>cambiarPaso(i,e.target.value)}
            placeholder="Nombre de la etapa"/>
          <button className="p-etapa-btn" onClick={()=>movePaso(i,-1)} disabled={i===0} title="Subir esta etapa">▲</button>
          <button className="p-etapa-btn" onClick={()=>movePaso(i,1)} disabled={i===pasos.length-1} title="Bajar esta etapa">▼</button>
          <button className="p-etapa-btn" onClick={()=>delPaso(i)} style={{color:'rgba(255,120,120,0.75)'}} title="Quitar esta etapa">✕</button>
        </div>
      ))}
      <div style={{display:'flex',gap:6,marginTop:10}}>
        <input className="p-input" placeholder="Nueva etapa…" value={nuevoPaso} onChange={e=>setNuevoPaso(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addPaso()} style={{flex:1,fontSize:13}}/>
        <button className="p-btn-secondary" onClick={addPaso}>＋ Etapa</button>
      </div>
      {dirty && <button className="p-btn-primary" onClick={pedirConfirmacion} disabled={consultando||guardando} style={{marginTop:12}}>{consultando?'Revisando casos…':'💾 Guardar cambios'}</button>}
      {impacto && <AvisoImpacto
        disciplina={nombre.trim()||plantilla.nombre}
        impacto={impacto}
        guardando={guardando}
        onCancelar={()=>setImpacto(null)}
        onConfirmar={confirmar}/>}
    </div>
  );
};
const PlantillasView: React.FC = () => {
  const [plantillas,setPlantillas]=useState<any[]>([]);
  const [loading,setLoading]=useState(true);
  const [nuevoNombre,setNuevoNombre]=useState('');
  const load=useCallback(async()=>{try{setPlantillas(await FS.loadPlantillas());}catch{}setLoading(false);},[]);
  useEffect(()=>{load();},[load]);
  const crear=async()=>{const n=nuevoNombre.trim();if(n.length<2)return;await FS.crearPlantilla(n,['Admisión','Seguimiento','Cierre']);setNuevoNombre('');load();};
  // Derecho Laboral no se borra: de ella dependen los plazos legales. El
  // servidor también lo rechaza; acá se explica antes de intentarlo.
  const eliminar=async(id:string)=>{
    if(plantillas.find(p=>p.id===id)?.nombre==='Derecho Laboral'){window.alert(NO_SE_ELIMINA_LABORAL);return;}
    if(window.confirm('¿Eliminar esta disciplina? Los casos existentes conservan sus etapas.')){await FS.eliminarPlantilla(id);load();}};
  return (
    <div className="p-section">
      <div className="p-section-title">Disciplinas y plantillas de proceso</div>
      <div className="p-info-box">Define las disciplinas de tu estudio y las etapas de cada proceso. Al crear un caso eliges la disciplina, y sus etapas se cargan solas. Puedes crear las tuyas propias.</div>
      <div style={{display:'flex',gap:8,margin:'16px 0'}}>
        <input className="p-input" placeholder="Nueva disciplina (ej. Derecho Penal)" value={nuevoNombre} onChange={e=>setNuevoNombre(e.target.value)} onKeyDown={e=>e.key==='Enter'&&crear()} style={{flex:1}}/>
        <button className="p-btn-primary" onClick={crear}>＋ Crear disciplina</button>
      </div>
      {loading ? <div className="p-empty">Cargando…</div> :
        plantillas.length ? plantillas.map(p=><PlantillaCard key={p.id} plantilla={p} onChange={load} onDelete={()=>eliminar(p.id)} />)
        : <div className="p-empty">Aún no hay disciplinas.</div>}
    </div>
  );
};
/** El check de fin de mes: lo cobrado en el mes calza con lo que entró al
 *  banco. Guarda el total que se vio al cuadrar; si después alguien cambia un
 *  pago de ese mes, el total ya no coincide y se avisa. */
const CuadreCajaMes:React.FC<{periodo:string;etiqueta:string;total:number;cuadre:FS.CuadreCaja|null;onGuardado:(q:FS.CuadreCaja)=>void}>=({periodo,etiqueta,total,cuadre,onGuardado})=>{
  const [nota,setNota]=useState(cuadre?.nota||'');
  const [guardando,setGuardando]=useState(false);
  const [error,setError]=useState('');
  const guardar=async(cuadrado:boolean)=>{
    setGuardando(true);setError('');
    try{onGuardado(await FS.guardarCuadreCaja(periodo,{cuadrado,nota,totalCobrado:total}));}
    catch(e:any){setError(e?.message||'No se pudo guardar');}
    finally{setGuardando(false);}
  };
  const cambio=!!cuadre?.cuadrado&&cuadre.totalCobrado!=null&&Math.round(cuadre.totalCobrado)!==Math.round(total);
  const notaCambiada=nota.trim()!==(cuadre?.nota||'');
  const cuando=cuadre?new Date(cuadre.actualizado).toLocaleString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}):'';
  return(
    <div style={{marginBottom:16,padding:16,borderRadius:10,background:'rgba(255,255,255,0.04)',border:`1px solid ${cuadre?.cuadrado?(cambio?'rgba(224,166,74,0.5)':'rgba(106,173,106,0.45)'):'rgba(255,255,255,0.12)'}`}}>
      <label style={{display:'flex',alignItems:'center',gap:10,cursor:guardando?'wait':'pointer',fontSize:14,color:'#F0F4FF',fontWeight:600}}>
        <input type="checkbox" checked={!!cuadre?.cuadrado} disabled={guardando} onChange={()=>guardar(!cuadre?.cuadrado)} style={{width:18,height:18}}/>
        Caja de {etiqueta} cuadrada con el banco
      </label>
      <div style={{fontSize:12,color:'var(--text-muted)',margin:'6px 0 10px 28px'}}>
        {cuadre?.cuadrado
          ?<>Marcado por {cuadre.usuarioNombre||'—'} el {cuando}{cuadre.totalCobrado!=null&&<> · total en ese momento {FS.fmtMoney(cuadre.totalCobrado)}</>}</>
          :'Compara los pagos de abajo con la cartola y marca cuando calcen.'}
      </div>
      {cambio&&<div style={{fontSize:12.5,color:'#E0A64A',margin:'0 0 10px 28px'}}>
        ⚠ El total cambió desde que se cuadró: era {FS.fmtMoney(cuadre!.totalCobrado!)} y ahora es {FS.fmtMoney(total)}. Revisa qué pago se agregó o modificó.
      </div>}
      <div style={{marginLeft:28}}>
        <textarea className="p-input" rows={2} maxLength={2000} value={nota} onChange={e=>setNota(e.target.value)}
          placeholder="Nota (ej.: falta la transferencia de Pérez del 28, llega el lunes)" style={{width:'100%',resize:'vertical',fontSize:13}}/>
        {notaCambiada&&<button className="p-btn-secondary" disabled={guardando} onClick={()=>guardar(!!cuadre?.cuadrado)} style={{marginTop:6,padding:'6px 14px',fontSize:12}}>{guardando?'Guardando…':'Guardar nota'}</button>}
        {error&&<div style={{fontSize:12,color:'#e57373',marginTop:6}}>{error}</div>}
      </div>
    </div>
  );
};
const Reportes:React.FC=()=>{
  const [data,setData]=useState<any[]>([]);
  const [vistaReporte,setVistaReporte]=useState<'resumen'|'totalCasos'|'totalCobrado'|'saldoPorCobrar'>('resumen');
  const {diaCorte,cargado}=useDiaCorte();
  const [sel,setSel]=useState<SeleccionPeriodo>({tipo:'periodo',anio:new Date().getFullYear(),mes:new Date().getMonth()});
  useEffect(()=>{FS.loadReportesData().then(setData).catch(()=>{});},[]);
  const [cuadres,setCuadres]=useState<FS.CuadreCaja[]>([]);
  // Check de cada pago contra la cartola. Se guarda en el pago mismo y acá se
  // actualiza en memoria, sin recargar todos los casos.
  const [marcando,setMarcando]=useState('');
  const marcarEnBanco=async(q:any,marcado:boolean)=>{
    const llave=`${q.caso.id}:${q.indice}`;
    setMarcando(llave);
    try{
      const enBanco=await FS.marcarPagoEnBanco(q.caso.id,q.indice,{concepto:q.concepto,fechaPago:q.fecha},marcado);
      setData(ds=>ds.map((c:any)=>c.id!==q.caso.id?c:{...c,pagos:(c.pagos||[]).map((p:any,i:number)=>{
        if(i!==q.indice)return p;
        const {enBanco:_e,...resto}=p;return enBanco?{...resto,enBanco}:resto;
      })}));
    }catch(e:any){alert(e?.message||'No se pudo guardar el check.');}
    finally{setMarcando('');}
  };
  useEffect(()=>{FS.getCuadresCaja().then(setCuadres).catch(()=>{});},[]);

  // Con corte al 20, el 25 de julio ya es agosto: al llegar los ajustes hay que
  // corregir el período que se muestra por omisión. Se hace una sola vez para
  // no pisar lo que el usuario ya haya elegido.
  const periodoAjustado=useRef(false);
  useEffect(()=>{
    if(!cargado||periodoAjustado.current)return;
    periodoAjustado.current=true;
    const p=PER.periodoDeFecha(new Date(),diaCorte)!;
    setSel(s=>s.tipo==='periodo'?{tipo:'periodo',anio:p.anio,mes:p.mes}:s);
  },[cargado,diaCorte]);

  const rango=PER.rangoDeSeleccion(sel,diaCorte);
  const inRange=(val:any)=>PER.enRango(val,rango);
  // "Al cierre": una cuota que se pagó DESPUÉS del cierre seguía impaga ese
  // día, así que para la foto del período cuenta como deuda.
  const impagoAlCierre=(p:any)=>p.estado!=='Pagado'||!PER.hastaCierre(p.fechaPago,rango);
  const pagadoAlCierre=(p:any)=>p.estado==='Pagado'&&PER.hastaCierre(p.fechaPago,rango);
  const vencidaAlCierre=(p:any)=>{
    const v=PER.aFecha(p.vencimiento||p.fecha);
    return !!v&&v.getTime()<=rango.hasta.getTime();
  };
  // Exigible al cierre: lo que a esa fecha ya debía estar pagado. Una cuota que
  // vence el mes siguiente todavía no se debe, así que no cuenta como falta por
  // cobrar del período. Las que no tienen fecha de vencimiento sí entran: son
  // deuda sin plazo pactado, y dejarlas fuera haría desaparecer plata del total.
  const exigibleAlCierre=(p:any)=>{
    if(!impagoAlCierre(p))return false;
    const v=PER.aFecha(p.vencimiento||p.fecha);
    return !v||v.getTime()<=rango.hasta.getTime();
  };

  // Total casos: filtrado por createdAt
  const casosFiltrados=data.filter(c=>inRange(c.createdAt));
  const total=casosFiltrados.length;
  const porEstado:Record<string,number>={};
  Object.keys(FS.statusColors).forEach(k=>porEstado[k]=0);
  casosFiltrados.forEach((c:any)=>{if(c.estado)porEstado[c.estado]=(porEstado[c.estado]||0)+1;});

  // Total cobrado: por fechaPago de pagos en todo el historial
  const totalPagado=data.reduce((sum,c)=>sum+(c.pagos||[])
    .filter((p:any)=>p.estado==='Pagado'&&inRange(p.fechaPago))
    .reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);

  // Detalle total casos
  const casosDetalle=casosFiltrados.map((c:any)=>{
    const pg=c.pagos||[];
    return{...c,
      cobrado:pg.filter(pagadoAlCierre).reduce((a:number,p:any)=>a+Number(p.monto||0),0),
      saldo:pg.filter(impagoAlCierre).reduce((a:number,p:any)=>a+Number(p.monto||0),0),
    };
  });

  // Cada pago del período por separado, en orden de fecha: es lo que se
  // compara contra la cartola del banco al cuadrar la caja.
  const pagosCobrados=data.flatMap((c:any)=>(c.pagos||[])
    .map((p:any,indice:number)=>({p,indice}))
    .filter(({p}:any)=>p.estado==='Pagado'&&inRange(p.fechaPago))
    .map(({p,indice}:any)=>({caso:c,indice,fecha:p.fechaPago,concepto:p.concepto||'',forma:p.formaPago||'',monto:Number(p.monto||0),
      enBanco:p.enBanco||null,orden:PER.aFecha(p.fechaPago)?.getTime()??0})))
    .sort((a:any,b:any)=>a.orden-b.orden||String(a.caso.nombreCliente||'').localeCompare(String(b.caso.nombreCliente||'')));

  // Quién falta por cobrar: la deuda exigible acumulada al cierre del período.
  // Arrastra lo impago de meses anteriores, pero deja fuera las cuotas que
  // recién vencen después del cierre: esas todavía no se deben. Los casos
  // abiertos después del cierre no existían aún, así que también quedan fuera.
  const saldoDetalle=data
    .filter((c:any)=>PER.hastaCierre(c.createdAt,rango))
    .map((c:any)=>{
      const pg=c.pagos||[];
      const cob=pg.filter(pagadoAlCierre).reduce((a:number,p:any)=>a+Number(p.monto||0),0);
      const pendPagos=pg.filter(exigibleAlCierre);
      const vencidas=pendPagos.filter(vencidaAlCierre);
      const montoVencido=vencidas.reduce((a:number,p:any)=>a+Number(p.monto||0),0);
      const pend=pendPagos.reduce((a:number,p:any)=>a+Number(p.monto||0),0);
      // Cada cuota impaga por separado: el saldo acumulado arrastra deudas de
      // meses anteriores, y sin ver el vencimiento de cada una no se sabe si el
      // total viene de una cuota vieja o de varias recién emitidas.
      const cuotas=pendPagos.map((p:any)=>{
        const venc=p.vencimiento||p.fecha||'';
        const f=PER.aFecha(venc);
        return{
          venc,
          concepto:p.concepto||'',
          monto:Number(p.monto||0),
          vencida:vencidaAlCierre(p),
          // Los días se cuentan contra el cierre del período, no contra hoy: es
          // la foto de ese momento, y si se consulta un mes pasado el atraso
          // que corresponde es el que había entonces.
          dias:f?Math.floor((rango.hasta.getTime()-f.getTime())/86400000):0,
          orden:f?f.getTime():Number.MAX_SAFE_INTEGER, // las sin fecha, al final
        };
      }).sort((a:any,b:any)=>a.orden-b.orden);
      const masAntigua=vencidas.map((p:any)=>p.vencimiento||p.fecha).filter(Boolean).sort()[0]||'';
      const f=PER.aFecha(masAntigua);
      const diasAtraso=f?Math.floor((rango.hasta.getTime()-f.getTime())/86400000):0;
      return{...c,cobrado:cob,saldo:pend,montoVencido,montoSinFecha:pend-montoVencido,cuotas,diasAtraso};
    })
    .filter(c=>c.saldo>0)
    .sort((a,b)=>b.montoVencido-a.montoVencido||b.saldo-a.saldo);
  const totalDeuda=saldoDetalle.reduce((s,c)=>s+c.saldo,0);
  const totalVencido=saldoDetalle.reduce((s,c)=>s+c.montoVencido,0);
  // Lo que se debe pero sin fecha de vencimiento pactada. Suele ser cero; si no
  // lo es, conviene decirlo aparte para que no se lea como atraso.
  const totalSinFecha=totalDeuda-totalVencido;

  const periodoLabel=`${PER.etiquetaSeleccion(sel)} · ${PER.etiquetaRango(rango)}`;
  // El cuadre es por mes contable; con fechas exactas no hay mes que marcar.
  const claveCuadre=sel.tipo==='periodo'?FS.clavePeriodo(sel.anio,sel.mes):null;
  const cuadre=claveCuadre?cuadres.find(q=>q.periodo===claveCuadre)||null:null;
  const fechaCierre=PER.aISO(rango.hasta).split('-').reverse().join('-');

  return(
    <div className="p-section">
      <div className="p-section-title">Reportes</div>

      <SelectorPeriodo sel={sel} onChange={setSel} diaCorte={diaCorte}/>

      {/* Tabs de vista */}
      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        {([{id:'resumen',label:'📊 Resumen'},{id:'totalCasos',label:'📋 Total Casos'},{id:'totalCobrado',label:'💰 Total Cobrado'},{id:'saldoPorCobrar',label:'⚠️ Saldo por cobrar'}] as const).map(v=>
          <button key={v.id} className={vistaReporte===v.id?'p-btn-primary':'p-btn-secondary'} onClick={()=>setVistaReporte(v.id)} style={{padding:'8px 16px',fontSize:13}}>{v.label}</button>
        )}
      </div>

      {/* KPI cards */}
      <div className="p-grid3" style={{marginBottom:20}}>
        <div className="fin-card" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('totalCasos')}>
          <div className="fin-label">Total casos</div>
          <div className="fin-val" style={{fontSize:'2rem'}}>{total}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>por fecha de ingreso</div>
        </div>
        <div className="fin-card ok" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('totalCobrado')}>
          <div className="fin-label">Total cobrado</div>
          <div className="fin-val">{FS.fmtMoney(totalPagado)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
            por fecha de pago
            {cuadre?.cuadrado&&<span style={{color:'#6aad6a',fontWeight:600}}> · ✓ cuadrado con el banco</span>}
          </div>
        </div>
        <div className="fin-card warn" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('saldoPorCobrar')}>
          <div className="fin-label">Falta por cobrar</div>
          <div className="fin-val">{FS.fmtMoney(totalDeuda)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
            vencido y acumulado al {fechaCierre}
            {totalSinFecha>0&&<> · {FS.fmtMoney(totalSinFecha)} sin fecha pactada</>}
          </div>
        </div>
      </div>

      {/* Resumen */}
      {vistaReporte==='resumen'&&<>
        {total===0
          ?<div className="p-empty">Sin casos en este período.</div>
          :Object.entries(porEstado).filter(([,v])=>v>0).map(([k,v])=>(
          <div key={k} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <div style={{width:120,fontSize:12,color:'var(--text-muted)'}}>{k}</div>
            <div style={{flex:1,height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.round(v/total*100)}%`,background:FS.statusColors[k],borderRadius:4}}/></div>
            <div style={{width:30,textAlign:'right',fontSize:13,color:'var(--white)'}}>{v}</div>
          </div>
        ))}
      </>}

      {/* Total casos */}
      {vistaReporte==='totalCasos'&&<div>
        <div className="p-section-title">Total Casos — {periodoLabel} ({casosDetalle.length})</div>
        {casosDetalle.length===0?<div className="p-empty">Sin casos en este período.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Área</th><th>Captador</th><th>Abogado</th><th>Cobrado al cierre</th><th>Pendiente al cierre</th></tr></thead><tbody>
          {casosDetalle.map(c=><tr key={c.id}><td>{c.nombreCliente||'—'}</td><td>{c.tema||'—'}</td><td>{c.captadora||'—'}</td><td>{c.abogada||'Sin asignar'}</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(c.cobrado)}</td><td style={{color:c.saldo>0?'#e57373':'var(--text-muted)'}}>{FS.fmtMoney(c.saldo)}</td></tr>)}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}><td colSpan={4} style={{textAlign:'right'}}>TOTAL</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(casosDetalle.reduce((s,c)=>s+c.cobrado,0))}</td><td style={{color:'#e57373'}}>{FS.fmtMoney(casosDetalle.reduce((s,c)=>s+c.saldo,0))}</td></tr>
        </tbody></table></div>}
      </div>}

      {/* Total cobrado — pago por pago, en orden de fecha, y el cuadre del mes */}
      {vistaReporte==='totalCobrado'&&<div>
        <div className="p-section-title">Total Cobrado — {periodoLabel}</div>
        {claveCuadre
          ?<CuadreCajaMes key={claveCuadre} periodo={claveCuadre} etiqueta={PER.etiquetaSeleccion(sel)} total={totalPagado} cuadre={cuadre}
             onGuardado={q=>setCuadres(cs=>[q,...cs.filter(x=>x.periodo!==q.periodo)])}/>
          :<div className="p-info-box" style={{marginBottom:14}}>El cuadre de caja se marca por mes: elige un mes en el selector de arriba.</div>}
        {pagosCobrados.length===0?<div className="p-empty">Sin pagos recibidos en este período.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th title="Visto en la cartola del banco" style={{textAlign:'center'}}>Banco</th><th>Fecha de pago</th><th>Cliente</th><th>Concepto</th><th>Forma de pago</th><th>Área</th><th>Abogado</th><th style={{textAlign:'right'}}>Monto</th></tr></thead><tbody>
          {pagosCobrados.map((p:any)=><tr key={`${p.caso.id}:${p.indice}`} style={p.enBanco?{background:'rgba(106,173,106,0.07)'}:undefined}>
            <td style={{textAlign:'center'}}>
              <input type="checkbox" checked={!!p.enBanco} disabled={marcando===`${p.caso.id}:${p.indice}`}
                onChange={()=>marcarEnBanco(p,!p.enBanco)} style={{width:17,height:17,cursor:'pointer'}}
                aria-label={`Pago de ${p.caso.nombreCliente||'cliente'} visto en el banco`}
                title={p.enBanco?`Revisado${p.enBanco.por?` por ${p.enBanco.por}`:''} el ${new Date(p.enBanco.el).toLocaleString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}`:'Marcar como visto en el banco'}/>
            </td>
            <td style={{whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums'}}>{FS.fmtDate(p.fecha)}</td>
            <td>{p.caso.nombreCliente||'—'}</td><td>{p.concepto||'—'}</td><td>{p.forma||'—'}</td>
            <td>{p.caso.tema||'—'}</td><td>{p.caso.abogada||'Sin asignar'}</td>
            <td style={{textAlign:'right',color:'#F0F4FF',fontWeight:600,whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums'}}>{FS.fmtMoney(p.monto)}</td>
          </tr>)}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}>
            <td colSpan={3} style={{fontWeight:400,fontSize:12,color:'var(--text-muted)'}}>
              {pagosCobrados.filter((p:any)=>p.enBanco).length} de {pagosCobrados.length} vistos en el banco
              {pagosCobrados.some((p:any)=>!p.enBanco)&&<> · falta {FS.fmtMoney(pagosCobrados.filter((p:any)=>!p.enBanco).reduce((a:number,p:any)=>a+p.monto,0))}</>}
            </td>
            <td colSpan={4} style={{textAlign:'right'}}>TOTAL COBRADO ({pagosCobrados.length} {pagosCobrados.length===1?'pago':'pagos'})</td><td style={{textAlign:'right',color:'#FFFFFF',whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums'}}>{FS.fmtMoney(totalPagado)}</td></tr>
        </tbody></table></div>}
      </div>}

      {/* Falta por cobrar — deuda exigible acumulada al cierre, cuota por cuota */}
      {vistaReporte==='saldoPorCobrar'&&<div>
        <div className="p-section-title">Falta por Cobrar — {periodoLabel}</div>
        <div className="p-info-box" style={{marginBottom:14}}>
          Lo que ya debía estar pagado al {fechaCierre} y seguía impago. Es un saldo acumulado: arrastra las
          cuotas que quedaron debiéndose en períodos anteriores, cada una con su vencimiento. Las que vencen
          después de esa fecha no aparecen, porque todavía no se deben. Una cuota pagada después del cierre
          sí aparece, porque ese día todavía se debía.
        </div>
        {saldoDetalle.length===0?<div className="p-empty">Nadie queda debiendo a esa fecha.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Área</th><th>Captador</th><th>Abogado</th><th>Ya cobrado</th><th>Total que debe</th><th>Cuotas impagas</th></tr></thead><tbody>
          {saldoDetalle.map(c=>(
            <tr key={c.id}>
              <td>{c.nombreCliente||'—'}</td><td>{c.tema||'—'}</td><td>{c.captadora||'—'}</td><td>{c.abogada||'Sin asignar'}</td>
              <td style={{color:'var(--gold)'}}>{FS.fmtMoney(c.cobrado)}</td>
              <td style={{fontWeight:600,color:c.montoVencido>0?'#e57373':'var(--white)'}}>
                {FS.fmtMoney(c.saldo)}
                {c.montoVencido>0&&c.diasAtraso>0&&<span style={{fontSize:11,fontWeight:400,color:'var(--text-muted)',display:'block'}}>{c.diasAtraso} {c.diasAtraso===1?'día':'días'} de atraso</span>}
              </td>
              <td style={{minWidth:210}}>
                {c.cuotas.length===0?<span style={{color:'var(--text-muted)'}}>—</span>:c.cuotas.map((q:any,i:number)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',gap:10,fontSize:12,padding:'1px 0',color:q.vencida?'#e57373':'var(--text-muted)'}}>
                    <span style={{fontVariantNumeric:'tabular-nums'}}>
                      {q.venc?FS.fmtDate(q.venc):'sin fecha'}
                      {q.vencida&&q.dias>0&&<span style={{fontSize:10,opacity:0.8}}> · {q.dias}d</span>}
                    </span>
                    <span style={{fontVariantNumeric:'tabular-nums',color:q.vencida?'#e57373':'var(--white)'}}>{FS.fmtMoney(q.monto)}</span>
                  </div>
                ))}
              </td>
            </tr>
          ))}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}>
            <td colSpan={5} style={{textAlign:'right'}}>TOTAL</td>
            <td style={{color:'#e57373'}}>{FS.fmtMoney(totalDeuda)}</td>
            <td></td>
          </tr>
        </tbody></table></div>}
      </div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   FLUJO DE CAJA
   50% abogadas (dividido equitativamente entre abogadas registradas)
   15% admin (del total)
   15% captadoras (15% de cada caso captado, no del total)
   20% oficina (de ahí se pagan gastos, lo que sobre es utilidad)
   ══════════════════════════════════════════════════════════════════════════ */
const FlujoCaja:React.FC<{soloLectura?:boolean}>=({soloLectura=false})=>{
  const [data,setData]=useState<any[]>([]);
  const [usuarios,setUsuarios]=useState<any[]>([]);
  const [sel,setSel]=useState<SeleccionPeriodo>({tipo:'periodo',anio:new Date().getFullYear(),mes:new Date().getMonth()});
  const [diaCorte,setDiaCorte]=useState<number>(PER.DIA_CORTE_DEFAULT);
  // Cada gasto con la fecha real en que se hizo: es lo único que permite
  // ubicarlo en el período contable correcto cuando el mes no cierra el día 1.
  const [gastos,setGastos]=useState<{inversion:number;gastos:Gasto[];legacy?:any}>({inversion:0,gastos:[]});
  // Reparto configurable. Por omisión: mitad abogados, mitad oficina. Sin
  // porcentaje para administración ni captadores, porque no todo estudio los
  // tiene y arrancar con cifras que no corresponden confunde más que ayudar.
  const [reparto,setReparto]=useState<{abogados:number;oficina:number;admin:number;captadores:number;porUsuario?:Record<string,number>;sueldoTope?:Record<string,number>}>(
    {abogados:50,oficina:50,admin:0,captadores:0,porUsuario:{},sueldoTope:{}});
  // El reparto se configura una vez y después casi no se toca, pero ocupaba
  // media pantalla arriba de las cifras del período, que es lo que se viene a
  // mirar. Arranca cerrado, con los porcentajes resumidos en el encabezado.
  const [repartoAbierto,setRepartoAbierto]=useState(false);

  const guardarReparto=(r:typeof reparto)=>{
    setReparto(r);
    // Solo se manda el reparto: el PATCH de ajustes combina con lo guardado,
    // así que no hace falta releer ni arriesgar pisar los gastos.
    FS.saveFlujoCajaConfig({reparto:r}).catch(()=>{});
  };
  const [nuevoGastoNombre,setNuevoGastoNombre]=useState('');
  const [nuevoGastoFecha,setNuevoGastoFecha]=useState(()=>PER.aISO(new Date()));
  const [saveStatus,setSaveStatus]=useState<'idle'|'saving'|'saved'|'error'>('idle');
  const [editingNameIdx,setEditingNameIdx]=useState<number|null>(null);
  const [editingNameVal,setEditingNameVal]=useState('');
  // Borrar un gasto se confirma antes: acá no hay deshacer, y dos gastos
  // pueden llamarse igual, así que la ventana repite monto y fecha para que se
  // vea cuál se está por eliminar.
  const [confirmGastoDel,setConfirmGastoDel]=useState<{idx:number;nombre:string;monto:number;fecha:string}|null>(null);
  const saveTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);
  const [showTooltipCobrado,setShowTooltipCobrado]=useState(false);
  const tooltipTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);

  useEffect(()=>{
    FS.loadReportesData().then(setData).catch(()=>{});
    FS.loadUsuarios().then(setUsuarios).catch(()=>{});
    // El servidor ya entrega los gastos migrados a la lista con fecha, así que
    // acá no hay que interpretar estructuras viejas: solo tomar lo que llega.
    FS.getFlujoCajaConfig().then((cfg:any)=>{
      if(cfg){
        setGastos({inversion:Number(cfg.inversion)||0,gastos:Array.isArray(cfg.gastos)?cfg.gastos:[],...(cfg.legacy?{legacy:cfg.legacy}:{})});
        if(cfg.reparto) setReparto({abogados:50,oficina:50,admin:0,captadores:0,porUsuario:{},sueldoTope:{},...cfg.reparto});
        const d=PER.normalizarDiaCorte(cfg.periodo?.diaCorte);
        setDiaCorte(d);
        // Con corte al 20, el 25 de julio ya es agosto: hay que mostrar el
        // período que de verdad corre hoy.
        const p=PER.periodoDeFecha(new Date(),d)!;
        setSel(s=>s.tipo==='periodo'?{tipo:'periodo',anio:p.anio,mes:p.mes}:s);
      }
    }).catch(()=>{});
  },[]);

  // Todo lo que sigue se mide contra el período contable del estudio: puede ser
  // el mes calendario o un corte tipo "del 21 al 20", según lo que tenga
  // configurado ESTE estudio.
  const rango=PER.rangoDeSeleccion(sel,diaCorte);
  const filteredData=data.filter((c:any)=>PER.enRango(c.createdAt,rango));

  // Filtra pagos por fechaPago dentro del período (no por createdAt del caso)
  const inRangeFCaja=(fechaPago:string|undefined|null):boolean=>PER.enRango(fechaPago,rango);

  const totalCobrado=data.reduce((s:number,c:any)=>s+(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);
  const casosConCobro=data.filter((c:any)=>(c.pagos||[]).some((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago))).length;

  const detalleTooltipCobrado=data
    .filter((c:any)=>(c.pagos||[]).some((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)))
    .map((c:any)=>{
      const pagados=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado');
      const pagosPeriodo=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago));
      const pagadoPeriodo=pagosPeriodo.reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const totalPagado=pagados.reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const totalHonorarios=(c.pagos||[]).reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const faltaPorPagar=Math.max(0,totalHonorarios-totalPagado);
      // Fecha del último pago DENTRO del período: es la que ordena la lista.
      // Se ordena por milisegundos y con el MISMO parser que decide si el pago
      // entra en el período, así lo que se ve y lo que ordena no pueden
      // discrepar. Un pago con fecha ilegible ya quedó fuera en ese filtro.
      const ultimoPagoMs=pagosPeriodo.reduce((mx:number,p:any)=>{
        const f=PER.aFecha(p.fechaPago); return f?Math.max(mx,f.getTime()):mx;
      },0);
      return{cliente:c.nombreCliente||'—',fechaIngreso:FS.fmtDate(c.createdAt),ultimoPago:ultimoPagoMs?FS.fmtDate(new Date(ultimoPagoMs)):'—',ultimoPagoMs,captadora:c.captadora||'—',totalPagado,pagadoPeriodo,faltaPorPagar};
    })
    // Del pago más nuevo al más antiguo: la revisión de cobranza se hace por
    // fecha, no por monto. A igual fecha manda el monto, para que los pagos de
    // un mismo día queden de mayor a menor y no en orden arbitrario.
    .sort((a:any,b:any)=>b.ultimoPagoMs-a.ultimoPagoMs||b.pagadoPeriodo-a.pagadoPeriodo);

  const enterCardCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);tooltipTimerRef.current=setTimeout(()=>setShowTooltipCobrado(true),350);};
  const leaveAreaCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);tooltipTimerRef.current=setTimeout(()=>setShowTooltipCobrado(false),150);};
  const enterPopoverCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);};
  const promedioPorCaso=casosConCobro>0?totalCobrado/casosConCobro:0;
  const totalCasosActivos=filteredData.length;

  // ── Reparto ─────────────────────────────────────────────────────────
  // Antes estos porcentajes estaban escritos a fuego en el código (50/15/15/20),
  // heredados del estudio para el que nació el sistema. Cada estudio reparte
  // distinto, así que ahora son configurables y el valor por omisión es el más
  // simple y neutro que existe: mitad para quien litiga, mitad para la oficina.
  const pctAbogados=reparto.abogados;
  const pctOficina=reparto.oficina;
  const pctAdmin=reparto.admin;
  const pctCaptadores=reparto.captadores;

  // Al cliente que llegó por la página no lo captó ninguna persona, así que esa
  // comisión se la queda la oficina: la web es del estudio. Sin esto el
  // porcentaje de captación de esos casos no se le asignaba a nadie y
  // desaparecía del reparto, aunque la plata estuviera cobrada.
  const captacionWeb=data.reduce((s:number,c:any)=>{
    if(!(esCaptadorWeb(c.captadoraId)||c.captadora===CAPTADOR_WEB_NOMBRE))return s;
    const cobradoCase=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    return s+cobradoCase*(pctCaptadores/100);
  },0);

  const abogadas50=totalCobrado*(pctAbogados/100);
  const admin15=totalCobrado*(pctAdmin/100);
  const oficina20=totalCobrado*(pctOficina/100)+captacionWeb;

  // El admin cuenta como abogado: en un estudio chico es quien litiga. El
  // admin/no abogado no: administra sin llevar causas, y meterlo acá le quitaba
  // porcentaje a los que sí litigan — para eso se creó el rol.
  //
  // El asociado sí entra: lleva causas como cualquier abogado del estudio. Que
  // no vea esta pantalla no significa que no le toque parte de lo cobrado; solo
  // que el reparto lo decide el admin.
  //
  // Quien fue desvinculado ya no entra al reparto: si siguiera en la lista, se
  // llevaría una parte igual de lo cobrado aunque ya no trabaje en el estudio.
  const abogadasList=usuarios.filter(u=>u.activo!==false&&(u.rol==='abogada'||u.rol==='abogado_asociado'||u.rol==='admin'));
  // Reparto entre abogados: por omisión en partes iguales, pero si se le fijó
  // un porcentaje propio a alguien, ese manda. Es lo que pasa de verdad en un
  // estudio: no todos los socios cobran lo mismo.
  // Porcentaje del total cobrado que le toca a cada abogado.
  const pctDeAbogado=(u:any):number=>{
    const propio=reparto.porUsuario?.[u.id];
    if(typeof propio==='number') return propio;
    const conPropio=abogadasList.filter(x=>typeof reparto.porUsuario?.[x.id]==='number');
    const usadoPorPropios=conPropio.reduce((a,x)=>a+(reparto.porUsuario![x.id]||0),0);
    const restantes=abogadasList.length-conPropio.length;
    return restantes>0?Math.max(0,pctAbogados-usadoPorPropios)/restantes:0;
  };
  const repartoAbogado=(u:any):number=>totalCobrado*(pctDeAbogado(u)/100);


  // Captadores: su porcentaje se aplica a cada caso que captaron, no al total.
  const captadoraMap:Record<string,{nombre:string;total:number;casos:number}>={};
  data.forEach((c:any)=>{
    const cobradoCase=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    if(cobradoCase===0)return;
    // El cliente que llegó por la página no lo captó nadie: la web se anota
    // como captador solo para medir por dónde entran los casos. Si entrara acá,
    // el detalle mostraría una comisión que no se le paga a ninguna persona.
    if(esCaptadorWeb(c.captadoraId)||c.captadora===CAPTADOR_WEB_NOMBRE)return;
    // Se agrupa por persona, no por el nombre: cada caso guarda el nombre que
    // tenía el captador al crearse, y si después lo cambió quedaba partido en
    // dos filas. El nombre que se muestra es el actual.
    const cap=c.captadoraId||c.captadora||'Sin captador';
    const nombre=usuarios.find(u=>u.id===c.captadoraId)?.nombre||c.captadora||'Sin captador';
    const comision=cobradoCase*(pctCaptadores/100);
    if(!captadoraMap[cap])captadoraMap[cap]={nombre,total:0,casos:0};
    captadoraMap[cap].total+=comision;
    captadoraMap[cap].casos++;
  });
  const totalCaptadores=Object.values(captadoraMap).reduce((s,v)=>s+v.total,0);
  const totalCaptadoras=totalCaptadores;
  const sumaReparto=pctAbogados+pctOficina+pctAdmin+pctCaptadores;

  const persistGastos=(updated:typeof gastos)=>{
    if(saveTimerRef.current)clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current=setTimeout(()=>{
      const clean=JSON.parse(JSON.stringify(updated));
      FS.saveFlujoCajaConfig(clean).then(()=>{setSaveStatus('saved');setTimeout(()=>setSaveStatus('idle'),2500);}).catch((err:any)=>{console.error('[FlujoCaja] Error al guardar:',err?.code,err?.message,err);setSaveStatus('error');});
    },800);
  };

  // ── Gastos ──────────────────────────────────────────────────────────────
  // Cada gasto se ubica por su fecha real. Se conserva el índice dentro de la
  // lista completa para poder editarlo aunque en pantalla solo se vea un
  // período.
  const gastosDelPeriodo=gastos.gastos
    .map((g,idx)=>({g,idx}))
    .filter(({g})=>PER.enRango(g.fecha,rango))
    .sort((a,b)=>a.g.fecha.localeCompare(b.g.fecha));
  const totalGastosPeriodo=gastosDelPeriodo.reduce((s,{g})=>s+(Number(g.monto)||0),0);
  // Lo que paga la oficina de su parte. Los sueldos y comisiones marcados
  // salen de la parte de cada uno, no de la de la oficina.
  const gastosOficinaPeriodo=gastosDelPeriodo.reduce((s,{g})=>s+(g.reparto?0:(Number(g.monto)||0)),0);
  const pagadoRepartoPeriodo=totalGastosPeriodo-gastosOficinaPeriodo;

  // Acumulados AL CIERRE del período: no es lo que se gastó en el mes, sino
  // todo lo que se llevaba gastado a esa fecha. Es lo que corresponde para
  // saber cuánta plata hay en la cuenta ese día.
  const gastosHastaCierre=gastos.gastos
    .filter(g=>PER.hastaCierre(g.fecha,rango))
    .reduce((s,g)=>s+(Number(g.monto)||0),0);
  const gastosOficinaHastaCierre=gastos.gastos
    .filter(g=>!g.reparto&&PER.hastaCierre(g.fecha,rango))
    .reduce((s,g)=>s+(Number(g.monto)||0),0);
  const repartoHastaCierre=gastosHastaCierre-gastosOficinaHastaCierre;
  const cobradoHastaCierre=data.reduce((s:number,c:any)=>s+(c.pagos||[])
    .filter((p:any)=>p.estado==='Pagado'&&PER.hastaCierre(p.fechaPago,rango))
    .reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);

  const utilidadOficina=oficina20-gastosOficinaPeriodo;
  // Lo que le tocó a la oficina de todo lo cobrado hasta el cierre, con el
  // porcentaje que tenga configurado el estudio (no un 20% fijo).
  const oficinaHastaCierre=cobradoHastaCierre*(pctOficina/100);
  const saldoInversion=gastos.inversion+oficinaHastaCierre-gastosOficinaHastaCierre;
  const totalFondos=gastos.inversion+oficinaHastaCierre;
  const saldoBanco=gastos.inversion+cobradoHastaCierre-gastosHastaCierre;
  const pctConsumido=totalFondos>0?Math.min(100,Math.max(0,Math.round(gastosOficinaHastaCierre/totalFondos*100))):0;
  const margenNeto=totalCobrado>0?(utilidadOficina/totalCobrado*100):0;

  // ── Equilibrio con sueldos con tope ─────────────────────────────────
  // Mientras el estudio no gane más, a cada abogado se le paga lo que le da su
  // porcentaje pero nunca más que su tope en pesos. Administración se rebaja en
  // la misma proporción que los abogados (si ellos reciben el 40% de lo que
  // les tocaría, administración también). Captadores cobran su % normal; la
  // captación web y todo lo que sobra queda en la oficina, que paga los gastos.
  const topeDe=(u:any):number|undefined=>{
    const t=reparto.sueldoTope?.[u.id];
    return typeof t==='number'?t:undefined;
  };
  // Qué parte del cobrado se va en comisiones a personas. Se toma la del
  // período, porque depende de qué casos pagaron; sin cobros se supone que
  // todos traen captador, que es el caso más exigente.
  const fraccionCaptadores=totalCobrado>0?totalCaptadores/totalCobrado:pctCaptadores/100;
  const escenario=(cobrado:number)=>{
    const sinTope=abogadasList.reduce((a,u)=>a+cobrado*pctDeAbogado(u)/100,0);
    const conTope=abogadasList.reduce((a,u)=>{
      const t=topeDe(u); const bruto=cobrado*pctDeAbogado(u)/100;
      return a+(t===undefined?bruto:Math.min(t,bruto));
    },0);
    const factor=sinTope>0?conTope/sinTope:1;
    const admin=cobrado*(pctAdmin/100)*factor;
    const captadores=cobrado*fraccionCaptadores;
    return{abogados:conTope,admin,captadores,oficina:cobrado-conTope-admin-captadores,factor};
  };
  const eqActual=escenario(totalCobrado);
  const resultadoMes=eqActual.oficina-gastosOficinaPeriodo;
  // Cuánto habría que cobrar en el período para que la oficina cubra los
  // gastos. La oficina crece con lo cobrado, así que basta una bisección.
  const cobradoEquilibrio=((): number|null=>{
    if(gastosOficinaPeriodo<=0) return 0;
    let hi=Math.max(1,gastosOficinaPeriodo);
    while(escenario(hi).oficina<gastosOficinaPeriodo){ hi*=2; if(hi>1e13) return null; }
    let lo=0;
    for(let i=0;i<80;i++){ const mid=(lo+hi)/2; if(escenario(mid).oficina>=gastosOficinaPeriodo) hi=mid; else lo=mid; }
    return Math.ceil(hi);
  })();
  const faltaCobrar=cobradoEquilibrio===null?null:Math.max(0,cobradoEquilibrio-totalCobrado);
  const guardarTope=(id:string,val:string)=>{
    const st={...(reparto.sueldoTope||{})};
    if(val.trim()==='') delete st[id]; else st[id]=Math.max(0,parseMoneyInput(val));
    guardarReparto({...reparto,sueldoTope:st});
  };

  // Los gastos se editan por su posición en la lista completa, no en la lista
  // filtrada que se ve en pantalla: así cambiar de período nunca edita el
  // gasto equivocado.
  const cambiarGasto=(idx:number,campos:Partial<Gasto>)=>{
    const updated={...gastos,gastos:gastos.gastos.map((g,i)=>i===idx?{...g,...campos}:g)};
    setGastos(updated);persistGastos(updated);
  };
  const removeGasto=(idx:number)=>{
    const updated={...gastos,gastos:gastos.gastos.filter((_,i)=>i!==idx)};
    setGastos(updated);persistGastos(updated);setConfirmGastoDel(null);
  };
  const addGasto=()=>{
    const nombre=nuevoGastoNombre.trim();
    if(!nombre)return;
    const fecha=/^\d{4}-\d{2}-\d{2}$/.test(nuevoGastoFecha)?nuevoGastoFecha:PER.aISO(new Date());
    const updated={...gastos,gastos:[...gastos.gastos,{nombre,monto:0,fecha}]};
    setGastos(updated);setNuevoGastoNombre('');persistGastos(updated);
  };
  const updateInversion=(val:number)=>{const updated={...gastos,inversion:val};setGastos(updated);persistGastos(updated);};

  const parseMoneyInput=(val:string):number=>{
    const sanitized = val.trim().replace(/\s+/g,'').replace(/\./g,'').replace(/,/g,'.').replace(/[^0-9.-]/g,'');
    const parsed = parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : 0;
  };



  return(
    <div className="p-section">
      <div className="p-section-title">💵 Flujo de Caja</div>
      {!soloLectura&&(
        <div style={{border:'1px solid rgba(255,255,255,0.10)',borderRadius:10,padding:'12px 14px',marginBottom:14,background:'rgba(255,255,255,0.02)'}}>
          {/* Cerrado, el encabezado tiene que decir el reparto completo: si hay
              que abrirlo para saber cómo se reparte, guardarlo no sirvió de
              nada. Y la suma que no da 100% se avisa siempre, abierto o no. */}
          <div onClick={()=>setRepartoAbierto(v=>!v)} role="button" tabIndex={0}
            onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();setRepartoAbierto(v=>!v);} }}
            style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8,cursor:'pointer'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--white)',display:'flex',alignItems:'center',gap:6}}>
              <span style={{fontSize:11,color:'var(--text-muted)'}}>{repartoAbierto?'▲':'▼'}</span>
              Cómo se reparte lo cobrado
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              {!repartoAbierto&&(
                <span style={{fontSize:12,color:'var(--text-muted)'}}>
                  Abogados {pctAbogados}% · Oficina {pctOficina}% · Administración {pctAdmin}% · Captadores {pctCaptadores}%
                </span>
              )}
              <span style={{fontSize:12,color:sumaReparto===100?'var(--text-muted)':'#e57373'}}>
                Suma {sumaReparto}%{sumaReparto!==100?' — debería sumar 100%':''}
              </span>
            </div>
          </div>
          {repartoAbierto&&(<>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginTop:10}}>
            {([['abogados','Abogados'],['oficina','Oficina'],['admin','Administración'],['captadores','Captadores']] as const).map(([k,label])=>(
              <div key={k}>
                <div className="p-field-label" style={{fontSize:11}}>{label}</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <input className="p-input" type="number" min={0} max={100} value={reparto[k]}
                    onChange={e=>guardarReparto({...reparto,[k]:Math.max(0,Math.min(100,Number(e.target.value)||0))})}
                    style={{width:70,textAlign:'right'}}/>
                  <span style={{color:'var(--text-muted)',fontSize:13}}>%</span>
                </div>
              </div>
            ))}
          </div>
          {abogadasList.length>0&&(
            <div style={{marginTop:12,paddingTop:10,borderTop:'1px solid rgba(255,255,255,0.07)'}}>
              <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:6}}>
                Porcentaje por abogado. Deja en blanco a quien reparta en partes iguales lo que sobre del {reparto.abogados}%.
              </div>
              {abogadasList.map((u:any)=>(
                <div key={u.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'3px 0'}}>
                  <span style={{fontSize:13,color:'var(--white)'}}>{u.nombre||u.email}{u.rol==='admin'?' · admin':''}</span>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <input className="p-input" type="number" min={0} max={100}
                      value={reparto.porUsuario?.[u.id]??''} placeholder="—"
                      onChange={e=>{
                        const v=e.target.value===''?undefined:Math.max(0,Math.min(100,Number(e.target.value)||0));
                        const pu={...(reparto.porUsuario||{})};
                        if(v===undefined) delete pu[u.id]; else pu[u.id]=v;
                        guardarReparto({...reparto,porUsuario:pu});
                      }}
                      style={{width:70,textAlign:'right'}}/>
                    <span style={{color:'var(--text-muted)',fontSize:13}}>%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{marginTop:10,fontSize:11.5,color:'var(--text-muted)',lineHeight:1.55}}>
            Los porcentajes de abogados, oficina y administración se aplican sobre el total cobrado del período.
            El de captadores se aplica a cada caso que esa persona captó, no al total.
          </div>
          </>)}
        </div>
      )}

      <SelectorPeriodo sel={sel} onChange={setSel} diaCorte={diaCorte}/>

      {/* KPIs principales */}
      <div className="p-grid3" style={{marginBottom:20}}>
        {/* Total cobrado — hover muestra detalle por cliente */}
        <div style={{position:'relative'}} onMouseEnter={enterCardCobrado} onMouseLeave={leaveAreaCobrado}>
          <div className="fin-card ok" style={{padding:'16px',cursor:'help'}}>
            <div className="fin-label" style={{fontSize:12}}>Total cobrado</div>
            <div className="fin-val" style={{fontSize:'1.6rem'}}>{FS.fmtMoney(totalCobrado)}</div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>{casosConCobro} casos con pago</div>
          </div>
          {showTooltipCobrado&&(
            <div onMouseEnter={enterPopoverCobrado} onMouseLeave={leaveAreaCobrado}
              style={{position:'absolute',top:'calc(100% + 8px)',left:0,zIndex:1000,background:'#0f1120',border:'1px solid rgba(255,255,255,0.13)',borderRadius:10,width:'min(760px, calc(100vw - 32px))',maxHeight:340,overflow:'auto',boxShadow:'0 12px 40px rgba(0,0,0,0.6)'}}>
              {/* En el celular el recuadro no pasa del ancho de la pantalla y la
                  tabla, que sí mide 760, se desliza hacia el lado adentro. */}
              <table className="p-table" style={{fontSize:12,margin:0,minWidth:760}}>
                <thead><tr><th>Cliente</th><th>Último pago</th><th>Ingreso</th><th>Captador</th><th style={{textAlign:'right'}}>Total pagado</th><th style={{textAlign:'right'}}>Pagado período</th><th style={{textAlign:'right'}}>Falta por pagar</th></tr></thead>
                <tbody>
                  {detalleTooltipCobrado.map((r:any,i:number)=>(
                    <tr key={i}>
                      <td>{r.cliente}</td>
                      <td style={{whiteSpace:'nowrap',fontWeight:600}}>{r.ultimoPago}</td>
                      <td style={{whiteSpace:'nowrap',color:'var(--text-muted)'}}>{r.fechaIngreso}</td>
                      <td>{r.captadora}</td>
                      <td style={{textAlign:'right',color:'var(--gold)'}}>{FS.fmtMoney(r.totalPagado)}</td>
                      <td style={{textAlign:'right',color:'#4CAF50',fontWeight:600}}>{FS.fmtMoney(r.pagadoPeriodo)}</td>
                      <td style={{textAlign:'right',color:r.faltaPorPagar>0?'#e57373':'var(--text-muted)'}}>{FS.fmtMoney(r.faltaPorPagar)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.12)'}}>
                    <td colSpan={4} style={{textAlign:'right',color:'var(--text-muted)'}}>TOTAL</td>
                    <td style={{textAlign:'right',color:'var(--gold)'}}>{FS.fmtMoney(detalleTooltipCobrado.reduce((s:number,r:any)=>s+r.totalPagado,0))}</td>
                    <td style={{textAlign:'right',color:'#4CAF50',fontWeight:700}}>{FS.fmtMoney(totalCobrado)}</td>
                    <td style={{textAlign:'right',color:'#e57373'}}>{FS.fmtMoney(detalleTooltipCobrado.reduce((s:number,r:any)=>s+r.faltaPorPagar,0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
        <div className="fin-card" style={{padding:'16px',background:'rgba(33,150,243,0.08)',borderColor:'rgba(33,150,243,0.2)'}}>
          <div className="fin-label" style={{fontSize:12}}>Promedio por caso</div>
          <div className="fin-val" style={{fontSize:'1.6rem',color:'#2196F3'}}>{FS.fmtMoney(promedioPorCaso)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>{totalCasosActivos} casos en periodo</div>
        </div>
        <div className="fin-card" style={{padding:'16px',background:'rgba(76,175,80,0.08)',borderColor:'rgba(76,175,80,0.2)'}}>
          <div className="fin-label" style={{fontSize:12}}>Margen neto %</div>
          <div className="fin-val" style={{fontSize:'1.6rem',color:'#4CAF50'}}>{margenNeto.toFixed(1)}%</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>de utilidad</div>
        </div>
      </div>

      {/* Distribución */}
      <div className="p-section-title" style={{marginBottom:14}}>Distribución de ingresos</div>
      <div className="p-grid2" style={{marginBottom:24}}>
        <div>
          {[
            {label:`Abogados (${pctAbogados}%)`,amount:abogadas50,color:'#4CAF50',nota:''},
            {label:`Admin (${pctAdmin}%)`,amount:admin15,color:'#2196F3',nota:''},
            {label:`Captadores (${pctCaptadores}% por caso)`,amount:totalCaptadoras,color:'#FF9800',nota:''},
            // El monto de oficina puede ser mayor que su porcentaje: se le suma
            // la captación de los casos que llegaron por la web. Se dice acá
            // mismo, porque si no la cifra no cuadra con el % y parece un error.
            {label:`Oficina (${pctOficina}%)`,amount:oficina20,color:'#9C27B0',
             nota:captacionWeb>0?`incluye ${FS.fmtMoney(captacionWeb)} de captación web`:''},
          ].map(r=>(
            <div key={r.label} style={{marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                <span style={{color:'#F0F4FF'}}>{r.label}</span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(r.amount)}</span>
              </div>
              {r.nota&&<div style={{fontSize:10.5,color:'var(--text-muted)',marginBottom:4}}>{r.nota}</div>}
              <div style={{height:10,background:'rgba(255,255,255,0.06)',borderRadius:5,overflow:'hidden'}}>
                <div style={{height:'100%',width:totalCobrado>0?`${(r.amount/totalCobrado*100)}%`:'0%',background:r.color,borderRadius:5,transition:'width 0.3s'}}/>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div style={{background:'rgba(76,175,80,0.08)',borderRadius:8,padding:14,marginBottom:12,border:'1px solid rgba(76,175,80,0.2)'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:8}}>Detalle de abogados — {pctAbogados}% del total</div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Total: {FS.fmtMoney(abogadas50)}</div>
            {abogadasList.length>0 ? abogadasList.map(a=>(
              <div key={a.id} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                {/* Se indica de dónde sale la cifra: si la persona tiene un
                    porcentaje propio se muestra, y si no, que va en partes
                    iguales. Un monto sin explicación genera desconfianza. */}
                <span>{a.nombre||a.email}
                  <span style={{color:'var(--text-muted)',fontSize:11}}>
                    {typeof reparto.porUsuario?.[a.id]==='number'?` · ${reparto.porUsuario[a.id]}%`:' · partes iguales'}
                  </span>
                </span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(repartoAbogado(a))}</span>
              </div>
            )) : <div style={{fontSize:12,color:'var(--text-muted)'}}>No hay abogados registrados</div>}
          </div>

          <div style={{background:'rgba(255,152,0,0.1)',borderRadius:8,padding:14,border:'1px solid rgba(255,152,0,0.2)'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:8}}>Detalle de captadores</div>
            {Object.values(captadoraMap).length>0 ? Object.entries(captadoraMap).map(([k,cap])=>(
              <div key={k}style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span>{cap.nombre} <span style={{color:'var(--text-muted)',fontSize:11}}>({cap.casos} caso{cap.casos>1?'s':''})</span></span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(cap.total)}</span>
              </div>
            )) : <div style={{fontSize:12,color:'var(--text-muted)'}}>Sin captaciones en este periodo</div>}
          </div>
        </div>
      </div>

      {/* Gastos de oficina */}
      <div style={{background:'rgba(156,39,176,0.06)',borderRadius:10,padding:22,border:'1px solid rgba(156,39,176,0.15)'}}>
        <div className="p-section-title" style={{marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>🏢 Gastos de oficina y utilidad</span>
          <span style={{fontSize:11,fontWeight:400}}>
            {!soloLectura&&saveStatus==='saving'&&<span style={{color:'var(--text-muted)'}}>Guardando...</span>}
            {!soloLectura&&saveStatus==='saved'&&<span style={{color:'#4CAF50'}}>Guardado ✓</span>}
            {!soloLectura&&saveStatus==='error'&&<span style={{color:'#e57373'}}>Error al guardar</span>}
            {soloLectura&&<span style={{color:'var(--text-muted)',fontSize:10,letterSpacing:'0.1em'}}>SOLO LECTURA</span>}
          </span>
        </div>
        <div className="p-info-box" style={{marginBottom:16}}>
          Del {pctOficina}% de oficina ({FS.fmtMoney(oficina20)}
          {captacionWeb>0&&<>, que incluye {FS.fmtMoney(captacionWeb)} de captación web</>}) se pagan los gastos de oficina.
          Lo que sobra es la utilidad neta. Los sueldos y comisiones que anotes como gasto márcalos como tales: salen del reparto, no de esta parte.
        </div>

        <div className="p-grid2">
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>Gastos del período</div>
              <div style={{fontSize:11,color:'var(--text-muted)'}}>{PER.etiquetaRango(rango)}</div>
            </div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10,lineHeight:1.6}}>
              Cada gasto se anota con la fecha real en que se hizo y entra al período que le corresponde por esa fecha.
            </div>
            {gastosDelPeriodo.length===0&&<div style={{fontSize:12,color:'var(--text-muted)',padding:'14px 0'}}>Sin gastos anotados en este período.</div>}
            {gastosDelPeriodo.map(({g,idx})=>(
              <div key={idx} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'7px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <div style={{flex:1,minWidth:0}}>
                  {!soloLectura&&editingNameIdx===idx?(
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      <input className="p-input" value={editingNameVal} onChange={e=>setEditingNameVal(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){cambiarGasto(idx,{nombre:editingNameVal.trim()||g.nombre});setEditingNameIdx(null);}if(e.key==='Escape')setEditingNameIdx(null);}} autoFocus style={{fontSize:12,flex:1}}/>
                      <button className="p-btn-secondary" style={{padding:'2px 8px',fontSize:11}} onClick={()=>{cambiarGasto(idx,{nombre:editingNameVal.trim()||g.nombre});setEditingNameIdx(null);}}>✓</button>
                      <button className="p-btn-danger-sm" style={{fontSize:11}} onClick={()=>setEditingNameIdx(null)}>✕</button>
                    </div>
                  ):(
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:13,color:'var(--white)'}}>{g.nombre}</span>
                      {!soloLectura&&<button onClick={()=>{setEditingNameIdx(idx);setEditingNameVal(g.nombre);}} title="Editar nombre" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:11,padding:'0 2px',lineHeight:1}}>✏️</button>}
                    </div>
                  )}
                  {soloLectura
                    ?<div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{FS.fmtDate(g.fecha)}{g.reparto?' · sueldo o comisión':''}</div>
                    :<div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginTop:3}}>
                      <input className="p-input" type="date" value={g.fecha} onChange={e=>e.target.value&&cambiarGasto(idx,{fecha:e.target.value})} style={{fontSize:11,padding:'2px 6px',width:135}}/>
                      <label title="Sueldo de un abogado, pago a administración o comisión de captación: sale del reparto, no de la parte de la oficina" style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:g.reparto?'#64B5F6':'var(--text-muted)',cursor:'pointer',whiteSpace:'nowrap'}}>
                        <input type="checkbox" checked={!!g.reparto} onChange={e=>cambiarGasto(idx,{reparto:e.target.checked||undefined})}/>
                        Sueldo o comisión
                      </label>
                    </div>}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                  <input className="p-input" type="text" inputMode="numeric" value={g.monto!==undefined?String(g.monto):''} onChange={e=>!soloLectura&&cambiarGasto(idx,{monto:parseMoneyInput(e.target.value)})} readOnly={soloLectura} style={{flex:1,minWidth:150,fontSize:13}} placeholder="0"/>
                  {!soloLectura&&<button className="p-btn-danger-sm" onClick={()=>setConfirmGastoDel({idx,nombre:g.nombre,monto:Number(g.monto)||0,fecha:g.fecha})} style={{marginLeft:4}} title="Eliminar gasto">✕</button>}
                </div>
              </div>
            ))}
            {!soloLectura&&<div style={{display:'flex',gap:8,marginTop:12,alignItems:'center',flexWrap:'wrap'}}>
              <input className="p-input" value={nuevoGastoNombre} onChange={e=>setNuevoGastoNombre(e.target.value)} placeholder="Nombre del gasto (arriendo, luz…)" style={{flex:1,minWidth:160,fontSize:12}} onKeyPress={e=>e.key==='Enter'&&addGasto()}/>
              <input className="p-input" type="date" value={nuevoGastoFecha} onChange={e=>setNuevoGastoFecha(e.target.value)} title="Fecha del gasto" style={{fontSize:12,padding:'5px 8px',width:145}}/>
              <button className="p-btn-secondary" onClick={addGasto} style={{padding:'6px 12px',fontSize:12,whiteSpace:'nowrap'}}>＋ Agregar gasto</button>
            </div>}
            <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0',fontWeight:700,fontSize:14,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:8}}>
              <span style={{color:'var(--white)'}}>Total del período</span>
              <span style={{color:'#e57373'}}>{FS.fmtMoney(totalGastosPeriodo)}</span>
            </div>
            {pagadoRepartoPeriodo>0&&<>
              <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0',fontSize:12,color:'var(--text-muted)'}}>
                <span>Gastos de oficina</span>
                <span style={{color:'#e57373'}}>{FS.fmtMoney(gastosOficinaPeriodo)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0',fontSize:12,color:'var(--text-muted)'}}>
                <span>Sueldos y comisiones</span>
                <span style={{color:'#64B5F6'}}>{FS.fmtMoney(pagadoRepartoPeriodo)}</span>
              </div>
            </>}
            <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12,color:'var(--text-muted)'}}>
              <span>Acumulado hasta el cierre</span>
              <span style={{color:'#e57373'}}>{FS.fmtMoney(gastosHastaCierre)}</span>
            </div>
          </div>

          <div>
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:16,marginBottom:14,border:'1px solid rgba(255,255,255,0.06)'}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:4,color:'var(--white)'}}>💰 Inversión inicial (capital inyectado)</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>Capital aportado a la sociedad que cubre los gastos hasta que el negocio sea autosustentable.</div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                <input className="p-input" type="text" inputMode="numeric" value={gastos.inversion !== undefined ? String(gastos.inversion) : ''} onChange={e=>!soloLectura&&updateInversion(parseMoneyInput(e.target.value))} readOnly={soloLectura} placeholder="0" style={{fontSize:14,flex:1}}/>
              </div>
            </div>

            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:8,padding:16,border:'1px solid rgba(255,255,255,0.08)'}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2,color:'var(--white)'}}>📊 Saldo de inversión</div>
              {/* Acumulado, no del período: es cuánto queda al cerrar esa fecha. */}
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>Al cierre del {PER.aISO(rango.hasta).split('-').reverse().join('-')}</div>

              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>Capital inicial</span>
                <span style={{color:'#2196F3',fontWeight:600}}>{FS.fmtMoney(gastos.inversion)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>+ Ingresos oficina ({pctOficina}%)</span>
                <span style={{color:'#4CAF50',fontWeight:600}}>+ {FS.fmtMoney(oficinaHastaCierre)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>− Gastos de oficina hasta el cierre</span>
                <span style={{color:'#e57373'}}>− {FS.fmtMoney(gastosOficinaHastaCierre)}</span>
              </div>
              {repartoHastaCierre>0&&<div style={{fontSize:10.5,color:'var(--text-muted)',marginTop:4}}>
                No se descuentan {FS.fmtMoney(repartoHastaCierre)} de sueldos y comisiones: salen del reparto, no de la parte de la oficina.
              </div>}

              {totalFondos>0&&(
                <div style={{margin:'12px 0 4px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:5}}>
                    <span>Gastos vs fondos disponibles</span>
                    <span style={{color:pctConsumido>=100?'#e57373':pctConsumido>=70?'#FF9800':'#4CAF50'}}>{pctConsumido}%</span>
                  </div>
                  <div style={{height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pctConsumido}%`,background:pctConsumido>=100?'#e57373':pctConsumido>=70?'#FF9800':'#4CAF50',borderRadius:4,transition:'width 0.4s'}}/>
                  </div>
                </div>
              )}

              <div style={{display:'flex',justifyContent:'space-between',fontSize:17,padding:'12px 0',fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:8}}>
                <span style={{color:'var(--white)'}}>Saldo disponible</span>
                <span style={{color:saldoInversion>=0?'#4CAF50':'#e57373'}}>{saldoInversion<0?'− ':''}{FS.fmtMoney(Math.abs(saldoInversion))}</span>
              </div>

              {saldoInversion<0&&<div style={{fontSize:12,color:'#e57373',marginTop:6,padding:'8px 10px',background:'rgba(229,115,115,0.08)',borderRadius:6}}>⚠️ Los gastos superan el capital invertido más los ingresos de oficina.</div>}
              {saldoInversion>=0&&saldoInversion<totalFondos*0.3&&totalFondos>0&&<div style={{fontSize:12,color:'#FF9800',marginTop:6,padding:'8px 10px',background:'rgba(255,152,0,0.08)',borderRadius:6}}>⚠️ Queda menos del 30% de los fondos disponibles.</div>}
              {saldoInversion>=totalFondos*0.3&&totalFondos>0&&<div style={{fontSize:12,color:'#4CAF50',marginTop:6,padding:'8px 10px',background:'rgba(76,175,80,0.08)',borderRadius:6}}>✓ Fondos disponibles.</div>}

              <div style={{marginTop:14,paddingTop:12,borderTop:'1px dashed rgba(255,255,255,0.07)'}}>
                <div style={{fontSize:10,color:'var(--text-muted)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:7}}>Cuenta banco al cierre</div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:2}}>
                  <span>Inversión + cobrado hasta el cierre</span>
                  <span>{FS.fmtMoney(gastos.inversion+cobradoHastaCierre)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:8}}>
                  <span>− Gastos hasta el cierre</span>
                  <span style={{color:'#e57373'}}>− {FS.fmtMoney(gastosHastaCierre)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:14,fontWeight:700}}>
                  <span style={{color:'var(--white)'}}>💳 Saldo banco</span>
                  <span style={{color:saldoBanco>=0?'#4CAF50':'#e57373'}}>{saldoBanco<0?'− ':''}{FS.fmtMoney(Math.abs(saldoBanco))}</span>
                </div>
                <div style={{fontSize:10,color:'var(--text-muted)',marginTop:6,lineHeight:1.5}}>
                  Todo lo cobrado menos todo lo gastado hasta el {PER.aISO(rango.hasta).split('-').reverse().join('-')}, más el capital inyectado.
                </div>
              </div>
            </div>

            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:8,padding:16,marginTop:14,border:'1px solid rgba(255,255,255,0.08)'}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2,color:'var(--white)'}}>🎯 Equilibrio del período</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10,lineHeight:1.5}}>
                {PER.etiquetaRango(rango)}. Cuánto hay que cobrar para que la oficina pague sus gastos del período sin usar capital,
                pagando a cada abogado lo que le da su % pero no más que su sueldo tope.
              </div>

              {abogadasList.length>0&&(
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4}}>Sueldo tope por abogado (en blanco: cobra su % completo)</div>
                  {abogadasList.map((u:any)=>{
                    const t=topeDe(u); const bruto=repartoAbogado(u);
                    return(
                      <div key={u.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'4px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{fontSize:12.5,color:'var(--white)',minWidth:0,overflow:'hidden',textOverflow:'ellipsis'}}>{u.nombre||u.email}</span>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap'}}>recibe {FS.fmtMoney(t===undefined?bruto:Math.min(t,bruto))}</span>
                          <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                          <input className="p-input" type="text" inputMode="numeric" placeholder="sin tope"
                            value={t===undefined?'':String(t)} readOnly={soloLectura}
                            onChange={e=>!soloLectura&&guardarTope(u.id,e.target.value)}
                            style={{width:110,fontSize:12,textAlign:'right'}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {[
                {label:'Total cobrado',v:totalCobrado,c:'var(--white)',signo:''},
                {label:'− Abogados (con tope)',v:eqActual.abogados,c:'#e57373',signo:'− '},
                {label:`− Administración (${totalCobrado>0?(eqActual.admin/totalCobrado*100).toFixed(1):(pctAdmin*eqActual.factor).toFixed(1)}% efectivo de ${pctAdmin}%)`,v:eqActual.admin,c:'#e57373',signo:'− '},
                {label:'− Captadores',v:eqActual.captadores,c:'#e57373',signo:'− '},
                {label:'= Queda para la oficina',v:eqActual.oficina,c:'#4CAF50',signo:''},
                {label:'− Gastos de oficina del período',v:gastosOficinaPeriodo,c:'#e57373',signo:'− '},
              ].map(r=>(
                <div key={r.label} style={{display:'flex',justifyContent:'space-between',fontSize:12.5,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                  <span style={{color:'var(--text-muted)'}}>{r.label}</span>
                  <span style={{color:r.c,fontWeight:600}}>{r.signo}{FS.fmtMoney(r.v)}</span>
                </div>
              ))}
              {captacionWeb>0&&<div style={{fontSize:10.5,color:'var(--text-muted)',marginTop:4}}>La oficina incluye {FS.fmtMoney(captacionWeb)} de captación web.</div>}
              {/* Control: lo anotado como sueldos y comisiones contra lo que
                  corresponde según el reparto con tope. */}
              {pagadoRepartoPeriodo>0&&<div style={{fontSize:10.5,color:'var(--text-muted)',marginTop:4}}>
                Sueldos y comisiones anotados en gastos: {FS.fmtMoney(pagadoRepartoPeriodo)} (según el reparto: {FS.fmtMoney(eqActual.abogados+eqActual.admin+eqActual.captadores)}). No se restan otra vez.
              </div>}

              <div style={{display:'flex',justifyContent:'space-between',fontSize:15,padding:'10px 0',fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:6}}>
                <span style={{color:'var(--white)'}}>Resultado del período</span>
                <span style={{color:resultadoMes>=0?'#4CAF50':'#e57373'}}>{resultadoMes<0?'− ':''}{FS.fmtMoney(Math.abs(resultadoMes))}</span>
              </div>

              {cobradoEquilibrio===null
                ?<div style={{fontSize:12,color:'#e57373',padding:'8px 10px',background:'rgba(229,115,115,0.08)',borderRadius:6}}>⚠️ Con este reparto a la oficina no le queda nada de lo cobrado: ningún monto cubre los gastos.</div>
                :faltaCobrar!==null&&faltaCobrar>0
                ?<div style={{fontSize:12.5,color:'#FF9800',padding:'8px 10px',background:'rgba(255,152,0,0.08)',borderRadius:6,lineHeight:1.55}}>
                  Falta cobrar <strong>{FS.fmtMoney(faltaCobrar)}</strong> en el período para no usar capital.
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Equilibrio: cobrar {FS.fmtMoney(cobradoEquilibrio)} en total.</div>
                </div>
                :<div style={{fontSize:12.5,color:'#4CAF50',padding:'8px 10px',background:'rgba(76,175,80,0.08)',borderRadius:6}}>✓ El período se paga solo: la oficina cubre los gastos{cobradoEquilibrio>0?<> (equilibrio en {FS.fmtMoney(cobradoEquilibrio)})</>:null}.</div>}
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:8,lineHeight:1.5}}>
                Administración recibe la misma fracción de su {pctAdmin}% que reciben los abogados del suyo. Captadores se estima con la proporción de este período.
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmGastoDel&&(
        <div className="panel-modal-fondo" onClick={()=>setConfirmGastoDel(null)}>
          <div className="panel-modal" style={{maxWidth:380,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'1.6rem',marginBottom:10}}>🗑️</div>
            <div style={{fontSize:15,color:'#FFFFFF',marginBottom:8,fontWeight:500}}>Eliminar gasto</div>
            <div style={{fontSize:13,color:'#9BB0CC',marginBottom:22,lineHeight:1.6}}>
              ¿Seguro que desea eliminar <strong style={{color:'#FFFFFF'}}>{confirmGastoDel.nombre}</strong>
              {' '}por <strong style={{color:'#FFFFFF'}}>{FS.fmtMoney(confirmGastoDel.monto)}</strong>
              {' '}del <strong style={{color:'#FFFFFF'}}>{FS.fmtDate(confirmGastoDel.fecha)}</strong>?
              <br/>Esta acción no se puede deshacer.
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>setConfirmGastoDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'#C9D6E8',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
              <button onClick={()=>removeGasto(confirmGastoDel.idx)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ANÁLISIS & FORECAST
   ══════════════════════════════════════════════════════════════════════════ */
const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

/* ══════════════════════════════════════════════════════════════════════════
   CAPTADORAS — Bonos y Rendimiento
   ══════════════════════════════════════════════════════════════════════════ */
const BonoRegla: React.FC<{ajustes:any; esAdmin:boolean; onSaved:(a:any)=>void}> = ({ajustes,esAdmin,onSaved}) => {
  const b = ajustes.bono;
  const [editando,setEditando]=useState(false);
  // Los montos viven en el formulario ya con los miles separados: es lo mismo
  // que ve la persona mientras escribe, y parseMoney los devuelve a número al
  // guardar. Así "50.000" no termina siendo 50.
  const valoresDelFormulario=()=>({metaCasos:b.metaCasos,montoBase:FS.fmtMiles(b.montoBase),montoPorCaso:FS.fmtMiles(b.montoPorCaso)});
  const [f,setF]=useState<any>(valoresDelFormulario);
  const [guardando,setGuardando]=useState(false);
  const abrir=()=>{setF(valoresDelFormulario());setEditando(true);};
  const guardar=async()=>{
    setGuardando(true);
    try{
      const nuevo=await FS.guardarAjustes({bono:{metaCasos:Math.max(1,Number(f.metaCasos)||1),montoBase:Math.max(0,FS.parseMoney(f.montoBase)),montoPorCaso:Math.max(0,FS.parseMoney(f.montoPorCaso))}});
      onSaved(nuevo); setEditando(false);
    }catch{alert('No se pudo guardar el ajuste.');}
    finally{setGuardando(false);}
  };
  const iSty:React.CSSProperties={background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:6,color:'var(--white)',padding:'6px 8px',width:130,fontSize:13};
  return (
    <div style={{ marginTop: 24, padding: '14px 18px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)', fontSize: 12, color: '#9BB0CC', lineHeight: 1.9 }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
        <div style={{ fontWeight: 600, color: 'var(--white)' }}>Regla de bonos vigente</div>
        {esAdmin && !editando && <button onClick={abrir} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'var(--text-muted)',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:12}}>✏️ Editar</button>}
      </div>
      {!editando ? (<>
        <div>• <strong style={{ color: 'var(--gold)' }}>{b.metaCasos} casos</strong> en el mes → Bono base de <strong style={{ color: 'var(--gold)' }}>{FS.fmtMoney(b.montoBase)}</strong></div>
        <div>• <strong style={{ color: 'var(--gold)' }}>{b.metaCasos+1}+ casos</strong> → {FS.fmtMoney(b.montoBase)} + <strong style={{ color: 'var(--gold)' }}>{FS.fmtMoney(b.montoPorCaso)} por cada caso desde el {b.metaCasos+1}°</strong></div>
      </>) : (
        <div style={{display:'grid',gap:10,marginTop:10,maxWidth:360}}>
          <label style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>Casos para el bono<input type="number" min={1} value={f.metaCasos} onChange={e=>setF({...f,metaCasos:e.target.value})} style={iSty}/></label>
          <label style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>Monto base ($)<input type="text" inputMode="numeric" value={f.montoBase} onChange={e=>setF({...f,montoBase:FS.fmtMiles(e.target.value)})} style={iSty}/></label>
          <label style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>Por cada caso extra ($)<input type="text" inputMode="numeric" value={f.montoPorCaso} onChange={e=>setF({...f,montoPorCaso:FS.fmtMiles(e.target.value)})} style={iSty}/></label>
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button className="p-btn-primary" onClick={guardar} disabled={guardando}>{guardando?'Guardando…':'💾 Guardar'}</button>
            <button onClick={()=>setEditando(false)} style={{background:'none',border:'1px solid rgba(255,255,255,0.15)',color:'var(--text-muted)',borderRadius:6,padding:'6px 14px',cursor:'pointer'}}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};
const Captadoras: React.FC = () => {
  const today = new Date();
  const { diaCorte, cargado: corteCargado } = useDiaCorte();
  // "Mes" acá es el mes contable del estudio, que puede no empezar el día 1.
  const mesActual = PER.periodoDeFecha(today, diaCorte)!;
  const [mes, setMes] = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());
  // Al llegar los ajustes se corrige el mes que se muestra: con corte al 20, el
  // 25 de julio ya cuenta para agosto. Una sola vez, para no pisar la
  // navegación del usuario.
  const mesAjustado = useRef(false);
  useEffect(() => {
    if (!corteCargado || mesAjustado.current) return;
    mesAjustado.current = true;
    setMes(mesActual.mes); setAnio(mesActual.anio);
  }, [corteCargado, mesActual.mes, mesActual.anio]);
  const [casos, setCasos] = useState<any[]>([]);
  const [captadoras, setCaptadoras] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hoveredBar, setHoveredBar] = useState<{capId: string; hi: number} | null>(null);
  const [rolActual, setRolActual] = useState<string>('');
  const [ajustes, setAjustes] = useState<any>({ bono: { metaCasos: 11, montoBase: 100000, montoPorCaso: 10000 } });

  // Se carga el equipo COMPLETO, no solo quienes tienen rol Captador: hay casos
  // captados por gente que hoy figura con otro rol —típicamente los que llegaron
  // en la importación, asignados a un admin— y armar el ranking solo con los
  // captadores actuales los borraba del mapa. El caso existe y alguien lo captó;
  // no aparecer en ninguna fila hace que el módulo se lea como si estuviera roto.
  const recargar = () => Promise.all([
    FS.loadReportesData().catch(() => [] as any[]),
    FS.loadUsuarios().catch(() => [] as any[]),
  ]).then(([c, us]) => {
    const conCasos = new Set(c.map((x: any) => x.captadoraId).filter(Boolean));
    setCasos(c);
    setCaptadoras(us.filter((u: any) => esCaptador(u.rol) || conCasos.has(u.id)));
  });

  useEffect(() => {
    recargar().then(() => setLoaded(true));
    FS.loadAjustes().then((a: any) => a?.bono && setAjustes(a)).catch(() => {});
    const uid = auth.currentUser?.uid;
    if (uid) FS.getUserData(uid).then((u: any) => setRolActual(u?.rol || ''));
  }, []);

  const prevMes = () => { if (mes === 0) { setMes(11); setAnio(a => a - 1); } else { setMes(m => m - 1); } };
  const nextMes = () => { if (mes === 11) { setMes(0); setAnio(a => a + 1); } else { setMes(m => m + 1); } };
  const isCurrentMonth = mes === mesActual.mes && anio === mesActual.anio;
  const isFuture = anio * 12 + mes > mesActual.anio * 12 + mesActual.mes;

  const bono = ajustes.bono;
  const calcBonus = (n: number) => n < bono.metaCasos ? 0 : bono.montoBase + Math.max(0, n - bono.metaCasos) * bono.montoPorCaso;

  // Los casos de un mes se cuentan con el mismo corte que usa el resto del
  // sistema: si el estudio cierra el 20, un caso aceptado el 25 de julio suma
  // al bono de agosto, no al de julio.
  const enMesContable = useCallback((fecha: any, m: number, a: number) => {
    const p = PER.periodoDeFecha(fecha, diaCorte);
    return !!p && p.mes === m && p.anio === a;
  }, [diaCorte]);

  const ESTADOS_CONTABLES = ['Aceptado', 'En tramitación', 'Resuelto / Cerrado'];
  // Si el caso tiene mesContabilizado, queda congelado en ese mes para siempre:
  // - no cambia aunque el estado varíe, y nunca se doble-contabiliza en otro mes.
  // Sin mesContabilizado, se usa lógica dinámica con fechaUltimoAceptado (actualizable):
  // - si un caso acepta en M1, revierte en M1 y vuelve a aceptar en M2, la fecha se mueve a M2.
  // - mes actual verifica estado en tiempo real; meses pasados no verifican estado.
  const casosEnMes = useCallback((captadoraId: string, m: number, a: number) => {
    const esMesActual = m === mesActual.mes && a === mesActual.anio;
    const mesKey = `${a}-${m}`;
    return casos.filter(c => {
      if (c.captadoraId !== captadoraId) return false;
      if (c.mesContabilizado) return c.mesContabilizado === mesKey;
      const fechaRef = c.fechaUltimoAceptado ?? c.fechaAceptado;
      if (fechaRef) {
        if (!enMesContable(fechaRef, m, a)) return false;
        return !esMesActual || ESTADOS_CONTABLES.includes(c.estado);
      }
      // Casos legacy sin ninguna fecha de aceptación: usar createdAt + estado actual
      if (!ESTADOS_CONTABLES.includes(c.estado)) return false;
      return enMesContable(c.createdAt, m, a);
    });
  }, [casos, enMesContable, mesActual]);

  // Días del mes contable y cuántos van corridos: con corte al 20 el período
  // no calza con el mes calendario, y la proyección se haría sobre un largo
  // equivocado.
  const rangoMes = PER.rangoPeriodo(anio, mes, diaCorte);
  const diasEnMes = Math.max(1, Math.round((rangoMes.hasta.getTime() - rangoMes.desde.getTime()) / 86400000));
  const diaActual = isCurrentMonth
    ? Math.min(diasEnMes, Math.floor((today.getTime() - rangoMes.desde.getTime()) / 86400000) + 1)
    : diasEnMes;

  const rankings = useMemo(() => captadoras
    .map(cap => {
      const casosDelMes = casosEnMes(cap.id, mes, anio);
      const count = casosDelMes.length;
      const pm = mes === 0 ? 11 : mes - 1;
      const pa = mes === 0 ? anio - 1 : anio;
      const prevCount = casosEnMes(cap.id, pm, pa).length;
      const bonus = calcBonus(count);
      const proyeccion = isCurrentMonth && diaActual > 0 ? Math.round((count / diaActual) * diasEnMes) : null;
      return { ...cap, count, prevCount, bonus, proyeccion, bonusProyectado: proyeccion !== null ? calcBonus(proyeccion) : null };
    })
    .sort((a, b) => b.count - a.count || a.nombre?.localeCompare(b.nombre || '') || 0),
  [captadoras, casos, mes, anio, ajustes]);

  const META = bono.metaCasos;
  const totalBonos = rankings.reduce((s, r) => s + r.bonus, 0);
  const bonusProyectadoTotal = isCurrentMonth ? rankings.reduce((s, r) => s + (r.bonusProyectado || 0), 0) : null;

  // Historial últimos 6 meses para sparklines
  const historial = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(anio, mes - 5 + i, 1);
      const m2 = d.getMonth(), a2 = d.getFullYear();
      return { label: MESES_CORTOS[m2], mes: m2, anio: a2 };
    });
  }, [mes, anio]);

  if (!loaded) return <div className="p-loading">Cargando datos de captadores...</div>;

  return (
    <div>
      <div className="p-section-title" style={{ marginBottom: 20 }}>🏆 Rendimiento de captadores</div>

      {/* Navegador de mes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 20px', border: '1px solid rgba(255,255,255,0.07)' }}>
        <button className="p-btn-secondary" onClick={prevMes} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--white)' }}>{MESES_NOMBRES[mes]} {anio}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {isCurrentMonth ? `Día ${diaActual} de ${diasEnMes} — mes en curso` : 'Mes completo'}
          </div>
        </div>
        <button className="p-btn-secondary" onClick={nextMes} disabled={isFuture} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1, opacity: isFuture ? 0.4 : 1 }}>›</button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 20 }}>
        <div className="fin-card">
          <div className="fin-label">Total bonos del mes</div>
          <div className="fin-val" style={{ fontSize: '1.2rem', color: totalBonos > 0 ? '#BFAF72' : 'var(--cream)' }}>{FS.fmtMoney(totalBonos)}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label">Con bono</div>
          <div className="fin-val" style={{ fontSize: '1.2rem', color: '#4CAF50' }}>{rankings.filter(r => r.count >= META).length} / {rankings.length}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label">Total casos captados</div>
          <div className="fin-val" style={{ fontSize: '1.2rem' }}>{rankings.reduce((s, r) => s + r.count, 0)}</div>
        </div>
        {isCurrentMonth && bonusProyectadoTotal !== null && (
          <div className="fin-card" style={{ borderColor: 'rgba(100,181,246,0.25)' }}>
            <div className="fin-label">Bonos proyectados fin de mes</div>
            <div className="fin-val" style={{ fontSize: '1.2rem', color: '#64B5F6' }}>{FS.fmtMoney(bonusProyectadoTotal)}</div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {rankings.length === 0
        ? <div className="p-empty">No hay captadores registrados.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rankings.map((cap, i) => {
              const pct = Math.min(100, Math.round((cap.count / META) * 100));
              const tieneBonus = cap.count >= META;
              const trend = cap.count - cap.prevCount;
              const faltanMeta = Math.max(0, META - cap.count);
              const extraCasos = Math.max(0, cap.count - META);

              return (
                <div key={cap.id} className={tieneBonus ? 'cap-card-bonus' : 'cap-card'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    {/* Posición */}
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      background: i === 0 ? 'linear-gradient(135deg,#B8965A,#E0C878)' : i === 1 ? 'rgba(160,174,192,0.25)' : i === 2 ? 'rgba(160,120,80,0.25)' : 'rgba(255,255,255,0.07)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: i === 0 ? '#2a1800' : i < 3 ? '#F0F4FF' : '#9BB0CC',
                    }}>{i + 1}</div>

                    {/* Nombre */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cap.nombre || cap.email || cap.id}
                        {/* Está en el ranking porque tiene casos captados, pero
                            su rol es otro. Sin decirlo, ver a un admin acá
                            compitiendo por el bono parece un error del sistema. */}
                        {cap.rol !== 'captadora' && (
                          <span title={`Aparece porque tiene casos captados, pero su rol es ${ROLES_USUARIO.find(r=>r.value===cap.rol)?.label||cap.rol}`}
                            style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                            {ROLES_USUARIO.find(r=>r.value===cap.rol)?.label||cap.rol}
                          </span>
                        )}
                      </div>
                      {cap.email && cap.nombre && <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cap.email}</div>}
                    </div>

                    {/* Tendencia */}
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: trend > 0 ? '#4CAF50' : trend < 0 ? '#e57373' : '#9BB0CC' }}>
                        {trend > 0 ? `▲ +${trend}` : trend < 0 ? `▼ ${trend}` : '─'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>vs mes ant.</div>
                    </div>

                    {/* Casos */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: tieneBonus ? 'var(--gold)' : 'var(--white)' }}>{cap.count}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>casos</div>
                    </div>
                  </div>

                  {/* Barra de progreso */}
                  <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 4, height: 6, marginBottom: 10, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: tieneBonus ? 'linear-gradient(90deg,#B8965A,#E0C878)' : 'linear-gradient(90deg,#4A7FA5,#64B5F6)', transition: 'width 0.5s ease' }} />
                    {/* Marca de la meta */}
                    <div style={{ position: 'absolute', top: 0, left: '100%', width: 2, height: '100%', background: 'rgba(255,255,255,0.3)' }} />
                  </div>

                  {/* Info fila */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {tieneBonus ? (
                      <div style={{ fontSize: 13, color: '#BFAF72', fontWeight: 600 }}>
                        💰 Bono: {FS.fmtMoney(cap.bonus)}
                        {extraCasos > 0 && <span style={{ fontSize: 11, color: '#9BB0CC', fontWeight: 400 }}> ({extraCasos} extra × {FS.fmtMoney(bono.montoPorCaso)})</span>}
                      </div>
                    ) : cap.count === 0 ? (
                      <div style={{ fontSize: 12, color: '#9BB0CC' }}>Sin casos este mes</div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#9BB0CC' }}>
                        🎯 Faltan <strong style={{ color: 'var(--white)' }}>{faltanMeta}</strong> para el bono
                      </div>
                    )}

                    {/* Proyección (solo mes actual) */}
                    {isCurrentMonth && cap.proyeccion !== null && cap.count > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', textAlign: 'right' }}>
                        Proyección: <strong style={{ color: cap.bonusProyectado > 0 ? '#4CAF50' : 'var(--white)' }}>{cap.proyeccion} casos</strong>
                        {cap.bonusProyectado > 0 && <span style={{ color: '#BFAF72' }}> → {FS.fmtMoney(cap.bonusProyectado)}</span>}
                      </div>
                    )}
                  </div>

                  {/* Historial sparkline (barras mini últimos 6 meses) */}
                  {cap.count > 0 && (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', height: 44 }}>
                      {historial.map((h, hi) => {
                        const clientesDelMes = casosEnMes(cap.id, h.mes, h.anio);
                        const n = clientesDelMes.length;
                        const maxN = Math.max(...historial.map(hh => casosEnMes(cap.id, hh.mes, hh.anio).length), 1);
                        const barH = n === 0 ? 3 : Math.max(16, Math.round((n / maxN) * 28) + 4);
                        const isSelected = h.mes === mes && h.anio === anio;
                        const barBg = isSelected ? '#64B5F6' : n >= META ? 'rgba(184,150,90,0.5)' : 'rgba(255,255,255,0.1)';
                        const numColor = isSelected ? '#0B1732' : n >= META ? '#2a1500' : '#F0F4FF';
                        const isHovered = hoveredBar?.capId === cap.id && hoveredBar?.hi === hi;
                        return (
                          <div key={hi} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}>
                            <div
                              style={{ width: '100%', height: barH, background: barBg, borderRadius: 2, transition: 'height 0.3s', position: 'relative', cursor: n > 0 ? 'pointer' : 'default' }}
                              onMouseEnter={() => n > 0 && setHoveredBar({ capId: cap.id, hi })}
                              onMouseLeave={() => setHoveredBar(null)}
                            >
                              {n > 0 && (
                                <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, textAlign: 'center', fontSize: 8, fontWeight: 700, color: numColor, lineHeight: 1 }}>{n}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 8, color: isSelected ? '#64B5F6' : '#6a82a0' }}>{h.label}</div>

                            {/* Tooltip con clientes del mes */}
                            {isHovered && n > 0 && (
                              <div style={{
                                position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                                background: '#0f1929', border: '1px solid rgba(100,181,246,0.3)', borderRadius: 8,
                                padding: '8px 12px', zIndex: 200, minWidth: 210, maxWidth: 300,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.5)', pointerEvents: 'none',
                              }}>
                                <div style={{ fontSize: 10, color: '#64B5F6', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  {h.label} {h.anio} · {n} caso{n !== 1 ? 's' : ''}
                                </div>
                                {[...clientesDelMes].sort((a: any, b: any) => {
                                  const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                                  const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                                  return da - db2;
                                }).map((caso: any, ci: number) => {
                                  const llegada = caso.createdAt?.toDate ? caso.createdAt.toDate() : new Date(caso.createdAt);
                                  const fechaLlegada = !isNaN(llegada.getTime())
                                    ? llegada.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                    : '—';
                                  return (
                                    <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0', borderTop: ci > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                                      <span style={{ fontSize: 12, color: 'var(--white)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {caso.nombreCliente || '—'}
                                      </span>
                                      <span style={{ fontSize: 10, color: '#6a82a0', flexShrink: 0 }}>
                                        {fechaLlegada}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
      }

      {/* Regla de bonos (editable por el admin) */}
      <BonoRegla ajustes={ajustes} esAdmin={(rolActual||'').toLowerCase()==='admin'} onSaved={setAjustes} />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ASISTENCIA — Control de asistencia de captadores
   ══════════════════════════════════════════════════════════════════════════ */
const Asistencia: React.FC<{ rol: string; userId: string; userNombre: string }> = ({ rol, userId, userNombre }) => {
  const today = new Date();
  const [mes, setMes] = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());
  const [captadoras, setCaptadoras] = useState<any[]>([]);
  // Map<key, valor>  donde valor = 1 (día completo) | 0.5 (medio día)
  const [marcados, setMarcados] = useState<Map<string, number>>(new Map());
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  const puedeEditar = ['admin', 'abogada'].includes(rol);
  const diasEnMes = new Date(anio, mes + 1, 0).getDate();
  const dias = Array.from({ length: diasEnMes }, (_, i) => i + 1);
  const DIAS_SEMANA = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

  const mkKey = (capId: string, dia: number) =>
    `${capId}_${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

  const fetchData = useCallback(async () => {
    setLoaded(false);
    const [caps, asist] = await Promise.all([
      FS.loadCaptadoras().catch(() => [] as any[]),
      FS.loadAsistenciaMes(mes, anio).catch(() => [] as any[]),
    ]);
    setCaptadoras(
      caps
        .filter((u: any) => esCaptador(u.rol))
        .sort((a: any, b: any) => (a.nombre || '').localeCompare(b.nombre || ''))
    );
    const map = new Map<string, number>();
    asist.forEach((a: any) => map.set(a.id, a.valor ?? 1));
    setMarcados(map);
    setLoaded(true);
  }, [mes, anio]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMes = () => { if (mes === 0) { setMes(11); setAnio(a => a - 1); } else setMes(m => m - 1); };
  const nextMes = () => { if (mes === 11) { setMes(0); setAnio(a => a + 1); } else setMes(m => m + 1); };
  const isFuture = anio > today.getFullYear() || (anio === today.getFullYear() && mes > today.getMonth());

  // Ciclo: ausente (0) → día completo (1) → medio día (0.5) → ausente (0)
  const toggle = async (cap: any, dia: number) => {
    if (!puedeEditar) return;
    const fecha = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const k = mkKey(cap.id, dia);
    if (savingKeys.has(k)) return;

    const valorActual = marcados.get(k) ?? 0;
    const nuevoValor = valorActual === 0 ? 1 : valorActual === 1 ? 0.5 : 0;

    setSavingKeys(s => { const n = new Set(s); n.add(k); return n; });
    setMarcados(s => { const n = new Map(s); nuevoValor === 0 ? n.delete(k) : n.set(k, nuevoValor); return n; });

    try {
      if (nuevoValor === 0) {
        await FS.desmarcarAsistencia(cap.id, fecha);
      } else {
        await FS.marcarAsistencia(cap.id, cap.nombre || cap.email, fecha, userId, userNombre, nuevoValor);
      }
    } catch {
      setMarcados(s => { const n = new Map(s); valorActual === 0 ? n.delete(k) : n.set(k, valorActual); return n; });
    } finally {
      setSavingKeys(s => { const n = new Set(s); n.delete(k); return n; });
    }
  };

  const totalDias = (capId: string) => {
    let total = 0;
    dias.forEach(d => { total += marcados.get(mkKey(capId, d)) ?? 0; });
    return total;
  };

  if (!loaded) return <div className="p-loading">Cargando asistencia...</div>;

  return (
    <div>
      <div className="p-section-title" style={{ marginBottom: 20 }}>📅 Asistencia de captadores</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 20px', border: '1px solid rgba(255,255,255,0.07)' }}>
        <button className="p-btn-secondary" onClick={prevMes} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--white)' }}>{MESES_NOMBRES[mes]} {anio}</div>
        </div>
        <button className="p-btn-secondary" onClick={nextMes} disabled={isFuture} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1, opacity: isFuture ? 0.4 : 1 }}>›</button>
      </div>

      {puedeEditar && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 11, color: 'var(--text-muted)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(76,175,80,0.25)', border: '1.5px solid rgba(76,175,80,0.6)', display: 'inline-block' }} />
            Día completo
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(245,158,11,0.25)', border: '1.5px solid rgba(245,158,11,0.6)', display: 'inline-block' }} />
            Medio día (0.5)
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>· Clic para cambiar estado</span>
        </div>
      )}

      {captadoras.length === 0 ? (
        <div className="p-empty">No hay captadores registrados.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 150, position: 'sticky', left: 0, zIndex: 2, background: '#0f1929' }}>
                  Captadora
                </th>
                {dias.map(d => {
                  const diaSemana = new Date(anio, mes, d).getDay();
                  const esHoy = d === today.getDate() && mes === today.getMonth() && anio === today.getFullYear();
                  const esFinde = diaSemana === 0 || diaSemana === 6;
                  return (
                    <th key={d} style={{ textAlign: 'center', padding: '4px 1px', color: esHoy ? '#64B5F6' : esFinde ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)', fontWeight: esHoy ? 700 : 400, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 28 }}>
                      <div style={{ fontSize: 9, marginBottom: 1 }}>{DIAS_SEMANA[diaSemana]}</div>
                      <div style={{ fontSize: 11 }}>{d}</div>
                    </th>
                  );
                })}
                <th style={{ textAlign: 'center', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 52 }}>
                  Días
                </th>
              </tr>
            </thead>
            <tbody>
              {captadoras.map((cap, ci) => {
                const total = totalDias(cap.id);
                const bgSticky = ci % 2 === 0 ? '#0c1829' : '#0f1929';
                return (
                  <tr key={cap.id} style={{ background: ci % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ padding: '6px 12px', color: 'var(--white)', borderBottom: '1px solid rgba(255,255,255,0.04)', position: 'sticky', left: 0, zIndex: 1, background: bgSticky, fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13 }}>
                      {cap.nombre || cap.email}
                    </td>
                    {dias.map(d => {
                      const diaSemana = new Date(anio, mes, d).getDay();
                      const esFinde = diaSemana === 0 || diaSemana === 6;
                      const esHoy = d === today.getDate() && mes === today.getMonth() && anio === today.getFullYear();
                      const k = mkKey(cap.id, d);
                      const valor = marcados.get(k) ?? 0;
                      const esCompleto = valor === 1;
                      const esMedio = valor === 0.5;
                      const saving = savingKeys.has(k);
                      const title = puedeEditar
                        ? esCompleto ? '1 clic → medio día | 2 clics → quitar'
                        : esMedio ? '1 clic → quitar'
                        : '1 clic → día completo'
                        : undefined;
                      return (
                        <td key={d} style={{ textAlign: 'center', padding: '3px 1px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: esFinde ? 'rgba(255,255,255,0.015)' : undefined }}>
                          <button
                            onClick={() => toggle(cap, d)}
                            disabled={!puedeEditar || saving}
                            title={title}
                            style={{
                              width: 22, height: 22, borderRadius: 4, margin: 'auto',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: esCompleto
                                ? '1.5px solid rgba(76,175,80,0.6)'
                                : esMedio
                                  ? '1.5px solid rgba(245,158,11,0.6)'
                                  : esHoy ? '1.5px solid rgba(100,181,246,0.35)'
                                  : '1.5px solid rgba(255,255,255,0.08)',
                              background: esCompleto
                                ? 'rgba(76,175,80,0.2)'
                                : esMedio ? 'rgba(245,158,11,0.2)' : 'transparent',
                              cursor: puedeEditar ? 'pointer' : 'default',
                              fontSize: esMedio ? 9 : 11,
                              fontWeight: 700,
                              color: esCompleto ? '#4CAF50' : esMedio ? '#F59E0B' : 'transparent',
                              opacity: saving ? 0.5 : 1,
                              transition: 'all 0.12s',
                            }}
                          >
                            {saving ? '·' : esCompleto ? '✓' : esMedio ? '½' : ''}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'center', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', fontWeight: 700, color: total > 0 ? '#BFAF72' : 'var(--text-muted)', fontSize: 14 }}>
                      {total > 0 ? total : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   PLAZOS LEGALES — lo primero que se ve en Notificaciones
   ══════════════════════════════════════════════════════════════════════════
   Va separado de los avisos del equipo a propósito: esto no lo escribió
   nadie, lo calcula la ley a partir de la fecha de despido y de las audiencias
   del caso. No se marca como realizado ni se borra — desaparece solo cuando el
   plazo lleva más de un mes vencido o cuando el caso se cierra.
   El cómputo (días hábiles de lunes a sábado, feriados, meses corridos) está
   en utils/plazos.ts, con sus pruebas. */
const PlazosLegalesView: React.FC<{rol:string;uid:string;nombre:string;onAbrirCaso:(casoId:string)=>void}> = ({rol,uid,nombre,onAbrirCaso}) => {
  const [casos,setCasos]=useState<any[]|null>(null);
  const [expanded,setExpanded]=useState(true);

  const cargar=useCallback(()=>{ FS.loadCasos(rol,uid).then(setCasos).catch(()=>setCasos([])); },[rol,uid]);
  useEffect(()=>{ cargar(); },[cargar]);
  const {marcar,hecho}=useMarcarPlazo(nombre,cargar);

  const plazos=useMemo(()=>casos?PLZ.plazosDeCasos(casos):[],[casos]);
  const pendientes=plazos.filter(p=>!hecho(p)).length;
  const urgentes=plazos.filter(p=>!hecho(p)&&p.dias<=5).length;

  // Mientras cargan los casos no se muestra nada: un "(0)" momentáneo en un
  // bloque de plazos se lee como "no tengo plazos", que es lo contrario.
  if(!casos) return null;

  return(
    <div style={{marginBottom:18}}>
      <div className="notif-view-header" onClick={()=>setExpanded(e=>!e)}
        style={{background:'rgba(184,150,90,0.10)',borderColor:'rgba(184,150,90,0.35)',color:'#E5D5AE'}}>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:11}}>{expanded?'▲':'▼'}</span>
          ⚖️ Plazos legales ({plazos.length})
        </span>
        {urgentes>0
          ? <span className="notif-badge" style={{position:'static',fontSize:'0.65rem',padding:'2px 7px'}}>{urgentes} por vencer</span>
          : pendientes>0&&<span style={{fontSize:'0.65rem',color:'#9BB0CC'}}>{pendientes} pendiente{pendientes!==1?'s':''}</span>}
      </div>

      {expanded&&(
        plazos.length===0?(
          <div className="p-empty" style={{padding:'22px 0',textAlign:'center'}}>
            Ningún plazo legal por vencer.<br/>
            <span style={{fontSize:11,opacity:0.6}}>Se calculan con la fecha de despido, la parte que representamos y la audiencia preparatoria de cada caso.</span>
          </div>
        ):(
          <div style={{overflowX:'auto',marginTop:2}}>
            <table className="p-table" style={{minWidth:820}}>
              <thead>
                <tr>
                  <th>Caso</th>
                  <th>Plazo</th>
                  <th>Norma</th>
                  <th>Se cuenta desde</th>
                  <th>Vence</th>
                  <th style={{textAlign:'center'}}>Restan</th>
                  <th style={{textAlign:'center'}}>Realizado</th>
                </tr>
              </thead>
              <tbody>
                {plazos.map(p=>{
                  const done=hecho(p);
                  const nivel=done?'realizado':PLZ.urgencia(p);
                  const color=COLOR_URGENCIA[nivel];
                  return(
                    <tr key={p.id} style={{opacity:done?0.45:1,transition:'opacity 0.3s',background:nivel==='vencido'?'rgba(229,57,53,0.07)':undefined}}>
                      <td style={{whiteSpace:'nowrap'}}>
                        <button onClick={()=>onAbrirCaso(p.casoId)}
                          style={{background:'none',border:'none',cursor:'pointer',color:'#BFAF72',fontSize:12,padding:0,textDecoration:'underline',textAlign:'left'}}>
                          {p.cliente}
                        </button>
                        <div style={{fontSize:10.5,color:'var(--text-muted)'}}>{p.codigoCaso} · {p.materia}</div>
                      </td>
                      <td style={{fontSize:12.5,color:'var(--white)',lineHeight:1.4,textDecoration:done?'line-through':'none'}}>
                        {p.titulo}
                        <div style={{fontSize:10.5,color:'var(--text-muted)',marginTop:2,textDecoration:'none'}}>{p.clase}</div>
                      </td>
                      <td style={{fontSize:11.5,color:'#9BB0CC',whiteSpace:'nowrap'}}>{p.norma}</td>
                      {/* El cómputo va a la vista para que el abogado pueda
                          revisar de dónde sale la fecha, no confiar a ciegas. */}
                      <td style={{fontSize:11.5,color:'#9BB0CC',lineHeight:1.4}}>
                        {PLZ.fmtDia(p.desde)}
                        <div style={{fontSize:10.5,opacity:0.75}}>{p.computo}</div>
                      </td>
                      <td style={{whiteSpace:'nowrap',fontWeight:600,fontSize:12.5,color}}>{PLZ.fmtDia(p.vence)}</td>
                      <td style={{textAlign:'center',whiteSpace:'nowrap',fontSize:12,color,fontWeight:nivel==='holgado'||done?400:600}}>
                        {done?textoCumplido(p):textoDias(p.dias)}
                      </td>
                      <td style={{textAlign:'center'}}
                        title={p.judicializado?tituloJudicializado(p.judicializado):p.realizado?`Marcado el ${FS.fmtDate(p.realizado.fecha)}${p.realizado.por?' por '+p.realizado.por:''}`:'Marcar como presentado'}>
                        <input type="checkbox" checked={done} disabled={!!p.judicializado} onChange={e=>marcar(p,e.target.checked)} style={{cursor:p.judicializado?'not-allowed':'pointer',width:16,height:16}}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8,lineHeight:1.5}}>
              Días hábiles de lunes a sábado; no cuentan domingos ni feriados legales. El día del hecho no se cuenta.
              Los plazos de meses son corridos y, si caen en día inhábil, se muestran en el hábil anterior.
              Un plazo desaparece de acá cuando lleva más de 30 días vencido o cuando el caso se cierra.
            </div>
          </div>
        )
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   TAREAS QUE DELEGASTE — seguimiento de quien asignó una acción del checklist
   ══════════════════════════════════════════════════════════════════════════
   El aviso de una @mención le llega a la persona mencionada; quien la pidió
   no tenía dónde ver si se hizo. Acá ve cada acción que asignó, a quién, si
   esa persona ya abrió el aviso, y la puede cerrar cuando está lista. Marcarla
   es lo mismo que tacharla en el caso: se refleja en el checklist, el
   calendario y la bandeja del destinatario. */
const DelegadasView: React.FC<{onAbrirCaso:(casoId:string)=>void}> = ({onAbrirCaso}) => {
  const [tareas,setTareas]=useState<any[]|null>(null);
  const [expanded,setExpanded]=useState(true);
  const [verRealizadas,setVerRealizadas]=useState(false);
  const [persona,setPersona]=useState('');
  const [working,setWorking]=useState<Set<string>>(new Set());

  const cargar=useCallback(()=>{ FS.loadDelegadas().then(setTareas).catch(()=>setTareas([])); },[]);
  useEffect(()=>{ cargar(); },[cargar]);

  const marcar=async(t:any,done:boolean)=>{
    setTareas(ts=>ts?ts.map(x=>x.clave===t.clave?{...x,realizado:done,realizadoEn:done?new Date().toISOString():null}:x):ts); // feedback inmediato
    setWorking(w=>{const s=new Set(w);s.add(t.clave);return s;});
    try{ await FS.actualizarRealizadaPorTexto(t.casoId,t.texto,done,t.tareaId||undefined); }
    finally{ setWorking(w=>{const s=new Set(w);s.delete(t.clave);return s;}); cargar(); }
  };

  if(!tareas) return null;
  const MS_DAY=1000*60*60*24;
  const diasPlazo=(plazo:string|null)=>{ const d=plazo?parsePlazoDate(plazo):null; return d?Math.ceil((d.getTime()-Date.now())/MS_DAY):null; };
  // Las personas a quienes se delegó, para filtrar ("las que le di a Paula").
  const porPersona=new Map<string,string>();
  for(const t of tareas) for(const p of t.para) porPersona.set(p.id,p.nombre);
  const personas=Array.from(porPersona.entries()).sort((a,b)=>a[1].localeCompare(b[1],'es'));
  const deLaPersona=persona?tareas.filter(t=>t.para.some((p:any)=>p.id===persona)):tareas;
  const pendientes=deLaPersona.filter(t=>!t.realizado);
  // Lo realizado se sigue viendo 7 días, para alcanzar a ver que se hizo;
  // después pasa a "realizadas anteriores". Las marcadas antes del
  // 10-oct-2026 no tienen la fecha guardada: para ellas se usa la más
  // reciente entre el plazo y la asignación, que es cuando se esperaba que
  // se hiciera. Sin esto, una tarea hecha esta semana quedaba escondida.
  const SIETE_DIAS=7*MS_DAY;
  const cuandoSeHizo=(t:any):number=>{
    if(t.realizadoEn) return new Date(t.realizadoEn).getTime();
    const plazo=t.plazo?parsePlazoDate(t.plazo)?.getTime()??0:0;
    return Math.max(plazo,new Date(t.creado).getTime());
  };
  const recientes=deLaPersona.filter(t=>t.realizado&&Date.now()-cuandoSeHizo(t)<SIETE_DIAS)
    .sort((a,b)=>cuandoSeHizo(b)-cuandoSeHizo(a));
  const anteriores=deLaPersona.filter(t=>t.realizado&&!recientes.includes(t));
  const realizadas=anteriores.length;
  // Pendientes primero, las de plazo más cercano arriba; las sin plazo, de la
  // más antigua a la más nueva: la que lleva más tiempo esperando es la que
  // hay que perseguir.
  const orden=(a:any,b:any)=>{
    const da=diasPlazo(a.plazo),db=diasPlazo(b.plazo);
    if((da===null)!==(db===null)) return da===null?1:-1;
    if(da!==null&&db!==null&&da!==db) return da-db;
    return new Date(a.creado).getTime()-new Date(b.creado).getTime();
  };
  // Con una persona elegida, primero lo que se le pidió a ella sola; lo de
  // @todos (que también le llegó) va después.
  const esGeneral=(t:any)=>/@todos\b/i.test(t.texto||'')||t.para.length>2;
  const propiasPrimero=(xs:any[])=>persona?[...xs.filter(t=>!esGeneral(t)),...xs.filter(esGeneral)]:xs;
  const visibles=[...propiasPrimero(pendientes.sort(orden)),...propiasPrimero(recientes),...(verRealizadas?propiasPrimero(anteriores):[])];
  const atrasadas=pendientes.filter(t=>{const d=diasPlazo(t.plazo);return d!==null&&d<0;}).length;

  return(
    <div style={{marginTop:22}}>
      <div className="notif-view-header" onClick={()=>setExpanded(e=>!e)}>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:11}}>{expanded?'▲':'▼'}</span>
          📤 Tareas que delegaste ({tareas.length})
        </span>
        {atrasadas>0
          ? <span className="notif-badge" style={{position:'static',fontSize:'0.65rem',padding:'2px 7px'}}>{atrasadas} atrasada{atrasadas!==1?'s':''}</span>
          : pendientes.length>0&&<span style={{fontSize:'0.65rem',color:'#9BB0CC'}}>{pendientes.length} pendiente{pendientes.length!==1?'s':''}</span>}
      </div>

      {expanded&&(
        tareas.length===0?(
          <div className="p-empty" style={{padding:'28px 0',textAlign:'center'}}>No has delegado tareas.<br/><span style={{fontSize:11,opacity:0.6}}>Cuando asignes una acción del checklist a alguien con @, la verás acá para hacerle seguimiento.</span></div>
        ):(
          <div style={{overflowX:'auto',marginTop:2}}>
            {personas.length>1&&(
              <div style={{display:'flex',alignItems:'center',gap:8,margin:'8px 0 10px',fontSize:12,color:'#9BB0CC',flexWrap:'wrap'}}>
                Para:
                <select className="p-select" value={persona} onChange={e=>setPersona(e.target.value)} style={{fontSize:12.5,maxWidth:260,padding:'4px 8px'}}>
                  <option value="">Todas las personas</option>
                  {personas.map(([id,nombre])=><option key={id} value={id}>{nombre}</option>)}
                </select>
                <span>{pendientes.length} pendiente{pendientes.length!==1?'s':''} · {recientes.length+anteriores.length} realizada{recientes.length+anteriores.length!==1?'s':''}</span>
              </div>
            )}
            {visibles.length===0?(
              <div className="p-empty" style={{padding:'22px 0',textAlign:'center'}}>{persona?'No hay tareas pendientes ni realizadas esta semana para esta persona.':'Todas las tareas que delegaste están realizadas.'}</div>
            ):(
            <table className="p-table" style={{minWidth:640}}>
              <thead>
                <tr>
                  <th>Para</th>
                  <th>Caso</th>
                  <th>Tarea</th>
                  <th>Asignada</th>
                  <th>Plazo</th>
                  <th style={{textAlign:'center'}}>Realizado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(t=>{
                  const dias=t.realizado?null:diasPlazo(t.plazo);
                  const color=dias===null?'#BFAF72':dias<0||dias===0?'#EF5350':dias<=3?'#FF7043':'#BFAF72';
                  // Las realizadas de esta semana se leen bien (son las que se
                  // están siguiendo); las anteriores, más apagadas.
                  const opacidad=!t.realizado?1:recientes.includes(t)?0.8:0.45;
                  return(
                    <tr key={t.clave} style={{opacity:opacidad,transition:'opacity 0.3s'}}>
                      <td style={{fontSize:12.5,lineHeight:1.5}}>
                        {esGeneral(t)?(
                          // @todos: la lista entera de nombres tapaba todo. Se
                          // resume, y los nombres quedan al pasar el cursor.
                          <div style={{whiteSpace:'nowrap',color:'#D6E4F4'}}
                            title={t.para.map((p:any)=>`${p.nombre}: ${p.leida?'visto':'sin ver'}`).join('\n')}>
                            Todo el estudio <span style={{fontSize:10.5,color:'#6a82a0'}}>· {t.para.filter((p:any)=>p.leida).length} de {t.para.length} lo vio</span>
                          </div>
                        ):t.para.map((p:any)=>(
                          <div key={p.id} style={{whiteSpace:'nowrap',color:'#D6E4F4'}}
                            title={p.leida?'Ya abrió el aviso':'Todavía no abre el aviso'}>
                            {p.nombre} <span style={{fontSize:10.5,color:p.leida?'#66BB6A':'#6a82a0'}}>{p.leida?'✓ visto':'· sin ver'}</span>
                          </div>
                        ))}
                      </td>
                      <td>
                        <button onClick={()=>onAbrirCaso(t.casoId)} style={{background:'none',border:'none',cursor:'pointer',color:'#BFAF72',fontSize:12,padding:0,textDecoration:'underline',textAlign:'left'}}>
                          {t.casoNombre||'—'}
                        </button>
                        <div style={{fontSize:10,color:'#9BB0CC',marginTop:1}}>{t.codigoCaso}</div>
                      </td>
                      <td style={{maxWidth:260,wordBreak:'break-word',fontSize:12,lineHeight:1.5,textDecoration:t.realizado?'line-through':'none'}}>
                        {renderMenciones(t.texto)}
                      </td>
                      <td style={{whiteSpace:'nowrap',fontSize:11.5,color:'#9BB0CC'}}>{FS.fmtDate(t.creado)}</td>
                      <td style={{whiteSpace:'nowrap',fontSize:12}}>
                        {t.realizado?(
                          <span style={{color:'#66BB6A',fontSize:12}}>✓ Realizada{t.realizadoEn?` el ${FS.fmtDate(t.realizadoEn)}`:''}</span>
                        ):t.plazo?(
                          <span style={{color,fontWeight:dias!==null&&dias<=3?700:400}}>
                            {t.plazo}{dias!==null&&<span style={{fontSize:10.5,marginLeft:5}}>{dias<0?'vencido':dias===0?'hoy':`${dias} día${dias!==1?'s':''}`}</span>}
                          </span>
                        ):<span style={{color:'#6a82a0',fontSize:11}}>—</span>}
                      </td>
                      <td style={{textAlign:'center'}}>
                        <input type="checkbox" checked={!!t.realizado}
                          disabled={!t.puedeCerrar||working.has(t.clave)}
                          onChange={e=>marcar(t,e.target.checked)}
                          style={{accentColor:'var(--gold)',width:15,height:15,cursor:t.puedeCerrar?'pointer':'not-allowed'}}
                          title={!t.puedeCerrar?'Ya no puedes editar este caso':t.realizado?'Marcar como pendiente':'Cerrar: marcar como realizada'}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
            {realizadas>0&&(
              <button onClick={()=>setVerRealizadas(v=>!v)}
                style={{background:'none',border:'none',cursor:'pointer',color:'#9BB0CC',fontSize:12,padding:'8px 0',textDecoration:'underline'}}>
                {verRealizadas?'Ocultar las realizadas anteriores':`Ver las ${realizadas} realizada${realizadas!==1?'s':''} anteriores (más de 7 días)`}
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   NOTIFICACIONES VIEW
   ══════════════════════════════════════════════════════════════════════════ */
const NotificacionesView: React.FC<{notifs:any[];uid:string;rol:string;nombre:string;onAbrirCaso:(casoId:string)=>void}> = ({notifs,uid,rol,nombre,onAbrirCaso}) => {
  const [expanded,setExpanded]=useState(true);
  const [working,setWorking]=useState<Set<string>>(new Set());
  const [confirmEliminar,setConfirmEliminar]=useState<string|null>(null);
  // Override local para feedback inmediato del checkbox (antes del próximo sondeo).
  const [realizadoLocal,setRealizadoLocal]=useState<Record<string,boolean>>({});
  const puedeEliminar=['admin','abogada','abogado_asociado','captadora','captadora_general','procuradora','abogado_externo'].includes(rol);

  const MS_DAY=1000*60*60*24;
  const now=Date.now();

  const getDaysLeft=(n:any):number=>{
    const d=n.createdAt?.toDate?n.createdAt.toDate():new Date(n.createdAt||0);
    return Math.max(0,20-Math.floor((now-d.getTime())/MS_DAY));
  };

  const isPlazoUrgente=(plazo:string):boolean=>{
    const d=parsePlazoDate(plazo);
    if(!d) return false;
    const diff=d.getTime()-Date.now();
    return diff>=-MS_DAY&&diff<=3*MS_DAY; // dentro de 3 días (incluye hoy/vencido)
  };

  const isPlazoVencido=(plazo:string):boolean=>{
    const d=parsePlazoDate(plazo);
    return !!d&&d.getTime()<Date.now()-MS_DAY;
  };

  const sorted=[...notifs].sort((a,b)=>{
    if(!!a.realizado!==!!b.realizado) return a.realizado?1:-1;
    const aU=a.plazo&&isPlazoUrgente(a.plazo);
    const bU=b.plazo&&isPlazoUrgente(b.plazo);
    if(aU!==bU) return aU?-1:1;
    if(a.plazo&&b.plazo){
      const dA=parsePlazoDate(a.plazo),dB=parsePlazoDate(b.plazo);
      if(dA&&dB) return dA.getTime()-dB.getTime();
    }
    return getDaysLeft(a)-getDaysLeft(b);
  });

  const diasHastaPlazo=(plazo:string):number=>{
    const d=parsePlazoDate(plazo);
    if(!d) return 0;
    return Math.ceil((d.getTime()-Date.now())/MS_DAY);
  };

  const handleRealizado=async(n:any,done:boolean)=>{
    setRealizadoLocal(m=>({...m,[n.id]:done})); // feedback inmediato
    setWorking(w=>{const s=new Set(w);s.add(n.id);return s;});
    try{ await FS.marcarRealizada(n.id,n.casoId,n.mensaje,done,n.tareaId); }
    catch{ setRealizadoLocal(m=>({...m,[n.id]:!done})); } // revertir si falla
    finally{ setWorking(w=>{const s=new Set(w);s.delete(n.id);return s;}); }
  };
  // Estado efectivo del "realizado": override local si existe, si no el del backend.
  const esRealizado=(n:any):boolean=> n.id in realizadoLocal ? realizadoLocal[n.id] : !!n.realizado;

  const handleEliminar=async(notifId:string)=>{
    setWorking(w=>{const s=new Set(w);s.add(notifId);return s;});
    try{ await FS.eliminarNotificacion(notifId); }
    finally{ setWorking(w=>{const s=new Set(w);s.delete(notifId);return s;}); }
  };

  const alertas=notifs.filter(n=>{
    if(n.realizado) return false;
    if(getDaysLeft(n)<=3) return true;
    if(n.plazo&&isPlazoUrgente(n.plazo)) return true;
    return false;
  });
  const pendientes=notifs.filter(n=>!n.realizado).length;

  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <div className="p-section-title" style={{marginBottom:0}}>Notificaciones</div>
        <span style={{fontSize:12,color:'#9BB0CC'}}>{notifs.length} notificación{notifs.length!==1?'es':''} · últimos 60 días</span>
      </div>

      {/* Lo primero de la pantalla, y aparte de los avisos del equipo: estos
          plazos los pone la ley, no un compañero, y perderlos no se arregla. */}
      <PlazosLegalesView rol={rol} uid={uid} nombre={nombre} onAbrirCaso={onAbrirCaso}/>

      {alertas.length>0&&(
        <div style={{background:'rgba(229,57,53,0.08)',border:'1px solid rgba(229,57,53,0.3)',borderRadius:8,padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:18}}>⚠️</span>
          <span style={{fontSize:13,color:'#EF9A9A',lineHeight:1.5}}>
            Tienes <strong style={{color:'#EF5350'}}>{alertas.length} tarea{alertas.length>1?'s':''}</strong> sin completar que vence{alertas.length>1?'n':''} en los próximos 3 días
          </span>
        </div>
      )}

      <div className="notif-view-header" onClick={()=>setExpanded(e=>!e)}>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:11}}>{expanded?'▲':'▼'}</span>
          Todas las notificaciones ({notifs.length})
        </span>
        {pendientes>0&&<span className="notif-badge" style={{position:'static',fontSize:'0.65rem',padding:'2px 7px'}}>{pendientes} pendiente{pendientes!==1?'s':''}</span>}
      </div>

      {expanded&&(
        notifs.length===0?(
          <div className="p-empty" style={{padding:'32px 0',textAlign:'center'}}>Sin notificaciones en los últimos 60 días.<br/><span style={{fontSize:11,opacity:0.6}}>Las menciones con @ en el checklist de Proceso aparecerán aquí.</span></div>
        ):(
          <div style={{overflowX:'auto',marginTop:2}}>
            <table className="p-table" style={{minWidth:560}}>
              <thead>
                <tr>
                  <th>De</th>
                  <th>Caso</th>
                  <th>Tarea asignada</th>
                  <th>Plazo</th>
                  <th style={{textAlign:'center'}}>Días restantes</th>
                  <th style={{textAlign:'center'}}>Realizado</th>
                  {puedeEliminar&&<th></th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(n=>{
                  const esMio=n.de===uid;
                  const plazoUrgente=n.plazo&&isPlazoUrgente(n.plazo)&&!n.realizado;
                  const plazoVencido=n.plazo&&isPlazoVencido(n.plazo)&&!n.realizado;
                  const dias=n.plazo?diasHastaPlazo(n.plazo):null;
                  return(
                    <tr key={n.id} style={{opacity:esRealizado(n)?0.45:1,transition:'opacity 0.3s'}}>
                      <td style={{whiteSpace:'nowrap'}}>
                        <span style={{fontWeight:600,color:esMio?'#BFAF72':'#D6E4F4',fontSize:13}}>
                          {esMio?'👤 Yo':n.deNombre}
                        </span>
                      </td>
                      <td>
                        <button onClick={()=>onAbrirCaso(n.casoId)} style={{background:'none',border:'none',cursor:'pointer',color:'#BFAF72',fontSize:12,padding:0,textDecoration:'underline',textAlign:'left'}}>
                          {n.casoNombre}
                        </button>
                        <div style={{fontSize:10,color:'#9BB0CC',marginTop:1}}>{n.codigoCaso}</div>
                      </td>
                      <td style={{maxWidth:240,wordBreak:'break-word',fontSize:12,lineHeight:1.5}}>
                        {renderMenciones(n.mensaje)}
                      </td>
                      <td style={{whiteSpace:'nowrap',minWidth:110}}>
                        {n.plazo?(
                          <span style={{color:plazoVencido?'#EF5350':plazoUrgente?'#FF7043':'#BFAF72',fontWeight:plazoUrgente||plazoVencido?700:400,fontSize:12}}>
                            {plazoVencido?'🔴 ':plazoUrgente?'⚠️ ':''}{n.plazo}
                          </span>
                        ):<span style={{color:'#6a82a0',fontSize:11}}>—</span>}
                      </td>
                      <td style={{textAlign:'center',whiteSpace:'nowrap'}}>
                        {dias!==null?(
                          dias<0
                            ?<span style={{color:'#EF5350',fontWeight:700,fontSize:12}}>Vencido</span>
                            :dias===0
                              ?<span style={{color:'#EF5350',fontWeight:700,fontSize:12}}>Hoy</span>
                              :<span style={{color:dias<=3?'#EF5350':dias<=7?'#FFA726':'#66BB6A',fontWeight:dias<=3?700:400,fontSize:12}}>{dias} día{dias!==1?'s':''}</span>
                        ):<span style={{color:'#6a82a0',fontSize:11}}>—</span>}
                      </td>
                      <td style={{textAlign:'center'}}>
                        <input
                          type="checkbox"
                          checked={esRealizado(n)}
                          disabled={working.has(n.id)}
                          onChange={e=>handleRealizado(n,e.target.checked)}
                          style={{accentColor:'var(--gold)',width:15,height:15,cursor:'pointer'}}
                          title={esRealizado(n)?'Marcar como pendiente':'Marcar como realizado'}
                        />
                      </td>
                      {puedeEliminar&&(
                        <td style={{textAlign:'center'}}>
                          <button
                            onClick={()=>setConfirmEliminar(n.id)}
                            disabled={working.has(n.id)}
                            style={{background:'none',border:'none',cursor:'pointer',color:'rgba(239,83,80,0.5)',fontSize:13,padding:'0 4px',lineHeight:1,transition:'color 0.2s'}}
                            onMouseEnter={e=>(e.currentTarget.style.color='#EF5350')}
                            onMouseLeave={e=>(e.currentTarget.style.color='rgba(239,83,80,0.5)')}
                            title="Eliminar notificación"
                          >✕</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      <DelegadasView onAbrirCaso={onAbrirCaso}/>

      {confirmEliminar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmEliminar(null)}>
          <div style={{background:'#141d2e',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'24px 28px',maxWidth:380,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:28,marginBottom:8}}>🗑️</div>
            <div style={{fontSize:14,color:'#e8e0d0',marginBottom:6,fontWeight:500}}>¿Eliminar notificación?</div>
            <div style={{fontSize:13,color:'#8a9bb5',marginBottom:20,lineHeight:1.5}}>Sale de tu bandeja. Si alguien te asignó la tarea, la seguirá viendo en su seguimiento.</div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>setConfirmEliminar(null)} style={{padding:'8px 20px',fontSize:13,fontWeight:600,color:'#fff',background:'#2563EB',border:'none',borderRadius:7,cursor:'pointer'}}>Cancelar</button>
              <button onClick={()=>{handleEliminar(confirmEliminar);setConfirmEliminar(null);}} style={{padding:'8px 18px',fontSize:13,fontWeight:500,color:'#f28b8b',background:'none',border:'1px solid rgba(229,115,115,0.4)',borderRadius:7,cursor:'pointer'}}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelApp;
